/** Production Canvas — the real interactive infinite canvas
 * (VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL).
 *
 * Thin orchestration layer over @xyflow/react: converts the backend's
 * layout DTOs to/from React Flow's node/edge model, wires the four node
 * creation interactions (spec §6), Scene grouping via `parentId` (spec
 * §8/§9), the collapsible Left/Right panels (spec §4), and a debounced
 * autosave (spec §15). Dragging a node ONLY ever changes `nodes` state
 * here — it never calls any business-mutation API. */

import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type OnConnectEnd,
  type OnConnectStart,
  type NodeMouseHandler,
  type OnNodeDrag,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Modal, message } from 'antd'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { CANVAS_NODE_TYPES } from './canvasNodeTypes'
import { isGenerationExpandFamily, usesContentMeasuredNodeGeometry } from './canvasNodeShellV2'
import {
  blockNativeCanvasDragStartIfNeeded,
  isImageAssetPickerDrag,
  isSupportedCanvasPaneDragOver,
  parseImageAssetDragPayload,
  PCV_IMAGE_ASSET_DND_MIME,
  PCV_NODE_TYPE_DND_MIME,
  setImageAssetDraggingChrome,
} from './canvasDragContract'
import {
  classifyDroppedFilesByMediaType,
  dragHasLikelyAcceptedMediaFile,
  dropGridColumns,
  isOsFileDrag,
} from './canvasFileDrop'
import { formatMaxSizeMb, isWithinCanvasMediaImportSizeLimit } from './canvasMediaImportLimits'
import {
  AUDIO_RECONCILIATION_LOOKBACK_MS,
  VIDEO_RECONCILIATION_LOOKBACK_MS,
  computeFileSha256Hex,
  attemptSiteAssetUploadReconciliation,
  findReconciledAssetMatch,
  findReconciledAssetMatchBySize,
  isAmbiguousUploadFailure,
  resolveOsFileDropImageUploadConcurrency,
} from './canvasMediaImportReconciliation'
import { isVisibleInAddNodeCatalog } from './canvasNodeCatalog'
import { isNodeCreatableInCatalog, isNodeOfflineInCatalog } from './nodeCatalogRuntime'
import { isQuickActionEnabled } from './canvasQuickActionRuntime'
import { canvasFieldsShallowEqual, patchCanvasFieldsIfChanged } from './canvasFieldsEqual'
import { shouldIgnoreCanvasShortcutEvent } from './canvasEditableKeyboardGuard'
import { isCanvasBlockingOverlayOpen } from './canvasBlockingOverlay'
import { clearVoiceReferenceRecordingDrafts } from '../../../voiceReference/voiceReferenceRecordingDraftStore'
import AuthMatrixLoadBanner from './AuthMatrixLoadBanner'
import LeftLibraryPanel from './LeftLibraryPanel'
import CanvasTopBar from './CanvasTopBar'
import GlobalAccountEntry from './GlobalAccountEntry'
import { registerCanvasAccountSwitchGuard } from '../../../accountSwitch/canvasAccountSwitchGuard'
import CanvasGlobalBrandBreath from './CanvasGlobalBrandBreath'
import { markCanvasBootstrap } from './canvasBootstrapTelemetry'
import {
  CANVAS_VIEWPORT_PERSIST_DEBOUNCE_MS,
  readPersistedCanvasViewport,
  writePersistedCanvasViewport,
} from './canvasViewportPersistence'
import CanvasContextMenu from './CanvasContextMenu'
import CanvasNodeContextMenu from './CanvasNodeContextMenu'
import QuickAddModal from './QuickAddModal'
import CanvasActionPopover from './CanvasActionPopover'
import CanvasVideoNodeDeleteModal from './CanvasVideoNodeDeleteModal'
import {
  deleteVideoAssetsForPlan,
  isVideoGeneratorNode,
  resolveVideoDeletePlan,
  type VideoNodeDeletePlan,
} from './canvasVideoNodeDelete'
import ConnectionSuggestPopup from './ConnectionSuggestPopup'
import NodeDetailShell from './nodeDetail/NodeDetailShell'
import { getNodeDetailRenderer, nodeTypeSupportsDetailPanel } from './nodeDetail/detailRendererRegistry'
import { markDownstreamStale, markNodeStaleIfHasResult } from './canvasDownstreamStale'
import {
  NODE_PLACEMENT_VERTICAL_NUDGE,
  DEFAULT_NODE_WIDTH_FALLBACK,
  GENERATE_IMAGE_EXPANDED_WIDTH_ESTIMATE,
  getNodeWidth,
  RESULT_SIBLING_CARD_WIDTH_PX,
  RESULT_SIBLING_GAP_PX,
  focusNodePreserveZoom,
  placeNodeBesideSource,
  placeNodeLeftOfTarget,
  placeResultSiblingsInRow,
  placeSiblingNodesBesideSource,
  placeSiblingNodesLeftOfTarget,
} from './canvasNodePlacement'
import { mapWithConcurrency, placeImageAssetGrid } from '../../../components/ImageAssetPicker/imageAssetPickerContract'
import { consumeJoinImageAssetSeed } from '../../imageAssetJoinCanvasSeed'
import { consumeJoinTextAssetSeed } from '../../textAssetJoinCanvasSeed'
import { TEXT_ASSET_CARD_WIDTH_PX } from './textAssetPresentation'
import { siteAssetUploadErrorMessage, uploadSiteAssetIngest } from '../../../siteAssetUpload'
import {
  normalizeFlowEdgesPromptEnhancementSource,
  normalizeHydratedCanvasEdgeDtos,
} from './canvasEdgeHandleHydration'
import {
  GENERIC_INPUT_HANDLE,
  TYPED_DATAFLOW_NODE_TYPES,
  VIDEO_KEYFRAME_END_HANDLE,
  VIDEO_KEYFRAME_MIDDLE_HANDLE,
  VIDEO_KEYFRAME_START_HANDLE,
  IMAGE_PROMPT_IN_HANDLE,
  VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
  VIDEO_PROMPT_ENHANCEMENT_HANDLE,
  imageReferenceAssetHandleId,
  isImageReferenceAssetHandle,
  isImagePromptInputHandle,
  isOriginalPromptInputHandle,
  isPromptEnhancementHandle,
  parseVideoDialogueAudioSegmentId,
  parseVideoKeyframeMiddleEntryId,
  videoDialogueAudioHandleId,
  videoKeyframeTargetHandleForEntry,
  getNodeIO,
  isDuplicateEdge,
  isVideoKeyframeHandle,
  isTryOnInputHandle,
  TRYON_GARMENT_HANDLE,
  TRYON_PERSON_HANDLE,
  validateDropZoneTarget,
  wouldCreateCycle,
  buildVideoStructuredAssetAssignment,
  buildVideoReferenceVideoAssignment,
  buildVideoAudioReferenceAssignment,
  isVideoStructuredAssetHandle,
  isVideoReferenceVideoHandle,
  isVideoAudioReferenceHandle,
  AUDIO_VOICE_TEMPLATE_IN_HANDLE,
  AUDIO_TEXT_INPUT_HANDLE,
  type ConnectedTryOnImage,
  type ConnectionVerdict,
  type DialogueAudioAssignment,
  type InputMaterial,
  type KeyframeAssignment,
  type KeyframeSlotMaterial,
} from './dataflow'
import {
  applyActiveVideoDropTargetOverride,
  assertCommittedDropMatchesPort,
  clearActiveVideoDropTarget,
  commitVideoInputConnection,
  logCanvasEdgeDiagnostic,
  setActiveVideoDropTarget,
  stripConflictingGenerateVideoEdges,
} from './canvasVideoInputConnection'
import {
  applyActiveScriptDropTargetOverride,
  clearActiveScriptDropTarget,
  setActiveScriptDropTarget,
} from './canvasScriptInputConnection'
import { resolveGenerateScriptDropTarget } from './scriptInputPorts'
import { resolveGenerateVideoDropTarget } from './videoInputPorts'
import { validateCanvasConnection } from './validateCanvasConnection'
import {
  buildScriptSourceEdgeFields,
  buildScriptSourceFromCreative,
  buildScriptSourceFromTextAsset,
  clearScriptSourceOnUnlink,
  mergeSupportingAssetsOnScriptSourceLink,
  patchScriptSourceFields,
  resolveScriptSourceContext,
  SCRIPT_SOURCE_EDGE_RELATION,
  type ScriptSourceKind,
} from './scriptSourceContext'
import { PINNED_EXPANDED_FIELD, isNodePinnedExpanded } from './useCanvasNodePin'
import { buildVideoContextFromShot, type InputAssetRef, type KeyframeTimelineEntry } from './videoKeyframeTimeline'
import {
  buildKeyframeImagePrompt,
  prioritizeRefsForKeyframe,
  referenceAssetsFingerprint,
  resolveStoryboardFormatInheritance,
  type SupportingAssetLike,
} from './videoShotReferenceAssets'
import { stripMentionsForEdge } from './assetMention'
import { resolveEdgeDisplayClass } from './canvasEdgeHighlight'
import { buildGenerationDuplicatePayload, isGenerationNodeType } from './canvasGenerationDuplicate'
import { buildRegenerateForkFields, copyIncomingEdgesForFork } from './canvasRegenerateFork'
import { normalizePersistedGenerationFields, stripTransientGenerationFields } from './generationInputFingerprint'
import {
  nodeFieldsIndicateActiveGeneration,
  resolveGenerationAssetDestination,
} from './generationAssetDestination'
import {
  countImageReferenceEdgesForTarget,
  parseImageReferenceCapability,
  relatedAssetsAddLimitMessage,
  referenceLimitConnectMessage,
  resolveCapabilityForGenerateImageNode,
  wouldExceedImageReferenceLimitOnConnect,
  type ImageReferenceCapability,
} from './imageGenerationReferenceCapability'
import { buildGenerateImageNodeDefaultFields } from './imageGenerationProvider'
import { buildGenerateVideoNodeDefaultFields } from './videoGenerationProvider'
import { buildGenerateTextNodeDefaultFields } from './generateTextCanvas'
import { wouldExceedGenerateTextReferenceLimitOnConnect } from './generateTextImageInput'
import {
  VIDEO_PROMPT_ENHANCEMENT_PURPOSE,
  VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
  VIDEO_PROMPT_ENHANCEMENT_SKILL_VERSION,
  VIDEO_PROMPT_ENHANCEMENT_DEFAULT_INSTRUCTION,
  PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION,
  PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION,
  buildVideoPromptEnhancementContextSnapshot,
  buildVideoPromptEnhancementWritebackPatch,
  clearEnhancedPromptPatch,
  isVideoPromptEnhancementPurpose,
  isPromptEnhancementFieldBindingRelation,
  looksLikePromptEnhancementRefusal,
} from './videoPromptEnhancement'
import { buildGenerateVoiceTemplateNodeDefaultFields } from './voiceTemplateProvider'
import { buildGenerateAudioNodeDefaultFields } from './audioGenerationProvider'
import { buildGenerateAudioLibraryVoiceTemplateReplacementFields } from './generateAudioVoiceTemplateReplacement'
import {
  audioOutputReady,
  audioOutputStale,
  buildConnectedVoiceTemplateMap,
  buildVoiceTemplateEdge,
  nodeTypeMapFromNodes,
  shouldAllowVoiceTemplateQuickCreate,
  stripVoiceTemplateEdgesToTarget,
  voiceTemplateEdgesToTarget,
} from './canvasVoiceTemplateBinding'
import { stripTextInputEdgesToTarget } from './canvasAudioTextInputBinding'
import { getNodeDefinition } from './nodeRegistry'
import { getCanvasNodeDeletionPolicy } from './canvasNodeDeletionPolicy'
import { useDebouncedSave } from './useDebouncedSave'
import {
  filterReadOnlyEdgeChanges,
  filterReadOnlyNodeChanges,
  installCanvasReadOnlyDomGuard,
  lockCanvasNodeData,
  readOnlyDeniedNoop,
  setCanvasReadOnlyDeniedNotifier,
} from './canvasReadOnly'
import { setCanvasReadOnlyNetworkFence } from './canvasReadOnlyFence'
import { useEffectiveTheme } from '../../../ui/themePreference'
import ImageAssetPicker from '../../../components/ImageAssetPicker/ImageAssetPicker'
import VideoAssetPicker from '../../../components/VideoAssetPicker/VideoAssetPicker'
import AudioAssetPicker from '../../../components/AudioAssetPicker/AudioAssetPicker'
import { toAudioAssetPickerSelection } from '../../../components/AudioAssetPicker/audioAssetPickerContract'
import AudioEditorModal, { type AudioEditorModalRequest } from './AudioEditorModal'
import {
  createVideoProductionAssembly,
  divergeScriptGeneration,
  divergeStoryboardGeneration,
  expandCreativeIdeation,
  generateCreativeIdeation,
  generateScriptGeneration,
  generateStoryboardGeneration,
  getCreativeIdeationRun,
  getImageGenerationConfig,
  getScriptGenerationRun,
  getStoryboardGenerationRun,
  recordCreativeCandidateEvent,
  listSiteAssets,
  listVideoAssets,
  resolveImagePickerSelection,
  videoGenTaskPosterUrl,
  type CreativeIdeationRunState,
  type ImageAssetPickerPurpose,
  type ImageAssetPickerSelection,
  type ImageGenerationConfig,
  type ImageGenerationTask,
  type ImageGenerationTaskOutput,
  type SaasApiError,
  type ScriptGenerationRunState,
  type ShotArtifact,
  type StoryboardArtifact,
  type StoryboardGenerationRunState,
  type AudioAssetItem,
  type VideoAssetItem,
  type VideoGenConfig,
  trimAudioAsset,
  fetchAudioTrimStatus,
  retryAudioTrim,
  uploadAudioAsset,
  uploadVideoAsset,
} from '../../../api'
import { isValidStoryboardArtifact } from './storyboardArtifactValidity'
import {
  briefFingerprint,
  buildBriefPayload,
  buildCandidateNodeFields,
  buildParentCandidatePayload,
  CREATIVE_CANDIDATE_EDGE_RELATION,
  markCandidatesStale,
} from './creativeIdeationCanvas'
import {
  applyDivergenceCandidateResult,
  buildDivergenceSiblingFields,
  buildResultFromCandidate,
  markGenerateCreativeResultStale,
} from './generateCreativeCanvas'
import {
  buildDivergeSiblingFields,
  buildScriptDivergePlaceholderFields,
  buildScriptInputPayload,
  applyGeneratedScript,
  CREATIVE_CONTEXT_EDGE_RELATION,
  liveCreativeContextFingerprint,
  type ScriptCreativeContextField,
  type ScriptSupportingAssetField,
} from './scriptGenerationCanvas'
import {
  buildImageGenerationBatchOriginPatch,
  buildImageGenerationBatchSiblingFields,
  buildImageGenerationSlotTaskFieldsPatch,
  completedSlotPatch,
  IMAGE_GENERATION_BATCH_COMMITTED_FIELD,
  IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD,
  IMAGE_GENERATION_BATCH_FOLLOWER_FIELD,
  IMAGE_GENERATION_BATCH_ID_FIELD,
  IMAGE_GENERATION_BATCH_ORIGIN_FIELD,
  IMAGE_GENERATION_BATCH_REQUESTED_FIELD,
  IMAGE_GENERATION_BATCH_SLOT_FIELD,
  imageGenerationBatchSlotIndex,
  isImageGenerationResultSlotRunning,
  outputsNeedingResultSlots,
  partitionImageGenerationSlotDeleteLock,
  planResultSlotReconciliation,
  requestedImageGenerationBatchCount,
  resetImageGenerationBatchFieldsPatch,
} from './imageGenerationBatchCanvas'
import { resolveCanvasImageTaskFieldStatus } from './imageGenerationProvider'
import {
  evaluateScriptGenerationInputReadiness,
  isCreativePayloadReadyForScriptLink,
  isTextAssetPayloadReadyForScriptLink,
} from './scriptGenerationReadiness'
import {
  applyGeneratedStoryboard,
  buildScriptContextDragPayloadFromScriptNode,
  buildScriptContextField,
  buildStoryboardDivergePlaceholderFields,
  buildStoryboardDivergeSiblingResultFields,
  buildStoryboardFieldsWithScriptContext,
  buildStoryboardInputPayload,
  readStoryboardRunFields,
  clearScriptContextOnUnlink,
  demoteNonCurrentStoryboardResult,
  hasValidStructuredScriptContext,
  liveScriptContextFingerprint,
  reconcileStoryboardResultAfterContextChange,
  SCRIPT_CONTEXT_EDGE_RELATION,
  type StoryboardScriptContextField,
} from './storyboardGenerationCanvas'
import { enrichShotWithEntityBindings, resolveStoryboardEntityBindings } from './storyboardEntityBindings'
import {
  isProductionFormatComplete,
  readProductionFormat,
  resolutionToQualityTier,
  buildLockedProductionFormatFields,
  buildStandaloneVideoFormatFields,
  classifyProductionFormatChange,
  deriveProductionFormatFromVideoFields,
  type ProductionFormatChangeImpact,
  type StoryboardProductionFormat,
} from './storyboardProductionFormat'
import {
  localizeStoryboardGenerationError,
  logStoryboardGenerationErrorDev,
} from './storyboardGenerationErrors'
import {
  beginStoryboardRegenerateOrchestratorPatch,
  buildDetachedVideoNodeFieldsPatch,
  collectStoryboardDerivedCanvasNodeIds,
  finalizeStoryboardRegenerateSuccess,
  readSupersededStoryboardSnapshot,
  restoreSupersededStoryboardFields,
  type StoryboardRegenerateMode,
} from './storyboardRegenerateCanvas'
import {
  buildAssemblyDeliveryContract,
  buildAssemblyManifest,
  buildFinalVideoArtifactSnapshot,
  deriveShotUnits,
  ensureVideoProductionShotBinding,
  readVideoProductionShotBindings,
  resolveCanonicalShotGenerator,
  type BoundVideoNodeSnapshot,
  type ShotBindingMap,
} from './videoProductionCanvas'
import {
  buildRelatedAssetFromImageNode,
  buildCreativePayloadFromNode,
  mergeRelatedAssetIntoFields,
  markGenerationStaleIfHasResult,
  removeRelatedAssetFromFields,
  replaceRelatedAssetsInFields,
  resolveCanvasInputSlot,
  RELATED_ASSET_EDGE_RELATION,
  nodeTypeOf,
  CANVAS_INPUT_COMPATIBILITY_MATRIX,
  scriptNeedsCreativeContextReconcile,
  storyboardNeedsScriptContextReconcile,
} from './canvasInputBinding'
import {
  bareVideoAssetEntityId,
  resolveBoundVideoPlaybackUrl,
  videoAssetSnapshotFromItem,
  type VideoAssetBindSnapshot,
} from './canvasVideoAssetResolve'
import { loadVideoGenConfigDeduped, loadAudioGenerationParameterCapabilityDeduped } from './canvasSharedConfigLoaders'
import { saveVoiceTemplateGenerationPreference, type AudioGenerationParameterCapabilityResponse } from '../../../api'
import type {
  CanvasAssetPickerRequest,
  CanvasTryOnAssetPickerRequest,
  CanvasVideoAssetPickerRequest,
  CanvasAudioAssetPickerRequest,
  CanvasEdgeDto,
  CanvasNodeDto,
  CanvasViewportDto,
  CreativeContextDragPayload,
  FlowNodeData,
  ProductionCanvasDto,
  ScriptContextDragPayload,
} from './types'
import './production-canvas.css'

const NODE_TYPES = CANVAS_NODE_TYPES

/** VISTOERA_CANVAS_GLOBAL_EMPTY_NODE_CLOSE_DIRECT_DELETE_AND_UNIFIED_
 * DELETION_CONFIRMATION_POLICY_P0_V1 (TASK-00096) — the per-family
 * `DELETE_CONFIRM_FAMILIES` allowlist this constant used to be (added by
 * VISTOERA_PRODUCTION_CANVAS_IMAGE_TO_IMAGE_GENERATION_FLOW_V1 §7/§31/§32,
 * extended by VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_
 * INTERACTION_V2) is GONE — confirmation is no longer "does this node's
 * family happen to be on a hardcoded list", it is "does this SPECIFIC
 * node already hold meaningful content", via the single canonical
 * `getCanvasNodeDeletionPolicy` (see `canvasNodeDeletionPolicy.ts`),
 * shared by the header × (`onRequestDeleteNode`), the right-click node
 * context menu (same function), and single-node keyboard Delete/
 * Backspace (`onNodesChange` below). Multi-node deletion still ALWAYS
 * confirms regardless of content (§38/§39) — see the `removeIds.length
 * > 1` branch below. */

/** VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
 * LINKING_V1 — shared active-run guard for both `script_generation` run
 * kinds (GENERATE's `runStatus` and DIVERGE's `divergeRunStatus`),
 * mirroring the inline `activeStatuses` set the Creative Ideation
 * callbacks above each construct locally. */
const ACTIVE_RUN_STATUSES_SCRIPT = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
const ACTIVE_RUN_STATUSES_STORYBOARD = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])

interface SavePayload {
  nodes: CanvasNodeDto[]
  edges: CanvasEdgeDto[]
  viewport: CanvasViewportDto
  /** Fencing token — autosave must only write when this matches the
   * currently routed projectId (project-switch P0). */
  projectId: string
}

interface Props {
  canvas: ProductionCanvasDto
  /** True while the route is still fetching canvas JSON — board mounts
   * immediately but skips autosave until hydration completes. */
  canvasHydrating?: boolean
  workspaceId: string
  /** VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
   * §16–§22 — the Canvas top bar needs these to render/rename the
   * project itself; passed straight through from `ProductionCanvasPage`
   * (which owns the `ContentProject` fetch/PATCH, this component never
   * talks to that API directly). */
  projectId: string
  projectTitle: string
  onRenameProject: (title: string) => Promise<void>
  /** Fixed Canvas Header "复制项目" (VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2
   * §41) — clones the whole `ContentProject` server-side and navigates
   * to the new project's own Canvas; owned by `ProductionCanvasPage`
   * (which knows how to `navigate()`), this component only renders the
   * button. */
  onDuplicateProject: () => Promise<void>
  /** VistoeraTASK-00250 — "项目操作" menu's other two actions, same
   * ownership split as `onDuplicateProject` above (API call + navigate
   * live in `ProductionCanvasPage`, this component only renders the
   * trigger/menu). */
  onCreateProject: () => Promise<void>
  onDeleteProject: () => Promise<void>
  /** The workspace's own default Site (spec §4) — `null` while it's
   * still loading; the Image Picker integration on `image_asset`/
   * `generate_image` nodes simply stays inert (no picker button/no
   * reference thumbnails) until this resolves. */
  siteId: string | null
  onSave: (payload: SavePayload) => Promise<void>
  /** Deep link from Overview recent-task "前往来源" — focus + select node once hydrated. */
  initialFocusNodeId?: string | null
  /** VistoeraTASK-00556 — READ_ONLY access mode (another member's TEAM_SHARED
   * project, or the Owner's management view). Same Canvas, write paths off. */
  readOnly?: boolean
  /** Creator display name for the read-only banner. */
  readOnlyCreatorName?: string | null
}

function dtoToFlowNode(dto: CanvasNodeDto): Node {
  const def = getNodeDefinition(dto.node_type)
  const data: FlowNodeData = {
    nodeType: dto.node_type,
    category: dto.category,
    entityType: dto.entity_type,
    entityId: dto.entity_id,
    collapsed: dto.collapsed,
    fields: normalizePersistedGenerationFields((dto.data || {}) as Record<string, unknown>),
    latestRunId: dto.latest_run_id,
  }
  const node: Node = {
    id: dto.id,
    type: def.family,
    position: dto.position,
    data: data as unknown as Record<string, unknown>,
    zIndex: dto.z_index || undefined,
  }
  if (dto.parent_node_id) {
    node.parentId = dto.parent_node_id
    node.extent = 'parent'
  }
  // Shell V2 ghost-hit contract: generation / video_media cards measure from
  // live DOM. Never restore persisted width/height onto the RF wrapper —
  // stale expanded heights become invisible hit areas below Header+Result.
  if (!usesContentMeasuredNodeGeometry(def.family)) {
    if (dto.width != null) node.width = dto.width
    if (dto.height != null) node.height = dto.height
  }
  return node
}

function flowNodeToDto(n: Node): CanvasNodeDto {
  const data = n.data as unknown as FlowNodeData
  const contentMeasured = usesContentMeasuredNodeGeometry(n.type)
  return {
    id: n.id,
    node_type: data.nodeType,
    category: data.category,
    entity_type: data.entityType,
    entity_id: data.entityId,
    parent_node_id: n.parentId ?? null,
    position: { x: n.position.x, y: n.position.y },
    // Do not bake expand/collapse transient measurements into canvas JSON.
    width: contentMeasured
      ? null
      : ((n.width as number | undefined) ?? (n.measured?.width as number | undefined) ?? null),
    height: contentMeasured
      ? null
      : ((n.height as number | undefined) ?? (n.measured?.height as number | undefined) ?? null),
    collapsed: !!data.collapsed,
    z_index: n.zIndex ?? 0,
    data: stripTransientGenerationFields((data.fields || {}) as Record<string, unknown>),
    ui_state: {},
    latest_run_id: data.latestRunId,
  }
}

function dtoToFlowEdge(dto: CanvasEdgeDto): Edge {
  return {
    id: dto.id,
    source: dto.source_node_id,
    target: dto.target_node_id,
    sourceHandle: dto.source_handle ?? undefined,
    targetHandle: dto.target_handle ?? undefined,
    data: { relationType: dto.relation_type },
    className: 'pcv-edge',
  }
}

function hydratedCanvasEdgesToFlowEdges(nodes: CanvasNodeDto[], edges: CanvasEdgeDto[]): Edge[] {
  const { edges: normalized } = normalizeHydratedCanvasEdgeDtos(nodes, edges)
  return normalizeFlowEdgesPromptEnhancementSource(normalized.map(dtoToFlowEdge))
}

function flowEdgeToDto(e: Edge): CanvasEdgeDto {
  return {
    id: e.id,
    source_node_id: e.source,
    target_node_id: e.target,
    source_handle: e.sourceHandle ?? null,
    target_handle: e.targetHandle ?? null,
    relation_type: ((e.data as { relationType?: string } | undefined)?.relationType as CanvasEdgeDto['relation_type']) || 'INPUT_TO',
  }
}

// LEGACY gate (spec §7, pre-P0) — "prevent obviously invalid connections
// where practical", deliberately narrow: block an OUTPUT-stage node
// feeding back into raw CONTENT input material (the spec's own
// "Performance -> Character" example). Still used, UNCHANGED, for every
// node type NOT in `TYPED_DATAFLOW_NODE_TYPES` (Scene, Character,
// Dialogue, Virtual IP, generate_production_plan, publish, ...) — P0
// §54 "不要扩大范围" deliberately leaves those on the old heuristic
// rather than folding everything into the new typed contract this
// round. The old `image-ref-in`-specific special case that lived here
// is GONE — every node type that ever used that handle id
// (`generate_image`/`generate_video`/`generate_text`) is now in
// `TYPED_DATAFLOW_NODE_TYPES`, so `onConnect` below never reaches this
// function for that handle anymore; see `dataflow.ts`'s own doc comment
// for the root-cause writeup of why that special case was both too
// strict (rejected legitimate gen-to-gen) and too loose (never rejected
// a generator feeding into an Asset node's plain target handle).
const LEGACY_RAW_INPUT_TYPES = new Set(['character', 'virtual_ip', 'reference_content', 'dialogue'])

function legacyIsConnectionAllowed(sourceType: string, targetType: string, t: TFunction<'canvas'>): ConnectionVerdict {
  if (sourceType === targetType && LEGACY_RAW_INPUT_TYPES.has(sourceType)) return { ok: true }
  const sourceDef = getNodeDefinition(sourceType)
  if (sourceDef.category === 'output' && LEGACY_RAW_INPUT_TYPES.has(targetType)) {
    return { ok: false, reason: t('board.connectionInvalid') }
  }
  return { ok: true }
}

/** Single dispatch point (P0 §1–§8, extended by
 * VISTOERA_CONTENT_CANVAS_VIDEO_NODE_DROPZONE_PORT_MODEL_SIZE_AND_BACKGROUND_FIX_P0
 * §7 with the `targetHandle` parameter) — Typed Dataflow Contract when
 * BOTH sides participate in it, the untouched legacy heuristic
 * otherwise. `targetHandle` only ever changes the verdict for
 * `generate_video`'s three KEYFRAME handles (see
 * `validateDropZoneTarget`) — every other target handle (the shared
 * generic Reference Materials handle, or any node type outside the
 * typed contract) is unaffected, so this stays a pure superset of the
 * old two-argument behavior. */
const INGESTION_MATRIX_SOURCES = new Set(CANVAS_INPUT_COMPATIBILITY_MATRIX.map((r) => r.sourceType))
const INGESTION_MATRIX_TARGETS = new Set(CANVAS_INPUT_COMPATIBILITY_MATRIX.map((r) => r.targetType))

function evaluateConnection(sourceType: string, targetType: string, targetHandle: string | null | undefined, t: TFunction<'canvas'>): ConnectionVerdict {
  // Structured Input Ingestion Contract — if both sides appear in the
  // compatibility inventory, require an explicit supported slot. Avoids
  // "visual edge OK, business state empty" for known generation cards.
  if (INGESTION_MATRIX_SOURCES.has(sourceType) && INGESTION_MATRIX_TARGETS.has(targetType)) {
    const slot = resolveCanvasInputSlot(sourceType, targetType)
    if (!slot) {
      return { ok: false, reason: '此资产不能作为该节点的输入' }
    }
  }
  if (TYPED_DATAFLOW_NODE_TYPES.has(sourceType) && TYPED_DATAFLOW_NODE_TYPES.has(targetType)) {
    return validateDropZoneTarget(sourceType, targetType, targetHandle, t)
  }
  return legacyIsConnectionAllowed(sourceType, targetType, t)
}

function genId(): string {
  return `n-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`
}

function extractTextFromTextSourceNode(sourceNode: Node, sourceType: string): string {
  const fields = ((sourceNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
  if (sourceType === 'text_asset') return String(fields.text ?? '').trim()
  if (sourceType === 'generate_text') {
    return String((fields.currentResult as { text?: string } | null | undefined)?.text || '').trim()
  }
  return ''
}

export default function CanvasBoard({
  canvas,
  canvasHydrating = false,
  workspaceId,
  projectId,
  projectTitle,
  onRenameProject,
  onDuplicateProject,
  onCreateProject,
  onDeleteProject,
  siteId,
  onSave,
  initialFocusNodeId,
  readOnly = false,
  readOnlyCreatorName = null,
}: Props) {
  // Canvas Light/Dark Theme (spec §39) — follows the SAME Vistoera
  // global theme preference every other tenant page already reads,
  // never a separate Canvas-owned toggle. `colorMode` only affects
  // @xyflow/react's own internal CSS variables; every `.pcv-*` class
  // below reacts to the `data-theme` attribute this hook mirrors,
  // via `production-canvas.css`'s own `[data-theme="light"]` overrides.
  const effectiveTheme = useEffectiveTheme()
  const { t, i18n } = useTranslation('canvas')
  const [imageGenerationConfig, setImageGenerationConfig] = useState<ImageGenerationConfig | null>(null)
  const [videoGenerationConfig, setVideoGenerationConfig] = useState<VideoGenConfig | null>(null)
  const [imageReferenceCapability, setImageReferenceCapability] = useState<ImageReferenceCapability>(() =>
    parseImageReferenceCapability(null),
  )
  const imageGenerationConfigRef = useRef(imageGenerationConfig)
  imageGenerationConfigRef.current = imageGenerationConfig
  const imageReferenceCapabilityRef = useRef(imageReferenceCapability)
  imageReferenceCapabilityRef.current = imageReferenceCapability

  // VistoeraTASK-00217 — board-level canonical read of the Generate Audio
  // node's Pitch/Speed Provider Capability. Used here (not by the node
  // itself) specifically so `onBindAudioVoiceTemplate` below can resolve
  // Voice Template Parameter Memory's precedence-3 fallback ("Provider
  // Capability default") synchronously at bind time. `GenerateAudioNode`
  // independently calls the same deduped loader for its own Slider
  // rendering, so both collapse into one request per workspace.
  const [audioGenerationParameterCapability, setAudioGenerationParameterCapability] =
    useState<AudioGenerationParameterCapabilityResponse | null>(null)
  const audioGenerationParameterCapabilityRef = useRef(audioGenerationParameterCapability)
  audioGenerationParameterCapabilityRef.current = audioGenerationParameterCapability

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    getImageGenerationConfig(workspaceId)
      .then(({ data }) => {
        if (!cancelled) {
          setImageGenerationConfig(data)
          setImageReferenceCapability(parseImageReferenceCapability(data))
        }
      })
      .catch(() => {
        /* Keep safe default (max=1) when config is unreachable. */
      })
    // P0-C — board is the canonical owner of this fetch; routed through
    // the same single-flight deduped loader every `generate_video`
    // node's own fallback effect uses, so this board-level call and any
    // racing per-node fallback for the same workspace always collapse
    // into exactly one real request (see `canvasSharedConfigLoaders.ts`).
    loadVideoGenConfigDeduped(workspaceId)
      .then((data) => {
        if (!cancelled) setVideoGenerationConfig(data)
      })
      .catch(() => {
        /* Video provider selector degrades until config loads. */
      })
    loadAudioGenerationParameterCapabilityDeduped(workspaceId)
      .then((data) => {
        if (!cancelled) setAudioGenerationParameterCapability(data)
      })
      .catch(() => {
        // Pitch/Speed Sliders degrade to hidden (never fabricate bounds)
        // until the capability loads — same "fail safe, not fake" rule
        // as every other capability-driven Canvas surface.
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const [nodes, setNodes] = useState<Node[]>(() => canvas.nodes.map(dtoToFlowNode))
  const [edges, setEdges] = useState<Edge[]>(() => hydratedCanvasEdgesToFlowEdges(canvas.nodes, canvas.edges))
  const nodesRef = useRef(nodes)
  const edgesRef = useRef(edges)
  const persistedViewportRef = useRef<CanvasViewportDto | null>(
    readPersistedCanvasViewport(workspaceId, projectId),
  )
  const [viewport, setViewport] = useState<CanvasViewportDto>(
    () => persistedViewportRef.current ?? canvas.viewport,
  )
  const viewportRef = useRef(viewport)
  const viewportPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Must start null so the first hydrate (including legacy edge→assets
  // reconcile) always runs. Initializing to `canvas.id` skipped reconcile
  // when the board mounted with an already-loaded document.
  const hydratedCanvasIdRef = useRef<string | null>(null)
  /** When load-time orphan reconcile mutates nodes/edges, queue one autosave
   * so Creative→Script context + DERIVED_FROM upgrade survive refresh. */
  const pendingHydratePersistRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(null)
  const firstNodeMarkedRef = useRef(false)
  const firstImageMarkedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // Poll timers live here so `performDeleteNodes` can stop caring about a
  // run the moment its Canvas node is removed (background job continues).
  const ideationPollTimersRef = useRef<Map<string, number>>(new Map())
  const scriptPollTimersRef = useRef<Map<string, number>>(new Map())
  const storyboardPollTimersRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    if (canvasHydrating || !canvas.id) return
    // Always replace local RF state when the server document identity
    // changes (project switch / first hydrate). The previous
    // `nodesRef.length === 0` gate left old-project nodes on screen
    // after navigate() — chrome switched, graph did not.
    if (hydratedCanvasIdRef.current === canvas.id) return
    markCanvasBootstrap('CANVAS_NODE_HYDRATION_START_AT')
    // Legacy reconcile: image_asset → generate_creative INPUT_TO edges
    // that never wrote fields.assets get repaired once on load.
    // Also repair Creative → Script orphan edges (visual wire without
    // creativeContext) — the P0 EDGE_EXISTS ≠ CONTEXT_EXISTS failure.
    {
      const flowNodes = canvas.nodes.map(dtoToFlowNode)
      let flowEdges = hydratedCanvasEdgesToFlowEdges(canvas.nodes, canvas.edges)
      let anyReconciled = false
      let reconciledNodes = flowNodes.map((n) => {
        const d = n.data as unknown as FlowNodeData
        if (
          d.nodeType !== 'generate_creative' &&
          d.nodeType !== 'project_start' &&
          d.nodeType !== 'script_generation' &&
          d.nodeType !== 'storyboard_generation'
        ) {
          return n
        }
        let fields = { ...(d.fields || {}) }
        let changed = false
        for (const e of flowEdges) {
          if (e.target !== n.id) continue
          const relation = (e.data as { relationType?: string } | undefined)?.relationType
          if (relation !== 'INPUT_TO' && relation !== RELATED_ASSET_EDGE_RELATION && relation !== 'REFERENCES') continue
          const source = flowNodes.find((s) => s.id === e.source)
          if (!source) continue
          const asset = buildRelatedAssetFromImageNode(source)
          if (!asset) continue
          const beforeKey = d.nodeType === 'script_generation' || d.nodeType === 'storyboard_generation' ? 'supportingAssets' : 'assets'
          const before = Array.isArray(fields[beforeKey]) ? (fields[beforeKey] as { asset_id: string }[]).length : 0
          fields = mergeRelatedAssetIntoFields(fields, asset, d.nodeType)
          const after = Array.isArray(fields[beforeKey]) ? (fields[beforeKey] as { asset_id: string }[]).length : 0
          if (after > before) changed = true
        }
        if (!changed) return n
        anyReconciled = true
        return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
      })

      // Creative → Script Primary Context hydrate for orphan edges.
      for (const n of reconciledNodes) {
        if (!scriptNeedsCreativeContextReconcile(n, flowEdges, reconciledNodes)) continue
        const edge = flowEdges.find((e) => {
          if (e.target !== n.id) return false
          const relation = (e.data as { relationType?: string } | undefined)?.relationType
          if (relation !== CREATIVE_CONTEXT_EDGE_RELATION && relation !== SCRIPT_SOURCE_EDGE_RELATION && relation !== 'INPUT_TO') {
            return false
          }
          const source = reconciledNodes.find((s) => s.id === e.source)
          const st = nodeTypeOf(source)
          return st === 'generate_creative' || st === 'creative_candidate' || st === 'text_asset'
        })
        if (!edge) continue
        const source = reconciledNodes.find((s) => s.id === edge.source)
        if (!source) continue
        const d = n.data as unknown as FlowNodeData
        const scriptFields = { ...(d.fields || {}) } as Record<string, unknown>
        const st = nodeTypeOf(source)
        let scriptSource: import('./scriptSourceContext').ScriptSourceContext | null = null
        let mergedAssets: ScriptSupportingAssetField[] = Array.isArray(scriptFields.supportingAssets)
          ? (scriptFields.supportingAssets as ScriptSupportingAssetField[])
          : []
        const previousSource =
          (scriptFields.scriptSourceContext as { sourceNodeId?: string } | null)?.sourceNodeId ??
          (scriptFields.creativeContext as ScriptCreativeContextField | null)?.sourceNodeId ??
          null
        if (st === 'text_asset') {
          scriptSource = buildScriptSourceFromTextAsset(source)
          mergedAssets = mergeSupportingAssetsOnScriptSourceLink(mergedAssets, [], previousSource)
        } else {
          const payload = buildCreativePayloadFromNode(source)
          if (!payload || (!payload.title && !payload.premise)) continue
          const briefNode = payload.assetsSourceNodeId
            ? reconciledNodes.find((s) => s.id === payload.assetsSourceNodeId)
            : source
          const briefFields = ((briefNode?.data as unknown as FlowNodeData | undefined)?.fields || {}) as Record<string, unknown>
          const briefAssets = Array.isArray(briefFields.assets) ? (briefFields.assets as Array<Record<string, unknown>>) : []
          const sourceKind: ScriptSourceKind = st === 'creative_candidate' ? 'creative_candidate' : 'generate_creative'
          const built = buildScriptSourceFromCreative(payload, briefAssets, sourceKind)
          scriptSource = built.scriptSource
          mergedAssets = mergeSupportingAssetsOnScriptSourceLink(mergedAssets, built.creativeContext.linkedAssetRefs, previousSource)
        }
        if (!scriptSource) continue
        anyReconciled = true
        reconciledNodes = reconciledNodes.map((node) =>
          node.id === n.id
            ? {
                ...node,
                data: {
                  ...d,
                  fields: patchScriptSourceFields(scriptFields, scriptSource!, mergedAssets),
                } as unknown as Record<string, unknown>,
              }
            : node,
        )
        flowEdges = [
          ...flowEdges.filter((e) => !(e.target === n.id && e.source === edge.source)),
          {
            id: edge.id || genId(),
            ...buildScriptSourceEdgeFields({
              sourceNodeId: edge.source,
              targetNodeId: n.id,
              sourceHandle: st === 'text_asset' ? 'text-out' : undefined,
            }),
          },
        ]
      }

      // Script → Storyboard Primary Context hydrate for orphan edges
      // (visual DERIVED_FROM without fields.scriptContext).
      for (const n of reconciledNodes) {
        if (!storyboardNeedsScriptContextReconcile(n, flowEdges, reconciledNodes)) continue
        const edge = flowEdges.find((e) => {
          if (e.target !== n.id) return false
          const relation = (e.data as { relationType?: string } | undefined)?.relationType
          if (relation !== SCRIPT_CONTEXT_EDGE_RELATION && relation !== 'INPUT_TO') return false
          return nodeTypeOf(reconciledNodes.find((s) => s.id === e.source)) === 'script_generation'
        })
        if (!edge) continue
        const source = reconciledNodes.find((s) => s.id === edge.source)
        if (!source) continue
        const sourceFields = ((source.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const payload = buildScriptContextDragPayloadFromScriptNode(source.id, sourceFields)
        if (!payload) continue
        const scriptSupporting = Array.isArray(sourceFields.supportingAssets)
          ? (sourceFields.supportingAssets as Array<Record<string, unknown>>)
          : []
        const contextField = buildScriptContextField(payload, scriptSupporting)
        const d = n.data as unknown as FlowNodeData
        const sbFields = { ...(d.fields || {}) } as Record<string, unknown>
        const existingAssets = Array.isArray(sbFields.supportingAssets)
          ? (sbFields.supportingAssets as Array<Record<string, unknown>>)
          : []
        const existingKeys = new Set(existingAssets.map((a) => (a.asset_id as string) || (a.display_name as string)))
        const mergedAssets = [
          ...existingAssets,
          ...contextField.linkedAssetRefs.filter((a) => !existingKeys.has(a.asset_id || a.display_name)),
        ]
        const reconciledFields = reconcileStoryboardResultAfterContextChange({
          ...sbFields,
          scriptContext: contextField,
          supportingAssets: mergedAssets,
        })
        anyReconciled = true
        reconciledNodes = reconciledNodes.map((node) =>
          node.id === n.id
            ? {
                ...node,
                data: {
                  ...d,
                  fields: reconciledFields,
                } as unknown as Record<string, unknown>,
              }
            : node,
        )
        flowEdges = [
          ...flowEdges.filter(
            (e) =>
              !(
                e.target === n.id &&
                nodeTypeOf(reconciledNodes.find((s) => s.id === e.source)) === 'script_generation'
              ),
          ),
          {
            id: edge.id || genId(),
            source: payload.sourceNodeId,
            target: n.id,
            data: { relationType: SCRIPT_CONTEXT_EDGE_RELATION },
            className: 'pcv-edge',
          },
        ]
      }

      // Orchestrator → Shot Generate Video edge hydrate from shotBindings
      // (P0_V1 §6/§36). Canvas DTO edges only persist relation_type — shot
      // identity lives on shotBindings; hydrate restores visible wires +
      // in-memory shotId metadata after refresh. Owner: `storyboard_generation`.
      for (const n of reconciledNodes) {
        const orchestratorType = nodeTypeOf(n)
        if (orchestratorType !== 'storyboard_generation') continue
        const sourceHandle = 'storyboard-generation-out'
        const d = n.data as unknown as FlowNodeData
        const bindings = readVideoProductionShotBindings((d.fields || {}) as Record<string, unknown>)
        for (const [shotId, binding] of Object.entries(bindings)) {
          const generatorNodeId = binding?.advancedVideoNodeId
          if (!generatorNodeId || !shotId) continue
          const generator = reconciledNodes.find((g) => g.id === generatorNodeId)
          if (!generator || nodeTypeOf(generator) !== 'generate_video') continue
          const shotData = {
            relationType: 'DERIVED_FROM',
            shotId,
            sourceVideoMergeNodeId: n.id,
            sourceShotId: shotId,
            targetGenerateVideoNodeId: generatorNodeId,
          }
          const existingIdx = flowEdges.findIndex(
            (e) =>
              e.source === n.id &&
              e.target === generatorNodeId &&
              ((e.data as { shotId?: string } | undefined)?.shotId === shotId ||
                (e.data as { shotId?: string } | undefined)?.shotId == null),
          )
          if (existingIdx >= 0) {
            const existing = flowEdges[existingIdx]
            const cur = (existing.data || {}) as Record<string, unknown>
            if (cur.shotId === shotId && existing.sourceHandle === sourceHandle) continue
            anyReconciled = true
            flowEdges = flowEdges.map((e, i) =>
              i === existingIdx
                ? {
                    ...e,
                    sourceHandle: e.sourceHandle || sourceHandle,
                    targetHandle: e.targetHandle || GENERIC_INPUT_HANDLE,
                    data: { ...cur, ...shotData },
                    className: e.className || 'pcv-edge',
                  }
                : e,
            )
            continue
          }
          anyReconciled = true
          flowEdges = [
            ...flowEdges.filter(
              (e) =>
                !(
                  e.source === n.id &&
                  (e.data as { shotId?: string } | undefined)?.shotId === shotId &&
                  e.target !== generatorNodeId
                ),
            ),
            {
              id: genId(),
              source: n.id,
              sourceHandle,
              target: generatorNodeId,
              targetHandle: GENERIC_INPUT_HANDLE,
              data: shotData,
              className: 'pcv-edge',
            },
          ]
        }
      }

      for (const n of reconciledNodes) {
        const d = n.data as unknown as FlowNodeData
        if (d.nodeType !== 'generate_voice') continue
        let fields = { ...(d.fields || {}) } as Record<string, unknown>
        let changed = false
        const pending = fields.pendingVoiceTemplateCandidateNodeId as string | null | undefined
        if (pending && !reconciledNodes.some((candidate) => candidate.id === pending)) {
          fields.pendingVoiceTemplateCandidateNodeId = null
          changed = true
        }
        const rawError = String(fields.errorMessage || '')
        if (
          fields.status === 'FAILED' &&
          fields.selectedVoiceTemplateId &&
          fields.selectedVoiceTemplateExecutable !== false &&
          /don't have access to this voice|voice not found|invalid voice/i.test(rawError)
        ) {
          fields.selectedVoiceTemplateExecutable = false
          changed = true
        }
        if (!changed) continue
        anyReconciled = true
        reconciledNodes = reconciledNodes.map((node) =>
          node.id === n.id
            ? { ...node, data: { ...d, fields } as unknown as Record<string, unknown> }
            : node,
        )
      }

      // Demote stale/invalid/mismatched Storyboard current results (keep history).
      for (const n of reconciledNodes) {
        if (nodeTypeOf(n) !== 'storyboard_generation') continue
        const d = n.data as unknown as FlowNodeData
        const before = { ...(d.fields || {}) } as Record<string, unknown>
        const after = demoteNonCurrentStoryboardResult(before)
        if (after === before || JSON.stringify(after) === JSON.stringify(before)) continue
        anyReconciled = true
        reconciledNodes = reconciledNodes.map((node) =>
          node.id === n.id
            ? { ...node, data: { ...d, fields: after } as unknown as Record<string, unknown> }
            : node,
        )
      }

      setNodes(anyReconciled ? reconciledNodes : flowNodes)
      setEdges(flowEdges)
      nodesRef.current = anyReconciled ? reconciledNodes : flowNodes
      edgesRef.current = flowEdges
      if (anyReconciled) {
        pendingHydratePersistRef.current = { nodes: reconciledNodes, edges: flowEdges }
      }
    }
    if (!persistedViewportRef.current) {
      setViewport(canvas.viewport)
    }
    if (canvas.nodes.length > 0) {
      firstNodeMarkedRef.current = true
      markCanvasBootstrap('CANVAS_FIRST_MEANINGFUL_NODE_READY_AT')
    }
    hydratedCanvasIdRef.current = canvas.id
    markCanvasBootstrap('CANVAS_NODE_HYDRATION_COMPLETE_AT')
  }, [canvas, canvasHydrating])

  useEffect(() => {
    if (firstNodeMarkedRef.current || nodes.length === 0) return
    firstNodeMarkedRef.current = true
    markCanvasBootstrap('CANVAS_FIRST_MEANINGFUL_NODE_READY_AT')
  }, [nodes.length])

  useEffect(() => {
    if (firstImageMarkedRef.current) return
    const area = containerRef.current
    if (!area) return

    const markFirstImage = () => {
      if (firstImageMarkedRef.current) return
      firstImageMarkedRef.current = true
      markCanvasBootstrap('CANVAS_ASSET_REQUEST_START_AT')
      markCanvasBootstrap('CANVAS_FIRST_IMAGE_READY_AT')
    }

    const imgs = area.querySelectorAll('img')
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        markFirstImage()
      } else {
        img.addEventListener('load', markFirstImage, { once: true })
      }
    })

    const observer = new MutationObserver(() => {
      area.querySelectorAll('img').forEach((img) => {
        img.addEventListener('load', markFirstImage, { once: true })
      })
    })
    observer.observe(area, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [nodes])

  const scheduleViewportPersist = useCallback(
    (nextViewport: CanvasViewportDto) => {
      if (viewportPersistTimerRef.current) {
        window.clearTimeout(viewportPersistTimerRef.current)
      }
      viewportPersistTimerRef.current = window.setTimeout(() => {
        writePersistedCanvasViewport(workspaceId, projectId, nextViewport)
        viewportPersistTimerRef.current = null
      }, CANVAS_VIEWPORT_PERSIST_DEBOUNCE_MS)
    },
    [workspaceId, projectId],
  )

  useEffect(() => {
    const flushViewport = () => {
      writePersistedCanvasViewport(workspaceId, projectId, viewportRef.current)
    }
    window.addEventListener('pagehide', flushViewport)
    return () => {
      window.removeEventListener('pagehide', flushViewport)
      if (viewportPersistTimerRef.current) {
        window.clearTimeout(viewportPersistTimerRef.current)
      }
    }
  }, [workspaceId, projectId])

  // Always-current mirrors of `nodes`/`edges`, read by handlers below
  // instead of the `nodes`/`edges` closure variables or `rf.getNodes()`/
  // `getEdges()` (both are stale/racy immediately after a `setState`
  // call, before React re-renders). Written synchronously by
  // `commitNodes`/`commitEdges` on every mutation; the effects here are
  // just a safety net for the rare external update to `nodes`/`edges`.
  useEffect(() => {
    nodesRef.current = nodes
  }, [nodes])
  useEffect(() => {
    edgesRef.current = edges
  }, [edges])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  // Node Detail Shell (spec §16) — transient UI state, never persisted
  // (see `dtoToFlowNode`/`flowNodeToDto`: neither reads nor writes this).
  // A single piece of state instead of one "expanded" flag per node is
  // what makes "only one panel expanded at a time" structural rather
  // than a rule every node has to independently enforce.
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null)
  /** V2 — transient collapse of the inspector while dragging an expanded card. */
  const [dragTempCollapsedNodeId, setDragTempCollapsedNodeId] = useState<string | null>(null)
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null)
  /** V2 — signals media surfaces to stop sticky playback and return to poster. */
  const [playbackResetKey, setPlaybackResetKey] = useState(0)
  const dragWasExpandedRef = useRef(false)

  // VISTOERA_CANVAS_EDGE_CONNECTION_DRAG_GLOBAL_TEXT_SELECTION_LEAK_P0_V1 —
  // Unified Canvas STRUCTURAL drag selection guard (extends
  // VISTOERA_CANVAS_NODE_DRAG_GLOBAL_TEXT_SELECTION_LEAK_REGRESSION_P0_V1).
  //
  // Prior node-drag-only guard (`activeDragKindsRef` with 'node'|'selection')
  // correctly locked selection during XYFlow node reposition, but connection
  // handle drags (output handle → preview edge → drop/cancel) are a separate
  // XYFlow lifecycle (`onConnectStart`/`onConnectEnd`) that never entered that
  // Set — so dragging a connection line across other nodes' readable bodies
  // still produced a native browser text selection. Same hole exists for
  // Scene `NodeResizer` (dimensions changes with `resizing: true|false`).
  //
  // One Set, one root class (`.is-structural-dragging`), one CSS selection
  // lock — never parallel node-/connection-/resize-specific selection locks.
  // State only mutates at gesture START/STOP (never per pointermove).
  type CanvasStructuralDragKind = 'node' | 'selection' | 'connection' | 'resize'
  const activeDragKindsRef = useRef<Set<CanvasStructuralDragKind>>(new Set())
  const [isStructuralDragging, setIsStructuralDragging] = useState(false)
  const syncStructuralDragging = useCallback(() => {
    setIsStructuralDragging((prev) => {
      const next = activeDragKindsRef.current.size > 0
      return prev === next ? prev : next
    })
  }, [])
  const beginCanvasDrag = useCallback(
    (kind: CanvasStructuralDragKind) => {
      activeDragKindsRef.current.add(kind)
      // Clear any pre-existing Canvas text highlight once a structural
      // gesture is confirmed — otherwise an earlier manual selection
      // (e.g. Text Asset drag-select) stays visible while the pointer
      // crosses other nodes, looking like a leak. Does NOT run on mere
      // handle hover / pointerdown before XYFlow confirms the gesture.
      // Note: window.getSelection().removeAllRanges() alone does NOT clear
      // a focused <textarea>/<input> selection — collapse that too without
      // blurring (IME/focus must survive).
      try {
        window.getSelection()?.removeAllRanges()
        const ae = document.activeElement
        if (ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement) {
          const end = ae.selectionEnd ?? ae.value.length
          ae.setSelectionRange(end, end)
        }
      } catch {
        /* ignore */
      }
      syncStructuralDragging()
    },
    [syncStructuralDragging],
  )
  const endCanvasDrag = useCallback(
    (kind: CanvasStructuralDragKind) => {
      activeDragKindsRef.current.delete(kind)
      syncStructuralDragging()
    },
    [syncStructuralDragging],
  )

  // VISTOERA_GENERATE_VIDEO_DROP_ZONE_IS_TARGET_PORT_AND_COLLAPSED_VISUAL_PROXY_P0_V3
  // §一/§三/§四/§二十七 — ONE board-level tracker (never per-node) computing
  // the SAME large-rectangle hit test the visual highlight already uses.
  // Pure `pointermove` + rAF-batched — no continuous geometry measurement.
  //
  // VISTOERA_CANVAS_GENERATE_CREATIVE_OUTPUT_EDGE_DRAG_CRASH_FORENSIC_AND_
  // INTERACTION_RESILIENCE_P0_V1 — MUST NOT subscribe to `useConnection()`
  // at board level (React #185 infinite update loop). Track imperatively via
  // refs + listeners attached in `onConnectStart` / torn down in
  // `onConnectEnd` / `forceEndAllCanvasDrag` instead.
  const connectionDropTrackRef = useRef<{ fromNodeId: string; cleanup: () => void } | null>(null)

  const detachConnectionDropTracker = useCallback(() => {
    const active = connectionDropTrackRef.current
    if (!active) return
    active.cleanup()
    connectionDropTrackRef.current = null
  }, [])

  const attachConnectionDropTracker = useCallback((fromNodeId: string) => {
    detachConnectionDropTracker()
    let raf = 0
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const el = document.elementFromPoint(e.clientX, e.clientY)
        const videoResolved = el ? resolveGenerateVideoDropTarget(el) : null
        const scriptResolved = el ? resolveGenerateScriptDropTarget(el) : null
        if (!videoResolved && !scriptResolved) {
          clearActiveVideoDropTarget()
          clearActiveScriptDropTarget()
          return
        }
        const targetNodeEl = (el as Element).closest('.react-flow__node[data-id]')
        const targetNodeId = targetNodeEl?.getAttribute('data-id') ?? null
        const targetNode = targetNodeId ? nodesRef.current.find((n) => n.id === targetNodeId) : null
        const targetType = (targetNode?.data as unknown as FlowNodeData | undefined)?.nodeType
        if (!targetNodeId || targetNodeId === fromNodeId) {
          clearActiveVideoDropTarget()
          clearActiveScriptDropTarget()
          return
        }
        if (targetType === 'generate_video' && videoResolved) {
          setActiveVideoDropTarget({
            sourceNodeId: fromNodeId,
            targetNodeId,
            port: videoResolved.targetHandle,
          })
          clearActiveScriptDropTarget()
          return
        }
        if (targetType === 'script_generation' && scriptResolved) {
          setActiveScriptDropTarget({
            sourceNodeId: fromNodeId,
            targetNodeId,
            port: scriptResolved.targetHandle,
          })
          clearActiveVideoDropTarget()
          return
        }
        clearActiveVideoDropTarget()
        clearActiveScriptDropTarget()
      })
    }
    window.addEventListener('pointermove', onMove)
    connectionDropTrackRef.current = {
      fromNodeId,
      cleanup: () => {
        window.removeEventListener('pointermove', onMove)
        cancelAnimationFrame(raf)
        clearActiveVideoDropTarget()
        clearActiveScriptDropTarget()
      },
    }
  }, [detachConnectionDropTracker])

  /** Defensive reset for pointercancel/window-blur/unmount/Escape — never
   * leaves `.is-structural-dragging` stuck on `.pcv-root`. Idempotent when
   * no drag is active, so capture-phase window `pointerup` is cheap. */
  const forceEndAllCanvasDrag = useCallback(() => {
    if (activeDragKindsRef.current.size === 0 && !connectionDropTrackRef.current) return
    activeDragKindsRef.current.clear()
    detachConnectionDropTracker()
    syncStructuralDragging()
  }, [detachConnectionDropTracker, syncStructuralDragging])
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') forceEndAllCanvasDrag()
    }
    window.addEventListener('pointerup', forceEndAllCanvasDrag, true)
    window.addEventListener('pointercancel', forceEndAllCanvasDrag, true)
    window.addEventListener('blur', forceEndAllCanvasDrag)
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('pointerup', forceEndAllCanvasDrag, true)
      window.removeEventListener('pointercancel', forceEndAllCanvasDrag, true)
      window.removeEventListener('blur', forceEndAllCanvasDrag)
      window.removeEventListener('keydown', onKeyDown, true)
      forceEndAllCanvasDrag()
    }
  }, [forceEndAllCanvasDrag])

  // Belt-and-suspenders for focused textarea/input: CSS `user-select:none`
  // alone does not always stop Chromium from extending an already-focused
  // field's selection while a structural drag pointer sweeps over it
  // (RF node-drag preventDefault can keep the field focused). Block
  // `selectstart` on the Canvas root only while the lock is active —
  // never a permanent listener, never document-wide, never pointermove
  // preventDefault.
  useEffect(() => {
    if (!isStructuralDragging) return
    const root = containerRef.current
    if (!root) return
    const onSelectStart = (e: Event) => {
      e.preventDefault()
    }
    root.addEventListener('selectstart', onSelectStart, true)
    return () => root.removeEventListener('selectstart', onSelectStart, true)
  }, [isStructuralDragging])
  const bumpPlaybackReset = useCallback(() => {
    setPlaybackResetKey((k) => k + 1)
  }, [])
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; flowX: number; flowY: number } | null>(null)
  const [nodeContextMenu, setNodeContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [connectionSuggest, setConnectionSuggest] = useState<{
    x: number
    y: number
    flowX: number
    flowY: number
    sourceNodeId: string
    sourceNodeType: string
  } | null>(null)
  // Cascades each Left-Library/Quick-Add placement by a fixed step so
  // repeated adds land at visibly distinct spots instead of stacking
  // exactly on top of one another at the viewport center.
  const libraryAddCounterRef = useRef(0)

  const rf = useReactFlow()

  useEffect(() => {
    viewportRef.current = viewport
  }, [viewport])

  useEffect(() => {
    const saved = persistedViewportRef.current
    if (!saved) return
    void rf.setViewport(saved)
  }, [rf])

  // Shell V2 collapsed ghost-hit fix: after expand/collapse, clear any
  // locked RF dimensions on the transitioning content-measured nodes and
  // ask RF to remeasure once layout settles (double rAF). No continuous
  // ResizeObserver / pointer listeners.
  const prevExpandedNodeIdRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevExpandedNodeIdRef.current
    prevExpandedNodeIdRef.current = expandedNodeId
    const touchIds = [prev, expandedNodeId].filter((id): id is string => Boolean(id))
    if (touchIds.length === 0) return
    const touch = new Set(touchIds)
    setNodes((nds) => {
      let changed = false
      const next = nds.map((n) => {
        if (!touch.has(n.id) || !usesContentMeasuredNodeGeometry(n.type)) return n
        if (n.width == null && n.height == null) return n
        changed = true
        return { ...n, width: undefined, height: undefined }
      })
      return changed ? next : nds
    })
    let cancelled = false
    const outer = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled) return
        for (const id of touch) (rf as { updateNodeInternals?: (id: string) => void }).updateNodeInternals?.(id)
        // Rebind PROMPT_ENHANCEMENT_SOURCE handles after expand/collapse layout
        // commits so edges attach to `original-prompt-out` / `prompt-context-in`
        // instead of stale legacy ports (e.g. `video-out`).
        setEdges((eds) => {
          const touchesPromptSource = eds.some(
            (e) =>
              (e.data as { relationType?: string } | undefined)?.relationType ===
                PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION &&
              (touch.has(e.source) || touch.has(e.target)),
          )
          if (!touchesPromptSource) return eds
          return normalizeFlowEdgesPromptEnhancementSource(eds)
        })
      })
    })
    return () => {
      cancelled = true
      window.cancelAnimationFrame(outer)
    }
  }, [expandedNodeId, rf])

  const doSave = useCallback(
    async (payload: SavePayload) => {
      // READ_ONLY: autosave must never reach the backend (TASK-00556).
      if (readOnly) return
      await onSave(payload)
    },
    [onSave, readOnly],
  )
  const {
    status: saveStatus,
    schedule: scheduleSave,
    flushNow,
    cancelPending,
    scheduleAndWaitForSettled,
  } = useDebouncedSave<SavePayload>(doSave, 800)

  // VistoeraTASK-00585 — leaving Canvas / switching project must FLUSH the
  // pending PUT, not cancelPending(). Paste (and fast edits) often leave
  // within the 800ms debounce window; dropping the payload left text only
  // in React state so Text Asset Center never saw a Workspace asset.
  useEffect(() => {
    return () => {
      void flushNow()
    }
  }, [projectId, flushNow])

  useEffect(() => {
    const onPageHide = () => {
      void flushNow()
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [flushNow])

  const buildPayload = useCallback(
    (nextNodes: Node[], nextEdges: Edge[], nextViewport: CanvasViewportDto): SavePayload => ({
      nodes: nextNodes.map(flowNodeToDto),
      edges: nextEdges.map(flowEdgeToDto),
      viewport: nextViewport,
      projectId,
    }),
    [projectId],
  )

  const scheduleFromState = useCallback(
    (nextNodes: Node[], nextEdges: Edge[], nextViewport?: CanvasViewportDto) => {
      if (canvasHydrating || readOnly) return
      scheduleSave(buildPayload(nextNodes, nextEdges, nextViewport ?? viewport))
    },
    [buildPayload, canvasHydrating, readOnly, scheduleSave, viewport],
  )

  useEffect(() => {
    // VistoeraTASK-00581 — expose live autosave status so account switch can
    // auto-flush (pending/saving) under THIS session and only surface a dialog
    // when the PUT actually fails. Workbench exits stay fire-and-forget.
    registerCanvasAccountSwitchGuard({
      getStatus: () => saveStatus,
      saveBeforeSwitch: async () =>
        scheduleAndWaitForSettled(buildPayload(nodesRef.current, edgesRef.current, viewport)),
      discardPending: () => cancelPending(),
    })
    return () => registerCanvasAccountSwitchGuard(null)
  }, [buildPayload, cancelPending, saveStatus, scheduleAndWaitForSettled, viewport])

  // Persist orphan Creative→Script (and other load-time) reconciles once
  // autosave is ready — hydrate effect itself cannot call scheduleFromState
  // (declared later) and must not rely on ephemeral React state alone.
  useEffect(() => {
    if (canvasHydrating) return
    const pending = pendingHydratePersistRef.current
    if (!pending) return
    pendingHydratePersistRef.current = null
    scheduleFromState(pending.nodes, pending.edges)
  }, [canvas.id, canvasHydrating, scheduleFromState, nodes, edges])

  // These write the ref synchronously (never waiting for the render
  // that follows a `setState` call) so any handler that needs to touch
  // BOTH nodes and edges in one gesture (e.g. Connection-to-Create:
  // create a node, then add an edge to it) always sees the just-created
  // node when it runs its own `setEdges` a moment later in the same
  // handler — a plain `useEffect`-synced ref would still be stale then.
  const commitNodes = useCallback((updater: (nds: Node[]) => Node[]) => {
    setNodes((nds) => {
      const next = updater(nds)
      nodesRef.current = next
      return next
    })
  }, [])
  const commitEdges = useCallback((updater: (eds: Edge[]) => Edge[]) => {
    setEdges((eds) => {
      const next = updater(eds)
      edgesRef.current = next
      return next
    })
  }, [])

  // Real delete (filters both the node(s) AND every edge touching them —
  // never the underlying `SiteAsset`/`ImageGenerationOutputAsset`, spec
  // §7/§31 "不是...底层图片资产文件"). Only ever called after the user
  // confirms in `confirmDeleteNodes` below.
  const performDeleteNodes = useCallback(
    (requestedNodeIds: string[]) => {
      // VistoeraTASK-00512 — the ONE choke point every delete entry funnels
      // through (header ×, keyboard, bulk, context menu, programmatic): a
      // Result Slot of a RUNNING multi-output run can never be deleted here.
      const { allowed: nodeIds, locked: lockedIds } = partitionImageGenerationSlotDeleteLock(
        nodesRef.current as unknown as Array<{ id: string; data?: unknown }>,
        requestedNodeIds,
      )
      if (lockedIds.length > 0) message.info(t('board.deleteLockedRunningSlot'))
      if (nodeIds.length === 0) return
      clearVoiceReferenceRecordingDrafts(nodeIds)
      const idSet = new Set(nodeIds)
      // Stop frontend polling for deleted nodes' runs. Backend generation
      // continues; success lands in Asset system — never resurrect nodes.
      for (const n of nodesRef.current) {
        if (!idSet.has(n.id)) continue
        const fields = ((n.data as unknown as FlowNodeData)?.fields || {}) as Record<string, unknown>
        for (const key of ['activeRunId', 'activeDivergeRunId', 'jobId', 'taskId'] as const) {
          const runId = fields[key]
          if (typeof runId !== 'string' || !runId) continue
          for (const map of [ideationPollTimersRef, scriptPollTimersRef, storyboardPollTimersRef]) {
            const timer = map.current.get(runId)
            if (timer != null) {
              window.clearTimeout(timer)
              map.current.delete(runId)
            }
          }
        }
      }
      const nextEdges = edgesRef.current.filter((e) => !idSet.has(e.source) && !idSet.has(e.target))
      commitEdges(() => nextEdges)
      commitNodes((nds) => {
        const next = nds
          .filter((n) => !idSet.has(n.id))
          .map((n) => {
            const data = n.data as unknown as FlowNodeData
            const fields = { ...(data.fields || {}) }
            let changed = false
            if (data.nodeType === 'generate_voice') {
              const pending = fields.pendingVoiceTemplateCandidateNodeId as string | null | undefined
              if (pending && idSet.has(pending)) {
                fields.pendingVoiceTemplateCandidateNodeId = null
                changed = true
              }
            }
            if (data.nodeType === 'generate_voice_template') {
              const candidate = fields.candidateForGenerateAudioNodeId as string | null | undefined
              if (candidate && idSet.has(candidate)) {
                fields.candidateForGenerateAudioNodeId = null
                changed = true
              }
            }
            if (!changed) return n
            return { ...n, data: { ...data, fields } as unknown as Record<string, unknown> }
          })
        scheduleFromState(next, nextEdges)
        return next
      })
      setSelectedNodeId((prev) => (prev && idSet.has(prev) ? null : prev))
      setExpandedNodeId((prev) => (prev && idSet.has(prev) ? null : prev))
    },
    [commitEdges, commitNodes, scheduleFromState, t],
  )

  // Shared business copy for BOTH delete entry points below — never
  // duplicated between them, so the mouse/icon anchored popover and the
  // keyboard `Modal.confirm` always agree on title/warnings for the same
  // node selection (VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13).
  const buildDeleteCopy = useCallback(
    (nodeIds: string[]) => {
      const targets = nodeIds.map((nid) => nodesRef.current.find((n) => n.id === nid)).filter((n): n is Node => !!n)
      const hasEdges = edgesRef.current.some((e) => nodeIds.includes(e.source) || nodeIds.includes(e.target))
      const isSingle = targets.length === 1
      const single = isSingle ? targets[0] : null
      const singleData = single ? (single.data as unknown as FlowNodeData) : null
      const singleFields = (singleData?.fields || {}) as Record<string, unknown>
      const isGenerating = isSingle && nodeFieldsIndicateActiveGeneration(singleFields)
      const destination = isSingle
        ? resolveGenerationAssetDestination({
            nodeType: singleData?.nodeType,
            family: single?.type,
          })
        : null
      const assetLabel = destination ? t(destination.assetLabelKey) : t('board.assetDestination.generic')
      const isSingleGenerateNode = isSingle && single?.type === 'generator'
      const isSingleImageAssetNode = isSingle && single?.type === 'media'
      const title = isGenerating
        ? t('board.deleteGeneratingNodeTitle')
        : isSingleGenerateNode
          ? t('board.deleteGenerateNode')
          : isSingleImageAssetNode
            ? t('board.deleteImageNode')
            : t('board.deleteSelectedNodes')
      const lines: string[] = []
      if (hasEdges) lines.push(t('board.deleteEdgesWarning'))
      if (isGenerating) {
        // Never promise an Asset Center page that does not exist yet.
        if (destination && destination.assetReady) {
          lines.push(t('board.deleteGeneratingWarningAssetReady', { assetLabel }))
        } else if (destination?.assetKind === 'audio') {
          lines.push(t('board.deleteGeneratingWarningAudioNotReady'))
        } else {
          lines.push(t('board.deleteGeneratingWarningAssetNotReady'))
        }
      }
      return { targets, title, lines, isGenerating }
    },
    [t],
  )

  // Keyboard Delete/Backspace entry (via `onNodesChange`'s gated `remove`
  // changes below) — no real DOM trigger element exists for a keyboard
  // shortcut, so this path deliberately keeps the pre-existing centered
  // `Modal.confirm` semantic rather than fabricating an anchor
  // (VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13 "不能强行制造假坐标").
  // Never deletes anything itself — only ever calls `performDeleteNodes`
  // from `onOk`.
  const confirmDeleteNodes = useCallback(
    (nodeIds: string[]) => {
      const { targets, title, lines, isGenerating } = buildDeleteCopy(nodeIds)
      if (targets.length === 0) return
      Modal.confirm({
        title,
        content: lines.length ? (
          <div>
            {lines.map((line, i) => (
              <p key={i} style={{ margin: i === 0 ? 0 : '6px 0 0', color: '#6b7280', fontSize: 13 }}>
                {line}
              </p>
            ))}
          </div>
        ) : undefined,
        okText: isGenerating ? t('board.deleteNodeConfirm') : t('common.delete'),
        okType: 'danger',
        cancelText: t('common.cancel'),
        centered: true,
        onOk: () => performDeleteNodes(nodeIds),
      })
    },
    [performDeleteNodes, buildDeleteCopy, t],
  )

  const beginDeleteNodes = useCallback(
    async (requestedNodeIds: string[], anchorEl?: HTMLElement | null) => {
      // VistoeraTASK-00512 — refuse locked Result Slots BEFORE any confirm UI.
      const { allowed: nodeIds, locked: lockedNodeIds } = partitionImageGenerationSlotDeleteLock(
        nodesRef.current as unknown as Array<{ id: string; data?: unknown }>,
        requestedNodeIds,
      )
      if (lockedNodeIds.length > 0) message.info(t('board.deleteLockedRunningSlot'))
      if (nodeIds.length === 0) return
      // TASK-00096 — canonical policy check FIRST, synchronously, before
      // the async video-asset-plan lookup below. A node the policy finds
      // genuinely empty (§4) can only ever be a SINGLE node here (§38/§39
      // "多选...继续保留确认" — the policy itself always requires
      // confirmation once `nodeIds.length > 1`), and an empty node by
      // definition has no generated video asset to worry about either,
      // so this also SKIPS `resolveVideoDeletePlan`'s network round-trip
      // for the empty case (§43/§52 "no extra network request").
      const policy = getCanvasNodeDeletionPolicy(nodeIds, nodesRef.current, edgesRef.current, {
        workspaceId,
        locale: i18n.language,
      })
      if (!policy.requiresConfirmation) {
        performDeleteNodes(nodeIds)
        setIconDeleteRequest(null)
        return
      }
      try {
        const plan = await resolveVideoDeletePlan(workspaceId, nodesRef.current, nodeIds)
        if (plan) {
          setVideoDeletePlan(plan)
          setIconDeleteRequest(null)
          return
        }
      } catch {
        /* Fall back to standard delete confirm when lookup fails. */
      }
      // TASK-00225 §1/§3/§40 — a real click/icon anchor exists (mouse
      // entry), so reuse the SAME canonical anchored popover every
      // other node type already uses instead of falling through to
      // the centered `Modal.confirm` below. That fallback stays
      // reserved for the genuine no-anchor case (keyboard Delete/
      // Backspace, where no real DOM trigger element exists at all —
      // see `confirmDeleteNodes`'s own doc comment above). This is the
      // ONLY change here: no delete/edge/asset-plan business logic
      // above is touched, and no per-node-type `if` patch is added —
      // `isVideoGeneratorNode` gating into this function is pre-
      // existing (TASK-00096), not new.
      if (anchorEl) {
        setIconDeleteRequest({ nodeIds, anchorEl })
        return
      }
      confirmDeleteNodes(nodeIds)
    },
    [workspaceId, i18n, confirmDeleteNodes, performDeleteNodes, t],
  )

  // Mouse/icon entry (node's own hover "×" button) — anchored beside the
  // REAL clicked DOM element via `CanvasActionPopover` (bounding-box +
  // portal positioning, immune to React Flow pan/zoom transforms), never
  // a centered Modal. `anchorEl` only ever comes from a real click
  // event's `e.currentTarget` (see e.g. `ImageAssetNode.tsx`'s
  // `handleDeleteClick`) — if it's ever missing, fall back to the exact
  // same keyboard-path confirm rather than guessing a position.
  const [iconDeleteRequest, setIconDeleteRequest] = useState<{ nodeIds: string[]; anchorEl: HTMLElement } | null>(
    null,
  )
  const [videoDeletePlan, setVideoDeletePlan] = useState<VideoNodeDeletePlan | null>(null)
  const [videoDeleteBusy, setVideoDeleteBusy] = useState(false)

  const onRequestDeleteNode = useCallback(
    (nodeId: string, anchorEl?: HTMLElement | null) => {
      // VistoeraTASK-00512 — running Result Slots cannot be deleted (no confirm UI either).
      if (
        partitionImageGenerationSlotDeleteLock(nodesRef.current as unknown as Array<{ id: string; data?: unknown }>, [nodeId])
          .locked.length > 0
      ) {
        message.info(t('board.deleteLockedRunningSlot'))
        return
      }
      const node = nodesRef.current.find((n) => n.id === nodeId)
      // `generate_video` keeps its own async asset-cleanup contract
      // untouched (`beginDeleteNodes` itself now short-circuits to an
      // immediate delete for an empty video node — see above — so this
      // branch is never slower than the generic path below for the
      // §17/§31 "空节点直接删除" case). TASK-00225 §1/§3 — the real
      // `anchorEl` from this click is now threaded through so a
      // simple (non-asset-plan) delete still opens the SAME canonical
      // anchored popover every other node type uses, instead of
      // `beginDeleteNodes`'s no-anchor `Modal.confirm` fallback.
      if (isVideoGeneratorNode(node)) {
        void beginDeleteNodes([nodeId], anchorEl)
        return
      }
      // TASK-00096 §2/§29 — canonical policy decides immediate delete
      // vs confirm for EVERY other node type, BEFORE branching on
      // whether a real anchor element exists. Header/context-menu code
      // never has to know this decision was even made (§28).
      const policy = getCanvasNodeDeletionPolicy([nodeId], nodesRef.current, edgesRef.current, {
        workspaceId,
        locale: i18n.language,
      })
      if (!policy.requiresConfirmation) {
        performDeleteNodes([nodeId])
        return
      }
      if (anchorEl) {
        setIconDeleteRequest({ nodeIds: [nodeId], anchorEl })
        return
      }
      void beginDeleteNodes([nodeId])
    },
    [beginDeleteNodes, performDeleteNodes, workspaceId, i18n, t],
  )

  const iconDeleteCopy = iconDeleteRequest ? buildDeleteCopy(iconDeleteRequest.nodeIds) : null

  const closeIconDelete = useCallback(() => {
    setIconDeleteRequest(null)
  }, [])

  const confirmIconDelete = useCallback(() => {
    if (!iconDeleteRequest) return
    // The anchored popover itself IS the confirmation (its own title +
    // warning lines already come from the SAME `buildDeleteCopy` the
    // keyboard path's `Modal.confirm` uses, via `iconDeleteCopy` below)
    // — clicking its own "删除" button goes straight to the canonical
    // mutation, never back through `beginDeleteNodes` (which would
    // re-open a SECOND, redundant confirmation layer on top of the one
    // the user just answered). `iconDeleteRequest` only ever holds
    // node ids the canonical policy already found non-empty (empty
    // ones never reach this popover — see `onRequestDeleteNode`) and
    // never a `generate_video` id (that family bypasses this popover
    // entirely for its own async asset-cleanup contract), so there is
    // no video-asset-plan lookup to preserve here.
    performDeleteNodes(iconDeleteRequest.nodeIds)
    setIconDeleteRequest(null)
  }, [iconDeleteRequest, performDeleteNodes])

  const closeVideoDeleteModal = useCallback(() => {
    if (videoDeleteBusy) return
    setVideoDeletePlan(null)
  }, [videoDeleteBusy])

  const finishVideoNodeDelete = useCallback(
    async (alsoDeleteAssets: boolean) => {
      if (!videoDeletePlan || videoDeleteBusy) return
      setVideoDeleteBusy(true)
      try {
        if (alsoDeleteAssets) {
          await deleteVideoAssetsForPlan(workspaceId, videoDeletePlan)
        }
        performDeleteNodes(videoDeletePlan.nodeIds)
        setVideoDeletePlan(null)
      } catch (e) {
        message.error((e as Error).message || t('board.deleteVideoNodeAssetFailed'))
      } finally {
        setVideoDeleteBusy(false)
      }
    },
    [videoDeletePlan, videoDeleteBusy, workspaceId, performDeleteNodes, t],
  )

  const onNodesChange = useCallback(
    (rawChanges: NodeChange[]) => {
      // VistoeraTASK-00512 — keyboard Delete/Backspace (and any other React
      // Flow `remove` change) must never remove a Result Slot of a running run.
      const lockedRemovalIds = partitionImageGenerationSlotDeleteLock(
        nodesRef.current as unknown as Array<{ id: string; data?: unknown }>,
        rawChanges.filter((c) => c.type === 'remove').map((c) => c.id),
      ).locked
      if (lockedRemovalIds.length > 0) message.info(t('board.deleteLockedRunningSlot'))
      const changes =
        lockedRemovalIds.length > 0
          ? rawChanges.filter((c) => !(c.type === 'remove' && lockedRemovalIds.includes(c.id)))
          : rawChanges
      // TASK-00096 — single-node keyboard Delete/Backspace now defers to
      // the SAME canonical policy the header × and right-click menu use
      // (replacing the old per-family `DELETE_CONFIRM_FAMILIES`
      // allowlist), while a MULTI-node keyboard delete (several nodes
      // selected at once) is gated unconditionally regardless of any
      // individual node's content (§38/§39 "多选...继续保留确认" — the
      // policy itself already returns `requiresConfirmation: true` once
      // more than one id is passed, but the branch below skips even
      // computing per-node emptiness for that case, since it can never
      // change the outcome).
      const removeIds = changes.filter((c) => c.type === 'remove').map((c) => c.id)
      const gatedIds: string[] = []
      if (removeIds.length > 1) {
        gatedIds.push(...removeIds)
      } else if (removeIds.length === 1) {
        const [onlyId] = removeIds
        const node = nodesRef.current.find((n) => n.id === onlyId)
        const policy = getCanvasNodeDeletionPolicy([onlyId], nodesRef.current, edgesRef.current, {
          workspaceId,
          locale: i18n.language,
        })
        // `generate_video` ALWAYS defers to `beginDeleteNodes`'s async
        // asset-plan lookup — it re-checks this exact same sync policy
        // itself first and short-circuits to an immediate delete when
        // empty (§43/§52 "no extra network request for an empty node"),
        // so routing it there never costs more than the passthrough
        // path below for the empty case.
        if (isVideoGeneratorNode(node) || policy.requiresConfirmation) {
          gatedIds.push(onlyId)
        }
      }
      const passthrough = changes.filter((c) => !(c.type === 'remove' && gatedIds.includes(c.id)))
      if (gatedIds.length > 0) void beginDeleteNodes(gatedIds)
      if (passthrough.length === 0) return
      commitNodes((nds) => {
        const next = applyNodeChanges(passthrough, nds).map((n) => {
          // Content-measured shells: never lock explicit RF width/height.
          // Automatic `dimensions` changes (and any leftover persisted
          // attrs) would otherwise keep a tall wrapper after collapse.
          if (!usesContentMeasuredNodeGeometry(n.type)) return n
          if (n.width == null && n.height == null) return n
          return { ...n, width: undefined, height: undefined }
        })
        // P0 mount-time PUT fix (VISTOERA_CANVAS_CAPACITY_CATEGORY_A_
        // SCALING_FIX_AND_REBASELINE_V1 §13-§15) — the REAL root cause of
        // "unchanged graph PUT within seconds of mount, zero user
        // interaction" turned out to be here, not the viewport-restore
        // effect: React Flow's own internal ResizeObserver
        // (`updateNodeInternals`) fires a `type: 'dimensions'` NodeChange
        // for EVERY node the very first time it measures its rendered
        // size — on every single mount, for every node, regardless of
        // node count — and that automatic-measurement change was being
        // treated as "meaningful" exactly like a real user edit, so it
        // scheduled a full-graph save every time. A REAL user-driven
        // resize (`SceneNode`'s `NodeResizer`) is distinguishable: it
        // ALWAYS sets an explicit `resizing: true` (while dragging) or
        // `resizing: false` (drag end) on its dimension changes (see
        // `@xyflow/react`'s `NodeResizeControl.onEnd`), whereas the
        // automatic ResizeObserver measurement change never sets
        // `resizing` at all (`undefined`). Gating on
        // `typeof c.resizing === 'boolean'` keeps real resize-drag
        // persistence working exactly as before while excluding every
        // automatic mount/content-driven measurement — hydration must
        // never be treated as a user edit (Dirty State Contract, §14).
        const meaningful = passthrough.some(
          (c) => c.type === 'position' || c.type === 'remove' || (c.type === 'dimensions' && typeof c.resizing === 'boolean'),
        )
        if (meaningful) scheduleFromState(next, edgesRef.current)
        // Scene NodeResizer — same Canvas-wide selection lock as node/connection
        // drag while `resizing: true`; released on `resizing: false` (and the
        // defensive window pointerup/cancel/blur/Escape cleanup).
        for (const c of changes) {
          if (c.type !== 'dimensions' || typeof c.resizing !== 'boolean') continue
          if (c.resizing) beginCanvasDrag('resize')
          else endCanvasDrag('resize')
        }
        return next
      })
    },
    [commitNodes, beginDeleteNodes, scheduleFromState, beginCanvasDrag, endCanvasDrag, workspaceId, i18n, t],
  )

  // VistoeraTASK-00512 — React Flow deletes a node AND its connected edges for
  // keyboard Delete; when the node is a locked running Result Slot, drop both
  // from the delete set so its edges are not silently removed either.
  const onBeforeDeleteGuard = useCallback(
    async ({ nodes: delNodes, edges: delEdges }: { nodes: Node[]; edges: Edge[] }) => {
      // VistoeraTASK-00609 — image editor (or other blocking Modal) owns the
      // keyboard; never delete Canvas nodes/edges while it is open.
      if (isCanvasBlockingOverlayOpen()) {
        return { nodes: [], edges: [] }
      }
      const { locked } = partitionImageGenerationSlotDeleteLock(
        nodesRef.current as unknown as Array<{ id: string; data?: unknown }>,
        delNodes.map((n) => n.id),
      )
      if (locked.length === 0) return { nodes: delNodes, edges: delEdges }
      message.info(t('board.deleteLockedRunningSlot'))
      const lockedSet = new Set(locked)
      return {
        nodes: delNodes.filter((n) => !lockedSet.has(n.id)),
        edges: delEdges.filter((e) => !lockedSet.has(e.source) && !lockedSet.has(e.target)),
      }
    },
    [t],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // §23 "删除输入连线后...节点应该标记...需要重新生成" — covers EVERY
      // edge-removal path (keyboard Delete on a selected edge, not just
      // the explicit chip "×" below via `onDisconnectMaterial`), so a
      // user can never delete a Generation Node's input edge and leave
      // it silently claiming a now-untrue result.
      const staleTargets: string[] = []
      const relatedAssetRemovals: Array<{ targetId: string; sourceId: string }> = []
      const creativeContextClears: Array<{ targetId: string; sourceId: string }> = []
      const scriptContextClears: Array<{ targetId: string; sourceId: string }> = []
      // T8 (deleting the GenerateText→GenerateVideo edge must ONLY clear
      // the prompt binding — reference-image binding is a separate edge/
      // handle and must be unaffected).
      const promptEnhancementClears: Array<{ videoNodeId: string; textNodeId: string }> = []
      const imagePromptClears: Array<{ targetId: string; sourceId: string }> = []
      const originalPromptClears: Array<{ targetId: string; sourceId: string }> = []
      for (const c of changes) {
        if (c.type !== 'remove') continue
        const edge = edgesRef.current.find((e) => e.id === c.id)
        if (!edge) continue
        const relation = (edge.data as { relationType?: string } | undefined)?.relationType
        if (
          isImageReferenceAssetHandle(edge.targetHandle) ||
          isVideoKeyframeHandle(edge.targetHandle) ||
          isTryOnInputHandle(edge.targetHandle)
        ) {
          staleTargets.push(edge.target)
        }
        const sourceType = nodeTypeOf(nodesRef.current.find((n) => n.id === edge.source))
        const targetType = nodeTypeOf(nodesRef.current.find((n) => n.id === edge.target))
        const isPromptResultEdge =
          relation === PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION ||
          (targetType === 'generate_video' &&
            sourceType === 'generate_text' &&
            edge.targetHandle === VIDEO_PROMPT_ENHANCEMENT_HANDLE)
        const isPromptSourceEdge =
          relation === PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION ||
          (sourceType === 'generate_video' &&
            targetType === 'generate_text' &&
            edge.sourceHandle === 'original-prompt-out' &&
            edge.targetHandle === 'prompt-context-in')
        if (isPromptResultEdge) {
          promptEnhancementClears.push({ videoNodeId: edge.target, textNodeId: edge.source })
          staleTargets.push(edge.target)
        }
        // The inverse SOURCE edge (video.original-prompt-out → text.prompt-context-in)
        // is the OTHER half of the same ONE_PRIMARY binding pair — deleting either
        // half of the pair must unwind the whole binding, never leave a dangling half.
        if (isPromptSourceEdge) {
          promptEnhancementClears.push({ videoNodeId: edge.source, textNodeId: edge.target })
          staleTargets.push(edge.source)
        }
        if (
          targetType === 'generate_image' &&
          edge.targetHandle === IMAGE_PROMPT_IN_HANDLE &&
          (sourceType === 'text_asset' || sourceType === 'generate_text')
        ) {
          imagePromptClears.push({ targetId: edge.target, sourceId: edge.source })
          staleTargets.push(edge.target)
        }
        if (
          targetType === 'generate_video' &&
          edge.targetHandle === VIDEO_ORIGINAL_PROMPT_IN_HANDLE &&
          (sourceType === 'text_asset' || sourceType === 'generate_text')
        ) {
          originalPromptClears.push({ targetId: edge.target, sourceId: edge.source })
          staleTargets.push(edge.target)
        }
        if (relation === RELATED_ASSET_EDGE_RELATION || relation === 'INPUT_TO') {
          if (sourceType === 'image_asset' && (targetType === 'generate_creative' || targetType === 'project_start' || targetType === 'script_generation' || targetType === 'storyboard_generation')) {
            relatedAssetRemovals.push({ targetId: edge.target, sourceId: edge.source })
            staleTargets.push(edge.target)
          }
          // Legacy Creative → Script INPUT_TO must clear Primary Creative Context.
          if (
            (sourceType === 'generate_creative' || sourceType === 'creative_candidate' || sourceType === 'text_asset') &&
            targetType === 'script_generation'
          ) {
            creativeContextClears.push({ targetId: edge.target, sourceId: edge.source })
            staleTargets.push(edge.target)
          }
          // Legacy Script → Storyboard INPUT_TO must clear Primary Script Context.
          if (sourceType === 'script_generation' && targetType === 'storyboard_generation') {
            scriptContextClears.push({ targetId: edge.target, sourceId: edge.source })
            staleTargets.push(edge.target)
          }
        }
        if (
          relation === CREATIVE_CONTEXT_EDGE_RELATION ||
          relation === SCRIPT_SOURCE_EDGE_RELATION ||
          relation === SCRIPT_CONTEXT_EDGE_RELATION
        ) {
          // Both relations are currently 'DERIVED_FROM' — disambiguate by node types.
          if (
            (sourceType === 'generate_creative' || sourceType === 'creative_candidate' || sourceType === 'text_asset') &&
            targetType === 'script_generation'
          ) {
            creativeContextClears.push({ targetId: edge.target, sourceId: edge.source })
            staleTargets.push(edge.target)
          } else if (sourceType === 'script_generation' && targetType === 'storyboard_generation') {
            scriptContextClears.push({ targetId: edge.target, sourceId: edge.source })
            staleTargets.push(edge.target)
          }
        }
      }
      commitEdges((eds) => {
        let next = applyEdgeChanges(changes, eds)
        // Unwind the OTHER half of any PROMPT_ENHANCEMENT_* pair whose first
        // half was just removed above — a half-deleted binding must never
        // leave a stale reverse edge pointing at a now-unbound node pair.
        if (promptEnhancementClears.length > 0) {
          next = next.filter((e) => {
            const relation = (e.data as { relationType?: string } | undefined)?.relationType
            if (!isPromptEnhancementFieldBindingRelation(relation)) return true
            return !promptEnhancementClears.some(
              (rem) =>
                (e.source === rem.videoNodeId && e.target === rem.textNodeId) ||
                (e.source === rem.textNodeId && e.target === rem.videoNodeId),
            )
          })
        }
        if (changes.some((c) => c.type === 'remove')) {
          commitNodes((nds) => {
            let nextNodes = nds
            for (const rem of relatedAssetRemovals) {
              nextNodes = nextNodes.map((n) => {
                if (n.id !== rem.targetId) return n
                const d = n.data as unknown as FlowNodeData
                const fields = removeRelatedAssetFromFields({ ...(d.fields || {}) }, { sourceNodeId: rem.sourceId })
                return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
              })
            }
            for (const rem of creativeContextClears) {
              nextNodes = nextNodes.map((n) => {
                if (n.id !== rem.targetId) return n
                const d = n.data as unknown as FlowNodeData
                const fields = clearScriptSourceOnUnlink({ ...(d.fields || {}) }, rem.sourceId)
                return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
              })
            }
            for (const rem of scriptContextClears) {
              nextNodes = nextNodes.map((n) => {
                if (n.id !== rem.targetId) return n
                const d = n.data as unknown as FlowNodeData
                const fields = clearScriptContextOnUnlink({ ...(d.fields || {}) }, rem.sourceId)
                return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
              })
            }
            for (const rem of promptEnhancementClears) {
              nextNodes = nextNodes.map((n) => {
                if (n.id === rem.videoNodeId) {
                  const d = n.data as unknown as FlowNodeData
                  const fields = {
                    ...(d.fields || {}),
                    ...clearEnhancedPromptPatch(),
                    boundPromptEnhancerNodeId: null,
                  }
                  return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
                }
                if (n.id === rem.textNodeId) {
                  const d = n.data as unknown as FlowNodeData
                  if ((d.fields || {}).boundVideoNodeId !== rem.videoNodeId) return n
                  const fields = {
                    ...(d.fields || {}),
                    boundVideoNodeId: null,
                    ...(isVideoPromptEnhancementPurpose((d.fields || {}).purpose)
                      ? { purpose: null, videoPromptContextSnapshot: null }
                      : {}),
                  }
                  return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
                }
                return n
              })
            }
            for (const rem of imagePromptClears) {
              const sourceNode = nodesRef.current.find((n) => n.id === rem.sourceId)
              const sourceType = nodeTypeOf(sourceNode)
              const sourceText = sourceNode ? extractTextFromTextSourceNode(sourceNode, sourceType) : ''
              nextNodes = nextNodes.map((n) => {
                if (n.id !== rem.targetId) return n
                const d = n.data as unknown as FlowNodeData
                const fields = { ...(d.fields || {}) }
                if (typeof fields.prompt === 'string' && fields.prompt.trim() === sourceText) {
                  fields.prompt = ''
                }
                return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
              })
            }
            for (const rem of originalPromptClears) {
              const sourceNode = nodesRef.current.find((n) => n.id === rem.sourceId)
              const sourceType = nodeTypeOf(sourceNode)
              const sourceText = sourceNode ? extractTextFromTextSourceNode(sourceNode, sourceType) : ''
              nextNodes = nextNodes.map((n) => {
                if (n.id !== rem.targetId) return n
                const d = n.data as unknown as FlowNodeData
                const fields = { ...(d.fields || {}) }
                if (typeof fields.promptText === 'string' && fields.promptText.trim() === sourceText) {
                  fields.promptText = ''
                  fields.effectivePromptSource = 'ORIGINAL'
                }
                return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
              })
            }
            for (const targetId of staleTargets) nextNodes = markNodeStaleIfHasResult(nextNodes, targetId)
            scheduleFromState(nextNodes, next)
            return nextNodes
          })
        }
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onConnect = useCallback(
    (incomingConnection: Connection) => {
      if (incomingConnection.source === incomingConnection.target) {
        message.warning(t('board.connectionSelf'))
        return
      }
      const sourceNode = nodesRef.current.find((n) => n.id === incomingConnection.source)
      const targetNode = nodesRef.current.find((n) => n.id === incomingConnection.target)
      if (!sourceNode || !targetNode) return
      const sourceType = (sourceNode.data as unknown as FlowNodeData).nodeType
      const targetType = (targetNode.data as unknown as FlowNodeData).nodeType
      // VISTOERA_GENERATE_VIDEO_DROP_ZONE_IS_TARGET_PORT_AND_COLLAPSED_VISUAL_PROXY_P0_V3
      // §一/§四/§十 — DROP ZONE IS THE TARGET PORT, authoritative over
      // whatever handle XYFlow's own nearest-handle search proposed.
      // Applies BEFORE any validation below so every downstream check
      // (type compatibility, duplicate, cycle, reference limit) already
      // sees the CORRECT target — never the "close but wrong" native
      // guess, and never re-resolved a second time later.
      const videoDropOverride = applyActiveVideoDropTargetOverride(incomingConnection, targetType)
      const scriptDropOverride = applyActiveScriptDropTargetOverride(videoDropOverride.connection, targetType)
      const connection = scriptDropOverride.connection
      const dropOverride = {
        overridden: videoDropOverride.overridden || scriptDropOverride.overridden,
        port: scriptDropOverride.port ?? videoDropOverride.port,
      }
      if (dropOverride.overridden) {
        logCanvasEdgeDiagnostic({
          sourceNode: connection.source as string,
          sourceHandle: connection.sourceHandle,
          targetNode: connection.target as string,
          targetHandle: connection.targetHandle,
          semanticPort: dropOverride.port,
          logicalEdgeKey: `${connection.source}::${connection.target}::${dropOverride.port ?? ''}`,
          createdBy: 'onConnect:active-drop-target-override',
        })
      }
      const activeDropPortForAssert = dropOverride.port
      const verdict = evaluateConnection(sourceType, targetType, connection.targetHandle, t)
      if (!verdict.ok) {
        message.warning(verdict.reason || t('board.connectionInvalid'))
        return
      }
      // §21 Connection Validation — duplicate edge (same source/target/
      // handle tuple) is rejected up front rather than only surfacing
      // as a silent no-op at the next autosave round-trip (the backend's
      // own `save_graph` already dedupes on this exact key).
      // PRIMARY_SCRIPT_SOURCE / PRIMARY_SCRIPT_CONTEXT are excluded: an
      // orphan INPUT_TO (legacy) or reconnect must still hydrate context via linker.
      const semanticSlotEarly = resolveCanvasInputSlot(sourceType, targetType)
      if (
        semanticSlotEarly?.slot !== 'PRIMARY_SCRIPT_SOURCE' &&
        semanticSlotEarly?.slot !== 'PRIMARY_CREATIVE_CONTEXT' &&
        semanticSlotEarly?.slot !== 'PRIMARY_SCRIPT_CONTEXT' &&
        isDuplicateEdge(edgesRef.current, connection)
      ) {
        message.warning(t('board.connectionDuplicate'))
        return
      }
      // §22 Cycle Policy — "阻止 generation dependency cycle" with a
      // short, explicit reason, checked against the WHOLE live edge
      // graph before the edge is ever added.
      if (wouldCreateCycle(edgesRef.current, connection.source as string, connection.target as string)) {
        message.warning(t('board.connectionCycle'))
        return
      }
      if (
        wouldExceedImageReferenceLimitOnConnect({
          edges: edgesRef.current,
          nodes: nodesRef.current.map((n) => ({
            id: n.id,
            data: n.data as { nodeType?: string; fields?: Record<string, unknown> },
          })),
          connection,
          config: imageGenerationConfigRef.current,
          workspaceId,
        })
      ) {
        const targetNode = nodesRef.current.find((n) => n.id === connection.target)
        const targetFields = (targetNode?.data as unknown as FlowNodeData | undefined)?.fields || {}
        message.warning(
          referenceLimitConnectMessage(
            resolveCapabilityForGenerateImageNode({
              config: imageGenerationConfigRef.current,
              fields: targetFields,
              workspaceId,
            }),
            t,
          ),
        )
        return
      }
      if (
        wouldExceedGenerateTextReferenceLimitOnConnect({
          edges: edgesRef.current,
          nodes: nodesRef.current.map((n) => ({
            id: n.id,
            data: n.data as { nodeType?: string; fields?: Record<string, unknown> },
          })),
          connection,
        })
      ) {
        message.warning(t('generateText.readiness.tooManyReferenceImages'))
        return
      }
      const semanticSlot = resolveCanvasInputSlot(sourceType, targetType)
      // Creative / Text → Script: canonical script-source linker (DnD ≡ Quick Action).
      if (semanticSlot?.slot === 'PRIMARY_SCRIPT_SOURCE' || semanticSlot?.slot === 'PRIMARY_CREATIVE_CONTEXT') {
        if (sourceType === 'text_asset') {
          const scriptSource = buildScriptSourceFromTextAsset(sourceNode)
          if (!scriptSource) {
            message.warning(t('board.scriptSourceTextRequired'))
            return
          }
          if (wouldCreateCycle(edgesRef.current, connection.source as string, connection.target as string)) {
            message.warning(t('board.connectionCycle'))
            return
          }
          onLinkTextScriptSourceRef.current?.(connection.target as string, sourceNode.id, scriptSource)
          return
        }
        const payload = buildCreativePayloadFromNode(sourceNode)
        if (!payload || (!payload.title && !payload.premise && !payload.candidateId)) {
          message.warning(t('board.creativeContextRequiredBeforeConnect'))
          return
        }
        if (wouldCreateCycle(edgesRef.current, connection.source as string, connection.target as string)) {
          message.warning(t('board.connectionCycle'))
          return
        }
        const sourceKind: ScriptSourceKind = sourceType === 'creative_candidate' ? 'creative_candidate' : 'generate_creative'
        onLinkCreativeContextRef.current?.(connection.target as string, payload, sourceKind)
        return
      }
      if (semanticSlot?.slot === 'PRIMARY_SCRIPT_CONTEXT') {
        const sourceFields = ((sourceNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const payload = buildScriptContextDragPayloadFromScriptNode(connection.source as string, sourceFields)
        if (!payload) {
          message.warning('请先生成剧本结果，再连接到生成分镜。')
          return
        }
        if (wouldCreateCycle(edgesRef.current, connection.source as string, connection.target as string)) {
          message.warning(t('board.connectionCycle'))
          return
        }
        onLinkScriptContextRef.current?.(connection.target as string, payload)
        return
      }

      // GenerateText → GenerateVideo Prompt Enhancement binding
      // (VISTOERA_GENERATE_VIDEO_SEMANTIC_EDGE_BINDING_REFERENCE_IMAGE_AND_LTX_I2V_FORENSIC_P0_V1
      // §二/§三/§十) — a manual drag from an EXISTING/independent
      // `generate_text` node onto Video's dedicated
      // `VIDEO_PROMPT_ENHANCEMENT_HANDLE` (the only handle
      // `validateDropZoneTarget` lets a TEXT source land on for
      // `generate_video`, per `dataflow.ts`) now wires the EXACT SAME
      // canonical binding the auto-created Prompt Enhancer button
      // (`onCreateVideoPromptEnhancerNode`) installs — one binding
      // contract, two entry points, never a second/competing field
      // schema. This is NOT a UI-only fix: it patches
      // `boundPromptEnhancerNodeId` / `boundVideoNodeId` (so the SAME
      // `onVideoPromptEnhancementResult` completion handler that already
      // writes `enhancedPromptText` on every future regeneration fires
      // for this pair too) and — if the connected Text node already has
      // a real result — seeds `enhancedPromptText` immediately, so
      // connecting an already-generated node is not a silent no-op
      // edge (§十一 "禁止用 UI 假修复").
      if (
        (sourceType === 'generate_text' || sourceType === 'text_asset') &&
        targetType === 'generate_video' &&
        isOriginalPromptInputHandle(connection.targetHandle)
      ) {
        const textNodeId = connection.source as string
        const videoNodeId = connection.target as string
        const textFields = ((sourceNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const existingResultText =
          sourceType === 'text_asset'
            ? String(textFields.text ?? '').trim()
            : String((textFields.currentResult as { text?: string } | null | undefined)?.text || '').trim()

        commitEdges((eds) => {
          let base = stripConflictingGenerateVideoEdges(eds, connection, targetType)
          base = base.filter(
            (e) => !(e.target === videoNodeId && e.targetHandle === VIDEO_ORIGINAL_PROMPT_IN_HANDLE),
          )
          const next = addEdge(
            {
              ...connection,
              id: genId(),
              sourceHandle: connection.sourceHandle ?? 'text-out',
              targetHandle: VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
              data: { relationType: 'INPUT_TO' },
              className: 'pcv-edge',
            },
            base,
          )
          logCanvasEdgeDiagnostic({
            sourceNode: textNodeId,
            sourceHandle: connection.sourceHandle,
            targetNode: videoNodeId,
            targetHandle: VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
            semanticPort: VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
            logicalEdgeKey: `${textNodeId}::${videoNodeId}::${VIDEO_ORIGINAL_PROMPT_IN_HANDLE}`,
            createdBy: 'onConnect:original-prompt-in',
          })
          scheduleFromState(nodesRef.current, next)
          return next
        })

        if (existingResultText) {
          commitNodes((nds) => {
            const next = nds.map((n) => {
              if (n.id !== videoNodeId) return n
              const d = n.data as unknown as FlowNodeData
              return {
                ...n,
                data: {
                  ...d,
                  fields: { ...d.fields, promptText: existingResultText, effectivePromptSource: 'ORIGINAL' },
                } as unknown as Record<string, unknown>,
              }
            })
            nodesRef.current = next
            scheduleFromState(next, edgesRef.current)
            return next
          })
        }
        return
      }

      if (
        (sourceType === 'text_asset' || sourceType === 'generate_text') &&
        targetType === 'generate_image' &&
        isImagePromptInputHandle(connection.targetHandle)
      ) {
        const imageNodeId = connection.target as string
        const sourceText = extractTextFromTextSourceNode(sourceNode, sourceType)

        commitEdges((eds) => {
          const base = eds.filter(
            (e) => !(e.target === imageNodeId && e.targetHandle === IMAGE_PROMPT_IN_HANDLE),
          )
          const next = addEdge(
            {
              ...connection,
              id: genId(),
              sourceHandle: connection.sourceHandle ?? 'text-out',
              targetHandle: IMAGE_PROMPT_IN_HANDLE,
              data: { relationType: 'INPUT_TO' },
              className: 'pcv-edge',
            },
            base,
          )
          logCanvasEdgeDiagnostic({
            sourceNode: connection.source as string,
            sourceHandle: connection.sourceHandle,
            targetNode: imageNodeId,
            targetHandle: IMAGE_PROMPT_IN_HANDLE,
            semanticPort: IMAGE_PROMPT_IN_HANDLE,
            logicalEdgeKey: `${connection.source}::${imageNodeId}::${IMAGE_PROMPT_IN_HANDLE}`,
            createdBy: 'onConnect:image-prompt-in',
          })
          scheduleFromState(nodesRef.current, next)
          return next
        })

        if (sourceText) {
          commitNodes((nds) => {
            const next = nds.map((n) => {
              if (n.id !== imageNodeId) return n
              const d = n.data as unknown as FlowNodeData
              return {
                ...n,
                data: {
                  ...d,
                  fields: { ...d.fields, prompt: sourceText },
                } as unknown as Record<string, unknown>,
              }
            })
            nodesRef.current = next
            scheduleFromState(next, edgesRef.current)
            return next
          })
        }
        return
      }

      if (
        sourceType === 'generate_text' &&
        targetType === 'generate_video' &&
        isPromptEnhancementHandle(connection.targetHandle)
      ) {
        const textNodeId = connection.source as string
        const videoNodeId = connection.target as string
        const videoFields = ((targetNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const textFields = ((sourceNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const snapshot = buildVideoPromptEnhancementContextSnapshot({ videoNodeId, fields: videoFields })
        const existingResultText = String(
          (textFields.currentResult as { text?: string } | null | undefined)?.text || '',
        ).trim()
        const seedPatch =
          existingResultText && !looksLikePromptEnhancementRefusal(existingResultText)
            ? buildVideoPromptEnhancementWritebackPatch({
                enhancedPrompt: existingResultText,
                runId: (textFields.activeRunId as string | null) ?? null,
                skillKey: (textFields.skillKey as string | null) ?? VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
                skillVersion: (textFields.skillVersion as string | null) ?? VIDEO_PROMPT_ENHANCEMENT_SKILL_VERSION,
                textProviderId: (textFields.providerOptionId as string | null) ?? null,
                forceApply: true,
              })
            : {}

        commitEdges((eds) => {
          // Single active enhancer per Video node (ONE_PRIMARY, same
          // invariant `onCreateVideoPromptEnhancerNode` enforces) — drop
          // any stale PROMPT_ENHANCEMENT_* edge touching either side of
          // this new pair before installing the fresh round-trip.
          let next = eds.filter((e) => {
            const relation = (e.data as { relationType?: string } | undefined)?.relationType
            if (!isPromptEnhancementFieldBindingRelation(relation)) return true
            return !(
              e.target === videoNodeId ||
              e.source === videoNodeId ||
              e.target === textNodeId ||
              e.source === textNodeId
            )
          })
          next = addEdge(
            {
              id: genId(),
              source: videoNodeId,
              sourceHandle: 'original-prompt-out',
              target: textNodeId,
              targetHandle: 'prompt-context-in',
              data: {
                relationType: PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION,
                fieldBinding: {
                  source_field: 'promptText',
                  target_field: 'videoPromptContextSnapshot.original_prompt',
                  binding_type: 'video_prompt_enhancement_input',
                },
              },
              className: 'pcv-edge',
            },
            next,
          )
          next = addEdge(
            {
              id: genId(),
              source: textNodeId,
              sourceHandle: 'text-out',
              target: videoNodeId,
              targetHandle: VIDEO_PROMPT_ENHANCEMENT_HANDLE,
              data: {
                relationType: PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION,
                fieldBinding: {
                  source_field: 'currentResult.text',
                  target_field: 'enhancedPromptText',
                  binding_type: 'video_prompt_enhancement_output',
                },
              },
              className: 'pcv-edge',
            },
            next,
          )
          logCanvasEdgeDiagnostic({
            sourceNode: textNodeId,
            sourceHandle: 'text-out',
            targetNode: videoNodeId,
            targetHandle: VIDEO_PROMPT_ENHANCEMENT_HANDLE,
            semanticPort: VIDEO_PROMPT_ENHANCEMENT_HANDLE,
            logicalEdgeKey: `${textNodeId}::${videoNodeId}::${VIDEO_PROMPT_ENHANCEMENT_HANDLE}`,
            createdBy: 'onConnect:prompt-enhancement',
          })
          scheduleFromState(nodesRef.current, next)
          return next
        })

        commitNodes((nds) => {
          const next = nds.map((n) => {
            if (n.id === textNodeId) {
              const d = n.data as unknown as FlowNodeData
              return {
                ...n,
                data: {
                  ...d,
                  fields: {
                    ...d.fields,
                    purpose: VIDEO_PROMPT_ENHANCEMENT_PURPOSE,
                    boundVideoNodeId: videoNodeId,
                    videoPromptContextSnapshot: snapshot,
                    ...(d.fields?.additionalInstruction
                      ? {}
                      : { additionalInstruction: VIDEO_PROMPT_ENHANCEMENT_DEFAULT_INSTRUCTION }),
                  },
                } as unknown as Record<string, unknown>,
              }
            }
            if (n.id === videoNodeId) {
              const d = n.data as unknown as FlowNodeData
              return {
                ...n,
                data: {
                  ...d,
                  fields: { ...d.fields, boundPromptEnhancerNodeId: textNodeId, ...seedPatch },
                } as unknown as Record<string, unknown>,
              }
            }
            return n
          })
          nodesRef.current = next
          scheduleFromState(next, edgesRef.current)
          return next
        })
        return
      }

      // §P14 — a manual `generate_image`/`generate_video` (gen-to-gen)
      // source is never in `CANVAS_INPUT_COMPATIBILITY_MATRIX` (only
      // real Asset-node source types are), so `semanticSlot` is always
      // `null` for these; the SAME `REFERENCES` relation the matrix row
      // would have picked for the Asset-node case is applied here too —
      // never a bespoke `INPUT_TO` for structurally-identical "an image/
      // video landed on a reference-shaped port" edges. `targetHandle`
      // (not `relationType`) is what actually distinguishes
      // character/product/scene/reference-asset/reference-video from
      // each other and from the generic reference tray — §P14 "relation
      // + targetHandle 共同确定...实际输入槽".
      // VistoeraTASK-00074 — Generate Audio structured ports. A
      // `generate_voice_template` VOICE_TEMPLATE source or a
      // `text_asset`/`generate_text` TEXT source landing on
      // `generate_voice` is ALWAYS classified by SOURCE/TARGET TYPE, not
      // by whatever raw `targetHandle` React Flow's nearest-handle guess
      // (or a collapsed-node drop onto the shared `GENERIC_INPUT_HANDLE`
      // dot) happened to resolve — see the `targetHandle:` override in
      // the `addEdge` call below. This guarantees the PERSISTED edge
      // always carries the one correct dedicated handle id regardless of
      // where the user actually dropped or whether the node was
      // collapsed at connect time (spec §10 "不得连接到节点中央/错误
      // section"), and that re-expanding the node always re-aligns the
      // edge to the real section (the dedicated id is a real DOM-owned
      // `<Handle>` inside that section once expanded — spec §18).
      const isVoiceTemplateToAudio =
        sourceType === 'generate_voice_template' &&
        targetType === 'generate_voice' &&
        (!connection.sourceHandle || connection.sourceHandle === 'voice-template-out')
      const isTextInputToAudio =
        (sourceType === 'text_asset' || sourceType === 'generate_text') &&
        targetType === 'generate_voice' &&
        (!connection.sourceHandle || connection.sourceHandle === 'text-out')
      const relationType =
        semanticSlot?.edgeRelation
          ? semanticSlot.edgeRelation
          : isVoiceTemplateToAudio
            ? 'VOICE_TEMPLATE'
            : isTextInputToAudio
              ? 'INPUT_TO'
              : connection.targetHandle === GENERIC_INPUT_HANDLE ||
                  isVideoKeyframeHandle(connection.targetHandle) ||
                  isVideoStructuredAssetHandle(connection.targetHandle) ||
                  isVideoReferenceVideoHandle(connection.targetHandle) ||
                  isVideoAudioReferenceHandle(connection.targetHandle)
                ? 'REFERENCES'
                : 'INPUT_TO'
      const audioTargetId = connection.target as string
      const nodeTypeById = nodeTypeMapFromNodes(nodesRef.current)
      const priorVoiceTemplateEdges = isVoiceTemplateToAudio
        ? voiceTemplateEdgesToTarget(edgesRef.current, audioTargetId, nodeTypeById)
        : []
      const templateSourceChanged =
        isVoiceTemplateToAudio &&
        (priorVoiceTemplateEdges.length === 0 || priorVoiceTemplateEdges[0]?.source !== connection.source)
      const generateAudioFields = (targetNode.data as unknown as FlowNodeData).fields || {}
      const targetHadAudioResult = generateAudioFields.status === 'COMPLETED' && Boolean(generateAudioFields.audioAssetId)
      // Atomic Related Assets bind: edge + fields.assets in one mutation
      // path (EDGE ⇔ BINDING). schedule once after both refs update.
      if (semanticSlot?.slot === 'RELATED_ASSETS') {
        const asset = buildRelatedAssetFromImageNode(sourceNode)
        if (!asset) {
          message.warning('此资产不能作为该节点的输入')
          return
        }
        commitEdges((eds) => {
          let base = eds.filter(
            (e) =>
              !(
                e.source === connection.source &&
                e.target === connection.target &&
                ((e.data as { relationType?: string } | undefined)?.relationType === RELATED_ASSET_EDGE_RELATION ||
                  (e.data as { relationType?: string } | undefined)?.relationType === 'INPUT_TO')
              ),
          )
          const nextEdges = addEdge(
            { ...connection, id: genId(), data: { relationType }, className: 'pcv-edge' },
            base,
          )
          commitNodes((nds) => {
            const nextNodes = nds.map((n) => {
              if (n.id !== connection.target) return n
              const d = n.data as unknown as FlowNodeData
              const fields = markGenerationStaleIfHasResult(
                mergeRelatedAssetIntoFields({ ...(d.fields || {}) }, asset, d.nodeType),
              )
              return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
            })
            scheduleFromState(nextNodes, nextEdges)
            return nextNodes
          })
          return nextEdges
        })
        return
      }

      commitEdges((eds) => {
        // Keyframe Drop Target (spec §7–§9) — each of START/MIDDLE/END is
        // exactly ONE slot this round (MIDDLE's backend contract stays
        // array-based, but this round's UI only ever offers a single
        // MIDDLE handle) — connecting a new image onto an already-
        // occupied slot REPLACES the previous occupant's edge rather
        // than silently stacking a second one nothing in the UI could
        // ever show.
        // Reference Video (spec §P16) is the SAME single-slot
        // replace-on-reconnect semantic as START/END above (never
        // accumulates a list) — filtered alongside it rather than as a
        // separate `base =` reassignment.
        let next: typeof eds
        if (targetType === 'generate_video') {
          // §九 — GenerateVideo semantic connections go through the ONE
          // canonical mutation boundary: canonical targetHandle (already
          // authoritative via the ACTIVE_DROP_TARGET override upstream in
          // `onConnect`) + strip-conflicting + single addEdge, in one call.
          next = commitVideoInputConnection(eds, connection, targetType, (c) => ({
            ...c,
            id: genId(),
            data: { relationType },
            className: 'pcv-edge',
          }))
          logCanvasEdgeDiagnostic({
            sourceNode: connection.source as string,
            sourceHandle: connection.sourceHandle,
            targetNode: connection.target as string,
            targetHandle: connection.targetHandle,
            semanticPort: connection.targetHandle ?? null,
            logicalEdgeKey: `${connection.source}::${connection.target}::${connection.targetHandle ?? ''}`,
            createdBy: 'onConnect:semantic',
          })
          assertCommittedDropMatchesPort(activeDropPortForAssert, connection.targetHandle ?? null)
        } else {
          let base: typeof eds =
            isVideoKeyframeHandle(connection.targetHandle) || isVideoReferenceVideoHandle(connection.targetHandle)
              ? eds.filter((e) => !(e.target === connection.target && e.targetHandle === connection.targetHandle))
              : eds
          if (connection.targetHandle === 'video-in' && targetType === 'motion_reconstruction') {
            base = eds.filter(
              (e) => !(e.target === connection.target && (e.targetHandle === 'video-in' || !e.targetHandle)),
            )
          }
          if (isTryOnInputHandle(connection.targetHandle) && targetType === 'generate_tryon') {
            base = eds.filter(
              (e) => !(e.target === connection.target && e.targetHandle === connection.targetHandle),
            )
          }
          if (isVoiceTemplateToAudio) {
            base = stripVoiceTemplateEdgesToTarget(base, audioTargetId, nodeTypeById)
          }
          if (isTextInputToAudio) {
            base = stripTextInputEdgesToTarget(base, audioTargetId, nodeTypeById)
          }
          const newRefEdgeId = genId()
          next = addEdge(
            {
              ...connection,
              id: newRefEdgeId,
              // Force the canonical dedicated handle regardless of the
              // raw drop target (spec §10/§18 — see comment above).
              targetHandle: isVoiceTemplateToAudio
                ? AUDIO_VOICE_TEMPLATE_IN_HANDLE
                : isTextInputToAudio
                  ? AUDIO_TEXT_INPUT_HANDLE
                  // VistoeraTASK-00144 — a manually-dragged reference-image
                  // connection onto `generate_image`'s aggregate handle
                  // gets its OWN stable per-edge id right at creation
                  // (never the bare shared id) so Related Assets can
                  // mount a real one-to-one target the instant it
                  // expands — canonical binding/relation unchanged.
                  : (targetType === 'generate_image' || targetType === 'generate_text') &&
                      (connection.targetHandle === GENERIC_INPUT_HANDLE || connection.targetHandle == null)
                    ? imageReferenceAssetHandleId(newRefEdgeId)
                    : connection.targetHandle,
              data: { relationType },
              className: 'pcv-edge',
            },
            base,
          )
        }
        scheduleFromState(nodesRef.current, next)
        return next
      })

      if (isVoiceTemplateToAudio && templateSourceChanged && targetHadAudioResult) {
        commitNodes((nds) => {
          const next = nds.map((n) => {
            if (n.id !== audioTargetId) return n
            const d = n.data as unknown as FlowNodeData
            const fields = { ...(d.fields || {}), needsRegeneration: true }
            return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
          })
          scheduleFromState(next, edgesRef.current)
          return next
        })
      }
      if (isTryOnInputHandle(connection.targetHandle) && targetType === 'generate_tryon') {
        commitNodes((nds) => {
          const next = nds.map((n) => {
            if (n.id !== connection.target) return n
            const d = n.data as unknown as FlowNodeData
            const fields = { ...(d.fields || {}) }
            if (connection.targetHandle === TRYON_PERSON_HANDLE) {
              fields.personAssetId = null
              fields.personLabel = null
            } else if (connection.targetHandle === TRYON_GARMENT_HANDLE) {
              fields.garmentAssetId = null
              fields.garmentLabel = null
            }
            const hadResult = !!(fields.generatedOutputId || fields.outputAssetId)
            if (hadResult) fields.needsRegeneration = true
            return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
          })
          scheduleFromState(next, edgesRef.current)
          return next
        })
      }
    },
    [commitEdges, commitNodes, scheduleFromState, workspaceId, t],
  )
  // (VISTOERA_CONTENT_CANVAS_VIDEO_NODE_DROPZONE_PORT_MODEL_SIZE_AND_BACKGROUND_FIX_P0
  // §8) — passed straight to `<ReactFlow isValidConnection>`, so React
  // Flow's OWN internal connection state (`useConnection()`, and the
  // `.valid`/`.connectingto` classes every `<Handle>` already gets for
  // free — see `GenerateVideoNode.tsx`'s drop-zone CSS) reflects the
  // EXACT SAME asset-type compatibility rule `onConnect` above enforces
  // at drop time — never a second, drifting copy of that rule. Runs
  // once per pointer-move against only the single nearest/hit-tested
  // candidate handle (`@xyflow/system`'s own `isValidHandle`), so it
  // stays cheap regardless of graph size; duplicate-edge/cycle checks
  // are deliberately NOT re-run here (both are "is this ADD legal"
  // checks with graph-wide cost, not "is this asset TYPE compatible"
  // — `onConnect` alone still gates them, unaffected by this hint).
  const isValidConnection = useCallback(
    (edgeOrConnection: Edge | Connection) => {
      try {
        const completeness = validateCanvasConnection(edgeOrConnection)
        if (completeness === 'INCOMPLETE') return false
        if (completeness === 'INVALID') return false
        const { source, target, targetHandle } = edgeOrConnection
        const sourceNode = nodesRef.current.find((n) => n.id === source)
        const targetNode = nodesRef.current.find((n) => n.id === target)
        if (!sourceNode || !targetNode) return false
        const sourceType = (sourceNode.data as unknown as FlowNodeData).nodeType
        const targetType = (targetNode.data as unknown as FlowNodeData).nodeType
        if (!evaluateConnection(sourceType, targetType, targetHandle, t).ok) return false
        if (
          wouldExceedImageReferenceLimitOnConnect({
            edges: edgesRef.current,
            nodes: nodesRef.current.map((n) => ({
              id: n.id,
              data: n.data as { nodeType?: string; fields?: Record<string, unknown> },
            })),
            connection: edgeOrConnection,
            config: imageGenerationConfigRef.current,
            workspaceId,
          })
        ) {
          return false
        }
        return true
      } catch (err) {
        console.error('[CanvasBoard] isValidConnection failed safely', err, edgeOrConnection)
        return false
      }
    },
    [workspaceId, t],
  )

  const addNodeAt = useCallback(
    (nodeType: string, flowX: number, flowY: number, parentId?: string) => {
      if (!isVisibleInAddNodeCatalog(nodeType) || !isNodeCreatableInCatalog(nodeType)) {
        message.warning('该功能当前已由平台管理员下线，无法新建。')
        return null
      }
      const def = getNodeDefinition(nodeType)
      let position = { x: flowX, y: flowY }
      if (parentId) {
        const parent = nodesRef.current.find((n) => n.id === parentId)
        if (parent) position = { x: flowX - parent.position.x, y: flowY - parent.position.y }
      }
      const data: FlowNodeData = {
        nodeType,
        category: def.category,
        entityType: null,
        entityId: null,
        collapsed: false,
        fields:
          nodeType === 'generate_image'
            ? buildGenerateImageNodeDefaultFields(workspaceId, imageGenerationConfigRef.current)
            : nodeType === 'generate_video'
              ? buildGenerateVideoNodeDefaultFields()
              : nodeType === 'generate_voice_template'
                ? buildGenerateVoiceTemplateNodeDefaultFields()
                : nodeType === 'generate_voice'
                  ? buildGenerateAudioNodeDefaultFields()
                  : nodeType === 'generate_text'
                    ? buildGenerateTextNodeDefaultFields({ locale: i18n.language })
                    : { ...(def.defaultFields || {}) },
        latestRunId: null,
      }
      const newNode: Node = {
        id: genId(),
        type: def.family,
        position,
        data: data as unknown as Record<string, unknown>,
      }
      if (parentId) {
        newNode.parentId = parentId
        newNode.extent = 'parent'
      }
      if (def.family === 'scene') {
        newNode.width = 760
        newNode.height = 280
      }
      commitNodes((nds) => {
        const next = [...nds, newNode]
        scheduleFromState(next, edgesRef.current)
        return next
      })
      setSelectedNodeId(newNode.id)
      return newNode.id
    },
    [commitNodes, i18n.language, scheduleFromState, workspaceId],
  )

  // Left Library / Quick Add have no drop point of their own (unlike
  // drag-and-drop or the context menu, which know exactly where the
  // user clicked), so cascade around the viewport center in a small
  // grid instead of stacking every add on the exact same coordinate.
  // `image_asset` opens the global multi-select picker (via ref — the
  // picker state lives further down) and materializes N independent
  // nodes (1 asset = 1 node) instead of an empty card.
  const openBatchImageAssetPickerRef = useRef<(anchor?: { x: number; y: number }) => void>(() => {})
  const materializeImageAssetSelectionsRef = useRef<
    (
      selections: ImageAssetPickerSelection[],
      options?: { bindNodeId?: string; anchor?: { x: number; y: number } },
    ) => void
  >(() => {})
  const onLinkCreativeContextRef = useRef<
    ((
      scriptNodeId: string,
      payload: import('./types').CreativeContextDragPayload,
      sourceKind?: ScriptSourceKind,
    ) => void) | null
  >(null)
  const onLinkTextScriptSourceRef = useRef<
    ((scriptNodeId: string, sourceNodeId: string, scriptSource: import('./scriptSourceContext').ScriptSourceContext) => void) | null
  >(null)
  const onLinkScriptContextRef = useRef<
    ((storyboardNodeId: string, payload: import('./types').ScriptContextDragPayload) => void) | null
  >(null)

  const addNodeFromLibrary = useCallback(
    (nodeType: string) => {
      if (nodeType === 'image_asset') {
        openBatchImageAssetPickerRef.current()
        return null
      }
      const step = libraryAddCounterRef.current % 8
      libraryAddCounterRef.current += 1
      const col = step % 4
      const row = Math.floor(step / 4)
      const center = rf.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
      return addNodeAt(nodeType, center.x + col * 220, center.y + row * 140)
    },
    [addNodeAt, rf],
  )

  // Connection drop tracker lives above `forceEndAllCanvasDrag` (must not use
  // `useConnection()` — see attachConnectionDropTracker comment there).

  const onConnectStart: OnConnectStart = useCallback(
    (_event, params) => {
      // XYFlow fires this only when a connection gesture actually starts
      // (not mere handle hover). Selection clear lives in beginCanvasDrag.
      beginCanvasDrag('connection')
      if (params.nodeId) attachConnectionDropTracker(params.nodeId)
    },
    [beginCanvasDrag, attachConnectionDropTracker],
  )

  const onConnectEnd: OnConnectEnd = useCallback(
    (event, connectionState) => {
      // ALWAYS release the structural selection lock first — successful
      // connect, cancel-on-pane, and invalid-target abort all share this
      // callback; early-returns below must never leave the lock stuck.
      endCanvasDrag('connection')
      detachConnectionDropTracker()
      // The gesture is over either way (native connect already fired —
      // and already consulted ACTIVE_DROP_TARGET inside `onConnect` — or
      // the fallback below is about to run once more); never let a
      // stale target leak into the NEXT, unrelated drag gesture.
      clearActiveVideoDropTarget()
      clearActiveScriptDropTarget()
      if (connectionState.isValid || !connectionState.fromNode) return
      const clientX = 'changedTouches' in event ? event.changedTouches[0].clientX : (event as MouseEvent).clientX
      const clientY = 'changedTouches' in event ? event.changedTouches[0].clientY : (event as MouseEvent).clientY
      // Real Drop Zone fallback
      // (VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
      // §五/§六/§七) — React Flow only ever reports `isValid` for a
      // pointer-precise landing on a real `<Handle>` DOM element. A
      // release ANYWHERE inside a semantic zone (角色/产品/场景/其他参考/
      // 关键帧 chip/参考视频 section) that is NOT pixel-perfect on that
      // zone's own tiny Handle dot must still resolve to the IDENTICAL
      // target handle — never silently drop the gesture, never guess a
      // DIFFERENT (wrong) handle. `resolveGenerateVideoDropTarget`
      // (`videoInputPorts.ts`) is the ONE canonical resolver; the
      // synthesized `Connection` is handed to the SAME `onConnect` a
      // Handle-precise drag already goes through — so a resolved drop
      // and a precise drag produce a BYTE-IDENTICAL structured
      // assignment (spec §一 "DROP TARGET == EDGE TARGET == STRUCTURED
      // INPUT PORT"), never a second/parallel assignment code path.
      const dropElement = document.elementFromPoint(clientX, clientY)
      const targetNodeEl = dropElement?.closest('.react-flow__node[data-id]') ?? null
      const targetNodeId = targetNodeEl?.getAttribute('data-id') ?? null
      if (targetNodeId) {
        const targetNode = nodesRef.current.find((n) => n.id === targetNodeId)
        const targetType = (targetNode?.data as unknown as FlowNodeData | undefined)?.nodeType
        if (targetNode && targetNodeId !== connectionState.fromNode.id) {
          if (targetType === 'generate_video') {
            const resolved = resolveGenerateVideoDropTarget(dropElement)
            if (resolved) {
              onConnect({
                source: connectionState.fromNode.id,
                sourceHandle: connectionState.fromHandle?.id ?? null,
                target: targetNodeId,
                targetHandle: resolved.targetHandle,
              })
              return
            }
          }
          if (targetType === 'script_generation') {
            const resolved = resolveGenerateScriptDropTarget(dropElement)
            if (resolved) {
              onConnect({
                source: connectionState.fromNode.id,
                sourceHandle: connectionState.fromHandle?.id ?? null,
                target: targetNodeId,
                targetHandle: resolved.targetHandle,
              })
              return
            }
          }
        }
      }
      const targetIsPane = (event.target as HTMLElement)?.classList?.contains('react-flow__pane')
      if (!targetIsPane) return
      const flowPos = rf.screenToFlowPosition({ x: clientX, y: clientY })
      const sourceType = (connectionState.fromNode.data as unknown as FlowNodeData).nodeType
      setConnectionSuggest({
        x: clientX,
        y: clientY,
        flowX: flowPos.x,
        flowY: flowPos.y,
        sourceNodeId: connectionState.fromNode.id,
        sourceNodeType: sourceType,
      })
    },
    [rf, endCanvasDrag, detachConnectionDropTracker, onConnect],
  )

  const onPaneClick = useCallback(() => {
    if (expandedNodeId) bumpPlaybackReset()
    setSelectedNodeId(null)
    setExpandedNodeId(null)
    setDragTempCollapsedNodeId(null)
    setContextMenu(null)
    setNodeContextMenu(null)
    setConnectionSuggest(null)
  }, [bumpPlaybackReset, expandedNodeId])

  const onNodeClick: NodeMouseHandler = useCallback(
    (_e, node) => {
      const prevExpanded = expandedNodeId
      setSelectedNodeId(node.id)
      const family = node.type || 'entity'
      // Shell V2: generation cards — single expanded. A clean node click
      // (not a drag — React Flow suppresses click after drag) switches
      // expansion. Result-area click also expands via onExpandFunctionCardDetail.
      // Box-select / multi-select collapse is owned by onSelectionChange.
      // VistoeraTASK-00067 §2/§3 — `media` (image_asset's family) joins
      // `video_media`/`text_asset` here so a click on an Image / Asset
      // node participates in the SAME single-expanded-card contract those
      // already use (auto-collapses any other expanded node, ESC/pane-
      // click collapses it) instead of silently no-op'ing. `ImageAssetNode`
      // renders its own always-visible inline `.pcv-gen-inspector` below
      // the media body when `isDetailExpanded` — exactly the `video_media`
      // pattern — never the generic `NodeDetailShell` (still excluded via
      // `nodeTypeSupportsDetailPanel`, unchanged, avoiding a redundant
      // second panel per that function's own doc comment).
      if (
        isGenerationExpandFamily(family) ||
        family === 'video_media' ||
        family === 'text_asset' ||
        family === 'media' ||
        // VistoeraTASK-00171 — `audio_asset` joins the same canonical
        // "result/body click expands" contract as Image/Video Asset
        // (`media`/`video_media`) instead of AudioAssetNode hand-rolling
        // its own conflicting stopPropagation+direct-call mechanism.
        family === 'audio_asset'
      ) {
        if (prevExpanded && prevExpanded !== node.id) bumpPlaybackReset()
        setExpandedNodeId(node.id)
        return
      }
      if (nodeTypeSupportsDetailPanel(family)) {
        setExpandedNodeId((prev) => {
          if (prev === node.id) {
            bumpPlaybackReset()
            return null
          }
          if (prev) bumpPlaybackReset()
          return node.id
        })
        return
      }
      if (prevExpanded) bumpPlaybackReset()
      setExpandedNodeId(null)
    },
    [bumpPlaybackReset, expandedNodeId],
  )

  // ESC collapses the detail panel (spec §16) — checked here, not via
  // React Flow's own `onKeyDown` (that only fires while the pane itself
  // has focus, not while the user is interacting with panel content
  // like a text field).
  const nodeContextMenuRef = useRef(nodeContextMenu)
  nodeContextMenuRef.current = nodeContextMenu

  // VistoeraTASK-00609 — track blocking Modals so React Flow's global
  // Backspace handler can be disabled (deleteKeyCode=null) while the
  // image editor (or other vst-modal-surface) is open.
  const [blockingOverlayOpen, setBlockingOverlayOpen] = useState(false)
  useEffect(() => {
    const sync = () => {
      const open = isCanvasBlockingOverlayOpen()
      setBlockingOverlayOpen(open)
      if (open) {
        setNodeContextMenu(null)
        setContextMenu(null)
      }
    }
    sync()
    const mo = new MutationObserver(sync)
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })
    return () => mo.disconnect()
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // IME composing + text fields own keyboard entirely (incl. Escape during
      // composition / Backspace-Delete while typing). Never collapse cards or
      // fire Canvas shortcuts while the user is editing form text.
      if (shouldIgnoreCanvasShortcutEvent(e)) return
      if (e.key === 'Escape') {
        if (nodeContextMenuRef.current) {
          setNodeContextMenu(null)
          return
        }
        // Full Script / Shot Detail Modals own Escape first — collapsing the
        // Canonical Card underneath would violate "input / viewer interaction
        // must not mis-collapse" (Script V2 §34 / §49).
        if (isCanvasBlockingOverlayOpen()) return
        if (expandedNodeId) bumpPlaybackReset()
        setExpandedNodeId(null)
        setDragTempCollapsedNodeId(null)
      }
      if (!readOnly && (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        if (isCanvasBlockingOverlayOpen()) return
        e.preventDefault()
        setQuickAddOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [bumpPlaybackReset, expandedNodeId, readOnly])

  const onPaneContextMenu = useCallback(
    (event: MouseEvent | ReactMouseEvent) => {
      // VistoeraTASK-00609 — never open Canvas "Add node" menu through an
      // open image editor Modal (transparent mask still leaves RF live).
      if (isCanvasBlockingOverlayOpen()) {
        event.preventDefault()
        return
      }
      event.preventDefault()
      setNodeContextMenu(null)
      const clientX = (event as ReactMouseEvent).clientX
      const clientY = (event as ReactMouseEvent).clientY
      const flowPos = rf.screenToFlowPosition({ x: clientX, y: clientY })
      setContextMenu({ x: clientX, y: clientY, flowX: flowPos.x, flowY: flowPos.y })
    },
    [rf],
  )

  const onNodeContextMenu: NodeMouseHandler = useCallback(
    (event, node) => {
      if (isCanvasBlockingOverlayOpen()) {
        event.preventDefault()
        return
      }
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, [contenteditable="true"]')) return
      event.preventDefault()
      setContextMenu(null)
      setNodeContextMenu({ x: event.clientX, y: event.clientY, nodeId: node.id })
      setSelectedNodeId(node.id)
    },
    [],
  )

  const onMove = useCallback(
    (_e: unknown, vp: CanvasViewportDto) => {
      viewportRef.current = vp
      setNodeContextMenu(null)
      scheduleViewportPersist(vp)
    },
    [scheduleViewportPersist],
  )

  const onMoveEnd = useCallback(
    // P0 (VISTOERA_CANVAS_CAPACITY_CATEGORY_A_SCALING_FIX_AND_REBASELINE_V1
    // §16-§18) — viewport pan/zoom is purely local UX state now:
    // localStorage only, via `writePersistedCanvasViewport` immediately
    // above. It used to ALSO call `scheduleFromState(...)` here, which
    // scheduled a full-graph `PUT` (nodes+edges byte-identical, only the
    // viewport differing) on every single pan/zoom gesture — including
    // the imperative `rf.setViewport(saved)` viewport-restore-on-mount
    // effect above, which is what produced the "unchanged graph PUT within
    // the first few seconds after mount, with zero user interaction" bug
    // the real-Preview audit measured. Node/edge mutations still schedule
    // a save via their own handlers (`onNodeDragStop`, `onNodesChange`,
    // edge create/delete, ...) — those are untouched and still call
    // `scheduleFromState` exactly as before; only the viewport-only path
    // no longer does.
    (_e: unknown, vp: CanvasViewportDto) => {
      setViewport(vp)
      viewportRef.current = vp
      writePersistedCanvasViewport(workspaceId, projectId, vp)
    },
    [workspaceId, projectId],
  )

  const onNodeDragStart: OnNodeDrag = useCallback(
    (_e, node) => {
      beginCanvasDrag('node')
      setDraggingNodeId(node.id)
      setNodeContextMenu(null)
      if (expandedNodeId === node.id) {
        dragWasExpandedRef.current = true
        setDragTempCollapsedNodeId(node.id)
      } else {
        dragWasExpandedRef.current = false
      }
    },
    [expandedNodeId, beginCanvasDrag],
  )

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_e, draggedNode) => {
      endCanvasDrag('node')
      setDraggingNodeId(null)
      if (dragWasExpandedRef.current) {
        setDragTempCollapsedNodeId(null)
        dragWasExpandedRef.current = false
      }
      if (draggedNode.type === 'scene') {
        scheduleFromState(nodesRef.current, edgesRef.current)
        return
      }
      const intersecting = rf.getIntersectingNodes(draggedNode).filter((n) => n.type === 'scene' && n.id !== draggedNode.id)
      const targetScene = intersecting[0]
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== draggedNode.id) return n
          if (targetScene) {
            if (n.parentId === targetScene.id) return n
            return {
              ...n,
              parentId: targetScene.id,
              extent: 'parent' as const,
              position: { x: n.position.x - targetScene.position.x, y: n.position.y - targetScene.position.y },
            }
          }
          if (n.parentId) {
            const parent = nds.find((p) => p.id === n.parentId)
            const parentX = parent?.position.x ?? 0
            const parentY = parent?.position.y ?? 0
            const { parentId: _drop, extent: _dropExtent, ...rest } = n
            return { ...rest, position: { x: parentX + n.position.x, y: parentY + n.position.y } }
          }
          return n
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, rf, scheduleFromState, endCanvasDrag],
  )

  /** Multi-node drag via a rubber-band selection (`selectionOnDrag`) fires
   * `onSelectionDrag*` IN ADDITION TO `onNodeDrag*` for the node under the
   * pointer — union via `activeDragKindsRef` so this covers "drag a group
   * of already-selected nodes" too, not just a single node. */
  const onSelectionDragStart = useCallback(() => {
    beginCanvasDrag('selection')
  }, [beginCanvasDrag])
  const onSelectionDragStop = useCallback(() => {
    endCanvasDrag('selection')
  }, [endCanvasDrag])

  const onToggleCollapsed = useCallback(
    (nodeId: string) => {
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          return { ...n, data: { ...data, collapsed: !data.collapsed } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  const onChangeFields = useCallback(
    (nodeId: string, fields: Record<string, unknown>) => {
      commitNodes((nds) => {
        const target = nds.find((n) => n.id === nodeId)
        if (!target) return nds
        const data = target.data as unknown as FlowNodeData
        if (canvasFieldsShallowEqual((data.fields || {}) as Record<string, unknown>, fields)) {
          return nds
        }
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const nodeData = n.data as unknown as FlowNodeData
          return { ...n, data: { ...nodeData, fields } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  // Rebinds a node's canonical `entity_type`/`entity_id` (spec §6 "更换
  // 图片") — a distinct top-level `CanvasNodeDto` field from `data.fields`,
  // never folded into `onChangeFields`.
  const onChangeEntity = useCallback(
    (nodeId: string, entity: { entityType: string | null; entityId: string | null }) => {
      commitNodes((nds) => {
        const withEntity = nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          return {
            ...n,
            data: { ...data, entityType: entity.entityType, entityId: entity.entityId } as unknown as Record<string, unknown>,
          }
        })
        // Replace / change-source must update downstream reference thumbnails
        // (derived live from edges + upstream entityId) and mark generators
        // stale — never auto-submit generation tasks (P0 §一/§五/§十五).
        const next = markDownstreamStale(withEntity, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  const onUnbindRelatedAsset = useCallback(
    (nodeId: string, assetId: string) => {
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const d = n.data as unknown as FlowNodeData
          const fields = markGenerationStaleIfHasResult(
            removeRelatedAssetFromFields({ ...(d.fields || {}) }, { assetId }),
          )
          return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
        })
        commitEdges((eds) => {
          const filtered = eds.filter((e) => {
            if (e.target !== nodeId) return true
            const relation = (e.data as { relationType?: string } | undefined)?.relationType
            if (relation !== RELATED_ASSET_EDGE_RELATION && relation !== 'INPUT_TO') return true
            const source = nodesRef.current.find((n) => n.id === e.source)
            const sourceData = source?.data as unknown as FlowNodeData | undefined
            return !(sourceData?.nodeType === 'image_asset' && sourceData.entityId === assetId)
          })
          scheduleFromState(next, filtered)
          return filtered
        })
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  /** Picker → same Related Assets business state as edge bind. When a
   * matching image_asset node already exists on the board, also upsert a
   * RELATED_ASSET edge so EDGE ⇔ BINDING holds. */
  const onBindRelatedAssetsFromPicker = useCallback(
    (
      nodeId: string,
      assets: Array<{ asset_id: string; picker_item_id?: string; asset_type: string; display_name: string }>,
    ) => {
      commitNodes((nds) => {
        const nextNodes = nds.map((n) => {
          if (n.id !== nodeId) return n
          const d = n.data as unknown as FlowNodeData
          // Typed Record helper — do not use `{ ...(d.fields||{}), assets }`
          // (TS narrows that literal to `{ assets }` and drops generation keys).
          const fields = markGenerationStaleIfHasResult(
            replaceRelatedAssetsInFields({ ...(d.fields || {}) }, assets, d.nodeType),
          )
          return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
        })
        const assetIds = new Set(assets.map((a) => a.asset_id))
        commitEdges((eds) => {
          let nextEdges = eds.filter((e) => {
            if (e.target !== nodeId) return true
            const relation = (e.data as { relationType?: string } | undefined)?.relationType
            if (relation !== RELATED_ASSET_EDGE_RELATION && relation !== 'INPUT_TO') return true
            const source = nextNodes.find((n) => n.id === e.source)
            const sourceData = source?.data as unknown as FlowNodeData | undefined
            if (sourceData?.nodeType !== 'image_asset') return true
            return assetIds.has(sourceData.entityId || '')
          })
          for (const asset of assets) {
            const source = nextNodes.find((n) => {
              const d = n.data as unknown as FlowNodeData
              return d.nodeType === 'image_asset' && d.entityId === asset.asset_id
            })
            if (!source) continue
            const exists = nextEdges.some(
              (e) =>
                e.source === source.id &&
                e.target === nodeId &&
                ((e.data as { relationType?: string } | undefined)?.relationType === RELATED_ASSET_EDGE_RELATION ||
                  (e.data as { relationType?: string } | undefined)?.relationType === 'INPUT_TO'),
            )
            if (exists) continue
            nextEdges = [
              ...nextEdges,
              {
                id: genId(),
                source: source.id,
                target: nodeId,
                data: { relationType: RELATED_ASSET_EDGE_RELATION },
                className: 'pcv-edge',
              },
            ]
          }
          scheduleFromState(nextNodes, nextEdges)
          return nextEdges
        })
        return nextNodes
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // Persists `CanvasNodeDto.latest_run_id` (spec §22) — same top-level
  // rationale as `onChangeEntity` above.
  const onChangeLatestRunId = useCallback(
    (nodeId: string, runId: string | null) => {
      commitNodes((nds) => {
        const target = nds.find((n) => n.id === nodeId)
        if (!target) return nds
        const data = target.data as unknown as FlowNodeData
        if (data.latestRunId === runId) return nds
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const nodeData = n.data as unknown as FlowNodeData
          return { ...n, data: { ...nodeData, latestRunId: runId } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  // Input Material chip "×" / "移除引用" (P1 spec §11, generalized by P0
  // §18/§23) — breaks the CONNECTION only, never deletes the upstream
  // node itself. Disambiguated by `edgeId` (not `sourceNodeId`) since a
  // single Generation Node can now receive an IMAGE material from one
  // upstream node AND a TEXT material from a different one at once
  // (P1's `onDisconnectReference` assumed exactly one edge per source).
  // No confirm needed (spec §11 "不需要确认，这里可直接断线"); marks the
  // TARGET stale if it already has a result (§23), never touches the
  // result itself.
  const onDisconnectMaterial = useCallback(
    (targetNodeId: string, edgeId: string) => {
      commitEdges((eds) => {
        const next = eds.filter((e) => e.id !== edgeId)
        commitNodes((nds) => {
          // §57 — sync-clear Prompt inline chips for the removed reference.
          const cleaned = nds.map((n) => {
            if (n.id !== targetNodeId) return n
            const data = n.data as unknown as FlowNodeData
            const fields = { ...(data.fields || {}) }
            if (typeof fields.promptText === 'string') {
              fields.promptText = stripMentionsForEdge(fields.promptText, edgeId)
            }
            if (typeof fields.prompt === 'string') {
              fields.prompt = stripMentionsForEdge(fields.prompt, edgeId)
            }
            return { ...n, data: { ...data, fields } as unknown as Record<string, unknown> }
          })
          const staled = markNodeStaleIfHasResult(cleaned, targetNodeId)
          scheduleFromState(staled, next)
          return staled
        })
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // Text Asset inline editor (P0 V1) — persistence owned by `onUpdateTextAsset`;
  // downstream Input Material "编辑源素材" expands the source node inline.
  // Unified Image Asset Picker (P0) — ONE modal instance owned here, never
  // nested inside a React Flow node DOM subtree, so × / Cancel / ESC /
  // mask-click all share a single canonical close path.
  const [assetPickerRequest, setAssetPickerRequest] = useState<CanvasAssetPickerRequest | null>(null)
  const assetPickerRequestRef = useRef<CanvasAssetPickerRequest | null>(null)
  assetPickerRequestRef.current = assetPickerRequest
  const [videoAssetPickerRequest, setVideoAssetPickerRequest] = useState<CanvasVideoAssetPickerRequest | null>(null)
  const [audioAssetPickerRequest, setAudioAssetPickerRequest] = useState<CanvasAudioAssetPickerRequest | null>(null)
  const [audioEditorRequest, setAudioEditorRequest] = useState<AudioEditorModalRequest | null>(null)
  const [tryonAssetPickerRequest, setTryonAssetPickerRequest] = useState<CanvasTryOnAssetPickerRequest | null>(null)

  const closeAssetPicker = useCallback(() => {
    setImageAssetDraggingChrome(false)
    setAssetPickerRequest(null)
  }, [])

  // ==========================================================
  // VistoeraTASK-00134 — Canvas Global OS File Drop
  // ==========================================================
  // A real OS/file-manager file drag dropped onto the blank Canvas
  // surface (see `canvasFileDrop.ts`'s doc comment for the drag-kind
  // taxonomy). Deliberately separate from `materializeImageAssetSelections`
  // below — that one always assumes every selection is ALREADY a fully
  // uploaded `SiteAsset` and creates nodes synchronously; an OS file drop
  // needs an optimistic client-only placeholder node PER FILE while its
  // OWN upload is independently in flight (spec §4/§5/§10), and must
  // never persist a node before its canonical asset actually exists
  // (spec §13 option A — a page refresh mid-upload just loses the
  // in-flight placeholder client-side, never an orphan/broken saved
  // node).
  const OS_FILE_DROP_UPLOAD_CONCURRENCY = 4 // same bound as ImageAssetPicker.tsx's canonical UPLOAD_CONCURRENCY

  // nodeId -> original File, kept OFF `data` (which must stay a plain,
  // eventually-persisted-shaped bag) so "重试" can re-run the exact same
  // upload without ever touching persisted node shape.
  const osFileDropFilesRef = useRef<Map<string, File>>(new Map())
  // nodeId -> live local object URL, so every one can be revoked
  // (finalize success / remove / component unmount) without ever
  // leaking (spec §4 "必须正确 revokeObjectURL").
  const osFileDropPreviewUrlsRef = useRef<Map<string, string>>(new Map())

  const revokeOsFileDropPreview = useCallback((nodeId: string) => {
    const url = osFileDropPreviewUrlsRef.current.get(nodeId)
    if (url) {
      URL.revokeObjectURL(url)
      osFileDropPreviewUrlsRef.current.delete(nodeId)
    }
    osFileDropFilesRef.current.delete(nodeId)
  }, [])

  // Catches whatever local preview URL was still outstanding
  // (uploading/error, never removed/finalized) when this Canvas
  // unmounts — every other exit path already revokes its own.
  useEffect(() => {
    return () => {
      for (const url of osFileDropPreviewUrlsRef.current.values()) {
        URL.revokeObjectURL(url)
      }
      osFileDropPreviewUrlsRef.current.clear()
      osFileDropFilesRef.current.clear()
    }
  }, [])

  const finalizeLocalImageUpload = useCallback(
    (nodeId: string, assetId: string) => {
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          return {
            ...n,
            data: {
              ...data,
              entityType: 'site_asset',
              entityId: assetId,
              localUpload: null,
            } as unknown as Record<string, unknown>,
          }
        })
        // First time this specific node is actually eligible for
        // persistence — it now has a real canonical asset (spec §13).
        scheduleFromState(next, edgesRef.current)
        return next
      })
      revokeOsFileDropPreview(nodeId)
    },
    [commitNodes, scheduleFromState, revokeOsFileDropPreview],
  )

  const markLocalImageUploadFailed = useCallback(
    (
      nodeId: string,
      errorMessage: string,
      failureStage: 'upload_rejected' | 'upload_transport' | 'reconciliation' = 'upload_transport',
    ) => {
      if (import.meta.env.DEV) {
        console.warn('[CanvasBoard] local image upload failed', { nodeId, failureStage, errorMessage })
      }
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: {
                ...data.localUpload,
                status: 'error' as const,
                errorMessage,
                failureStage,
              },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
      // Still local-only — nothing to persist (spec §13).
    },
    [commitNodes],
  )

  // VistoeraTASK-00229 — sets the brief, automatic `'reconciling'` state
  // (see `types.ts`'s `localUpload.status` doc comment and
  // `canvasMediaImportReconciliation.ts`'s module doc comment for the
  // full forensic root cause: Cloudflare's edge-to-origin timeout budget
  // for `api-preview.vistoera.com` — proxied "Full (strict)" — can be
  // shorter than this app's own deliberately generous client timeout,
  // so a large-but-legal image can get an ambiguous no-response/gateway-
  // style failure while the origin actually finishes creating the Asset
  // seconds later). Never shown for a definite 4xx business rejection.
  const setLocalImageUploadReconciling = useCallback(
    (nodeId: string) => {
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: { ...data.localUpload, status: 'reconciling' as const, errorMessage: undefined },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
    },
    [commitNodes],
  )

  const uploadOneLocalImageFile = useCallback(
    async (file: File, nodeId: string) => {
      try {
        const { id } = await uploadSiteAssetIngest(workspaceId, siteId as string, file, {
          onReconciling: () => setLocalImageUploadReconciling(nodeId),
        })
        finalizeLocalImageUpload(nodeId, id)
      } catch (err) {
        const msg = siteAssetUploadErrorMessage(err, t('mediaCard.uploadImageFailed'))
        const reason = isAmbiguousUploadFailure(err) ? 'reconciliation' : 'upload_rejected'
        markLocalImageUploadFailed(nodeId, msg, reason)
      }
    },
    [
      workspaceId,
      siteId,
      finalizeLocalImageUpload,
      markLocalImageUploadFailed,
      setLocalImageUploadReconciling,
      t,
    ],
  )

  const retryLocalImageUpload = useCallback(
    (nodeId: string) => {
      const file = osFileDropFilesRef.current.get(nodeId)
      if (!file || !siteId) return
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: {
                ...data.localUpload,
                status: 'reconciling' as const,
                errorMessage: undefined,
                failureStage: undefined,
              },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
      void (async () => {
        const existing = await attemptSiteAssetUploadReconciliation(file, () =>
          listSiteAssets(workspaceId, siteId as string),
        )
        if (existing) {
          finalizeLocalImageUpload(nodeId, existing.id)
          return
        }
        commitNodes((nds) =>
          nds.map((n) => {
            if (n.id !== nodeId) return n
            const data = n.data as unknown as FlowNodeData
            if (!data.localUpload) return n
            return {
              ...n,
              data: {
                ...data,
                localUpload: { ...data.localUpload, status: 'uploading' as const, errorMessage: undefined },
              } as unknown as Record<string, unknown>,
            }
          }),
        )
        void uploadOneLocalImageFile(file, nodeId)
      })()
    },
    [commitNodes, uploadOneLocalImageFile, finalizeLocalImageUpload, workspaceId, siteId],
  )

  const removeLocalImageUpload = useCallback(
    (nodeId: string) => {
      // Never persisted while local-only (spec §13) — safe to just drop
      // it client-side, no Canvas-node-delete-confirm / asset-lifecycle
      // path applies here at all.
      commitNodes((nds) => nds.filter((n) => n.id !== nodeId))
      revokeOsFileDropPreview(nodeId)
    },
    [commitNodes, revokeOsFileDropPreview],
  )

  // Entry point: real OS files dropped on the blank Canvas surface
  // (spec §4/§5/§6/§7). Creates every placeholder node synchronously —
  // so the user sees ALL dropped images land immediately, none waiting
  // on any other — then kicks off a bounded-concurrency upload per file;
  // each file finalizes/fails completely independently (spec §5 "不能
  // ...因一张失败导致整批失败", §10 bounded concurrency).
  //
  // VistoeraTASK-00220 — receives an ALREADY-classified, ALREADY-size-
  // checked `images` array from `handleOsMediaFilesDropped` below (the
  // single real entry point for the `onDrop` dispatcher now); this
  // function itself never classifies or size-checks anymore, only
  // `siteId`-guards (the one thing genuinely specific to the image path
  // — `uploadSiteAsset` needs an explicit `site_id`, unlike
  // audio/video's workspace-scoped upload routes).
  const resolveEmptyImageAssetBindTarget = useCallback((bindNodeId?: string): string | undefined => {
    if (!bindNodeId) return undefined
    const target = nodesRef.current.find((n) => n.id === bindNodeId)
    if (!target) return undefined
    const data = target.data as unknown as FlowNodeData
    if (data.nodeType !== 'image_asset' || data.entityId || data.localUpload) return undefined
    return bindNodeId
  }, [])

  const handleOsImageFilesDropped = useCallback(
    (images: File[], anchor: { x: number; y: number }, options?: { bindNodeId?: string }) => {
      if (images.length === 0) return
      if (!siteId) {
        message.warning(t('mediaCard.selectImage'))
        return
      }

      const def = getNodeDefinition('image_asset')
      const bindNodeId = resolveEmptyImageAssetBindTarget(options?.bindNodeId)
      const cols = dropGridColumns(images.length)
      const positions = placeImageAssetGrid(images.length, anchor, { cols, colWidth: 500, rowHeight: 540 })
      const placeholderIds = images.map((_, i) => (i === 0 && bindNodeId ? bindNodeId : genId()))
      const newNodes: Node[] = []

      images.forEach((file, i) => {
        const previewUrl = URL.createObjectURL(file)
        const nodeId = placeholderIds[i]
        osFileDropFilesRef.current.set(nodeId, file)
        osFileDropPreviewUrlsRef.current.set(nodeId, previewUrl)
        const localPayload = {
          nodeType: 'image_asset' as const,
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: { ...(def.defaultFields || {}) },
          latestRunId: null,
          localUpload: { status: 'uploading' as const, previewUrl, filename: file.name },
        }
        if (i === 0 && bindNodeId) return
        newNodes.push({
          id: nodeId,
          type: def.family,
          position: positions[i],
          data: localPayload as unknown as Record<string, unknown>,
        })
      })

      commitNodes((nds) => {
        const withBind =
          bindNodeId && images[0]
            ? nds.map((n) => {
                if (n.id !== bindNodeId) return n
                const data = n.data as unknown as FlowNodeData
                const previewUrl = osFileDropPreviewUrlsRef.current.get(bindNodeId) || null
                return {
                  ...n,
                  data: {
                    ...data,
                    entityType: null,
                    entityId: null,
                    localUpload: {
                      status: 'uploading' as const,
                      previewUrl,
                      filename: images[0].name,
                    },
                  } as unknown as Record<string, unknown>,
                }
              })
            : nds
        return [...withBind, ...newNodes]
      })
      setSelectedNodeId(placeholderIds[0])

      const imageUploadConcurrency = resolveOsFileDropImageUploadConcurrency(images)
      void mapWithConcurrency(images, imageUploadConcurrency, (file, i) =>
        uploadOneLocalImageFile(file, placeholderIds[i]),
      )
    },
    [siteId, t, commitNodes, uploadOneLocalImageFile, resolveEmptyImageAssetBindTarget],
  )

  const handleOsImageFilesDroppedOnNode = useCallback(
    (nodeId: string, files: File[]) => {
      const target = nodesRef.current.find((n) => n.id === nodeId)
      if (!target) return
      const { images } = classifyDroppedFilesByMediaType(files)
      const accepted = images.filter((f) => isWithinCanvasMediaImportSizeLimit(f, 'image'))
      if (accepted.length === 0) {
        message.warning(t('canvasFileDrop.unsupportedAllToast'))
        return
      }
      handleOsImageFilesDropped(accepted, target.position, { bindNodeId: nodeId })
    },
    [handleOsImageFilesDropped, t],
  )

  // ==========================================================
  // VistoeraTASK-00220 — Canvas OS File Drop: Audio + Video
  // ==========================================================
  // Generalizes the exact same client-only local-upload lifecycle
  // `handleOsImageFilesDropped` above already ships for `image_asset`
  // (placeholder node -> bounded-concurrency upload -> finalize/fail,
  // never persisted until a real canonical asset exists, spec §13
  // option A) into ONE shared implementation for `audio_asset`/
  // `video_asset`, parameterized by `kind` (spec §四 "提取最小、安全的
  // 共享层，不要三套并行的状态机"). `image_asset` itself is deliberately
  // left as its own already-shipped, already-verified implementation
  // above rather than folded into this generic one, to avoid any
  // regression risk to a path already live in production — see the
  // TASK-00220 forensic report's `SHARED_STATE_MACHINE` field for the
  // honest accounting of this tradeoff.
  type OsDropMediaKind = 'audio' | 'video'

  // nodeId -> which of the two generic kinds this placeholder is, so
  // Retry knows which upload function to re-run without needing to
  // inspect the node's own `data.nodeType` (kept off `data` for the
  // same "never on the persisted-shaped bag" reason as the File/preview
  // refs above).
  const osMediaDropKindRef = useRef<Map<string, OsDropMediaKind>>(new Map())

  const finalizeLocalMediaUpload = useCallback(
    // `extraFields` (VistoeraTASK-00232) merges into `data.fields` at the
    // same time as the entityId/entityType write - used by the video
    // branch to also stash `videoAssetSnapshot` so playback/poster
    // resolution never depends on entityId carrying a "kind:" prefix
    // (see `bareVideoAssetEntityId` in canvasVideoAssetResolve.ts for
    // why entityId itself must stay a bare, <=36-char id here).
    (nodeId: string, entityId: string, entityType: string, extraFields?: Record<string, unknown>) => {
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          return {
            ...n,
            data: {
              ...data,
              entityType,
              entityId,
              localUpload: null,
              fields: extraFields ? { ...(data.fields || {}), ...extraFields } : data.fields,
            } as unknown as Record<string, unknown>,
          }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
      revokeOsFileDropPreview(nodeId)
      osMediaDropKindRef.current.delete(nodeId)
    },
    [commitNodes, scheduleFromState, revokeOsFileDropPreview],
  )

  const markLocalMediaUploadFailed = useCallback(
    (nodeId: string, errorMessage: string) => {
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: { ...data.localUpload, status: 'error' as const, errorMessage },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
    },
    [commitNodes],
  )

  // VistoeraTASK-00232 - same 'reconciling' transient state
  // setLocalImageUploadReconciling already shows for image (spec
  // section 16, "Ambiguous Timeout != FAILED"), extended here to the
  // shared audio/video media-drop path.
  const setLocalMediaUploadReconciling = useCallback(
    (nodeId: string) => {
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: { ...data.localUpload, status: 'reconciling' as const, errorMessage: undefined },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
    },
    [commitNodes],
  )

  // VistoeraTASK-00220/00221 §六/§七 "UNKNOWN ≠ FAILED" — every error this
  // catches (network drop, client-side timeout, definite HTTP 4xx from
  // the backend) is still surfaced as the SAME local `error` status
  // today (no separate UI state exists yet for "ambiguous, retry-safe"
  // vs "definitely rejected"); a more granular RECONCILING state is
  // recorded as a FOLLOW_UP (see `MEDIA_IMPORT_IDEMPOTENT_RETRY` in the
  // final report), not silently dropped.
  //
  // What makes a Retry SAFE here is NOT server-side checksum dedup — an
  // earlier draft of this change added exactly that to
  // `asset_runtime_service.create_asset`/`upload_audio_asset` and it was
  // reverted: this codebase has a pre-existing, deliberately-tested
  // contract (`test_create_asset_duplicate_bytes_are_safe`) that
  // uploading identical bytes twice MUST produce two independent Asset
  // rows (independent lifecycle/delete semantics — deduping risks one
  // node's delete silently taking down an asset a DIFFERENT node still
  // references). Content dedup and import-ATTEMPT idempotency are two
  // different problems; conflating them was the bug in that earlier
  // draft.
  //
  // The actual per-attempt identity already exists here for free: this
  // placeholder node's own `nodeId` (`genId()`'d once at drop time,
  // cached in `osFileDropFilesRef`/`osMediaDropKindRef`, and reused
  // UNCHANGED by every `retryLocalMediaUpload` call below) IS the
  // client-side "import attempt" id the spec calls `client_import_id` —
  // Retry re-runs the SAME attempt against the SAME node; a genuinely
  // new user-initiated drop of the identical file gets a brand-new
  // `nodeId` from a fresh `genId()` call, never reusing the old one.
  // There is currently no backend-side attempt-identity column/endpoint
  // this id gets sent to (`ASSET_CREATE_REQUEST_ID_EXISTING=NO`,
  // confirmed by reading `asset_runtime_service.py`/
  // `audio_asset_library_service.py`/`video_generation_assets.py` — none
  // of the three upload paths accept or store any request/operation id
  // today), so a Retry after a genuinely ambiguous (server-may-have-
  // succeeded) timeout can still legitimately create a second,
  // independent asset — which, per the contract above, is by design a
  // SAFE outcome in this codebase (never corruption, never a crash,
  // never an orphaned reference), just not byte-for-byte
  // cross-request idempotent. That gap is recorded as a FOLLOW_UP
  // (`CANONICAL_ASSET_IMPORT_IDEMPOTENCY`) rather than papered over with
  // checksum dedup.
  const uploadOneLocalMediaFile = useCallback(
    async (file: File, nodeId: string, kind: OsDropMediaKind) => {
      const msgFor = () => t(kind === 'audio' ? 'mediaCard.uploadAudioFailed' : 'mediaCard.uploadVideoFailed')
      if (kind === 'audio') {
        try {
          // A Canvas OS-dropped audio file is the user's own arbitrary
          // existing audio, never a categorized library item —
          // `RECORDING` ("未命名录音") is the closest real, already-
          // existing `UPLOADABLE_PRESENTATION_TYPES` value for "an
          // uncategorized audio file a tenant already has", never
          // invented from the prompt.
          const { data } = await uploadAudioAsset(
            workspaceId,
            file,
            'RECORDING',
            file.name.replace(/\.[^/.]+$/, '') || undefined,
          )
          finalizeLocalMediaUpload(nodeId, data.asset_id, 'site_asset')
        } catch (err) {
          const msg = (err as SaasApiError).message || msgFor()
          // VistoeraTASK-00232 - audio uploads are canonical SiteAsset
          // rows (same table + same real sha256 checksum image uploads
          // already use, see audio_asset_library_service.
          // upload_audio_asset), and list_assets_for_site filters only
          // by site_id (never media_type) - so listSiteAssets already
          // returns audio rows too. Reuses TASK-00229's exact checksum
          // reconciliation unchanged, just a longer lookback window
          // (audio's real client timeout is 20min vs image's 3min).
          if (!isAmbiguousUploadFailure(err) || !siteId) {
            markLocalMediaUploadFailed(nodeId, msg)
            return
          }
          setLocalMediaUploadReconciling(nodeId)
          try {
            const [checksum, { data: listData }] = await Promise.all([
              computeFileSha256Hex(file),
              listSiteAssets(workspaceId, siteId),
            ])
            const match = findReconciledAssetMatch(
              listData.items,
              checksum,
              file.name,
              Date.now(),
              AUDIO_RECONCILIATION_LOOKBACK_MS,
            )
            if (match) {
              finalizeLocalMediaUpload(nodeId, match.id, 'site_asset')
              return
            }
          } catch {
            // Reconciliation itself failed/couldn't confirm - fall
            // through to failed below, same as TASK-00229's image path.
          }
          markLocalMediaUploadFailed(nodeId, msg)
        }
        return
      }
      try {
        const { data } = await uploadVideoAsset(workspaceId, file, file.name.replace(/\.[^/.]+$/, '') || undefined)
          // VistoeraTASK-00232 - data.asset_id (bare id, <=36
          // chars) not data.id (the "upload:<uuid>" composite
          // presentation string, 43+ chars) - CanvasNode.entity_id
          // is a VARCHAR(36) column and the composite form overflows
          // it, failing the WHOLE canvas autosave PUT with a DB
          // error the moment this node is saved (confirmed via a
          // real Preview network response while testing this exact
          // path - see bareVideoAssetEntityId in
          // canvasVideoAssetResolve.ts for the full story, including
          // why the videoAssetSnapshot below is what actually feeds
          // playback/poster resolution, not entity_id itself).
          finalizeLocalMediaUpload(nodeId, data.asset_id, 'video_asset', {
            videoAssetSnapshot: { upload_asset_id: data.asset_id },
          })
      } catch (err) {
        const msg = (err as SaasApiError).message || msgFor()
        // VistoeraTASK-00232 - VideoGenerationAsset has NO checksum
        // column (forensically confirmed) and this codebase has no
        // migration tooling to safely add one this round. Falls back to
        // filename+size_bytes+recency via listVideoAssets's existing
        // upload-sourced items - never filename alone - and
        // deliberately never calls computeFileSha256Hex here (hashing a
        // legal-sized-up-to-300MB video would cost real JS heap for a
        // check with no server-side checksum to compare against anyway).
        if (!isAmbiguousUploadFailure(err)) {
          markLocalMediaUploadFailed(nodeId, msg)
          return
        }
        setLocalMediaUploadReconciling(nodeId)
        try {
          const { data: listData } = await listVideoAssets(workspaceId, { source: 'upload' })
          const candidates = listData.items
            .filter((it) => it.upload_asset_id)
            .map((it) => ({
              // VistoeraTASK-00232 - bare upload_asset_id, NOT the
              // "upload:<uuid>" composite form - entity_id is a
              // VARCHAR(36) column and the composite form overflows it
              // (see bareVideoAssetEntityId's doc comment for the full
              // story). This candidate id IS the value that gets
              // persisted as entity_id below, so it must stay bare.
              id: it.upload_asset_id as string,
              size_bytes: it.size_bytes,
              filename: it.filename,
              created_at: it.created_at,
            }))
          const match = findReconciledAssetMatchBySize(
            candidates,
            file.name,
            file.size,
            Date.now(),
            VIDEO_RECONCILIATION_LOOKBACK_MS,
          )
          if (match) {
            finalizeLocalMediaUpload(nodeId, match.id, 'video_asset', {
              videoAssetSnapshot: { upload_asset_id: match.id },
            })
            return
          }
        } catch {
          // Reconciliation itself failed/couldn't confirm - fall through.
        }
        markLocalMediaUploadFailed(nodeId, msg)
      }
    },
    [workspaceId, siteId, finalizeLocalMediaUpload, markLocalMediaUploadFailed, setLocalMediaUploadReconciling, t],
  )

  const retryLocalMediaUpload = useCallback(
    (nodeId: string) => {
      const file = osFileDropFilesRef.current.get(nodeId)
      const kind = osMediaDropKindRef.current.get(nodeId)
      if (!file || !kind) return
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (!data.localUpload) return n
          return {
            ...n,
            data: {
              ...data,
              localUpload: { ...data.localUpload, status: 'uploading' as const, errorMessage: undefined },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
      void uploadOneLocalMediaFile(file, nodeId, kind)
    },
    [commitNodes, uploadOneLocalMediaFile],
  )

  const removeLocalMediaUpload = useCallback(
    (nodeId: string) => {
      commitNodes((nds) => nds.filter((n) => n.id !== nodeId))
      revokeOsFileDropPreview(nodeId)
      osMediaDropKindRef.current.delete(nodeId)
    },
    [commitNodes, revokeOsFileDropPreview],
  )

  const handleOsAudioFilesDropped = useCallback(
    (files: File[], anchor: { x: number; y: number }) => {
      if (files.length === 0) return
      const def = getNodeDefinition('audio_asset')
      const cols = dropGridColumns(files.length)
      const positions = placeImageAssetGrid(files.length, anchor, { cols, colWidth: 320, rowHeight: 190 })
      const placeholderIds = files.map(() => genId())
      const newNodes: Node[] = files.map((file, i) => {
        const nodeId = placeholderIds[i]
        osFileDropFilesRef.current.set(nodeId, file)
        osMediaDropKindRef.current.set(nodeId, 'audio')
        return {
          id: nodeId,
          type: def.family,
          position: positions[i],
          data: {
            nodeType: 'audio_asset',
            category: def.category,
            entityType: null,
            entityId: null,
            collapsed: false,
            fields: { ...(def.defaultFields || {}) },
            latestRunId: null,
            // No local blob preview for audio (spec §十四 only requires
            // filename/uploading state, not a waveform) — never fetched/
            // decoded synchronously either way.
            localUpload: { status: 'uploading' as const, previewUrl: null, filename: file.name },
          } as unknown as Record<string, unknown>,
        }
      })
      commitNodes((nds) => [...nds, ...newNodes])
      setSelectedNodeId(placeholderIds[0])
      void mapWithConcurrency(files, OS_FILE_DROP_UPLOAD_CONCURRENCY, (file, i) =>
        uploadOneLocalMediaFile(file, placeholderIds[i], 'audio'),
      )
    },
    [commitNodes, uploadOneLocalMediaFile],
  )

  const handleOsVideoFilesDropped = useCallback(
    (files: File[], anchor: { x: number; y: number }) => {
      if (files.length === 0) return
      const def = getNodeDefinition('video_asset')
      const cols = dropGridColumns(files.length)
      const positions = placeImageAssetGrid(files.length, anchor, { cols, colWidth: 480, rowHeight: 380 })
      const placeholderIds = files.map(() => genId())
      const newNodes: Node[] = files.map((file, i) => {
        const nodeId = placeholderIds[i]
        osFileDropFilesRef.current.set(nodeId, file)
        osMediaDropKindRef.current.set(nodeId, 'video')
        return {
          id: nodeId,
          type: def.family,
          position: positions[i],
          data: {
            nodeType: 'video_asset',
            category: def.category,
            entityType: null,
            entityId: null,
            collapsed: false,
            fields: { ...(def.defaultFields || {}) },
            latestRunId: null,
            // No local blob preview for video either (spec §二十五 — no
            // main-thread heavy media processing in the Drop handler;
            // even though `URL.createObjectURL` itself is O(1)/no-decode,
            // skipping it keeps this generic path's behavior identical
            // for both kinds and avoids an extra revocable resource
            // that's never actually rendered).
            localUpload: { status: 'uploading' as const, previewUrl: null, filename: file.name },
          } as unknown as Record<string, unknown>,
        }
      })
      commitNodes((nds) => [...nds, ...newNodes])
      setSelectedNodeId(placeholderIds[0])
      void mapWithConcurrency(files, OS_FILE_DROP_UPLOAD_CONCURRENCY, (file, i) =>
        uploadOneLocalMediaFile(file, placeholderIds[i], 'video'),
      )
    },
    [commitNodes, uploadOneLocalMediaFile],
  )

  // Single real entry point for the `onDrop` dispatcher below (spec §四
  // canonical flow: OS Drop -> Media Type Detection -> ... -> per-type
  // branch). Classifies ONCE, strips oversized files immediately (spec
  // §十一 "立即拒绝，而不是上传半天再失败") with ONE combined toast, then
  // fans out to the three independent per-type handlers above — each
  // one's own placeholder-creation + bounded-concurrency upload loop
  // runs fully independently, so one media type's slow/failed file can
  // never block or fail another's (spec §十七 per-file isolation).
  // Groups are stacked vertically (own row-count * rowHeight + a fixed
  // gap) so a real mixed drop (image+audio+video at once) lays out as
  // up to three non-overlapping groups (spec §十六) regardless of how
  // many files land in each group — reusing the SAME per-kind
  // colWidth/rowHeight each handler's own `placeImageAssetGrid` call
  // already uses, never a second/duplicated layout formula.
  const handleOsMediaFilesDropped = useCallback(
    (files: File[], anchor: { x: number; y: number }, options?: { bindNodeId?: string }) => {
      const { images, audios, videos, unsupported: unsupportedByType } = classifyDroppedFilesByMediaType(files)

      const oversized: File[] = []
      const acceptedImages = images.filter((f) => {
        if (isWithinCanvasMediaImportSizeLimit(f, 'image')) return true
        oversized.push(f)
        return false
      })
      const acceptedAudios = audios.filter((f) => {
        if (isWithinCanvasMediaImportSizeLimit(f, 'audio')) return true
        oversized.push(f)
        return false
      })
      const acceptedVideos = videos.filter((f) => {
        if (isWithinCanvasMediaImportSizeLimit(f, 'video')) return true
        oversized.push(f)
        return false
      })

      const acceptedTotal = acceptedImages.length + acceptedAudios.length + acceptedVideos.length

      if (acceptedTotal === 0) {
        if (oversized.length > 0 && unsupportedByType.length === 0) {
          const maxMb =
            images.length > 0 ? formatMaxSizeMb('image') : audios.length > 0 ? formatMaxSizeMb('audio') : formatMaxSizeMb('video')
          message.warning(t('canvasFileDrop.oversizedToast', { count: oversized.length, maxMb }))
        } else {
          message.warning(t('canvasFileDrop.unsupportedAllToast'))
        }
        return
      }

      if (oversized.length > 0 || unsupportedByType.length > 0) {
        message.info(
          t('canvasFileDrop.mixedMediaToast', {
            count: acceptedTotal,
            unsupportedCount: unsupportedByType.length + oversized.length,
          }),
        )
      }

      const groupGapY = 60
      let groupY = anchor.y
      const advanceGroup = (count: number, rowHeight: number) => {
        const cols = dropGridColumns(count)
        const rows = Math.max(1, Math.ceil(count / cols))
        const startY = groupY
        groupY += rows * rowHeight + groupGapY
        return { x: anchor.x, y: startY }
      }

      if (acceptedImages.length > 0) {
        handleOsImageFilesDropped(acceptedImages, advanceGroup(acceptedImages.length, 540), {
          bindNodeId: options?.bindNodeId,
        })
      }
      if (acceptedAudios.length > 0) {
        handleOsAudioFilesDropped(acceptedAudios, advanceGroup(acceptedAudios.length, 190))
      }
      if (acceptedVideos.length > 0) {
        handleOsVideoFilesDropped(acceptedVideos, advanceGroup(acceptedVideos.length, 380))
      }
    },
    [t, handleOsImageFilesDropped, handleOsAudioFilesDropped, handleOsVideoFilesDropped],
  )


  const materializeImageAssetSelections = useCallback(
    (
      selections: ImageAssetPickerSelection[],
      options?: { bindNodeId?: string; anchor?: { x: number; y: number } },
    ) => {
      if (selections.length === 0) return
      const def = getNodeDefinition('image_asset')
      const bindNodeId = options?.bindNodeId
      const remaining = bindNodeId ? selections.slice(1) : selections
      const first = selections[0]

      if (bindNodeId && first) {
        commitNodes((nds) => {
          const withEntity = nds.map((n) => {
            if (n.id !== bindNodeId) return n
            const data = n.data as unknown as FlowNodeData
            return {
              ...n,
              data: {
                ...data,
                entityType: 'site_asset',
                entityId: first.assetId,
              } as unknown as Record<string, unknown>,
            }
          })
          const next = markDownstreamStale(withEntity, edgesRef.current, bindNodeId)
          if (remaining.length === 0) {
            scheduleFromState(next, edgesRef.current)
            return next
          }
          const anchorNode = next.find((n) => n.id === bindNodeId)
          const positions = anchorNode
            ? placeSiblingNodesBesideSource(anchorNode, remaining.length, { rowHeight: 280, verticalGap: 24 })
            : placeImageAssetGrid(remaining.length, options?.anchor || { x: 0, y: 0 })
          const newNodes: Node[] = remaining.map((sel, i) => ({
            id: genId(),
            type: def.family,
            position: positions[i],
            data: {
              nodeType: 'image_asset',
              category: def.category,
              entityType: 'site_asset',
              entityId: sel.assetId,
              collapsed: false,
              fields: { ...(def.defaultFields || {}) },
              latestRunId: null,
            } as unknown as Record<string, unknown>,
          }))
          const withNew = [...next, ...newNodes]
          scheduleFromState(withNew, edgesRef.current)
          return withNew
        })
        setSelectedNodeId(bindNodeId)
        return
      }

      const center =
        options?.anchor ||
        rf.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
      const positions = placeImageAssetGrid(selections.length, center)
      const newNodes: Node[] = selections.map((sel, i) => ({
        id: genId(),
        type: def.family,
        position: positions[i],
        data: {
          nodeType: 'image_asset',
          category: def.category,
          entityType: 'site_asset',
          entityId: sel.assetId,
          collapsed: false,
          fields: { ...(def.defaultFields || {}) },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }))
      commitNodes((nds) => {
        const next = [...nds, ...newNodes]
        scheduleFromState(next, edgesRef.current)
        return next
      })
      if (newNodes[0]) setSelectedNodeId(newNodes[0].id)
    },
    [commitNodes, scheduleFromState, rf],
  )

  // VistoeraTASK-00625 — Image Assets "加入画布": after a NEW blank project
  // hydrates, consume the one-shot seed and materialize a single image_asset
  // node at the Canvas pane center (same factory as picker insert). Idempotent:
  // consume clears sessionStorage; mount ref blocks React Strict Mode double-fire.
  const joinImageSeedAppliedRef = useRef(false)
  useEffect(() => {
    if (canvasHydrating || readOnly || !siteId || !workspaceId || !projectId) return
    if (joinImageSeedAppliedRef.current) return
    const seed = consumeJoinImageAssetSeed(workspaceId, projectId)
    if (!seed) return
    joinImageSeedAppliedRef.current = true
    // Prefer `.react-flow__renderer` bounds — Canvas visible pane, not the
    // browser window and not chrome outside the RF surface.
    const pane = document.querySelector<HTMLElement>('.react-flow__renderer')
    const paneRect = pane?.getBoundingClientRect()
    const screenCenter = paneRect
      ? { x: paneRect.left + paneRect.width / 2, y: paneRect.top + paneRect.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    materializeImageAssetSelections(
      [
        {
          assetId: seed.siteAssetId,
          pickerItemId: seed.pickerItemId || `sa:${seed.siteAssetId}`,
          referenceType: 'site_asset',
          filename: seed.filename ?? null,
        },
      ],
      {
        anchor: rf.screenToFlowPosition(screenCenter),
      },
    )
    // After RF commits the node, force-pan so node center = pane center
    // (do not rely on focusNodePreserveZoom's "already visible" no-op).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const created = nodesRef.current.find((n) => {
          const d = n.data as unknown as FlowNodeData
          return d.nodeType === 'image_asset' && d.entityId === seed.siteAssetId
        })
        if (!created) return
        setSelectedNodeId(created.id)
        const width = getNodeWidth(created)
        const height = created.measured?.height || created.height || DEFAULT_NODE_WIDTH_FALLBACK
        const cx = created.position.x + width / 2
        const cy = created.position.y + height / 2
        const { zoom } = rf.getViewport()
        rf.setCenter(cx, cy, { zoom, duration: 400 })
      })
    })
  }, [
    canvasHydrating,
    readOnly,
    siteId,
    workspaceId,
    projectId,
    materializeImageAssetSelections,
    rf,
  ])

  // VistoeraTASK-00626 — Text Assets "加入画布": same blank-project + center
  // pattern as TASK-00625, but materializes the existing `text_asset` node
  // with a content snapshot (does not mutate the library asset row).
  const joinTextSeedAppliedRef = useRef(false)
  useEffect(() => {
    if (canvasHydrating || readOnly || !workspaceId || !projectId) return
    if (joinTextSeedAppliedRef.current) return
    const seed = consumeJoinTextAssetSeed(workspaceId, projectId)
    if (!seed) return
    joinTextSeedAppliedRef.current = true
    const def = getNodeDefinition('text_asset')
    const pane = document.querySelector<HTMLElement>('.react-flow__renderer')
    const paneRect = pane?.getBoundingClientRect()
    const screenCenter = paneRect
      ? { x: paneRect.left + paneRect.width / 2, y: paneRect.top + paneRect.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
    const flowCenter = rf.screenToFlowPosition(screenCenter)
    const width = TEXT_ASSET_CARD_WIDTH_PX
    const heightEstimate = 180
    const position = {
      x: flowCenter.x - width / 2,
      y: flowCenter.y - heightEstimate / 2,
    }
    const nodeId = genId()
    const data: FlowNodeData = {
      nodeType: 'text_asset',
      category: def.category,
      entityType: null,
      entityId: null,
      collapsed: false,
      fields: {
        text: seed.text,
        label: seed.title || undefined,
        // Provenance only — Canvas edits write this node's fields / its own
        // WorkspaceTextAsset via save_graph, never the library source row.
        libraryPresentationId: seed.presentationId,
        libraryCanonicalSourceType: seed.libraryCanonicalSourceType,
        libraryCanonicalSourceId: seed.libraryCanonicalSourceId,
        libraryAssetType: seed.assetType,
      },
      latestRunId: null,
    }
    const newNode: Node = {
      id: nodeId,
      type: def.family,
      position,
      data: data as unknown as Record<string, unknown>,
    }
    commitNodes((nds) => {
      const next = [...nds, newNode]
      scheduleFromState(next, edgesRef.current)
      return next
    })
    setSelectedNodeId(nodeId)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const created = nodesRef.current.find((n) => n.id === nodeId)
        if (!created) return
        const w = getNodeWidth(created) || width
        const h = created.measured?.height || created.height || heightEstimate
        const cx = created.position.x + w / 2
        const cy = created.position.y + h / 2
        const { zoom } = rf.getViewport()
        rf.setCenter(cx, cy, { zoom, duration: 400 })
      })
    })
  }, [canvasHydrating, readOnly, workspaceId, projectId, commitNodes, scheduleFromState, rf])

  const openBatchImageAssetPicker = useCallback(
    (anchor?: { x: number; y: number }) => {
      if (!siteId) {
        message.warning(t('mediaCard.selectImage'))
        return
      }
      setAssetPickerRequest({
        nodeId: '__canvas_batch_image_assets__',
        purpose: 'canvas_insert',
        title: t('mediaCard.selectImage'),
        selectionMode: 'multiple',
        maxSelection: 20,
        onConfirm: (selections) => {
          materializeImageAssetSelections(selections, { anchor })
        },
      })
    },
    [siteId, t, materializeImageAssetSelections],
  )

  openBatchImageAssetPickerRef.current = openBatchImageAssetPicker
  materializeImageAssetSelectionsRef.current = materializeImageAssetSelections

  const imageAssetPickerDropInFlightRef = useRef(false)

  /** VistoeraTASK-00591 — drop a single picker thumbnail onto the canvas.
   * Resolves via the same `resolveImagePickerSelection` path as「添加到画布」
   * (workspace ACL + asset reference), then reuses
   * `materializeImageAssetSelections` at the drop flow position. Never
   * expands multi-select — only the dragged card is placed. */
  const handleImageAssetPickerDropped = useCallback(
    async (pickerItemId: string, filename: string | null | undefined, flowPos: { x: number; y: number }) => {
      if (!siteId || !workspaceId || !pickerItemId) return
      if (imageAssetPickerDropInFlightRef.current) return
      imageAssetPickerDropInFlightRef.current = true
      try {
        const purpose = (assetPickerRequestRef.current?.purpose || 'canvas_insert') as ImageAssetPickerPurpose
        const target =
          purpose === 'video_input'
            ? 'video_gen_asset'
            : purpose === 'ai_chat_input'
              ? 'ai_chat_asset'
              : 'site_asset'
        const { data } = await resolveImagePickerSelection(workspaceId, {
          picker_item_id: pickerItemId,
          target,
          site_id: siteId,
        })
        const selection: ImageAssetPickerSelection = {
          assetId: data.asset_id,
          pickerItemId,
          referenceType: data.reference_type as ImageAssetPickerSelection['referenceType'],
          filename: filename ?? null,
        }
        materializeImageAssetSelectionsRef.current([selection], { anchor: flowPos })
        closeAssetPicker()
      } catch (err) {
        message.error((err as SaasApiError).message || t('mediaCard.selectImage'))
      } finally {
        imageAssetPickerDropInFlightRef.current = false
        setImageAssetDraggingChrome(false)
      }
    },
    [siteId, workspaceId, closeAssetPicker, t],
  )

  /** VistoeraTASK-00574 — Related Assets 「+ 添加」 on Generate Image.
   * Creates missing `image_asset` nodes left of the target and wires each
   * with a real REFERENCES edge (same contract as manual connect / video
   * keyframe shot refs). All-or-nothing when the remaining slot budget
   * would be exceeded; never silently truncates. */
  const onMaterializeAndConnectImageReferences = useCallback(
    (generateImageNodeId: string, selections: ImageAssetPickerSelection[]) => {
      if (!selections.length) return
      const target = nodesRef.current.find((n) => n.id === generateImageNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_image') return

      const graphNodes = nodesRef.current.map((n) => ({
        id: n.id,
        data: n.data as { nodeType?: string; fields?: Record<string, unknown> },
      }))
      const capability = resolveCapabilityForGenerateImageNode({
        config: imageGenerationConfigRef.current,
        fields: targetData.fields || {},
        workspaceId,
      })
      const currentCount = countImageReferenceEdgesForTarget(edgesRef.current, graphNodes, generateImageNodeId)
      const remaining = Math.max(0, capability.maxReferenceImages - currentCount)

      const seen = new Set<string>()
      const uniqueSelections: ImageAssetPickerSelection[] = []
      for (const sel of selections) {
        const assetId = (sel.assetId || '').trim()
        if (!assetId || seen.has(assetId)) continue
        seen.add(assetId)
        uniqueSelections.push(sel)
      }
      if (!uniqueSelections.length) return

      const connectedAssetIds = new Set<string>()
      for (const edge of edgesRef.current) {
        if (edge.target !== generateImageNodeId) continue
        const handle = edge.targetHandle ?? null
        if (handle !== null && !isImageReferenceAssetHandle(handle)) continue
        const source = nodesRef.current.find((n) => n.id === edge.source)
        const sourceData = source?.data as unknown as FlowNodeData | undefined
        if (sourceData?.nodeType === 'image_asset' && sourceData.entityId) {
          connectedAssetIds.add(sourceData.entityId)
        }
      }

      const toConnect = uniqueSelections.filter((s) => !connectedAssetIds.has(s.assetId))
      if (!toConnect.length) return
      if (remaining <= 0 || toConnect.length > remaining) {
        message.warning(relatedAssetsAddLimitMessage(capability, t))
        return
      }

      const def = getNodeDefinition('image_asset')
      const IMAGE_ASSET_CARD_WIDTH = DEFAULT_NODE_WIDTH_FALLBACK
      const newNodes: Node[] = []
      const edgeSpecs: Array<{ sourceId: string; edgeId: string }> = []

      for (const sel of toConnect) {
        const existing = nodesRef.current.find((n) => {
          const d = n.data as unknown as FlowNodeData
          return d.nodeType === 'image_asset' && d.entityId === sel.assetId
        }) || newNodes.find((n) => {
          const d = n.data as unknown as FlowNodeData
          return d.nodeType === 'image_asset' && d.entityId === sel.assetId
        })
        if (existing) {
          edgeSpecs.push({ sourceId: existing.id, edgeId: genId() })
          continue
        }
        const nodeId = genId()
        newNodes.push({
          id: nodeId,
          type: def.family,
          position: { x: 0, y: 0 }, // filled after we know how many to place
          data: {
            nodeType: 'image_asset',
            category: def.category,
            entityType: 'site_asset',
            entityId: sel.assetId,
            collapsed: false,
            fields: { ...(def.defaultFields || {}) },
            latestRunId: null,
          } as unknown as Record<string, unknown>,
        })
        edgeSpecs.push({ sourceId: nodeId, edgeId: genId() })
      }

      if (newNodes.length > 0) {
        const positions = placeSiblingNodesLeftOfTarget(target, newNodes.length, IMAGE_ASSET_CARD_WIDTH, {
          rowHeight: 220,
          verticalGap: 24,
        })
        newNodes.forEach((n, i) => {
          n.position = positions[i] || placeNodeLeftOfTarget(target, IMAGE_ASSET_CARD_WIDTH)
          if (target.parentId) {
            n.parentId = target.parentId
            n.extent = 'parent'
          }
        })
      }

      commitNodes((nds) => {
        const nextNodes = newNodes.length ? [...nds, ...newNodes] : nds
        nodesRef.current = nextNodes
        commitEdges((eds) => {
          let next = eds
          for (const spec of edgeSpecs) {
            const already = next.some((e) => {
              if (e.source !== spec.sourceId || e.target !== generateImageNodeId) return false
              const handle = e.targetHandle ?? null
              // Match countImageReferenceEdgesForTarget: bare/null and scoped
              // reference handles all count as an existing image input.
              if (handle !== null && !isImageReferenceAssetHandle(handle)) return false
              return true
            })
            if (already) continue
            next = addEdge(
              {
                id: spec.edgeId,
                source: spec.sourceId,
                sourceHandle: 'image-out',
                target: generateImageNodeId,
                targetHandle: imageReferenceAssetHandleId(spec.edgeId),
                data: { relationType: 'REFERENCES' },
                className: 'pcv-edge',
              },
              next,
            )
          }
          scheduleFromState(nextNodes, next)
          return next
        })
        return nextNodes
      })
    },
    [commitEdges, commitNodes, scheduleFromState, t, workspaceId],
  )

  const closeVideoAssetPicker = useCallback(() => {
    setVideoAssetPickerRequest(null)
  }, [])

  const closeTryonAssetPicker = useCallback(() => {
    setTryonAssetPickerRequest(null)
  }, [])

  const onOpenAssetPicker = useCallback((request: CanvasAssetPickerRequest) => {
    setAssetPickerRequest(request)
  }, [])

  const onOpenVideoAssetPicker = useCallback((request: CanvasVideoAssetPickerRequest) => {
    setVideoAssetPickerRequest(request)
  }, [])

  const closeAudioAssetPicker = useCallback(() => {
    setAudioAssetPickerRequest(null)
  }, [])

  const onOpenAudioAssetPicker = useCallback((request: CanvasAudioAssetPickerRequest) => {
    setAudioAssetPickerRequest(request)
  }, [])

  const onOpenAudioEditor = useCallback((request: AudioEditorModalRequest) => {
    setAudioEditorRequest(request)
  }, [])

  const closeAudioEditor = useCallback(() => {
    setAudioEditorRequest(null)
  }, [])

  const onOpenTryOnAssetPicker = useCallback((request: CanvasTryOnAssetPickerRequest) => {
    setTryonAssetPickerRequest(request)
  }, [])

  const onBindVideoAsset = useCallback(
    (nodeId: string, item: VideoAssetItem) => {
      commitNodes(() => {
        const withEntity = nodesRef.current.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType === 'motion_reconstruction') {
            return {
              ...n,
              data: {
                ...data,
                fields: {
                  ...(data.fields || {}),
                  inputVideoAssetId: item.id,
                  inputVideoLabel: item.title || item.filename || undefined,
                },
              } as unknown as Record<string, unknown>,
            }
          }
          if (data.nodeType === 'apparel_video_replication') {
            // VistoeraTASK-00161 — `source_video` (spec §4) binds the
            // canonical `VideoGenerationAsset` id into this node's OWN
            // field, never `entityId` (this node's identity IS the
            // generation job, not the reference video). The Video Asset
            // Library's unified id space also covers `job:`/`composite:`
            // generation OUTPUTS (VideoGenerationOutputAsset rows), which
            // the backend's `source_video_asset_id` contract cannot yet
            // resolve (no re-hosting/materialize step exists for those —
            // see `asset_delivery.py`'s `deliver_source_video`, which only
            // ever loads a `VideoGenerationAsset` row). Rather than send an
            // id the backend will 404 on, reject non-`upload:` picks here
            // with an honest message — a real, scoped V1 limitation, not a
            // silent guess.
            if (!item.id.startsWith('upload:')) {
              message.error(
                t('apparelVideoReplication.sourceVideoUploadOnly', {
                  defaultValue: '暂不支持直接选择生成结果作为参考视频，请选择已上传的视频文件',
                }),
              )
              return n
            }
            return {
              ...n,
              data: {
                ...data,
                fields: {
                  ...(data.fields || {}),
                  sourceVideoAssetId: item.id.slice('upload:'.length),
                  sourceVideoLabel: item.title || item.filename || undefined,
                },
              } as unknown as Record<string, unknown>,
            }
          }
          return {
            ...n,
            data: {
              ...data,
              entityType: 'video_asset',
              // VistoeraTASK-00232 - was `item.id` (a "kind:<uuid>"
              // composite presentation string, e.g. "upload:<uuid>" is
              // 43+ chars, "composite:<uuid>" is 46+) which overflows
              // CanvasNode.entity_id's VARCHAR(36) column and fails the
              // WHOLE canvas save with a DB error the moment this node
              // is persisted (confirmed via a real Preview network
              // response while testing the new OS-drop video path, same
              // bug, same fix). The snapshot two lines below already
              // carries every field playback/poster resolution actually
              // needs, so this bare id is a lossless, safe substitute.
              entityId: bareVideoAssetEntityId(item),
              fields: {
                ...(data.fields || {}),
                videoAssetSnapshot: videoAssetSnapshotFromItem(item),
              },
            } as unknown as Record<string, unknown>,
          }
        })
        const next = markDownstreamStale(withEntity, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState, t],
  )

  const onBindReferenceAudio = useCallback(
    (nodeId: string, item: AudioAssetItem) => {
      if (item.presentation_type === 'VOICE_TEMPLATE') return
      if (!item.usable) return
      commitNodes(() => {
        const nextNodes = nodesRef.current.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'generate_voice_template') return n
          const fields = { ...(data.fields || {}) }
          const hadResult = fields.status === 'COMPLETED' && !!fields.templateId
          return {
            ...n,
            data: {
              ...data,
              fields: {
                ...fields,
                referenceAssetId: item.id,
                referenceFilename: item.filename || item.name,
                referenceDurationMs: item.duration_ms ?? item.preview_duration_ms ?? null,
                referenceCaptureMethod: 'LIBRARY',
                referencePlaybackUrl: item.playback_url ?? null,
                ...(hadResult ? { needsRegeneration: true } : {}),
              },
            } as unknown as Record<string, unknown>,
          }
        })
        const next = markDownstreamStale(nextNodes, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  const onBindAudioVoiceTemplate = useCallback(
    (nodeId: string, item: AudioAssetItem) => {
      const selection = toAudioAssetPickerSelection(item)
      const capability = audioGenerationParameterCapabilityRef.current
      const capabilityDefaults = capability
        ? {
            pitch: capability.pitch.supported ? capability.pitch.default ?? null : null,
            speed: capability.speed.supported ? capability.speed.default ?? null : null,
          }
        : null
      commitNodes(() => {
        let didUpdate = false
        let previousTemplateId: string | null = null
        let previousPitch: number | null = null
        let previousSpeed: number | null = null
        const nextNodes = nodesRef.current.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'generate_voice') return n
          const fields = { ...(data.fields || {}) } as Record<string, unknown>
          const replacement = buildGenerateAudioLibraryVoiceTemplateReplacementFields({
            currentFields: fields,
            selection,
            capabilityDefaults,
          })
          if (!replacement) return n
          // VistoeraTASK-00217 — capture the OLD template's own
          // currently-live Pitch/Speed BEFORE overwriting `fields`, so
          // they can be remembered against that OLD template id (not
          // the new one) once we're outside this synchronous state
          // updater (spec §12: switching away must not lose what was
          // just dialed in for the template being left).
          if (fields.selectedVoiceTemplateId && fields.selectedVoiceTemplateId !== selection.voiceTemplateId) {
            previousTemplateId = fields.selectedVoiceTemplateId as string
            previousPitch = (fields.pitch as number | null | undefined) ?? null
            previousSpeed = (fields.speed as number | null | undefined) ?? null
          }
          didUpdate = true
          return {
            ...n,
            data: {
              ...data,
              fields: {
                ...fields,
                ...replacement,
              },
            } as unknown as Record<string, unknown>,
          }
        })
        if (!didUpdate) return nextNodes
        if (previousTemplateId && (previousPitch !== null || previousSpeed !== null)) {
          // Fire-and-forget — this is a courtesy "remember what I had"
          // write for the template being LEFT, never blocking/awaited by
          // the actual node switch, and never a Slider-drag-frequency
          // write (only fires once per template switch).
          saveVoiceTemplateGenerationPreference(workspaceId, previousTemplateId, {
            pitch: previousPitch,
            speed: previousSpeed,
          }).catch(() => {
            /* Best-effort memory write; the switch itself must not fail. */
          })
        }
        const next = markDownstreamStale(nextNodes, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState, workspaceId],
  )

  // VistoeraTASK-00115 — Audio Asset Node binding. Distinct from
  // `onBindAudioVoiceTemplate` above: persists the canonical `SiteAsset`
  // id as this node's own `entityId` (never a Voice Template id) plus a
  // tiny display snapshot, never a signed URL / binary (spec §6/§7/§43).
  // Defensively rejects a Voice Template selection even though the
  // picker request already excludes that category (spec §49/§51 —
  // belt-and-suspenders, never trust the picker's own filter alone).
  const onBindAudioAsset = useCallback(
    (nodeId: string, item: AudioAssetItem) => {
      const selection = toAudioAssetPickerSelection(item)
      if (selection.voiceTemplateId || !selection.usable) return
      const rawAssetId = item.id.startsWith('asset:') ? item.id.slice('asset:'.length) : item.id
      commitNodes(() => {
        const nextNodes = nodesRef.current.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'audio_asset') return n
          return {
            ...n,
            data: {
              ...data,
              entityType: 'site_asset',
              entityId: rawAssetId,
              fields: {
                ...(data.fields || {}),
                audioAssetSnapshot: {
                  name: selection.name,
                  presentation_type: selection.presentationType,
                },
              },
            } as unknown as Record<string, unknown>,
          }
        })
        const next = markDownstreamStale(nextNodes, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  // VistoeraTASK-00161 — 服装视频换装's optional BGM slot (spec §4:
  // `bgm_asset_id`, max 1, Audio Asset, connectable). Distinct from
  // `onBindAudioAsset` above: persists the SiteAsset id as THIS node's
  // own `fields.bgmAssetId`, never `entityId` (this node's identity is
  // the generation job, not the audio asset). Defensively rejects a
  // Voice Template selection the same way `onBindAudioAsset` does.
  const onBindApparelVideoBgm = useCallback(
    (nodeId: string, item: AudioAssetItem) => {
      const selection = toAudioAssetPickerSelection(item)
      if (selection.voiceTemplateId || !selection.usable) return
      const rawAssetId = item.id.startsWith('asset:') ? item.id.slice('asset:'.length) : item.id
      commitNodes(() => {
        const nextNodes = nodesRef.current.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'apparel_video_replication') return n
          return {
            ...n,
            data: {
              ...data,
              fields: {
                ...(data.fields || {}),
                bgmAssetId: rawAssetId,
                bgmLabel: selection.name,
              },
            } as unknown as Record<string, unknown>,
          }
        })
        const next = markDownstreamStale(nextNodes, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  const onBindTryOnAsset = useCallback(
    (nodeId: string, slot: 'person' | 'garment', selection: ImageAssetPickerSelection) => {
      const handle = slot === 'person' ? TRYON_PERSON_HANDLE : TRYON_GARMENT_HANDLE
      const assetId = selection.assetId
      const label = selection.filename || undefined
      commitEdges((eds) => {
        const nextEdges = eds.filter((e) => !(e.target === nodeId && e.targetHandle === handle))
        commitNodes(() => {
          const nextNodes = nodesRef.current.map((n) => {
            if (n.id !== nodeId) return n
            const data = n.data as unknown as FlowNodeData
            if (data.nodeType !== 'generate_tryon') return n
            const fields = { ...(data.fields || {}) }
            if (slot === 'person') {
              fields.personAssetId = assetId
              fields.personLabel = label
              const hadResult = !!(fields.generatedOutputId || fields.outputAssetId)
              if (hadResult) fields.needsRegeneration = true
            } else {
              fields.garmentAssetId = assetId
              fields.garmentLabel = label
              const hadResult = !!(fields.generatedOutputId || fields.outputAssetId)
              if (hadResult) fields.needsRegeneration = true
            }
            return { ...n, data: { ...data, fields } as unknown as Record<string, unknown> }
          })
          scheduleFromState(nextNodes, nextEdges)
          return nextNodes
        })
        return nextEdges
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onRequestEditText = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId)
      setExpandedNodeId((prev) => {
        if (prev === nodeId) return prev
        if (prev) bumpPlaybackReset()
        return nodeId
      })
    },
    [bumpPlaybackReset],
  )

  // §16–§18 "禁止自动重新生成" — updating a Text Asset only ever
  // patches this node's own `fields.text` + marks every downstream
  // Generation Node consuming it `needsRegeneration: true` (via
  // `markDownstreamStale`, same helper `onChangeEntity`/Replace already
  // uses); it NEVER re-submits any generation task itself.
  const onUpdateTextAsset = useCallback(
    (nodeId: string, text: string) => {
      commitNodes((nds) => {
        const target = nds.find((n) => n.id === nodeId)
        if (!target) return nds
        const data = target.data as unknown as FlowNodeData
        const prevText = (data.fields?.text as string | undefined) ?? ''
        if (prevText === text) return nds
        const withText = nds.map((n) => {
          if (n.id !== nodeId) return n
          const nodeData = n.data as unknown as FlowNodeData
          return { ...n, data: { ...nodeData, fields: { ...nodeData.fields, text } } as unknown as Record<string, unknown> }
        })
        const next = markDownstreamStale(withText, edgesRef.current, nodeId)
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  const onUpdateTextAssetPresentation = useCallback(
    (nodeId: string, patch: Record<string, unknown>) => {
      commitNodes((nds) => {
        const target = nds.find((n) => n.id === nodeId)
        if (!target) return nds
        const data = target.data as unknown as FlowNodeData
        const prevFields = data.fields || {}
        let changed = false
        const nextFields: Record<string, unknown> = { ...prevFields }
        for (const [key, value] of Object.entries(patch)) {
          if (nextFields[key] !== value) {
            nextFields[key] = value
            changed = true
          }
        }
        if (!changed) return nds
        const next = nds.map((n) => {
          if (n.id !== nodeId) return n
          const nodeData = n.data as unknown as FlowNodeData
          return { ...n, data: { ...nodeData, fields: nextFields } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  // Input Material chip "查看来源" (§15) — reuses the SAME
  // `selectedNodeId` state that drives Lineage Highlight below, so
  // "jump to the upstream node that produced this material" and
  // "select a node to see its full lineage" are one mechanism.
  const onSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId)
    // Shell V2: lineage / "查看来源" selects without expanding generation cards.
    // Expansion is owned exclusively by Result click → onExpandFunctionCardDetail.
  }, [])

  /** Media single-click: expand + sticky — never toggle-collapse when already expanded. */
  const onExpandFunctionCardDetail = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId)
      setExpandedNodeId((prev) => {
        if (prev === nodeId) return prev
        if (prev) bumpPlaybackReset()
        return nodeId
      })
    },
    [bumpPlaybackReset],
  )

  /** VISUAL_GROUPING_SOURCE_FOCUS_IMPORT_P0_V1 §13/§14/§15 — Keyframe
   * Card click → "focus/reveal Generate Image Node A". Deliberately a
   * thin wrapper around the EXISTING single-global-slot
   * `onExpandFunctionCardDetail` (never a second, parallel
   * expand/focus mechanism, spec-wide reuse discipline) plus a gentle
   * `fitView` so an off-screen source node is actually revealed, not
   * just marked expanded. Because `expandedNodeId` is a single value,
   * switching to a different keyframe's source automatically
   * un-expands the previous one — SOURCE_NODE_FOCUS_MODE=SINGLE for
   * free, with zero new focus-tracking state (spec §14/CASE_I/J). */
  const onFocusVideoKeyframeSourceNode = useCallback(
    (nodeId: string) => {
      onExpandFunctionCardDetail(nodeId)
      // Pan only — never fitView zoom-out that hides the Generate Video card
      // when both are expanded side-by-side (P0_V3 spatial placement).
      focusNodePreserveZoom(rf, nodeId, { duration: 400 })
    },
    [onExpandFunctionCardDetail, rf],
  )

  const initialFocusAppliedRef = useRef<string | null>(null)
  useEffect(() => {
    if (canvasHydrating || !initialFocusNodeId) return
    if (initialFocusAppliedRef.current === initialFocusNodeId) return
    const target = nodesRef.current.find((n) => n.id === initialFocusNodeId)
    if (!target) return
    initialFocusAppliedRef.current = initialFocusNodeId
    onExpandFunctionCardDetail(initialFocusNodeId)
    focusNodePreserveZoom(rf, initialFocusNodeId, { duration: 400 })
  }, [canvasHydrating, initialFocusNodeId, onExpandFunctionCardDetail, rf, nodes])

  // Hover "复制" on ANY media card family (spec §4/§12/§35) — new
  // Canvas node id, offset placement, same entity/asset reference
  // (never a second binary copy); for a `generate_image`/`generate_video`
  // source, also copies its incoming reference edges so the clone keeps
  // the same input relationship as the original. Placement (P1 spec
  // §20 "复制节点也审计 placement") goes through the SAME
  // `placeNodeBesideSource` helper as the Contextual Rail / "不覆盖，
  // 另外生成" below, instead of the old `+40/+40` diagonal nudge that
  // actually overlapped the source node.
  const onDuplicateNode = useCallback(
    (nodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === nodeId)
      if (!source) return
      const originData = source.data as unknown as FlowNodeData
      if (isNodeOfflineInCatalog(originData.nodeType) || !isNodeCreatableInCatalog(originData.nodeType)) {
        message.warning('该节点当前已下线，无法复制。')
        return
      }
      const newId = genId()

      if (isGenerationNodeType(originData.nodeType)) {
        const { nodeData, copiedEdges } = buildGenerationDuplicatePayload(
          originData,
          newId,
          nodeId,
          edgesRef.current,
          genId,
        )
        const newNode: Node = {
          id: newId,
          type: source.type,
          selected: false,
          position: placeNodeBesideSource(source),
          data: nodeData as unknown as Record<string, unknown>,
        }
        if (source.parentId) {
          newNode.parentId = source.parentId
          newNode.extent = 'parent'
        }
        commitNodes((nds) => [...nds, newNode])
        commitEdges((eds) => {
          const next = [...eds, ...copiedEdges]
          scheduleFromState(nodesRef.current, next)
          return next
        })
        return
      }

      const newNode: Node = {
        ...source,
        id: newId,
        selected: false,
        position: placeNodeBesideSource(source),
        data: { ...(source.data as unknown as FlowNodeData) } as unknown as Record<string, unknown>,
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const incoming = eds.filter((e) => e.target === nodeId)
        const copied = incoming.map((e) => ({ ...e, id: genId(), target: newId }))
        const next = [...eds, ...copied]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
      [commitEdges, commitNodes, scheduleFromState],
  )

  // VISTOERA_CANVAS_IMAGE_EDIT_P0 §27/§28/§29 — Canvas "编辑图片" → Vistoera
  // 图像编辑器 → "另存编辑图片" already created the new `SiteAsset` itself
  // (the overlay owns the upload call); this handler's only job is the
  // Canvas-side half — a new sibling `image_asset` node beside the source,
  // via the SAME `placeNodeBesideSource` collision-avoidance helper
  // `onDuplicateNode` uses above. Source node/asset are never touched, and
  // deliberately no edge (asset derivation, not a workflow dependency).
  const patchAudioAssetNodeFields = useCallback(
    (nodeId: string, patch: Record<string, unknown>) => {
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'audio_asset') return n
          return {
            ...n,
            data: {
              ...data,
              fields: { ...(data.fields || {}), ...patch },
            } as unknown as Record<string, unknown>,
          }
        }),
      )
    },
    [commitNodes],
  )

  // VistoeraTASK-00171 §八/§十 — ONE generic poller covers BOTH "just
  // accepted, show pending now" AND "page refresh mid-processing, still
  // pending" (a rehydrated node with `fields.audioTrimPending===true`
  // simply matches the same scan on the next tick — no separate
  // hydrate-time code path needed). Never flips a node to failed on a
  // mere network hiccup while polling — only an explicit
  // `status: 'failed'` response does that.
  const audioTrimPollInFlightRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      for (const n of nodesRef.current) {
        const d = n.data as unknown as FlowNodeData
        if (d.nodeType !== 'audio_asset' || !d.fields?.audioTrimPending || !d.entityId) continue
        if (audioTrimPollInFlightRef.current.has(n.id)) continue
        audioTrimPollInFlightRef.current.add(n.id)
        const entityId = d.entityId
        void fetchAudioTrimStatus(workspaceId, entityId)
          .then(({ data: res }) => {
            if (res.status === 'ready') {
              patchAudioAssetNodeFields(n.id, {
                audioTrimPending: false,
                audioTrimFailed: false,
                audioTrimError: null,
                audioAssetSnapshot: { name: res.name, presentation_type: res.presentation_type ?? null },
              })
            } else if (res.status === 'failed') {
              patchAudioAssetNodeFields(n.id, {
                audioTrimPending: false,
                audioTrimFailed: true,
                audioTrimError: res.error_message || '音频生成失败',
              })
            }
          })
          .catch(() => {
            /* transient poll failure — leave pending, next tick retries */
          })
          .finally(() => {
            audioTrimPollInFlightRef.current.delete(n.id)
          })
      }
    }
    tick()
    const interval = window.setInterval(tick, 3000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [workspaceId, patchAudioAssetNodeFields])

  const onRetryAudioTrim = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      const entityId = node ? (node.data as unknown as FlowNodeData).entityId : null
      if (!entityId) return
      patchAudioAssetNodeFields(nodeId, { audioTrimPending: true, audioTrimFailed: false, audioTrimError: null })
      void retryAudioTrim(workspaceId, entityId).catch(() => {
        patchAudioAssetNodeFields(nodeId, {
          audioTrimPending: false,
          audioTrimFailed: true,
          audioTrimError: '重试失败，请重试',
        })
      })
    },
    [workspaceId, patchAudioAssetNodeFields],
  )

  /** VistoeraTASK-00171 §八/§十 — creates the FINAL-identity sibling
   * `audio_asset` node right at accept-time, in `pending` state
   * (`fields.audioTrimPending=true`, `entityId` already the real,
   * server-persisted `asset_id`). This node is never deleted-and-
   * recreated: the poller above (and `onRetryAudioTrim`) only ever PATCH
   * this same node's `fields` in place as the backend's own
   * `status` transitions PROCESSING → READY/FAILED. */
  const createPendingAudioTrimSiblingNode = useCallback(
    (sourceNodeId: string, assetId: string, snapshot: { name: string; presentationType?: string | null }) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const def = getNodeDefinition('audio_asset')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: 'audio_asset',
        position: placeNodeBesideSource(source),
        data: {
          nodeType: 'audio_asset',
          category: def.category,
          entityType: 'site_asset',
          entityId: assetId,
          collapsed: false,
          fields: {
            ...(def.defaultFields || {}),
            audioAssetSnapshot: {
              name: snapshot.name,
              presentation_type: snapshot.presentationType ?? null,
            },
            audioTrimPending: true,
            audioTrimFailed: false,
            audioTrimError: null,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      commitNodes((nds) => {
        const next = [...nds, newNode]
        scheduleFromState(next, edgesRef.current)
        return next
      })
      setSelectedNodeId(newId)
    },
    [commitNodes, scheduleFromState],
  )

  const onCommitAudioTrim = useCallback(
    (payload: {
      sourceNodeId: string
      sourceAssetId: string
      workspaceId: string
      trimStartMs: number
      trimEndMs: number
    }) => {
      // TASK-00168 behavior preserved exactly: the Audio Editor modal
      // closes the instant the backend ACCEPTS the trim, never waiting
      // for the (potentially slow) ffmpeg finalize.
      closeAudioEditor()
      void trimAudioAsset(payload.workspaceId, payload.sourceAssetId, {
        trim_start_ms: payload.trimStartMs,
        trim_end_ms: payload.trimEndMs,
      })
        .then(({ data: res }) => {
          // §十 — the pending sibling node appears the INSTANT accept
          // resolves, using the accept response's own real `asset_id` as
          // this node's permanent identity (never a later-swapped id).
          createPendingAudioTrimSiblingNode(payload.sourceNodeId, res.asset_id, {
            name: res.name || '剪辑',
            presentationType: res.presentation_type ?? null,
          })
        })
        .catch(() => {
          // No node exists yet at this point (accept itself failed) — a
          // normal top-level error is correct here, NOT a node-level
          // failure banner (that only exists once accept has actually
          // succeeded and a node is on Canvas — see the poller/
          // `onRetryAudioTrim` above, spec §十四 "不允许出现两套不一致的
          // 错误 UI").
          message.error('保存剪辑失败，请重试')
        })
    },
    [closeAudioEditor, createPendingAudioTrimSiblingNode],
  )

  // VistoeraTASK-00300 — "另存编辑图片" must never declare success (and
  // let the Image Editor close) before the new Canvas node has genuinely
  // been persisted. Previously this handler only updated local React
  // state and handed the actual backend write to the general-purpose
  // 800ms-debounced autosave (`scheduleFromState`/`scheduleSave`) with
  // zero synchronization back to the caller — the overlay's `onSaveAsNew`
  // showed "已另存为新的图片素材" and closed the instant this synchronous
  // function returned, with no guarantee the debounced PUT had even
  // started, let alone succeeded. If that PUT later failed (observed live
  // on Preview during this task's own Forensics: a transient
  // `QueuePool ... connection timed out` 500), the failure only ever
  // reached `useDebouncedSave`'s internal `status: 'error'`, which has NO
  // user-facing surface anywhere in this file (`data-save-status` is a
  // test hook, not UI) — the new node would exist only in since-discarded
  // local state and silently vanish on next reload. Now: still add the
  // node optimistically (so Canvas visibility is still immediate, no
  // regression — §9/§10), but `await` the SAME debounced-save state
  // machine's `scheduleAndWaitForSettled` escape hatch (no second
  // parallel Canvas-save system — §9's explicit ban) before returning
  // success to the caller; roll the optimistic node back out and report
  // failure if persistence genuinely didn't happen, so the overlay can
  // show an explicit error instead of a false "另存成功" (§7).
  //
  // VistoeraTASK-00440 — this handler is no longer only ever called with
  // an `image_asset` SOURCE node (that was the only caller through
  // TASK-00300; `GenerateImageNode`'s "编辑图片" used to intentionally
  // no-op here instead, see that file's own doc comment). The NEW node
  // this handler creates is unconditionally an `image_asset` node
  // regardless of what kind of node it was saved FROM — so its React
  // Flow `type` (the key `canvasNodeTypes.ts`'s `CANVAS_NODE_TYPES` uses
  // to pick a component) must be `image_asset`'s OWN family (`def.family`,
  // currently `'media'` → `ImageAssetNode`), never `source.type` (a
  // `generate_image` source's `type` is `'generator'` → `GenerateImageNode`
  // — reusing it here would render the new node with the WRONG component
  // against `image_asset`-shaped `data`). This was a latent bug that
  // never surfaced while the only caller's `source.type` happened to
  // already equal `'media'` (an `image_asset` source edits itself via
  // this same family) — fixed here as part of wiring in the new caller.
  const onSaveEditedImageAsNewAsset = useCallback(
    async (sourceNodeId: string, newAssetId: string): Promise<boolean> => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return false
      const def = getNodeDefinition('image_asset')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source),
        data: {
          nodeType: 'image_asset',
          category: def.category,
          entityType: 'site_asset',
          entityId: newAssetId,
          collapsed: false,
          fields: { ...(def.defaultFields || {}) },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      let nextNodes: Node[] = nodesRef.current
      commitNodes((nds) => {
        nextNodes = [...nds, newNode]
        return nextNodes
      })
      setSelectedNodeId(newId)

      // Still hydrating from a prior load: `scheduleFromState` itself
      // would silently no-op in this state (see its own guard above), so
      // there is genuinely nothing to await yet — report failure rather
      // than a false success rather than assume it will "catch up" later.
      if (canvasHydrating) {
        commitNodes((nds) => nds.filter((n) => n.id !== newId))
        setSelectedNodeId(sourceNodeId)
        return false
      }

      const ok = await scheduleAndWaitForSettled(buildPayload(nextNodes, edgesRef.current, viewport))
      if (!ok) {
        // Roll back the optimistic node — never leave a Canvas node on
        // screen that looks saved but was never actually persisted. The
        // uploaded SiteAsset itself is untouched (still real, still
        // tagged with source_asset_id/source_canvas_id/source_node_id
        // provenance — not an untrackable orphan, §7); the caller can
        // simply retry "另存编辑图片" to attempt node creation again.
        commitNodes((nds) => nds.filter((n) => n.id !== newId))
        setSelectedNodeId(sourceNodeId)
      }
      return ok
    },
    [buildPayload, canvasHydrating, commitNodes, scheduleAndWaitForSettled, viewport],
  )

  const VOICE_TEMPLATE_NODE_WIDTH = 520

  const onCreateVoiceTemplateNode = useCallback(
    (targetNodeId: string) => {
      const target = nodesRef.current.find((n) => n.id === targetNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_voice') return
      const def = getNodeDefinition('generate_voice_template')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeLeftOfTarget(target, VOICE_TEMPLATE_NODE_WIDTH, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_voice_template',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateVoiceTemplateNodeDefaultFields(),
            candidateForGenerateAudioNodeId: targetNodeId,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (target.parentId) {
        newNode.parentId = target.parentId
        newNode.extent = 'parent'
      }
      // VistoeraTASK-00074 P5/P6 forensic fix — the new Voice Template
      // Generation node used to be created WITHOUT any real Canvas edge:
      // only `pendingVoiceTemplateCandidateNodeId`/
      // `candidateForGenerateAudioNodeId` FIELD pointers were set, and a
      // real `VOICE_TEMPLATE` edge was only ever created later by
      // `onConfirmCandidateVoiceTemplate` once generation completed. §6
      // explicitly requires the edge itself to exist immediately (source
      // handle `voice-template-out` → target handle
      // `AUDIO_VOICE_TEMPLATE_IN_HANDLE`, canvas-persisted, restored
      // after reload) — not deferred to completion. Reuses the EXACT
      // SAME `buildVoiceTemplateEdge`/`stripVoiceTemplateEdgesToTarget`
      // single-cardinality helpers `onConfirmCandidateVoiceTemplate`
      // already established, never a second edge-building path. The
      // node's own `status` stays IDLE/GENERATING until it really
      // completes, so `connectedVoiceTemplateByNodeId`'s `ready` stays
      // `false` and Generate Audio correctly shows "等待声音模板生成"
      // (§7) rather than treating the pending node id as a usable
      // template — never a fake-ready state.
      const nodeTypeById = nodeTypeMapFromNodes(nodesRef.current)
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== targetNodeId) return n
          const d = n.data as unknown as FlowNodeData
          return {
            ...n,
            data: {
              ...d,
              fields: { ...(d.fields || {}), pendingVoiceTemplateCandidateNodeId: newId },
            } as unknown as Record<string, unknown>,
          }
        })
        return [...next, newNode]
      })
      commitEdges((eds) => {
        const stripped = stripVoiceTemplateEdgesToTarget(eds, targetNodeId, nodeTypeById)
        const nextEdges = addEdge(
          buildVoiceTemplateEdge({ id: genId(), sourceId: newId, targetId: targetNodeId }),
          stripped,
        )
        scheduleFromState(nodesRef.current, nextEdges)
        return nextEdges
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // Voice Template hover Contextual Creation Rail — auto-creates a
  // downstream Generate Audio node, wires the template in via the
  // canonical VOICE_TEMPLATE typed edge (voice-template-out → image-ref-in),
  // and expands its Inspector so the user can enter new text immediately.
  const onQuickCreateGenerateAudioLastFiredAtRef = useRef<Map<string, number>>(new Map())

  const onQuickCreateGenerateAudio = useCallback(
    (sourceNodeId: string) => {
      // VistoeraTASK-00187 §十八 — a rapid duplicate fire (double click /
      // duplicate React event) for the SAME source node must never create
      // two nodes + two edges from one user action.
      const now = Date.now()
      if (!shouldAllowVoiceTemplateQuickCreate(sourceNodeId, onQuickCreateGenerateAudioLastFiredAtRef.current, now)) {
        return
      }
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sData = source.data as unknown as FlowNodeData
      if (sData.nodeType !== 'generate_voice_template') return
      const templateId = sData.fields?.templateId as string | undefined
      if (sData.fields?.status !== 'COMPLETED' || !templateId) return
      onQuickCreateGenerateAudioLastFiredAtRef.current.set(sourceNodeId, now)

      const def = getNodeDefinition('generate_voice')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_voice',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: buildGenerateAudioNodeDefaultFields(),
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = addEdge(
          buildVoiceTemplateEdge({ id: genId(), sourceId: sourceNodeId, targetId: newId }),
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onQuickCreateMotionReconstruction = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sData = source.data as unknown as FlowNodeData
      if (sData.nodeType !== 'video_asset') return
      const videoAssetId = sData.entityId as string | undefined
      if (!videoAssetId) return

      const def = getNodeDefinition('motion_reconstruction')
      const videoLabel = (sData.fields?.label as string | undefined) || def.label
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'motion_reconstruction',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            qualityMode: 'balanced',
            status: 'READY',
            inputVideoAssetId: videoAssetId,
            inputVideoLabel: videoLabel,
            reconstructionId: null,
            jobId: null,
            outputMotionAssetId: null,
            errorCode: null,
            errorMessage: null,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = addEdge(
          {
            id: genId(),
            source: sourceNodeId,
            sourceHandle: 'video-out',
            target: newId,
            targetHandle: 'video-in',
            data: { relationType: 'VIDEO' },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  /** Motion Reconstruction → Generate Video Quick Action.
   * Legal MOTION_ASSET edge + `referenceMotionAssetId` context bind only —
   * never auto-submits a video generation job. */
  const onQuickCreateGenerateVideoFromMotion = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sData = source.data as unknown as FlowNodeData
      if (sData.nodeType !== 'motion_reconstruction') return
      const fields = (sData.fields || {}) as Record<string, unknown>
      const motionAssetId = fields.outputMotionAssetId as string | undefined
      if (fields.status !== 'COMPLETED' || !motionAssetId) return

      const def = getNodeDefinition('generate_video')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_video',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateVideoNodeDefaultFields(),
            referenceMotionAssetId: motionAssetId,
            referenceMotionSourceNodeId: sourceNodeId,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = addEdge(
          {
            id: genId(),
            source: sourceNodeId,
            sourceHandle: 'motion-out',
            target: newId,
            targetHandle: GENERIC_INPUT_HANDLE,
            data: { relationType: 'REFERENCES' },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onConfirmCandidateVoiceTemplate = useCallback(
    (voiceTemplateNodeId: string, generateAudioNodeId: string) => {
      const vtNode = nodesRef.current.find((n) => n.id === voiceTemplateNodeId)
      const gaNode = nodesRef.current.find((n) => n.id === generateAudioNodeId)
      if (!vtNode || !gaNode) return
      const vtData = vtNode.data as unknown as FlowNodeData
      const gaData = gaNode.data as unknown as FlowNodeData
      if (vtData.nodeType !== 'generate_voice_template' || gaData.nodeType !== 'generate_voice') return
      const templateId = vtData.fields?.templateId as string | undefined
      if (vtData.fields?.status !== 'COMPLETED' || !templateId) return

      const nodeTypeById = nodeTypeMapFromNodes(nodesRef.current)
      const gaFields = gaData.fields || {}
      const hadAudioResult = gaFields.status === 'COMPLETED' && Boolean(gaFields.audioAssetId)
      const templateLabel = (vtData.fields?.templateName as string | undefined) || getNodeDefinition(vtData.nodeType).label

      commitEdges((eds) => {
        const stripped = stripVoiceTemplateEdgesToTarget(eds, generateAudioNodeId, nodeTypeById)
        const next = addEdge(
          buildVoiceTemplateEdge({ id: genId(), sourceId: voiceTemplateNodeId, targetId: generateAudioNodeId }),
          stripped,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id === generateAudioNodeId) {
            const d = n.data as unknown as FlowNodeData
            const fields = {
              ...(d.fields || {}),
              pendingVoiceTemplateCandidateNodeId: null,
              selectedVoiceTemplateId: templateId,
              selectedSystemVoiceKey: null,
              selectedVoiceLabel: templateLabel,
              ...(hadAudioResult ? { needsRegeneration: true } : {}),
            }
            return { ...n, data: { ...d, fields } as unknown as Record<string, unknown> }
          }
          if (n.id === voiceTemplateNodeId) {
            const d = n.data as unknown as FlowNodeData
            return {
              ...n,
              data: {
                ...d,
                fields: { ...(d.fields || {}), candidateForGenerateAudioNodeId: null },
              } as unknown as Record<string, unknown>,
            }
          }
          return n
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onQuickCreateGenerateVideo = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sData = source.data as unknown as FlowNodeData
      if (sData.nodeType !== 'generate_voice') return
      const fields = (sData.fields || {}) as Record<string, unknown>
      if (!audioOutputReady(fields) || audioOutputStale(fields)) return

      const def = getNodeDefinition('generate_video')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_video',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: buildGenerateVideoNodeDefaultFields(),
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = addEdge(
          {
            id: genId(),
            source: sourceNodeId,
            sourceHandle: 'audio-out',
            target: newId,
            targetHandle: GENERIC_INPUT_HANDLE,
            data: { relationType: 'REFERENCES' },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // Canvas Unified Video Generation Node P0 (spec §7–§9) — the SAME
  // "derive fresh from the live edge graph every render" pattern as
  // `inputMaterialsByNodeId` above, but grouped by deterministic
  // START/MIDDLE/END role via the edge's OWN `targetHandle` (never
  // guessed from source type — spec §8 "不要出现...系统猜它是 START"). Only
  // `generate_video`'s own dedicated keyframe handles ever populate
  // this map; a plain generic-input edge never appears here.
  const keyframeAssignmentByNodeId = useMemo(() => {
    const map = new Map<string, KeyframeAssignment>()
    for (const e of edges) {
      if (!isVideoKeyframeHandle(e.targetHandle)) continue
      const sourceNode = nodes.find((n) => n.id === e.source)
      if (!sourceNode) continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const sType = sData.nodeType
      const sourceLabel = (sData.fields?.label as string | undefined) || getNodeDefinition(sType).label
      let slot: KeyframeSlotMaterial | null = null
      if (sType === 'image_asset' && sData.entityId) {
        slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready: true, entityId: sData.entityId }
      } else if (sType === 'generate_image') {
        const outputId = sData.fields?.generatedOutputId as string | undefined
        const formatOk = (sData.fields?.formatCompatibility as string | undefined) !== 'format_mismatch'
        slot = {
          edgeId: e.id,
          nodeId: sourceNode.id,
          nodeType: sType,
          sourceLabel,
          ready: Boolean(outputId) && formatOk,
          generatedOutputId: formatOk ? outputId : undefined,
        }
      }
      if (!slot) continue
      const entry = map.get(e.target) || { middle: [], middleByEntryId: new Map<string, KeyframeSlotMaterial>() }
      if (e.targetHandle === VIDEO_KEYFRAME_START_HANDLE) entry.start = slot
      else if (e.targetHandle === VIDEO_KEYFRAME_END_HANDLE) entry.end = slot
      else if (e.targetHandle === VIDEO_KEYFRAME_MIDDLE_HANDLE) {
        // Legacy bare-handle MIDDLE edge (pre-dates the Advanced Video
        // Generation Card N-keyframe timeline) — `middleKeyframeTimePosition`
        // was the single scalar field the old single-MIDDLE UI patched.
        const targetNode = nodes.find((n) => n.id === e.target)
        const timePosition = (targetNode?.data as unknown as FlowNodeData | undefined)?.fields
          ?.middleKeyframeTimePosition as number | undefined
        const legacySlot = { ...slot, timePosition }
        entry.legacyMiddle = legacySlot
        entry.middle = [legacySlot, ...entry.middle.filter((m) => m.edgeId !== legacySlot.edgeId)]
      } else {
        // ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4/§40
        // — one of N MIDDLE handles minted as `video-kf-middle-in::<entryId>`.
        const entryId = parseVideoKeyframeMiddleEntryId(e.targetHandle)
        if (entryId) {
          const targetNode = nodes.find((n) => n.id === e.target)
          const timeline = ((targetNode?.data as unknown as FlowNodeData | undefined)?.fields
            ?.keyframeTimeline || []) as KeyframeTimelineEntry[]
          const timePosition = timeline.find((k) => k.id === entryId)?.relativeTimeSec
          const middleSlot = { ...slot, timePosition }
          entry.middleByEntryId.set(entryId, middleSlot)
          entry.middle = [...entry.middle.filter((m) => m.edgeId !== middleSlot.edgeId), middleSlot]
        }
      }
      map.set(e.target, entry)
    }
    return map
  }, [edges, nodes])

  /** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §10/§11/§22/§40
   * — mirrors `keyframeAssignmentByNodeId` above for the Dialogue/Audio
   * Timeline Segment slots (`video-dialogue-audio-in::<segmentId>`
   * handles). Kept as its own map (not folded into `KeyframeAssignment`)
   * because it is keyed by `DialogueSegment.id`, an entirely different
   * id space from keyframe timeline entries. */
  const dialogueAudioAssignmentByNodeId = useMemo(() => {
    const map = new Map<string, DialogueAudioAssignment>()
    for (const e of edges) {
      const segmentId = parseVideoDialogueAudioSegmentId(e.targetHandle)
      if (!segmentId) continue
      const sourceNode = nodes.find((n) => n.id === e.source)
      if (!sourceNode) continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const sType = sData.nodeType
      if (sType !== 'generate_voice') continue
      const sourceLabel = (sData.fields?.label as string | undefined) || getNodeDefinition(sType).label
      const audioAssetId = sData.fields?.audioAssetId as string | undefined
      const ready = audioOutputReady(sData.fields)
      const entry = map.get(e.target) || new Map()
      entry.set(segmentId, { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready, audioAssetId })
      map.set(e.target, entry)
    }
    return map
  }, [edges, nodes])

  /** VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
   * §P6/§P7/§P15/§P19 — derives the four character/product/scene/other-
   * reference structured-asset ports' live bindings straight from the
   * edge graph via the pure builder in `dataflow.ts` (same "recompute
   * every render, never persisted itself" contract as
   * `keyframeAssignmentByNodeId` above). `nodes`/`edges` are already the
   * exact plain shape `StructuredAssetNodeLike`/`StructuredAssetEdgeLike`
   * expect (`data.nodeType`/`data.fields`/`data.entityId`), so no
   * adapter layer is needed — passed straight through. */
  const structuredAssetAssignmentByNodeId = useMemo(
    () => buildVideoStructuredAssetAssignment(edges, nodes as unknown as Parameters<typeof buildVideoStructuredAssetAssignment>[1]),
    [edges, nodes],
  )

  /** Same pattern for the single-slot global Reference Video port
   * (spec §P7 item 7/§P16/§P17). */
  const videoReferenceVideoAssignmentByNodeId = useMemo(
    () => buildVideoReferenceVideoAssignment(edges, nodes as unknown as Parameters<typeof buildVideoReferenceVideoAssignment>[1]),
    [edges, nodes],
  )

  /** Same pattern for the MULTI-slot global Audio Reference port
   * (VistoeraTASK-00143 §五/§七). */
  const videoAudioReferenceAssignmentByNodeId = useMemo(
    () => buildVideoAudioReferenceAssignment(edges, nodes as unknown as Parameters<typeof buildVideoAudioReferenceAssignment>[1]),
    [edges, nodes],
  )

  const VIDEO_KEYFRAME_IMAGE_NODE_WIDTH = GENERATE_IMAGE_EXPANDED_WIDTH_ESTIMATE
  const VIDEO_DIALOGUE_AUDIO_NODE_WIDTH = 480

  /** "生成关键帧" backward expansion (spec §18/§19/§20/§21/CASE_L/CASE_M) —
   * never calls an Image Provider from inside the Video card itself.
   * Creates a canonical `generate_image` node to the LEFT of the Video
   * node (same `placeNodeLeftOfTarget` primitive as
   * `onCreateVoiceTemplateNode`) and an immediate slot-aware edge into
   * the exact keyframe timeline entry that requested it — START/END use
   * their fixed legacy handle; any MIDDLE entry gets its own
   * `video-kf-middle-in::<entryId>` handle (spec §40 "Canvas Edges 必须
   * 具有明确 input role"), never a generic/ambiguous target.
   *
   * Continuity (spec §20/§21) — when an earlier keyframe in the same
   * timeline already has a bound image, that source node is wired in as
   * a plain REFERENCE material on the new `generate_image` node's
   * generic input handle too, so identity/scene/object continuity is at
   * least available to the image Skill as a real connected reference,
   * never only a prose hint. */
  const onCreateVideoKeyframeImageNode = useCallback(
    (videoNodeId: string, timelineEntryId: string) => {
      const target = nodesRef.current.find((n) => n.id === videoNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_video') return
      const timeline = ((targetData.fields?.keyframeTimeline || []) as KeyframeTimelineEntry[])
      const ordered = [...timeline].sort((a, b) => a.relativeTimeSec - b.relativeTimeSec)
      const entry = ordered.find((e) => e.id === timelineEntryId)
      if (!entry) return

      // Reuse existing bound source for this slot (P0_V2 §16/CASE_L) —
      // never spawn a second Generate Image for the same keyframe.
      const existingAssignment = keyframeAssignmentByNodeId.get(videoNodeId)
      const existingSlot =
        entry.role === 'start'
          ? existingAssignment?.start
          : entry.role === 'end'
            ? existingAssignment?.end
            : existingAssignment?.middleByEntryId.get(entry.id)
      if (existingSlot?.nodeId) {
        const existingNode = nodesRef.current.find((n) => n.id === existingSlot.nodeId)
        const existingType = (existingNode?.data as unknown as FlowNodeData | undefined)?.nodeType
        if (existingType === 'generate_image') {
          onFocusVideoKeyframeSourceNode(existingSlot.nodeId)
          return
        }
        setSelectedNodeId(existingSlot.nodeId)
        setExpandedNodeId(existingSlot.nodeId)
        return
      }

      const inputAssetRefs = (targetData.fields?.inputAssetRefs || []) as InputAssetRef[]
      const prioritizedRefs = prioritizeRefsForKeyframe(inputAssetRefs, 4)
      const prompt = buildKeyframeImagePrompt({
        visualDescription: entry.visualDescription,
        motionIntent: entry.motionIntent,
        cameraIntent: entry.cameraIntent,
        refs: inputAssetRefs,
      })

      // Inherit Production Format: Storyboard orchestrator first, else this Video node (CASE B).
      const orchId =
        (typeof targetData.fields?.boundOrchestratorNodeId === 'string'
          ? targetData.fields.boundOrchestratorNodeId
          : null) ||
        (typeof targetData.fields?.boundVideoProductionNodeId === 'string'
          ? targetData.fields.boundVideoProductionNodeId
          : null)
      const orchNode = orchId ? nodesRef.current.find((n) => n.id === orchId) : null
      const orchFields = ((orchNode?.data as unknown as FlowNodeData | undefined)?.fields || {}) as Record<
        string,
        unknown
      >
      const fromStoryboard = readProductionFormat(orchFields)
      const fromVideo = deriveProductionFormatFromVideoFields((targetData.fields || {}) as Record<string, unknown>)
      const productionFormat =
        fromStoryboard && isProductionFormatComplete(fromStoryboard)
          ? fromStoryboard
          : fromVideo && isProductionFormatComplete(fromVideo)
            ? fromVideo
            : null
      // Never spawn a keyframe Generate Image on silent 1:1 defaults — Product Format Contract required.
      if (!productionFormat || !isProductionFormatComplete(productionFormat)) {
        message.error(
          orchId
            ? '请先在「生成分镜」中选择成片规格（长宽比与清晰度），再生成关键帧'
            : '请先在本视频节点选择成片规格（长宽比与清晰度），再生成关键帧',
        )
        return
      }
      const formatSource = fromStoryboard && isProductionFormatComplete(fromStoryboard) ? 'storyboard' : 'video_node'
      const lockedFormatFields = {
        ...buildLockedProductionFormatFields(productionFormat),
        formatSource,
      }

      const def = getNodeDefinition('generate_image')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeLeftOfTarget(target, VIDEO_KEYFRAME_IMAGE_NODE_WIDTH, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_image',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateImageNodeDefaultFields(workspaceId, imageGenerationConfigRef.current),
            prompt,
            candidateForVideoKeyframe: { videoNodeId, timelineEntryId },
            purpose: 'video_keyframe',
            expectedReferenceAssetIds: prioritizedRefs.map((r) => r.assetId).filter(Boolean),
            requireReferenceImages: prioritizedRefs.length > 0,
            boundOrchestratorNodeId: orchId,
            boundVideoNodeId: videoNodeId,
            boundKeyframeEntryId: timelineEntryId,
            ...lockedFormatFields,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (target.parentId) {
        newNode.parentId = target.parentId
        newNode.extent = 'parent'
      }

      // Continuity — find the nearest PRECEDING timeline entry that
      // already has a real bound+ready image and wire its source node in
      // as a reference material (spec §20/§21).
      const assignment = keyframeAssignmentByNodeId.get(videoNodeId)
      const entryIndex = ordered.findIndex((e) => e.id === timelineEntryId)
      let previousSourceNodeId: string | null = null
      for (let i = entryIndex - 1; i >= 0; i -= 1) {
        const prevEntry = ordered[i]
        const prevSlot =
          prevEntry.role === 'start'
            ? assignment?.start
            : prevEntry.role === 'end'
              ? assignment?.end
              : assignment?.middleByEntryId.get(prevEntry.id)
        if (prevSlot?.ready) {
          previousSourceNodeId = prevSlot.nodeId
          break
        }
      }

      const targetHandle = videoKeyframeTargetHandleForEntry(entry)

      // Shot reference assets with real assetIds → image_asset nodes +
      // REFERENCES edges into the new generate_image (multimodal path).
      const refAssetNodes: Node[] = prioritizedRefs.map((ref, idx) => {
        const refDef = getNodeDefinition('image_asset')
        const refId = genId()
        const base = placeNodeLeftOfTarget(target, VIDEO_KEYFRAME_IMAGE_NODE_WIDTH, {
          verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE - 40 - idx * 120,
        })
        const refNode: Node = {
          id: refId,
          type: refDef.family,
          position: { x: base.x - 220, y: base.y + idx * 100 },
          data: {
            nodeType: 'image_asset',
            category: refDef.category,
            entityType: 'site_asset',
            entityId: ref.assetId,
            collapsed: false,
            fields: {
              ...(refDef.defaultFields || {}),
              label: ref.label,
              shotReferenceCategory: ref.category,
              shotReferenceForVideoNodeId: videoNodeId,
            },
            latestRunId: null,
          } as unknown as Record<string, unknown>,
        }
        if (target.parentId) {
          refNode.parentId = target.parentId
          refNode.extent = 'parent'
        }
        return refNode
      })

      // Atomic create: node + slot-aware edge in one autosave cycle (P0_V3).
      commitNodes((nds) => {
        const nextNodes = [...nds, ...refAssetNodes, newNode]
        nodesRef.current = nextNodes
        return nextNodes
      })
      commitEdges((eds) => {
        let next = addEdge(
          {
            id: genId(),
            source: newId,
            sourceHandle: 'image-out',
            target: videoNodeId,
            targetHandle,
            data: { relationType: 'INPUT_TO', keyframeRole: entry.role, keyframeEntryId: entry.id },
            className: 'pcv-edge',
          },
          eds,
        )
        for (const refNode of refAssetNodes) {
          // VistoeraTASK-00144 — same per-edge handle scoping as the
          // manual onConnect path: `newId` here is the freshly-created
          // `generate_image` node, so each shot-reference edge gets its
          // own stable target anchor from the start.
          const shotRefEdgeId = genId()
          next = addEdge(
            {
              id: shotRefEdgeId,
              source: refNode.id,
              sourceHandle: 'image-out',
              target: newId,
              targetHandle: imageReferenceAssetHandleId(shotRefEdgeId),
              data: { relationType: 'REFERENCES', role: 'shot_reference' },
              className: 'pcv-edge',
            },
            next,
          )
        }
        if (previousSourceNodeId) {
          const continuityEdgeId = genId()
          next = addEdge(
            {
              id: continuityEdgeId,
              source: previousSourceNodeId,
              sourceHandle: 'image-out',
              target: newId,
              targetHandle: imageReferenceAssetHandleId(continuityEdgeId),
              data: { relationType: 'REFERENCES', role: 'keyframe_continuity' },
              className: 'pcv-edge',
            },
            next,
          )
        }
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
      focusNodePreserveZoom(rf, newId, { duration: 400 })
    },
    [commitEdges, commitNodes, keyframeAssignmentByNodeId, onFocusVideoKeyframeSourceNode, rf, scheduleFromState, workspaceId],
  )

  /** Video → ONE_PRIMARY bound Generate Text prompt enhancer. */
  const onCreateVideoPromptEnhancerNode = useCallback(
    (videoNodeId: string) => {
      const target = nodesRef.current.find((n) => n.id === videoNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_video') return
      const videoFields = (targetData.fields || {}) as Record<string, unknown>
      const snapshot = buildVideoPromptEnhancementContextSnapshot({
        videoNodeId,
        fields: videoFields,
      })
      if (!String(snapshot.original_prompt || '').trim()) {
        message.warning(t('generation.promptEnhanceNeedsOriginal'))
        return
      }

      const ensurePromptEnhancementFieldBindingEdges = (enhancerId: string) => {
        commitEdges((eds) => {
          // Drop legacy INPUT_TO / stale field-binding edges between this pair,
          // then reinstall the dual FIELD_BINDING round-trip.
          let next = eds.filter((e) => {
            const touches =
              (e.source === videoNodeId && e.target === enhancerId) ||
              (e.source === enhancerId && e.target === videoNodeId)
            if (!touches) return true
            const relation = (e.data as { relationType?: string } | undefined)?.relationType
            if (isPromptEnhancementFieldBindingRelation(relation)) return false
            if (relation === 'INPUT_TO' || !relation) return false
            return true
          })
          next = addEdge(
            {
              id: genId(),
              source: videoNodeId,
              sourceHandle: 'original-prompt-out',
              target: enhancerId,
              targetHandle: 'prompt-context-in',
              data: {
                relationType: PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION,
                fieldBinding: {
                  source_field: 'promptText',
                  target_field: 'videoPromptContextSnapshot.original_prompt',
                  binding_type: 'video_prompt_enhancement_input',
                },
              },
            },
            next,
          )
          next = addEdge(
            {
              id: genId(),
              source: enhancerId,
              sourceHandle: 'text-out',
              target: videoNodeId,
              targetHandle: 'enhanced-prompt-in',
              data: {
                relationType: PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION,
                fieldBinding: {
                  source_field: 'currentResult.text',
                  target_field: 'enhancedPromptText',
                  binding_type: 'video_prompt_enhancement_output',
                },
              },
            },
            next,
          )
          edgesRef.current = next
          scheduleFromState(nodesRef.current, next)
          return next
        })
      }

      const focusAndRefresh = (enhancerId: string) => {
        commitNodes((nds) => {
          const next = nds.map((n) => {
            if (n.id === enhancerId) {
              const data = n.data as unknown as FlowNodeData
              const prevSnap = data.fields?.videoPromptContextSnapshot as
                | { original_prompt?: string }
                | null
                | undefined
              const prevOriginal = String(prevSnap?.original_prompt || '').trim()
              const nextOriginal = String(snapshot.original_prompt || '').trim()
              const hasResult = Boolean(
                (data.fields?.currentResult as { text?: string } | null)?.text?.trim(),
              )
              return {
                ...n,
                data: {
                  ...data,
                  fields: {
                    ...data.fields,
                    purpose: VIDEO_PROMPT_ENHANCEMENT_PURPOSE,
                    boundVideoNodeId: videoNodeId,
                    videoPromptContextSnapshot: snapshot,
                    ...(data.fields?.additionalInstruction
                      ? {}
                      : { additionalInstruction: VIDEO_PROMPT_ENHANCEMENT_DEFAULT_INSTRUCTION }),
                    ...(hasResult && prevOriginal && prevOriginal !== nextOriginal
                      ? { needsRegeneration: true }
                      : {}),
                  },
                } as unknown as Record<string, unknown>,
              }
            }
            if (n.id === videoNodeId) {
              const data = n.data as unknown as FlowNodeData
              if (data.fields?.boundPromptEnhancerNodeId === enhancerId) return n
              return {
                ...n,
                data: {
                  ...data,
                  fields: { ...data.fields, boundPromptEnhancerNodeId: enhancerId },
                } as unknown as Record<string, unknown>,
              }
            }
            return n
          })
          nodesRef.current = next
          scheduleFromState(next, edgesRef.current)
          return next
        })
        ensurePromptEnhancementFieldBindingEdges(enhancerId)
        onExpandFunctionCardDetail(enhancerId)
        focusNodePreserveZoom(rf, enhancerId, { duration: 400 })
        message.info(t('generation.promptEnhanceFocusExisting'))
      }

      const pointerId =
        typeof videoFields.boundPromptEnhancerNodeId === 'string' && videoFields.boundPromptEnhancerNodeId.trim()
          ? videoFields.boundPromptEnhancerNodeId.trim()
          : null
      if (pointerId) {
        const existing = nodesRef.current.find((n) => n.id === pointerId)
        const existingData = existing?.data as unknown as FlowNodeData | undefined
        if (
          existing &&
          existingData?.nodeType === 'generate_text' &&
          isVideoPromptEnhancementPurpose(existingData.fields?.purpose)
        ) {
          focusAndRefresh(pointerId)
          return
        }
      }

      // Repair missing pointer by scanning purpose + boundVideoNodeId.
      const scanned = nodesRef.current.find((n) => {
        const data = n.data as unknown as FlowNodeData
        return (
          data.nodeType === 'generate_text' &&
          isVideoPromptEnhancementPurpose(data.fields?.purpose) &&
          data.fields?.boundVideoNodeId === videoNodeId
        )
      })
      if (scanned) {
        focusAndRefresh(scanned.id)
        return
      }

      const def = getNodeDefinition('generate_text')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeLeftOfTarget(target, 720, {
          verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE,
        }),
        data: {
          nodeType: 'generate_text',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateTextNodeDefaultFields({ locale: i18n.language }),
            purpose: VIDEO_PROMPT_ENHANCEMENT_PURPOSE,
            boundVideoNodeId: videoNodeId,
            skillSelectionSource: 'EXPLICIT',
            skillKey: VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
            skillVersion: VIDEO_PROMPT_ENHANCEMENT_SKILL_VERSION,
            preferredSkillKey: VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
            videoPromptContextSnapshot: snapshot,
            additionalInstruction: VIDEO_PROMPT_ENHANCEMENT_DEFAULT_INSTRUCTION,
            contentLength: 'LONG',
            outputFormat: 'plain',
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (target.parentId) {
        newNode.parentId = target.parentId
        newNode.extent = 'parent'
      }

      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== videoNodeId) return n
          const data = n.data as unknown as FlowNodeData
          return {
            ...n,
            data: {
              ...data,
              fields: { ...data.fields, boundPromptEnhancerNodeId: newId },
            } as unknown as Record<string, unknown>,
          }
        })
        const withNew = [...next, newNode]
        nodesRef.current = withNew
        return withNew
      })
      ensurePromptEnhancementFieldBindingEdges(newId)
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
      focusNodePreserveZoom(rf, newId, { duration: 400 })
    },
    [commitEdges, commitNodes, i18n.language, onExpandFunctionCardDetail, rf, scheduleFromState, t],
  )

  const onVideoPromptEnhancementResult = useCallback(
    (
      videoNodeId: string,
      result: {
        text: string
        runId: string | null
        skillKey: string | null
        skillVersion: string | null
        providerOptionId?: string | null
      },
    ) => {
      const patch = buildVideoPromptEnhancementWritebackPatch({
        enhancedPrompt: result.text,
        runId: result.runId,
        skillKey: result.skillKey,
        skillVersion: result.skillVersion,
        textProviderId: result.providerOptionId ?? null,
        currentBindingMode: (() => {
          const target = nodesRef.current.find((n) => n.id === videoNodeId)
          const mode = (target?.data as unknown as FlowNodeData | undefined)?.fields
            ?.enhancedPromptBindingMode
          return typeof mode === 'string' ? mode : null
        })(),
        // Regenerate success always applies (product: new current result wins).
        forceApply: true,
      })
      if (!Object.keys(patch).length) return
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id !== videoNodeId) return n
          const data = n.data as unknown as FlowNodeData
          // NEVER clear / overwrite promptText on writeback.
          return {
            ...n,
            data: {
              ...data,
              fields: { ...data.fields, ...patch },
            } as unknown as Record<string, unknown>,
          }
        })
        nodesRef.current = next
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  /** Live resolve Video.promptText → enhancer scoped_context at Generate submit. */
  const onResolveVideoPromptEnhancementContext = useCallback((textNodeId: string) => {
    const textNode = nodesRef.current.find((n) => n.id === textNodeId)
    const textData = textNode?.data as unknown as FlowNodeData | undefined
    if (!textData || !isVideoPromptEnhancementPurpose(textData.fields?.purpose)) return null
    const videoNodeId =
      typeof textData.fields?.boundVideoNodeId === 'string' ? textData.fields.boundVideoNodeId : null
    if (!videoNodeId) return null
    const videoNode = nodesRef.current.find((n) => n.id === videoNodeId)
    const videoData = videoNode?.data as unknown as FlowNodeData | undefined
    if (!videoData || videoData.nodeType !== 'generate_video') return null
    return buildVideoPromptEnhancementContextSnapshot({
      videoNodeId,
      fields: (videoData.fields || {}) as Record<string, unknown>,
    })
  }, [])

  /** "导入关键帧" (VISUAL_GROUPING_SOURCE_FOCUS_IMPORT_P0_V1 §17-§20) —
   * `generate_video` ONLY. Reuses the EXACT SAME canonical
   * `ImageAssetPicker` modal every other Canvas node already opens via
   * `onOpenAssetPicker`/`assetPickerRequest` (library selection + local
   * upload in one UI, spec §20 "不要造第二套上传器") — never a bespoke
   * picker just for Video Keyframes.
   *
   * If the targeted slot already has a bound `image_asset` source node,
   * rebinds that node's `entityId` in place (same "replace image"
   * pattern `ImageAssetNode` itself uses for `purpose: 'graphic_replace'`
   * — no duplicate node, no edge churn). Otherwise creates a fresh
   * `image_asset` node + slot-aware edge, replacing ONLY the one prior
   * edge into this exact `targetHandle` if there was one (e.g. the user
   * is deliberately importing over a previously-`generate_image`-sourced
   * keyframe — spec §27 "用户真正替换 source" is exactly this case). */
  const onImportVideoKeyframeImage = useCallback(
    (videoNodeId: string, timelineEntryId: string) => {
      const target = nodesRef.current.find((n) => n.id === videoNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_video') return
      const timeline = ((targetData.fields?.keyframeTimeline || []) as KeyframeTimelineEntry[])
      const ordered = [...timeline].sort((a, b) => a.relativeTimeSec - b.relativeTimeSec)
      const entry = ordered.find((e) => e.id === timelineEntryId)
      if (!entry) return

      const targetHandle = videoKeyframeTargetHandleForEntry(entry)

      setAssetPickerRequest({
        nodeId: videoNodeId,
        purpose: 'canvas_insert',
        title: t('generation.keyframeImportPickerTitle'),
        selectionMode: 'single',
        maxSelection: 1,
        onConfirm: (selections: ImageAssetPickerSelection[]) => {
          const picked = selections[0]
          if (!picked) return

          const assignment = keyframeAssignmentByNodeId.get(videoNodeId)
          const existingSlot =
            entry.role === 'start'
              ? assignment?.start
              : entry.role === 'end'
                ? assignment?.end
                : assignment?.middleByEntryId.get(entry.id)

          if (existingSlot && existingSlot.nodeType === 'image_asset') {
            commitNodes((nds) => {
              const withEntity = nds.map((n) => {
                if (n.id !== existingSlot.nodeId) return n
                const data = n.data as unknown as FlowNodeData
                return {
                  ...n,
                  data: { ...data, entityType: 'site_asset', entityId: picked.assetId } as unknown as Record<string, unknown>,
                }
              })
              const next = markDownstreamStale(withEntity, edgesRef.current, existingSlot.nodeId)
              scheduleFromState(next, edgesRef.current)
              return next
            })
            setSelectedNodeId(existingSlot.nodeId)
            return
          }

          const def = getNodeDefinition('image_asset')
          const newId = genId()
          const newNode: Node = {
            id: newId,
            type: def.family,
            position: placeNodeLeftOfTarget(target, VIDEO_KEYFRAME_IMAGE_NODE_WIDTH, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
            data: {
              nodeType: 'image_asset',
              category: def.category,
              entityType: 'site_asset',
              entityId: picked.assetId,
              collapsed: false,
              fields: { ...(def.defaultFields || {}) },
              latestRunId: null,
            } as unknown as Record<string, unknown>,
          }
          if (target.parentId) {
            newNode.parentId = target.parentId
            newNode.extent = 'parent'
          }

          commitNodes((nds) => {
            const nextNodes = [...nds, newNode]
            nodesRef.current = nextNodes
            return nextNodes
          })
          commitEdges((eds) => {
            const withoutOldSlotEdge = eds.filter((e) => !(e.target === videoNodeId && e.targetHandle === targetHandle))
            const next = addEdge(
              {
                id: genId(),
                source: newId,
                sourceHandle: 'image-out',
                target: videoNodeId,
                targetHandle,
                data: { relationType: 'INPUT_TO', keyframeRole: entry.role, keyframeEntryId: entry.id },
                className: 'pcv-edge',
              },
              withoutOldSlotEdge,
            )
            scheduleFromState(nodesRef.current, next)
            return next
          })
          setSelectedNodeId(newId)
        },
      })
    },
    [commitEdges, commitNodes, keyframeAssignmentByNodeId, scheduleFromState, t],
  )

  /** P0_V3 — REMOVE KEYFRAME IMAGE: delete only the slot-aware Edge.
   * Never deletes the keyframe timeline entry, never deletes the upstream
   * Generate Image / Image Asset source node or underlying asset. */
  const onRemoveVideoKeyframeImage = useCallback(
    (videoNodeId: string, timelineEntryId: string) => {
      const target = nodesRef.current.find((n) => n.id === videoNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_video') return
      const timeline = (targetData.fields?.keyframeTimeline || []) as KeyframeTimelineEntry[]
      const entry = timeline.find((e) => e.id === timelineEntryId)
      if (!entry) return
      const targetHandle = videoKeyframeTargetHandleForEntry(entry)
      commitEdges((eds) => {
        const next = eds.filter((e) => !(e.target === videoNodeId && e.targetHandle === targetHandle))
        if (next.length === eds.length) return eds
        const staled = markNodeStaleIfHasResult(nodesRef.current, videoNodeId)
        if (staled !== nodesRef.current) {
          commitNodes(() => {
            nodesRef.current = staled
            return staled
          })
        }
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  /** KEYFRAME_NATIVE_VS_DELIVERY_P0 — propagate Video format to bound keyframes.
   * Aspect change → format_mismatch + needsRegeneration (AI source stale).
   * Resolution-only → restamp delivery target + deliveryRenderStale; keep AI. */
  const onVideoProductionFormatChanged = useCallback(
    (
      videoNodeId: string,
      nextFormat: { aspect_ratio: string; resolution: string; version: number },
      impact?: {
        aspectChanged: boolean
        resolutionChanged: boolean
        aiGenerationStale: boolean
        deliveryRenderStale: boolean
      },
    ) => {
      const locked = buildLockedProductionFormatFields(nextFormat as StoryboardProductionFormat)
      const aiStale = Boolean(impact?.aiGenerationStale ?? true)
      const deliveryStale = Boolean(impact?.deliveryRenderStale)
      const assignment = keyframeAssignmentByNodeId.get(videoNodeId)
      const sourceIds = new Set<string>()
      if (assignment?.start?.nodeId) sourceIds.add(assignment.start.nodeId)
      if (assignment?.end?.nodeId) sourceIds.add(assignment.end.nodeId)
      for (const slot of assignment?.middleByEntryId?.values() || []) {
        if (slot.nodeId) sourceIds.add(slot.nodeId)
      }
      if (sourceIds.size === 0) return
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (!sourceIds.has(n.id)) return n
          const data = n.data as unknown as FlowNodeData
          if (data.nodeType !== 'generate_image') return n
          const curFields = (data.fields || {}) as Record<string, unknown>
          const hasOutput = Boolean(curFields.generatedOutputId)
          const patched = patchCanvasFieldsIfChanged(curFields, {
            ...locked,
            productionFormatLocked: true,
            formatCompatibility: aiStale && hasOutput ? 'format_mismatch' : 'ok',
            needsRegeneration: aiStale && hasOutput,
            deliveryRenderStale: deliveryStale || (aiStale && hasOutput),
          })
          if (!patched) return n
          return { ...n, data: { ...data, fields: patched } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, keyframeAssignmentByNodeId, scheduleFromState],
  )

  /** "生成语音" backward expansion for a Dialogue/Audio Timeline Segment
   * (spec §22/CASE_N) — creates a canonical `generate_voice` node to the
   * LEFT of the Video node, pre-filled with the segment's own text, and
   * an immediate slot-aware edge (`video-dialogue-audio-in::<segmentId>`,
   * spec §40) into exactly that segment. Voice Template backward
   * expansion (spec §23/CASE_O) needs NO new wiring here — the new node
   * is a completely ordinary `generate_voice` node, so its existing
   * "语音模板" empty-state action (`onCreateVoiceTemplateNode`) already
   * works unchanged the moment it renders. */
  const onCreateVideoDialogueAudioNode = useCallback(
    (videoNodeId: string, dialogueSegmentId: string) => {
      const target = nodesRef.current.find((n) => n.id === videoNodeId)
      if (!target) return
      const targetData = target.data as unknown as FlowNodeData
      if (targetData.nodeType !== 'generate_video') return
      const segments = (targetData.fields?.dialogueSegments || []) as Array<{
        id: string
        text: string
        speakerEntityId?: string | null
        speakerLabel?: string
      }>
      const segment = segments.find((s) => s.id === dialogueSegmentId)
      if (!segment) return

      const def = getNodeDefinition('generate_voice')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeLeftOfTarget(target, VIDEO_DIALOGUE_AUDIO_NODE_WIDTH, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_voice',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateAudioNodeDefaultFields(),
            localText: segment.text,
            candidateForVideoDialogueSegment: { videoNodeId, dialogueSegmentId },
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (target.parentId) {
        newNode.parentId = target.parentId
        newNode.extent = 'parent'
      }

      commitNodes((nds) => {
        // Mark the segment as having a pending audio source (spec §22 —
        // Detail Panel suppresses a duplicate "生成语音" action while one
        // is already pending) without touching any other field.
        const next = nds.map((n) => {
          if (n.id !== videoNodeId) return n
          const d = n.data as unknown as FlowNodeData
          const nextSegments = ((d.fields?.dialogueSegments || []) as typeof segments).map((s) =>
            s.id === dialogueSegmentId ? { ...s, audioSourceNodeId: newId } : s,
          )
          return { ...n, data: { ...d, fields: { ...(d.fields || {}), dialogueSegments: nextSegments } } as unknown as Record<string, unknown> }
        })
        return [...next, newNode]
      })
      commitEdges((eds) => {
        const next = addEdge(
          {
            id: genId(),
            source: newId,
            sourceHandle: 'audio-out',
            target: videoNodeId,
            targetHandle: videoDialogueAudioHandleId(dialogueSegmentId),
            data: { relationType: 'INPUT_TO', dialogueSegmentId },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  /** Storyboard Shot → Video Generation Context hydration (spec §12/§13/
   * §42/CASE_F) — creates ONE canonical `generate_video` node per Shot,
   * fully hydrated (keyframe intents, dialogue, character/scene/object
   * refs, duration, overall prompt) but with every image/audio asset id
   * left empty (spec §42 "如果还没有生成：保持 empty") — never auto-
   * generates anything, never auto-submits. */
  const onCreateVideoFromShot = useCallback(
    (storyboardNodeId: string, shot: ShotArtifact) => {
      const source = nodesRef.current.find((n) => n.id === storyboardNodeId)
      if (!source) return
      const sourceFields = ((source.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const storyboard = sourceFields.storyboardResult as StoryboardArtifact | null
      const supportingAssets: SupportingAssetLike[] = [
        ...((Array.isArray(sourceFields.supportingAssets) ? sourceFields.supportingAssets : []) as SupportingAssetLike[]),
        ...((((sourceFields.scriptContext as { linkedAssetRefs?: SupportingAssetLike[] } | null)?.linkedAssetRefs) ||
          []) as SupportingAssetLike[]),
      ]
      const ctx = buildVideoContextFromShot(shot, { supportingAssets })
      const format =
        storyboard && isValidStoryboardArtifact(storyboard)
          ? resolveStoryboardFormatInheritance({
              aspectRatio: storyboard.aspect_ratio,
              qualityTier: storyboard.quality_tier || (sourceFields.qualityTier as string | null | undefined) || null,
              storyboardTitle: storyboard.title,
            })
          : null
      const def = getNodeDefinition('generate_video')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_video',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateVideoNodeDefaultFields(),
            promptText: ctx.overallPrompt,
            durationSeconds: Math.max(1, Math.round(ctx.durationSeconds)),
            ...(format
              ? {
                  ratio: format.ratio,
                  qualityTier: format.qualityTier,
                  formatLocked: true,
                  inheritedFormatLabel: format.inheritedFormatLabel,
                  boundStoryboardArtifactId: storyboard?.storyboard_artifact_id,
                }
              : {}),
            keyframeTimeline: ctx.keyframeTimeline,
            dialogueSegments: ctx.dialogueSegments,
            inputAssetRefs: ctx.inputAssetRefs,
            referenceAssetsFingerprint: referenceAssetsFingerprint(ctx.inputAssetRefs),
            sourceShotId: ctx.sourceShotId,
            boundShotId: shot.shot_id,
            boundOrchestratorNodeId: storyboardNodeId,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = addEdge(
          {
            id: genId(),
            source: storyboardNodeId,
            sourceHandle: 'storyboard-out',
            target: newId,
            targetHandle: GENERIC_INPUT_HANDLE,
            data: { relationType: 'DERIVED_FROM', shotId: shot.shot_id },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onQuickGenerate = useCallback(
    (sourceNodeId: string, kind: 'image' | 'video' | 'text') => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sourceData = source.data as unknown as FlowNodeData
      const nodeType = kind === 'image' ? 'generate_image' : kind === 'video' ? 'generate_video' : 'generate_text'
      // TASK-00584 execution guard — the Platform Admin Quick Action config
      // is the canonical gate. A disabled / unknown / not-yet-loaded action
      // can never create a node through this entry (stale handler, old
      // component state, cached callback). Fail-closed.
      if (!isQuickActionEnabled(sourceData.nodeType, nodeType)) return
      const def = getNodeDefinition(nodeType)
      const newId = genId()
      let textDefaultFields: Record<string, unknown> | null = null
      if (nodeType === 'generate_text') {
        const sType = sourceData.nodeType
        const inputAssets: Array<{
          assetId: string
          assetType: string
          sourceNodeId: string
          displayName: string | null
          role: string
          generatedOutputId?: string | null
        }> = []
        const sourceLabel =
          (sourceData.fields?.label as string | undefined) || getNodeDefinition(sType).label
        if (sType === 'image_asset' && sourceData.entityId) {
          inputAssets.push({
            assetId: sourceData.entityId,
            assetType: 'image',
            sourceNodeId,
            displayName: sourceLabel,
            role: 'primary',
            generatedOutputId: null,
          })
        } else if (sType === 'generate_image' || sType === 'generate_tryon') {
          const siteAssetId = (sourceData.fields?.outputAssetId as string | undefined) || null
          const outputId = (sourceData.fields?.generatedOutputId as string | undefined) || null
          if (siteAssetId || outputId) {
            inputAssets.push({
              assetId: siteAssetId || outputId || '',
              assetType: 'image',
              sourceNodeId,
              displayName: sourceLabel,
              role: 'primary',
              generatedOutputId: outputId,
            })
          }
        }
        textDefaultFields = buildGenerateTextNodeDefaultFields({
          locale: i18n.language,
          sourceNodeType: sType,
          inputAssets,
        })
      }
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source),
        data: {
          nodeType,
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields:
            nodeType === 'generate_image'
              ? buildGenerateImageNodeDefaultFields(workspaceId, imageGenerationConfigRef.current)
              : nodeType === 'generate_video'
                ? buildGenerateVideoNodeDefaultFields()
                : textDefaultFields || { ...(def.defaultFields || {}) },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const quickGenEdgeId = genId()
        const next = addEdge(
          {
            id: quickGenEdgeId,
            source: sourceNodeId,
            sourceHandle: 'image-out',
            target: newId,
            // `generate_text` reuses the SAME `image-ref-in` handle id
            // as `generate_image`/`generate_video` (spec §13 "自动把
            // Image A 作为 image context") — one canonical
            // "image reference in" contract, never a bespoke handle
            // per node type (spec §14 "不要...临时造一个假 backend").
            // VistoeraTASK-00144 — when the new node IS `generate_image`,
            // scope this edge's handle to its own id right away (same
            // per-edge target-anchor contract as the manual onConnect
            // path); `generate_video`/`generate_text` targets keep the
            // exact bare id unchanged.
            targetHandle:
              nodeType === 'generate_image' || nodeType === 'generate_text'
                ? imageReferenceAssetHandleId(quickGenEdgeId)
                : 'image-ref-in',
            data: { relationType: 'REFERENCES' },
            className: 'pcv-edge',
          },
          eds,
        )
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, i18n.language, scheduleFromState, workspaceId],
  )

  // "One card = one result" (spec §9) — the origin node already kept
  // candidate 0 itself; every OTHER output candidate becomes its own
  // sibling card here, with the SAME snapshot fields (minus the
  // candidate-specific `generatedOutputId`) and the SAME upstream
  // reference edges, cascaded to the right so they never stack exactly
  // on top of one another.
  const recoveredSlotRunsRef = useRef<Set<string>>(new Set())
  const onSyncImageGenerationBatchFromTask = useCallback(
    (leaderNodeId: string, task: ImageGenerationTask, opts?: { allowSlotRecovery?: boolean }) => {
      const leader = nodesRef.current.find((n) => n.id === leaderNodeId)
      if (!leader) return
      const leaderFields = ((leader.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const batchId = leaderFields[IMAGE_GENERATION_BATCH_ID_FIELD]
      if (!batchId || typeof batchId !== 'string') return
      let requested = requestedImageGenerationBatchCount(leaderFields)
      const batchNodes = nodesRef.current.filter((n) => {
        const f = ((n.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        return f[IMAGE_GENERATION_BATCH_ID_FIELD] === batchId
      })

      // VistoeraTASK-00512 — a succeeded run's outputs are already persisted
      // backend-side regardless of the Canvas. If an output has no Canvas
      // carrier (slot deleted by a legacy bug, or the Provider returned more
      // than requested), recreate/append a Result Slot for it. Only when this
      // run was OBSERVED in flight, so a slot the user deleted AFTER completion
      // is never resurrected on refresh. Keyed by candidate_index/output id,
      // never by image URL.
      const occupied = new Set(batchNodes.map((n) => imageGenerationBatchSlotIndex(((n.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)))
      const materializedForTask = new Set<string>()
      for (const n of nodesRef.current) {
        const d = n.data as unknown as FlowNodeData
        const gid = ((d.fields || {}) as Record<string, unknown>).generatedOutputId
        if (d.latestRunId === task.id && typeof gid === 'string') materializedForTask.add(gid)
      }
      // Once per (group, run) per session — repeated polling / hydration /
      // StrictMode re-invocation can never create the same slots twice.
      const recoveryKey = `${batchId}:${task.id}`
      const recover =
        opts?.allowSlotRecovery && !recoveredSlotRunsRef.current.has(recoveryKey)
          ? outputsNeedingResultSlots(task, occupied, materializedForTask)
          : []
      if (opts?.allowSlotRecovery && task.status === 'succeeded') recoveredSlotRunsRef.current.add(recoveryKey)
      if (recover.length > 0) {
        requested = Math.max(requested, ...(task.outputs || []).map((o) => o.candidate_index + 1))
      }

      for (const node of batchNodes) {
        const fields = ((node.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const slot = imageGenerationBatchSlotIndex(fields)
        const patch = buildImageGenerationSlotTaskFieldsPatch(task, slot, requested, resolveCanvasImageTaskFieldStatus)
        const merged = recover.length > 0 ? { ...(patch || {}), [IMAGE_GENERATION_BATCH_REQUESTED_FIELD]: requested } : patch
        if (!merged) continue
        const next = patchCanvasFieldsIfChanged(fields, merged)
        if (next) onChangeFields(node.id, next)
      }

      if (recover.length > 0) {
        const originNodeId = (leaderFields[IMAGE_GENERATION_BATCH_ORIGIN_FIELD] as string | undefined) || leaderNodeId
        const leaderData = leader.data as unknown as FlowNodeData
        const incomingRefEdges = edgesRef.current.filter(
          (e) => e.target === leaderNodeId && isImageReferenceAssetHandle(e.targetHandle),
        )
        const rightEdge = Math.max(...batchNodes.map((n) => n.position.x + getNodeWidth(n)))
        const created: Node[] = []
        const createdEdges: Edge[] = []
        recover.forEach((output, i) => {
          const newId = genId()
          created.push({
            id: newId,
            type: leader.type,
            position: {
              x: rightEdge + RESULT_SIBLING_GAP_PX + i * (RESULT_SIBLING_CARD_WIDTH_PX + RESULT_SIBLING_GAP_PX),
              y: leader.position.y,
            },
            ...(leader.parentId ? { parentId: leader.parentId, extent: 'parent' as const } : {}),
            data: {
              ...leaderData,
              collapsed: true,
              latestRunId: task.id,
              fields: {
                ...buildImageGenerationBatchSiblingFields(leaderFields, {
                  batchId,
                  slotIndex: output.candidate_index,
                  originNodeId,
                  requestedCount: requested,
                  taskId: task.id,
                }),
                ...completedSlotPatch(output),
              },
            } as unknown as Record<string, unknown>,
          })
          incomingRefEdges.forEach((e) => {
            const splitEdgeId = genId()
            const targetHandle = isImageReferenceAssetHandle(e.targetHandle)
              ? imageReferenceAssetHandleId(splitEdgeId)
              : e.targetHandle
            createdEdges.push({ ...e, id: splitEdgeId, target: newId, targetHandle })
          })
        })
        commitNodes((nds) => nds.concat(created))
        if (createdEdges.length > 0) {
          commitEdges((eds) => {
            const next = [...eds, ...createdEdges]
            scheduleFromState(nodesRef.current, next)
            return next
          })
        }
      }
    },
    [commitEdges, commitNodes, onChangeFields, scheduleFromState],
  )

  const onCommitImageGenerationBatchFanOut = useCallback(
    (originNodeId: string, ctx: { taskId: string; requestedCount: number; fieldStatus: string }) => {
      const { taskId, requestedCount } = ctx
      if (requestedCount <= 1) return
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return
      const originData = origin.data as unknown as FlowNodeData
      const originFields = (originData.fields || {}) as Record<string, unknown>
      if (originFields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) return

      const batchId = genId()
      const incomingRefEdges = edgesRef.current.filter(
        (e) => e.target === originNodeId && isImageReferenceAssetHandle(e.targetHandle),
      )
      const siblingCount = requestedCount - 1
      const positions = placeSiblingNodesBesideSource(origin, siblingCount, {})
      const newNodes: Node[] = []
      const newEdges: Edge[] = []

      for (let i = 0; i < siblingCount; i++) {
        const slotIndex = i + 1
        const newId = genId()
        const newNode: Node = {
          id: newId,
          type: origin.type,
          position: positions[i],
          data: {
            ...originData,
            collapsed: true,
            latestRunId: taskId,
            fields: buildImageGenerationBatchSiblingFields(originFields, {
              batchId,
              slotIndex,
              originNodeId,
              requestedCount,
              taskId,
            }),
          } as unknown as Record<string, unknown>,
        }
        if (origin.parentId) {
          newNode.parentId = origin.parentId
          newNode.extent = 'parent'
        }
        newNodes.push(newNode)
        incomingRefEdges.forEach((e) => {
          const splitEdgeId = genId()
          const targetHandle = isImageReferenceAssetHandle(e.targetHandle)
            ? imageReferenceAssetHandleId(splitEdgeId)
            : e.targetHandle
          newEdges.push({ ...e, id: splitEdgeId, target: newId, targetHandle })
        })
      }

      const originPatch = buildImageGenerationBatchOriginPatch({ batchId, originNodeId, requestedCount })
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id !== originNodeId) return n
          const data = n.data as unknown as FlowNodeData
          const fields = (data.fields || {}) as Record<string, unknown>
          const nextFields = { ...fields, ...originPatch }
          return { ...n, data: { ...data, latestRunId: taskId, fields: nextFields } as unknown as Record<string, unknown> }
        }).concat(newNodes),
      )
      commitEdges((eds) => {
        const next = [...eds, ...newEdges]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // VistoeraTASK-00242 P0 — this is the "requested≠actual excess-output"
  // safety-net split path (distinct from `onCommitImageGenerationBatchFanOut`'s
  // accept-time happy path above). A real Preview incident showed this
  // function creating a FULL duplicate set of sibling nodes on EVERY
  // repeat invocation (re-poll, mount-hydration effect re-fire,
  // StrictMode double-invoke, reconnect) — because, unlike its sibling
  // function above, it never wrote `IMAGE_GENERATION_BATCH_COMMITTED_FIELD`
  // back onto the origin, so the guard below could never actually engage.
  // It ALSO left each new sibling's `outputAssetId`/dimensions stale
  // (inherited from `originData.fields` instead of the real output being
  // materialized), which produced sibling cards whose `generatedOutputId`
  // and `outputAssetId` referred to two DIFFERENT candidates. Fixed by:
  // (1) actually committing the batch on the origin (mirroring
  // `buildImageGenerationBatchOriginPatch`, exactly like the accept-time
  // path), so this guard blocks every subsequent call for the same
  // already-split origin; (2) building each sibling's fields via the
  // SAME `buildImageGenerationBatchSiblingFields` + `completedSlotPatch`
  // helpers the happy path uses, keyed by the output's own
  // `candidate_index`/`site_asset_id`/dimensions — never a stale spread.
  const onSplitGenerationOutputs = useCallback(
    (originNodeId: string, extraOutputs: ImageGenerationTaskOutput[]) => {
      if (extraOutputs.length === 0) return
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return
      const originData = origin.data as unknown as FlowNodeData
      const originFields = (originData.fields || {}) as Record<string, unknown>
      if (originFields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) return
      const taskId = (originData.latestRunId as string | null | undefined) || ''
      // VistoeraTASK-00242 — canvas-WIDE idempotency, not just per-origin:
      // the per-origin `committed` guard above is necessary but, on its
      // own, is not sufficient against pre-existing pollution — many
      // separate (already-duplicated) "origin-shaped" nodes for the SAME
      // real task, each individually still uncommitted, could otherwise
      // each independently decide to (re-)materialize the SAME real
      // output the first time THEY happen to mount/reconcile after this
      // fix ships. Re-derive, from `nodesRef.current` itself (never a
      // stale local list — this always reflects the LATEST committed
      // state), which real output identities (`generatedOutputId`) are
      // ALREADY materialized ANYWHERE in this canvas for this task_id,
      // and skip re-creating a sibling for any of them — the actual
      // architectural defense §18 calls for (a stable business identity,
      // not "this one caller promises to only run once").
      const alreadyMaterializedOutputIds = new Set(
        nodesRef.current
          .filter((n) => (n.data as unknown as FlowNodeData).latestRunId === taskId)
          .map((n) => (((n.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>).generatedOutputId)
          .filter((v): v is string => typeof v === 'string'),
      )
      const outputsToMaterialize = extraOutputs.filter((output) => !alreadyMaterializedOutputIds.has(output.id))
      if (outputsToMaterialize.length === 0) {
        // Every real output this origin would have split is already
        // materialized elsewhere in the canvas (pre-existing pollution
        // settling) — just commit THIS origin so it stops re-checking,
        // without creating any further duplicate.
        commitNodes((nds) =>
          nds.map((n) => {
            if (n.id !== originNodeId) return n
            const data = n.data as unknown as FlowNodeData
            const fields = (data.fields || {}) as Record<string, unknown>
            return {
              ...n,
              data: {
                ...data,
                fields: { ...fields, [IMAGE_GENERATION_BATCH_COMMITTED_FIELD]: true },
              } as unknown as Record<string, unknown>,
            }
          }),
        )
        return
      }
      const batchId = genId()
      const requestedCount = outputsToMaterialize.length + 1
      // VistoeraTASK-00144 — match bare AND per-edge-scoped reference
      // handles alike (isImageReferenceAssetHandle), not just the exact
      // legacy literal, so newly-scoped edges keep splitting correctly.
      const incomingRefEdges = edgesRef.current.filter(
        (e) => e.target === originNodeId && isImageReferenceAssetHandle(e.targetHandle),
      )
      const newNodes: Node[] = []
      const newEdges: Edge[] = []
      // VistoeraTASK-00509 — stable output order (candidate_index) and a
      // non-overlapping row to the right of the origin (result 2, 3, …).
      outputsToMaterialize.sort((a, b) => a.candidate_index - b.candidate_index)
      const splitPositions = placeResultSiblingsInRow(origin, outputsToMaterialize.length)
      outputsToMaterialize.forEach((output, outputOrdinal) => {
        const newId = genId()
        const slotIndex = output.candidate_index
        const siblingFields = {
          ...buildImageGenerationBatchSiblingFields(originFields, {
            batchId,
            slotIndex,
            originNodeId,
            requestedCount,
            taskId,
          }),
          ...completedSlotPatch(output),
        }
        const newNode: Node = {
          id: newId,
          type: origin.type,
          position: splitPositions[outputOrdinal],
          data: {
            ...originData,
            collapsed: true,
            latestRunId: originData.latestRunId,
            fields: siblingFields,
          } as unknown as Record<string, unknown>,
        }
        if (origin.parentId) {
          newNode.parentId = origin.parentId
          newNode.extent = 'parent'
        }
        newNodes.push(newNode)
        incomingRefEdges.forEach((e) => {
          const splitEdgeId = genId()
          // VistoeraTASK-00144 — re-scope to the SIBLING'S OWN new edge
          // id (never keep the origin edge's suffix), so the sibling's
          // Related Assets can resolve a real, unique DOM target too.
          const targetHandle = isImageReferenceAssetHandle(e.targetHandle)
            ? imageReferenceAssetHandleId(splitEdgeId)
            : e.targetHandle
          newEdges.push({ ...e, id: splitEdgeId, target: newId, targetHandle })
        })
      })
      const originPatch = buildImageGenerationBatchOriginPatch({ batchId, originNodeId, requestedCount })
      commitNodes((nds) =>
        nds
          .map((n) => {
            if (n.id !== originNodeId) return n
            const data = n.data as unknown as FlowNodeData
            const fields = (data.fields || {}) as Record<string, unknown>
            return { ...n, data: { ...data, fields: { ...fields, ...originPatch } } as unknown as Record<string, unknown> }
          })
          .concat(newNodes),
      )
      commitEdges((eds) => {
        const next = [...eds, ...newEdges]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // VistoeraTASK-00512 — Result Slot lifecycle.
  //
  // `onPrepareImageGenerationResultSlots` runs BEFORE a generate request is
  // submitted (first run, "覆盖生成" and "另外生成" alike). It guarantees that
  // exactly `requestedCount` Result Slots exist for the new run: it reuses the
  // clicked node's existing group members (stable slot order), creates the
  // missing empty slots, and detaches (never deletes) members beyond the target.
  // It deliberately does NOT write the clicked node itself — that node's own
  // component owns its `fields` (a Board-side write would be clobbered by its
  // stale `fieldsRef`); the caller applies the returned `originPatch`.
  const onPrepareImageGenerationResultSlots = useCallback(
    (
      originNodeId: string,
      ctx: { requestedCount: number },
    ): { ok: boolean; reason?: 'GROUP_RUNNING'; batchId: string | null; originPatch: Record<string, unknown> | null } => {
      const requested = Math.max(1, Math.floor(ctx.requestedCount || 1))
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return { ok: true, batchId: null, originPatch: null }
      const originData = origin.data as unknown as FlowNodeData
      const originFields = (originData.fields || {}) as Record<string, unknown>
      const oldBatchId =
        originFields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD] === true &&
        typeof originFields[IMAGE_GENERATION_BATCH_ID_FIELD] === 'string'
          ? (originFields[IMAGE_GENERATION_BATCH_ID_FIELD] as string)
          : null
      const fieldsOf = (n: Node) => (((n.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)
      const members = oldBatchId
        ? nodesRef.current.filter((n) => n.id !== originNodeId && fieldsOf(n)[IMAGE_GENERATION_BATCH_ID_FIELD] === oldBatchId)
        : []
      if (members.some((m) => isImageGenerationResultSlotRunning(fieldsOf(m)))) {
        return { ok: false, reason: 'GROUP_RUNNING', batchId: null, originPatch: null }
      }
      // Plain single-result node (no group): untouched, current behavior.
      if (requested <= 1 && !oldBatchId) return { ok: true, batchId: null, originPatch: null }

      const plan = planResultSlotReconciliation({
        members: members.map((m) => ({ id: m.id, slotIndex: imageGenerationBatchSlotIndex(fieldsOf(m)) })),
        requestedCount: requested,
      })
      const batchId = requested > 1 ? genId() : null
      const keptById = new Map(plan.kept.map((k) => [k.id, k.slotIndex]))
      const detached = new Set(plan.detachedIds)

      const newNodes: Node[] = []
      const newEdges: Edge[] = []
      if (batchId && plan.createSlotIndexes.length > 0) {
        const incomingRefEdges = edgesRef.current.filter(
          (e) => e.target === originNodeId && isImageReferenceAssetHandle(e.targetHandle),
        )
        let rightEdge = origin.position.x + getNodeWidth(origin)
        for (const m of members) {
          if (keptById.has(m.id)) rightEdge = Math.max(rightEdge, m.position.x + getNodeWidth(m))
        }
        plan.createSlotIndexes.forEach((slotIndex, i) => {
          const newId = genId()
          newNodes.push({
            id: newId,
            type: origin.type,
            position: {
              x: rightEdge + RESULT_SIBLING_GAP_PX + i * (RESULT_SIBLING_CARD_WIDTH_PX + RESULT_SIBLING_GAP_PX),
              y: origin.position.y,
            },
            ...(origin.parentId ? { parentId: origin.parentId, extent: 'parent' as const } : {}),
            data: {
              ...originData,
              collapsed: true,
              latestRunId: null,
              fields: buildImageGenerationBatchSiblingFields(originFields, {
                batchId,
                slotIndex,
                originNodeId,
                requestedCount: requested,
                taskId: '',
              }),
            } as unknown as Record<string, unknown>,
          })
          incomingRefEdges.forEach((e) => {
            const splitEdgeId = genId()
            const targetHandle = isImageReferenceAssetHandle(e.targetHandle)
              ? imageReferenceAssetHandleId(splitEdgeId)
              : e.targetHandle
            newEdges.push({ ...e, id: splitEdgeId, target: newId, targetHandle })
          })
        })
      }

      commitNodes((nds) =>
        nds
          .map((n) => {
            if (n.id === originNodeId) return n
            const data = n.data as unknown as FlowNodeData
            const fields = (data.fields || {}) as Record<string, unknown>
            if (batchId && keptById.has(n.id)) {
              const nextFields = {
                ...fields,
                [IMAGE_GENERATION_BATCH_ID_FIELD]: batchId,
                [IMAGE_GENERATION_BATCH_SLOT_FIELD]: keptById.get(n.id),
                [IMAGE_GENERATION_BATCH_ORIGIN_FIELD]: originNodeId,
                [IMAGE_GENERATION_BATCH_REQUESTED_FIELD]: requested,
                [IMAGE_GENERATION_BATCH_COMMITTED_FIELD]: true,
                [IMAGE_GENERATION_BATCH_FOLLOWER_FIELD]: true,
                [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: null,
                // Same convention as the clicked node: keep showing the prior
                // result under the spinner until the new output lands.
                previousOutputId: (fields.generatedOutputId as string | undefined) ?? undefined,
                status: 'GENERATING',
              }
              return { ...n, data: { ...data, fields: nextFields } as unknown as Record<string, unknown> }
            }
            if (detached.has(n.id) || (!batchId && fields[IMAGE_GENERATION_BATCH_ID_FIELD] === oldBatchId)) {
              return {
                ...n,
                data: { ...data, fields: { ...fields, ...resetImageGenerationBatchFieldsPatch() } } as unknown as Record<string, unknown>,
              }
            }
            return n
          })
          .concat(newNodes),
      )
      if (newEdges.length > 0) {
        commitEdges((eds) => {
          const next = [...eds, ...newEdges]
          scheduleFromState(nodesRef.current, next)
          return next
        })
      }
      return {
        ok: true,
        batchId,
        originPatch: batchId
          ? buildImageGenerationBatchOriginPatch({ batchId, originNodeId, requestedCount: requested })
          : resetImageGenerationBatchFieldsPatch(),
      }
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // After SaaS accepted the run: every slot of the group now points at it.
  const onBindImageGenerationBatchRun = useCallback(
    (originNodeId: string, taskId: string) => {
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return
      const batchId = (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)[
        IMAGE_GENERATION_BATCH_ID_FIELD
      ]
      if (typeof batchId !== 'string' || !batchId) return
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id === originNodeId) return n
          const data = n.data as unknown as FlowNodeData
          const fields = (data.fields || {}) as Record<string, unknown>
          if (fields[IMAGE_GENERATION_BATCH_ID_FIELD] !== batchId || data.latestRunId === taskId) return n
          return { ...n, data: { ...data, latestRunId: taskId } as unknown as Record<string, unknown> }
        }),
      )
    },
    [commitNodes],
  )

  // The run was never accepted (validation/network failure after the slots were
  // prepared): don't leave sibling slots spinning forever.
  const onSettleImageGenerationBatchSubmitFailure = useCallback(
    (originNodeId: string, errorSummary: string | null) => {
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return
      const batchId = (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)[
        IMAGE_GENERATION_BATCH_ID_FIELD
      ]
      if (typeof batchId !== 'string' || !batchId) return
      commitNodes((nds) =>
        nds.map((n) => {
          if (n.id === originNodeId) return n
          const data = n.data as unknown as FlowNodeData
          const fields = (data.fields || {}) as Record<string, unknown>
          if (fields[IMAGE_GENERATION_BATCH_ID_FIELD] !== batchId || !isImageGenerationResultSlotRunning(fields)) return n
          const restored = fields.generatedOutputId
            ? { status: 'COMPLETED', previousOutputId: undefined }
            : { status: 'FAILED', [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: errorSummary }
          return { ...n, data: { ...data, fields: { ...fields, ...restored } } as unknown as Record<string, unknown> }
        }),
      )
    },
    [commitNodes],
  )

  // "不覆盖，另外生成" (spec §27, placement corrected by P1 spec §19) —
  // copies prompt/refs/params (clears the result pointer) onto a
  // brand-new sibling card placed BESIDE the origin node (never behind/
  // overlapping it — the old `+40/+40` diagonal nudge visually stacked
  // the new card almost directly on top of the original), inherits the
  // SAME upstream reference edges, and marks it `pendingAutoGenerate` so
  // that node's own component fires its own generation exactly once on
  // mount — never this component reaching into another node's API call.
  const onDuplicateForRegenerate = useCallback(
    (nodeId: string) => {
      const origin = nodesRef.current.find((n) => n.id === nodeId)
      if (!origin) return
      const originData = origin.data as unknown as FlowNodeData
      const newId = genId()
      const { copiedEdges, edgeIdMap } = copyIncomingEdgesForFork(edgesRef.current, nodeId, newId, genId)
      const forkFields = buildRegenerateForkFields(originData.nodeType, originData.fields, edgeIdMap)
      if (originData.nodeType === 'generate_tryon') {
        Object.assign(forkFields, {
          origin_node_id: nodeId,
          source_run_id: (originData.fields?.jobId as string | undefined) || null,
          duplication_reason: 'regenerate_as_new',
        })
      }
      const newNode: Node = {
        id: newId,
        type: origin.type,
        position: placeNodeBesideSource(origin, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: originData.nodeType,
          category: originData.category,
          entityType: originData.entityType,
          entityId: originData.entityId,
          collapsed: originData.collapsed,
          latestRunId: null,
          fields: forkFields,
        } as unknown as Record<string, unknown>,
      }
      if (origin.parentId) {
        newNode.parentId = origin.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = [...eds, ...copiedEdges]
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  // ---------------------------------------------------------------
  // VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_IDEATION_BRANCHING_
  // NODE_V1 — `project_start` "生成创意方向" + `creative_candidate` "发散".
  // All state read/written off `nodesRef`/`edgesRef` (never captured
  // component state) so a poll tick that lands after navigation/reload
  // still finds the right node; mirrors `onSplitGenerationOutputs`'
  // sibling-node-creation shape above but adds the extra polling loop
  // real PC3/browser-dispatched runs need (spec §68 "SUBMITTING → QUEUED
  // → GENERATING → SUCCESS").
  // ---------------------------------------------------------------
  const materializedIdeationRunIdsRef = useRef<Set<string>>(new Set())

  useEffect(
    () => () => {
      ideationPollTimersRef.current.forEach((timer) => window.clearTimeout(timer))
      ideationPollTimersRef.current.clear()
    },
    [],
  )

  const materializeCreativeCandidates = useCallback(
    (
      originNodeId: string,
      run: CreativeIdeationRunState,
      ctx: { rootBriefNodeId: string; parentCandidateNodeId: string | null; generationDepth: number; briefFingerprintAtRun: string },
    ) => {
      if (materializedIdeationRunIdsRef.current.has(run.run_id)) return
      const candidates = run.candidates || []
      if (candidates.length === 0) return
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      if (!origin) return
      materializedIdeationRunIdsRef.current.add(run.run_id)

      const def = getNodeDefinition('creative_candidate')
      // "再生成一批"/repeated "发散" from the SAME origin (spec §41 —
      // never overwrite, always add a new batch) would otherwise fan
      // out centered on the exact same spot as the previous batch;
      // nudge each successive batch further down by how many candidate
      // children this origin already has so batches stack rather than
      // stack ON TOP of each other.
      const existingChildren = edgesRef.current.filter(
        (e) => e.source === originNodeId && (e.data as { relationType?: string } | undefined)?.relationType === CREATIVE_CANDIDATE_EDGE_RELATION,
      ).length
      const positions = placeSiblingNodesBesideSource(origin, candidates.length, { verticalNudge: existingChildren * 40 })
      const newNodes: Node[] = candidates.map((candidate, idx) => ({
        id: genId(),
        type: def.family,
        position: positions[idx],
        data: {
          nodeType: 'creative_candidate',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          latestRunId: null,
          fields: buildCandidateNodeFields(candidate, {
            rootBriefNodeId: ctx.rootBriefNodeId,
            parentCandidateNodeId: ctx.parentCandidateNodeId,
            generationDepth: ctx.generationDepth,
            sourceRunId: run.run_id,
            rootRunId: run.root_run_id || run.run_id,
            briefFingerprintAtRun: ctx.briefFingerprintAtRun,
          }),
        } as unknown as Record<string, unknown>,
      }))
      commitNodes((nds) => [...nds, ...newNodes])
      commitEdges((eds) => {
        const newEdges: Edge[] = newNodes.map((n) => ({
          id: genId(),
          source: originNodeId,
          target: n.id,
          data: { relationType: CREATIVE_CANDIDATE_EDGE_RELATION },
          className: 'pcv-edge',
        }))
        const next = [...eds, ...newEdges]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const clearIdeationPoll = useCallback((runId: string) => {
    const timer = ideationPollTimersRef.current.get(runId)
    if (timer != null) {
      window.clearTimeout(timer)
      ideationPollTimersRef.current.delete(runId)
    }
  }, [])

  const applyIdeationRunToNode = useCallback(
    (
      trackedNodeId: string,
      run: CreativeIdeationRunState,
      ctx: { rootBriefNodeId: string; parentCandidateNodeId: string | null; generationDepth: number; briefFingerprintAtRun: string },
      fieldKeys: { runIdKey: string; statusKey: string; errorKey: string },
    ) => {
      const node = nodesRef.current.find((n) => n.id === trackedNodeId)
      if (node) {
        const data = node.data as unknown as FlowNodeData
        const patch = {
          [fieldKeys.runIdKey]: run.run_id,
          [fieldKeys.statusKey]: run.status,
          [fieldKeys.errorKey]: run.error_message,
        }
        const next = patchCanvasFieldsIfChanged((data.fields || {}) as Record<string, unknown>, patch)
        if (next) onChangeFields(trackedNodeId, next)
      }
      if (run.status === 'SUCCESS') {
        // `trackedNodeId` IS the fan-out origin either way — the
        // `project_start` node itself for an IDEATION run, or the
        // expanded `creative_candidate` node itself for an EXPAND run
        // (both call sites below always set `ctx.parentCandidateNodeId`
        // to that SAME id for the EXPAND case, `null` for IDEATION).
        materializeCreativeCandidates(trackedNodeId, run, ctx)
        clearIdeationPoll(run.run_id)
      } else if (run.status === 'ERROR') {
        message.error(run.error_message || '创意方向生成失败')
        clearIdeationPoll(run.run_id)
      }
    },
    [clearIdeationPoll, materializeCreativeCandidates, onChangeFields],
  )

  const pollIdeationRun = useCallback(
    (
      trackedNodeId: string,
      runId: string,
      ctx: { rootBriefNodeId: string; parentCandidateNodeId: string | null; generationDepth: number; briefFingerprintAtRun: string },
      fieldKeys: { runIdKey: string; statusKey: string; errorKey: string },
    ) => {
      clearIdeationPoll(runId)
      const tick = async () => {
        try {
          const run = await getCreativeIdeationRun(workspaceId, runId)
          applyIdeationRunToNode(trackedNodeId, run, ctx, fieldKeys)
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            const timer = window.setTimeout(tick, 2500)
            ideationPollTimersRef.current.set(runId, timer)
          }
        } catch {
          const timer = window.setTimeout(tick, 5000)
          ideationPollTimersRef.current.set(runId, timer)
        }
      }
      void tick()
    },
    [applyIdeationRunToNode, clearIdeationPoll, workspaceId],
  )

  // "生成创意方向" (spec §27/§28) — `project_start` node's own click
  // handler. Idempotency is enforced both here (disallow a second click
  // while THIS node's own `runStatus` is already active — spec §28) and
  // server-side (`_get_active_run` returns the SAME run for a repeat
  // click that races past this guard).
  const onGenerateCreativeIdeation = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
      if (activeStatuses.has(fields.runStatus as string)) return
      const intent = String(fields.userIntent ?? '').trim()
      if (!intent) {
        message.error('请先填写创作意图')
        return
      }
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'
      const fingerprint = briefFingerprint(fields)
      const patch = patchCanvasFieldsIfChanged(fields, { runStatus: 'SUBMITTING', runErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      generateCreativeIdeation(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        brief: buildBriefPayload(fields),
        candidate_count: (fields.candidateCount as number) || undefined,
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-${Date.now()}`,
      })
        .then((run) => {
          const ctx = { rootBriefNodeId: nodeId, parentCandidateNodeId: null, generationDepth: 0, briefFingerprintAtRun: fingerprint }
          applyIdeationRunToNode(nodeId, run, ctx, { runIdKey: 'activeRunId', statusKey: 'runStatus', errorKey: 'runErrorMessage' })
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            pollIdeationRun(nodeId, run.run_id, ctx, { runIdKey: 'activeRunId', statusKey: 'runStatus', errorKey: 'runErrorMessage' })
          }
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '创意方向生成失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { runStatus: 'ERROR', runErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [applyIdeationRunToNode, onChangeFields, pollIdeationRun, projectId, workspaceId],
  )

  // "发散" (spec §37-§39) — a `creative_candidate` node's own hover
  // action. Always resolves the CURRENT `project_start` brief (via
  // `rootBriefNodeId`) rather than any snapshot on the candidate, so an
  // Expand run reflects whatever the user has edited since — the SAME
  // "changing the Brief never auto-regenerates, but a NEW explicit
  // generate/expand click always reads live state" rule §43 applies to
  // both actions identically.
  const onExpandCreativeCandidate = useCallback(
    (candidateNodeId: string, instruction: string | null) => {
      const candidateNode = nodesRef.current.find((n) => n.id === candidateNodeId)
      if (!candidateNode) return
      const cData = candidateNode.data as unknown as FlowNodeData
      const cFields = (cData.fields || {}) as Record<string, unknown>
      const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
      if (activeStatuses.has(cFields.expandRunStatus as string)) return

      const rootBriefNodeId = (cFields.rootBriefNodeId as string) || null
      const briefNode = rootBriefNodeId ? nodesRef.current.find((n) => n.id === rootBriefNodeId) : null
      if (!briefNode || !rootBriefNodeId) {
        message.error('找不到原始项目发起节点')
        return
      }
      const briefFields = ((briefNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const skillSelectionSource = (briefFields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (briefFields.providerSelectionMode as string) || 'DEFAULT'
      const generationDepth = ((cFields.generationDepth as number) || 0) + 1
      const rootRunId = (cFields.generationDepth as number) > 0 ? (cFields.rootRunId as string) : (cFields.sourceRunId as string)
      const fingerprint = briefFingerprint(briefFields)

      const patch = patchCanvasFieldsIfChanged(cFields, { expandRunStatus: 'SUBMITTING', expandRunErrorMessage: null })
      if (patch) onChangeFields(candidateNodeId, patch)

      expandCreativeIdeation(workspaceId, {
        canvas_node_id: candidateNodeId,
        candidate_node_id: candidateNodeId,
        project_id: projectId,
        root_canvas_node_id: rootBriefNodeId,
        root_run_id: rootRunId || null,
        generation_depth: generationDepth,
        brief: buildBriefPayload(briefFields),
        parent_candidate: buildParentCandidatePayload(cFields),
        expand_instruction: instruction && instruction.trim() ? instruction.trim() : null,
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (briefFields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (briefFields.providerOptionId as string | null) : null,
        idempotency_key: `${candidateNodeId}-${Date.now()}`,
      })
        .then((run) => {
          const ctx = {
            rootBriefNodeId: rootBriefNodeId as string,
            parentCandidateNodeId: candidateNodeId,
            generationDepth,
            briefFingerprintAtRun: fingerprint,
          }
          applyIdeationRunToNode(candidateNodeId, run, ctx, {
            runIdKey: 'activeExpandRunId',
            statusKey: 'expandRunStatus',
            errorKey: 'expandRunErrorMessage',
          })
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            pollIdeationRun(candidateNodeId, run.run_id, ctx, {
              runIdKey: 'activeExpandRunId',
              statusKey: 'expandRunStatus',
              errorKey: 'expandRunErrorMessage',
            })
          }
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '发散生成失败'
          const cur = nodesRef.current.find((n) => n.id === candidateNodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { expandRunStatus: 'ERROR', expandRunErrorMessage: display })
            if (next) onChangeFields(candidateNodeId, next)
          }
          message.error(display)
        })
    },
    [applyIdeationRunToNode, onChangeFields, pollIdeationRun, projectId, workspaceId],
  )

  // Resume polling any run left QUEUED/GENERATING/SUBMITTING across a
  // refresh (spec §68's loading states must survive reload, not just
  // the in-session click) — mirrors `GenerateTryOnNode`'s own per-node
  // `runHydrating` reconciliation, done once here for both run kinds
  // since candidate materialization needs the shared cross-node helpers
  // above rather than a per-node effect.
  const ideationHydratedRef = useRef(false)
  useEffect(() => {
    if (ideationHydratedRef.current) return
    if (canvasHydrating) return
    ideationHydratedRef.current = true
    const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
    for (const n of nodesRef.current) {
      const data = n.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      if (data.nodeType === 'project_start' && activeStatuses.has(fields.runStatus as string) && fields.activeRunId) {
        pollIdeationRun(
          n.id,
          fields.activeRunId as string,
          { rootBriefNodeId: n.id, parentCandidateNodeId: null, generationDepth: 0, briefFingerprintAtRun: briefFingerprint(fields) },
          { runIdKey: 'activeRunId', statusKey: 'runStatus', errorKey: 'runErrorMessage' },
        )
      } else if (data.nodeType === 'creative_candidate' && activeStatuses.has(fields.expandRunStatus as string) && fields.activeExpandRunId) {
        const rootBriefNodeId = (fields.rootBriefNodeId as string) || n.id
        pollIdeationRun(
          n.id,
          fields.activeExpandRunId as string,
          {
            rootBriefNodeId,
            parentCandidateNodeId: n.id,
            generationDepth: ((fields.generationDepth as number) || 0) + 1,
            briefFingerprintAtRun: briefFingerprint(fields),
          },
          { runIdKey: 'activeExpandRunId', statusKey: 'expandRunStatus', errorKey: 'expandRunErrorMessage' },
        )
      }
    }
  }, [canvasHydrating, pollIdeationRun])

  // -------------------------------------------------------------------
  // VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
  // LINKING_V1 — `script_generation` node's own "生成剧本" (writes INTO
  // this SAME node, spec §12) and "受控发散" (creates 3 NEW independent
  // sibling nodes, spec §13). Mirrors the Creative Ideation poll-loop
  // shape immediately above (SUBMITTING/QUEUED/GENERATING → SUCCESS/
  // ERROR survives reload the same way), but the two run kinds share
  // this ORIGIN node's `canvas_node_id` on the backend (`_dispatch`'s
  // `canvas_node_id=canvas_node_id` for both `generate`/`diverge`), so
  // GENERATE and DIVERGE fields are tracked under separate key pairs
  // (`activeRunId`/`runStatus` vs `activeDivergeRunId`/`divergeRunStatus`)
  // on the SAME node rather than needing two different tracked node ids.
  // -------------------------------------------------------------------
  const materializedDivergeRunIdsRef = useRef<Set<string>>(new Set())

  useEffect(
    () => () => {
      scriptPollTimersRef.current.forEach((timer) => window.clearTimeout(timer))
      scriptPollTimersRef.current.clear()
    },
    [],
  )

  const clearScriptPoll = useCallback((runId: string) => {
    const timer = scriptPollTimersRef.current.get(runId)
    if (timer != null) {
      window.clearTimeout(timer)
      scriptPollTimersRef.current.delete(runId)
    }
  }, [])

  // Diverge SUCCESS (spec §13) — 3 NEW independent `script_generation`
  // sibling nodes, one per directionally-distinct script the backend
  // returned; the ORIGIN node itself is never overwritten (still its own
  // separate node afterwards, unlike GENERATE). Each sibling keeps the
  // Canonical Generation Card diverge for Script: TOTAL=3 = origin A +
  // 2 NEW placeholder siblings B/C created after dispatch succeeds.
  // On SUCCESS, script[0]→A, [1]→B, [2]→C. No visible sibling edges.
  const applyScriptDivergenceGroupResult = useCallback(
    (originNodeId: string, run: ScriptGenerationRunState) => {
      if (materializedDivergeRunIdsRef.current.has(run.run_id)) return
      materializedDivergeRunIdsRef.current.add(run.run_id)
      const scripts = run.scripts || []
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      const originFields = origin ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>) : {}
      const groupId = originFields.divergenceGroupId as string | undefined
      const runMetaBase = {
        runId: run.run_id,
        skillKey: run.skill_key,
        skillVersion: run.skill_version,
        providerOptionId: run.resolved_provider_option_id,
      }

      const siblings = groupId
        ? nodesRef.current.filter(
            (n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId,
          )
        : []
      const siblingAt = (idx: number) =>
        siblings.find((n) => ((n.data as unknown as FlowNodeData).fields || {}).divergenceSiblingIndex === idx) || null

      const applyToOrigin = () => {
        const cur = nodesRef.current.find((n) => n.id === originNodeId)
        if (!cur) return
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const script = scripts[0]
        if (script) {
          const next = {
            ...applyGeneratedScript(curFields as Record<string, unknown>, script, runMetaBase),
            activeDivergeRunId: null,
            divergeRunStatus: null,
            divergeRunErrorMessage: null,
          }
          onChangeFields(originNodeId, next)
        } else {
          const next = patchCanvasFieldsIfChanged(curFields, {
            activeDivergeRunId: null,
            divergeRunStatus: 'ERROR',
            divergeRunErrorMessage: '该方向未返回有效结果，可重新发散或单独重新生成',
          })
          if (next) onChangeFields(originNodeId, next)
        }
      }

      const applyToSibling = (targetId: string | null, idx: number) => {
        if (!targetId) return
        const cur = nodesRef.current.find((n) => n.id === targetId)
        if (!cur) return
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const script = scripts[idx]
        if (script) {
          const next = buildDivergeSiblingFields(originFields, script, {
            ...runMetaBase,
            divergenceGroupId: (groupId as string) || '',
            siblingIndex: idx,
            originNodeId,
          })
          onChangeFields(targetId, next)
        } else {
          const next = patchCanvasFieldsIfChanged(curFields, {
            activeRunId: null,
            runStatus: 'ERROR',
            runErrorMessage: '该方向未返回有效结果，可重新发散或单独重新生成',
          })
          if (next) onChangeFields(targetId, next)
        }
      }

      applyToOrigin()
      applyToSibling(siblingAt(1)?.id ?? null, 1)
      applyToSibling(siblingAt(2)?.id ?? null, 2)
    },
    [onChangeFields],
  )

  const applyScriptDivergenceGroupError = useCallback(
    (originNodeId: string, groupId: string | undefined, message_: string) => {
      const cur = nodesRef.current.find((n) => n.id === originNodeId)
      if (cur) {
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(curFields, {
          activeDivergeRunId: null,
          divergeRunStatus: 'ERROR',
          divergeRunErrorMessage: message_,
        })
        if (next) onChangeFields(originNodeId, next)
      }
      if (!groupId) return
      const siblings = nodesRef.current.filter(
        (n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId,
      )
      for (const sib of siblings) {
        const sibFields = (sib.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(sibFields, { activeRunId: null, runStatus: 'ERROR', runErrorMessage: message_ })
        if (next) onChangeFields(sib.id, next)
      }
    },
    [onChangeFields],
  )

  const applyScriptRunToNode = useCallback(
    (trackedNodeId: string, run: ScriptGenerationRunState, mode: 'GENERATE' | 'DIVERGE') => {
      const keys =
        mode === 'GENERATE'
          ? { runIdKey: 'activeRunId', statusKey: 'runStatus', errorKey: 'runErrorMessage' }
          : { runIdKey: 'activeDivergeRunId', statusKey: 'divergeRunStatus', errorKey: 'divergeRunErrorMessage' }
      const node = nodesRef.current.find((n) => n.id === trackedNodeId)
      if (node) {
        const data = node.data as unknown as FlowNodeData
        const patch = { [keys.runIdKey]: run.run_id, [keys.statusKey]: run.status, [keys.errorKey]: run.error_message }
        const next = patchCanvasFieldsIfChanged((data.fields || {}) as Record<string, unknown>, patch)
        if (next) onChangeFields(trackedNodeId, next)
      }
      if (run.status === 'SUCCESS' && run.scripts && run.scripts.length) {
        // Backend already classifies MODEL_REFUSAL vs parse failure. Only
        // defend against accidental SUCCESS with title-level refusal and
        // no usable scenes — never map that to "missing user input".
        const isTitleRefusal = (s: { title?: string; scenes?: unknown[] }) => {
          const title = String(s.title || '').trim().toLowerCase()
          const hasScenes = Array.isArray(s.scenes) && s.scenes.length > 0
          if (hasScenes) return false
          return (
            title.startsWith('无法生成') ||
            title.startsWith('cannot generate') ||
            title.includes('缺少必要输入')
          )
        }
        if (run.scripts.some(isTitleRefusal)) {
          const cur = nodesRef.current.find((n) => n.id === trackedNodeId)
          if (cur) {
            const curFields = ((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
            const next = patchCanvasFieldsIfChanged(curFields, {
              [keys.statusKey]: 'ERROR',
              [keys.errorKey]: '模型未能完成此请求，请重试或切换 Provider',
            })
            if (next) onChangeFields(trackedNodeId, next)
          }
          message.error('模型未能完成此请求，请重试或切换 Provider')
          clearScriptPoll(run.run_id)
          return
        }
        if (mode === 'GENERATE') {
          const cur = nodesRef.current.find((n) => n.id === trackedNodeId)
          if (cur) {
            const curData = cur.data as unknown as FlowNodeData
            const curFields = (curData.fields || {}) as Record<string, unknown>
            const nextFields = applyGeneratedScript(curFields, run.scripts[0], {
              runId: run.run_id,
              skillKey: run.skill_key,
              skillVersion: run.skill_version,
              providerOptionId: run.resolved_provider_option_id,
            })
            onChangeFields(trackedNodeId, nextFields)
          }
        } else {
          applyScriptDivergenceGroupResult(trackedNodeId, run)
        }
        clearScriptPoll(run.run_id)
      } else if (run.status === 'ERROR') {
        const code = String(run.error_code || '').toUpperCase()
        const tenantMsg =
          code === 'MODEL_REFUSAL'
            ? '模型未能完成此请求，请重试或切换 Provider'
            : code === 'STRUCTURED_PARSE_FAILED' || code === 'STRUCTURED_RESULT_INVALID'
              ? '模型返回结果无法解析'
              : code === 'EMPTY_MODEL_OUTPUT' || code === 'OUTPUT_MISSING'
                ? '模型未返回可用剧本结果'
                : code === 'PROVIDER_EXECUTION_FAILED' || code === 'GENERATION_FAILED'
                  ? '当前 Provider 暂时无法完成生成'
                  : run.error_message || '剧本生成失败'
        if (mode === 'DIVERGE') {
          const cur = nodesRef.current.find((n) => n.id === trackedNodeId)
          const groupId = cur
            ? ((((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>).divergenceGroupId as string | undefined)
            : undefined
          applyScriptDivergenceGroupError(trackedNodeId, groupId, tenantMsg)
        } else {
          const cur = nodesRef.current.find((n) => n.id === trackedNodeId)
          if (cur) {
            const curFields = ((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
            const next = patchCanvasFieldsIfChanged(curFields, {
              [keys.statusKey]: 'ERROR',
              [keys.errorKey]: tenantMsg,
            })
            if (next) onChangeFields(trackedNodeId, next)
          }
          message.error(tenantMsg)
        }
        clearScriptPoll(run.run_id)
      }
    },
    [applyScriptDivergenceGroupError, applyScriptDivergenceGroupResult, clearScriptPoll, onChangeFields],
  )

  const pollScriptRun = useCallback(
    (trackedNodeId: string, runId: string, mode: 'GENERATE' | 'DIVERGE') => {
      clearScriptPoll(runId)
      const tick = async () => {
        try {
          const run = await getScriptGenerationRun(workspaceId, runId)
          applyScriptRunToNode(trackedNodeId, run, mode)
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            const timer = window.setTimeout(tick, 2500)
            scriptPollTimersRef.current.set(runId, timer)
          }
        } catch {
          const timer = window.setTimeout(tick, 5000)
          scriptPollTimersRef.current.set(runId, timer)
        }
      }
      void tick()
    },
    [applyScriptRunToNode, clearScriptPoll, workspaceId],
  )

  // spec §3/§4/§6/§8 — a Creative Idea node's own current result (either
  // the LEGACY `creative_candidate` node's own fields, or the unified
  // `generate_creative` node's `fields.currentResult`) was dropped onto a
  // `script_generation` node's drop target. Builds the canonical
  // `ScriptCreativeContextField` snapshot (title/premise/hook/tone/
  // storyDirection/.../tags/rationale straight off the LIVE dragged node,
  // plus its own Structured Context Assets for Supporting Asset
  // inheritance — never a duplicate of the asset entity, spec §6), then
  // creates/replaces the ONE canonical `DERIVED_FROM` provenance edge to
  // that source node (spec §8 "不要仅仅把 source_node_id 写进前端
  // node.data").
  const stripConflictingScriptSourceEdges = useCallback((eds: Edge[], scriptNodeId: string) => {
    return eds.filter((e) => {
      if (e.target !== scriptNodeId) return true
      const relation = (e.data as { relationType?: string } | undefined)?.relationType
      if (relation === CREATIVE_CONTEXT_EDGE_RELATION || relation === SCRIPT_SOURCE_EDGE_RELATION) return false
      if (relation === 'INPUT_TO') {
        const srcType = nodeTypeOf(nodesRef.current.find((n) => n.id === e.source))
        if (srcType === 'generate_creative' || srcType === 'creative_candidate' || srcType === 'text_asset') return false
      }
      return true
    })
  }, [])

  const onLinkCreativeContext = useCallback(
    (scriptNodeId: string, payload: CreativeContextDragPayload, sourceKind: ScriptSourceKind = 'generate_creative') => {
      const scriptNode = nodesRef.current.find((n) => n.id === scriptNodeId)
      if (!scriptNode) return
      const scriptFields = ((scriptNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>

      const briefNode = payload.assetsSourceNodeId ? nodesRef.current.find((n) => n.id === payload.assetsSourceNodeId) : null
      const briefFields = briefNode ? ((briefNode.data as unknown as FlowNodeData).fields || {}) : {}
      const briefAssets = Array.isArray((briefFields as Record<string, unknown>).assets)
        ? ((briefFields as Record<string, unknown>).assets as Array<Record<string, unknown>>)
        : []

      const { scriptSource, creativeContext: contextField } = buildScriptSourceFromCreative(payload, briefAssets, sourceKind)

      const existingAssets = Array.isArray(scriptFields.supportingAssets)
        ? (scriptFields.supportingAssets as ScriptSupportingAssetField[])
        : []
      const previousSource =
        (scriptFields.scriptSourceContext as { sourceNodeId?: string } | null)?.sourceNodeId ??
        (scriptFields.creativeContext as ScriptCreativeContextField | null | undefined)?.sourceNodeId ??
        null
      const mergedAssets = mergeSupportingAssetsOnScriptSourceLink(
        existingAssets,
        contextField.linkedAssetRefs,
        previousSource,
      )

      const patch = patchCanvasFieldsIfChanged(scriptFields, patchScriptSourceFields(scriptFields, scriptSource, mergedAssets))
      if (patch) onChangeFields(scriptNodeId, patch)

      commitEdges((eds) => {
        const withoutOldContext = stripConflictingScriptSourceEdges(eds, scriptNodeId)
        const next = [
          ...withoutOldContext,
          {
            id: genId(),
            ...buildScriptSourceEdgeFields({ sourceNodeId: payload.sourceNodeId, targetNodeId: scriptNodeId }),
          } as Edge,
        ]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, onChangeFields, scheduleFromState, stripConflictingScriptSourceEdges],
  )
  onLinkCreativeContextRef.current = onLinkCreativeContext

  const onLinkTextScriptSource = useCallback(
    (scriptNodeId: string, sourceNodeId: string, scriptSource: import('./scriptSourceContext').ScriptSourceContext) => {
      const scriptNode = nodesRef.current.find((n) => n.id === scriptNodeId)
      if (!scriptNode) return
      const scriptFields = ((scriptNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const previousSource =
        (scriptFields.scriptSourceContext as { sourceNodeId?: string } | null)?.sourceNodeId ??
        (scriptFields.creativeContext as ScriptCreativeContextField | null | undefined)?.sourceNodeId ??
        null
      const existingAssets = Array.isArray(scriptFields.supportingAssets)
        ? (scriptFields.supportingAssets as ScriptSupportingAssetField[])
        : []
      const mergedAssets = mergeSupportingAssetsOnScriptSourceLink(existingAssets, [], previousSource)
      const patch = patchCanvasFieldsIfChanged(scriptFields, patchScriptSourceFields(scriptFields, scriptSource, mergedAssets))
      if (patch) onChangeFields(scriptNodeId, patch)
      commitEdges((eds) => {
        const withoutOld = stripConflictingScriptSourceEdges(eds, scriptNodeId)
        const next = [
          ...withoutOld,
          {
            id: genId(),
            ...buildScriptSourceEdgeFields({
              sourceNodeId,
              targetNodeId: scriptNodeId,
              sourceHandle: 'text-out',
            }),
          } as Edge,
        ]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, onChangeFields, scheduleFromState, stripConflictingScriptSourceEdges],
  )
  onLinkTextScriptSourceRef.current = onLinkTextScriptSource

  // VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
  // TRIPLET_AND_SINGLE_EXPANSION_V3 §5/§6/§35/§36 — Result Area hover
  // Quick Action "生成剧本": creates a brand-new `script_generation`
  // node beside the source and links it through the EXACT SAME
  // `onLinkCreativeContext` + `CreativeContextDragPayload` contract the
  // drag-and-drop entry point already uses
  // (`DRAG_TO_SCRIPT_CONTEXT_CONTRACT = QUICK_ACTION_TO_SCRIPT_CONTEXT_
  // CONTRACT`, spec §36) — never a second, parallel handoff shape. Does
  // NOT touch `expandedNodeId` for the SOURCE node (spec §35 "不能先强
  // 制展开当前 Creative Card"); the new downstream node becomes the
  // focused/expanded one, same convention `onCreateVoiceTemplateNode`
  // already established for other hover-created downstream nodes.
  const onQuickCreateScriptFromCreative = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const payload = buildCreativePayloadFromNode(source)
      // Same readiness SoT as Generate — candidateId alone is not enough.
      if (!payload || !isCreativePayloadReadyForScriptLink(payload)) return

      const briefNode = payload.assetsSourceNodeId
        ? nodesRef.current.find((n) => n.id === payload.assetsSourceNodeId)
        : source
      const briefFields = briefNode ? ((briefNode.data as unknown as FlowNodeData).fields || {}) : {}
      const briefAssets = Array.isArray((briefFields as Record<string, unknown>).assets)
        ? ((briefFields as Record<string, unknown>).assets as Array<Record<string, unknown>>)
        : []
      const sourceKind: ScriptSourceKind = nodeTypeOf(source) === 'creative_candidate' ? 'creative_candidate' : 'generate_creative'
      const { scriptSource, creativeContext: contextField } = buildScriptSourceFromCreative(payload, briefAssets, sourceKind)

      const def = getNodeDefinition('script_generation')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'script_generation',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: patchScriptSourceFields(
            { ...def.defaultFields },
            scriptSource,
            contextField.linkedAssetRefs,
          ),
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = [
          ...eds,
          {
            id: genId(),
            ...buildScriptSourceEdgeFields({ sourceNodeId: payload.sourceNodeId, targetNodeId: newId }),
          } as Edge,
        ]
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onQuickCreateScriptFromTextAsset = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const fields = ((source.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const text = String(fields.text ?? '')
      if (!isTextAssetPayloadReadyForScriptLink(text)) return
      const scriptSource = buildScriptSourceFromTextAsset(source)
      if (!scriptSource) return

      const def = getNodeDefinition('script_generation')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'script_generation',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: patchScriptSourceFields({ ...def.defaultFields }, scriptSource, []),
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = [
          ...eds,
          {
            id: genId(),
            ...buildScriptSourceEdgeFields({
              sourceNodeId,
              targetNodeId: newId,
              sourceHandle: 'text-out',
            }),
          } as Edge,
        ]
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const textAssetQuickActionLockRef = useRef(false)

  const runTextAssetQuickAction = useCallback((run: () => void) => {
    if (textAssetQuickActionLockRef.current) return
    textAssetQuickActionLockRef.current = true
    try {
      run()
    } finally {
      window.setTimeout(() => {
        textAssetQuickActionLockRef.current = false
      }, 400)
    }
  }, [])

  const onQuickCreateGenerateTextFromTextAsset = useCallback(
    (sourceNodeId: string) => {
      runTextAssetQuickAction(() => {
        const source = nodesRef.current.find((n) => n.id === sourceNodeId)
        if (!source) return
        const text = String(((source.data as unknown as FlowNodeData).fields?.text as string | undefined) ?? '').trim()
        if (!text) return
        const def = getNodeDefinition('generate_text')
        const newId = genId()
        const newNode: Node = {
          id: newId,
          type: def.family,
          position: placeNodeBesideSource(source),
          data: {
            nodeType: 'generate_text',
            category: def.category,
            entityType: null,
            entityId: null,
            collapsed: false,
            // VistoeraTASK-00583 — do NOT mint a duplicate Workspace text
            // asset here. The REFERENCES edge below is the SoT; CanvasBoard
            // derives a TEXT InputMaterial from it and GenerateTextNode
            // syncs that into inputAssets / submit context.
            fields: buildGenerateTextNodeDefaultFields({
              locale: i18n.language,
              sourceNodeType: 'text_asset',
              inputAssets: [],
            }),
            latestRunId: null,
          } as unknown as Record<string, unknown>,
        }
        if (source.parentId) {
          newNode.parentId = source.parentId
          newNode.extent = 'parent'
        }
        commitNodes((nds) => [...nds, newNode])
        commitEdges((eds) => {
          const next = addEdge(
            {
              id: genId(),
              source: sourceNodeId,
              sourceHandle: 'text-out',
              target: newId,
              targetHandle: GENERIC_INPUT_HANDLE,
              data: { relationType: 'REFERENCES' },
              className: 'pcv-edge',
            },
            eds,
          )
          scheduleFromState(nodesRef.current, next)
          return next
        })
        setSelectedNodeId(newId)
        setExpandedNodeId(newId)
      })
    },
    [commitEdges, commitNodes, i18n.language, runTextAssetQuickAction, scheduleFromState],
  )

  const onQuickCreateGenerateImageFromTextAsset = useCallback(
    (sourceNodeId: string) => {
      runTextAssetQuickAction(() => {
        const source = nodesRef.current.find((n) => n.id === sourceNodeId)
        if (!source) return
        const text = String(((source.data as unknown as FlowNodeData).fields?.text as string | undefined) ?? '').trim()
        if (!text) return
        const def = getNodeDefinition('generate_image')
        const newId = genId()
        const newNode: Node = {
          id: newId,
          type: def.family,
          position: placeNodeBesideSource(source),
          data: {
            nodeType: 'generate_image',
            category: def.category,
            entityType: null,
            entityId: null,
            collapsed: false,
            fields: {
              ...buildGenerateImageNodeDefaultFields(workspaceId, imageGenerationConfigRef.current),
              prompt: text,
            },
            latestRunId: null,
          } as unknown as Record<string, unknown>,
        }
        if (source.parentId) {
          newNode.parentId = source.parentId
          newNode.extent = 'parent'
        }
        commitNodes((nds) => [...nds, newNode])
        commitEdges((eds) => {
          const next = addEdge(
            {
              id: genId(),
              source: sourceNodeId,
              sourceHandle: 'text-out',
              target: newId,
              targetHandle: IMAGE_PROMPT_IN_HANDLE,
              data: { relationType: 'INPUT_TO' },
              className: 'pcv-edge',
            },
            eds,
          )
          scheduleFromState(nodesRef.current, next)
          return next
        })
        setSelectedNodeId(newId)
        setExpandedNodeId(newId)
      })
    },
    [commitEdges, commitNodes, runTextAssetQuickAction, scheduleFromState, workspaceId],
  )

  const onQuickCreateGenerateVideoFromTextAsset = useCallback(
    (sourceNodeId: string) => {
      runTextAssetQuickAction(() => {
        const source = nodesRef.current.find((n) => n.id === sourceNodeId)
        if (!source) return
        const text = String(((source.data as unknown as FlowNodeData).fields?.text as string | undefined) ?? '').trim()
        if (!text) return
        const def = getNodeDefinition('generate_video')
        const newId = genId()
        const newNode: Node = {
          id: newId,
          type: def.family,
          position: placeNodeBesideSource(source),
          data: {
            nodeType: 'generate_video',
            category: def.category,
            entityType: null,
            entityId: null,
            collapsed: false,
            fields: {
              ...buildGenerateVideoNodeDefaultFields(),
              promptText: text,
              effectivePromptSource: 'ORIGINAL',
            },
            latestRunId: null,
          } as unknown as Record<string, unknown>,
        }
        if (source.parentId) {
          newNode.parentId = source.parentId
          newNode.extent = 'parent'
        }
        commitNodes((nds) => [...nds, newNode])
        commitEdges((eds) => {
          const next = commitVideoInputConnection(
            eds,
            {
              source: sourceNodeId,
              target: newId,
              sourceHandle: 'text-out',
              targetHandle: VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
            },
            'generate_video',
            (connection) => ({
              id: genId(),
              ...connection,
              data: { relationType: 'INPUT_TO' },
              className: 'pcv-edge',
            }),
          )
          scheduleFromState(nodesRef.current, next)
          return next
        })
        setSelectedNodeId(newId)
        setExpandedNodeId(newId)
      })
    },
    [commitEdges, commitNodes, runTextAssetQuickAction, scheduleFromState],
  )

  // "生成剧本" (spec §12) — writes the result INTO `nodeId` itself,
  // pushing any existing result into ITS OWN Generation History first
  // (`applyGeneratedScript`). Idempotency mirrors `onGenerateCreativeIdeation`
  // exactly (client-side active-status guard + server-side
  // `idempotency_key`/`_get_active_run` double guard).
  const onGenerateScript = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      if (ACTIVE_RUN_STATUSES_SCRIPT.has((fields.runStatus as string) || '')) return
      const inputReady = evaluateScriptGenerationInputReadiness(fields)
      if (!inputReady.ready) {
        message.error('请先连接或选择一个创意来源。')
        return
      }
      const scriptSource = resolveScriptSourceContext(fields)
      const creativeContext = fields.creativeContext as ScriptCreativeContextField
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'
      const patch = patchCanvasFieldsIfChanged(fields, { runStatus: 'SUBMITTING', runErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      generateScriptGeneration(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        input: buildScriptInputPayload(fields),
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-${Date.now()}`,
        source_creative_node_id: scriptSource?.sourceNodeId ?? creativeContext?.sourceNodeId,
        source_creative_run_id: scriptSource?.sourceRunId ?? creativeContext?.sourceRunId ?? null,
        source_creative_candidate_id: scriptSource?.candidateId ?? creativeContext?.candidateId,
        source_creative_fingerprint: scriptSource?.fingerprint ?? creativeContext?.fingerprint,
      })
        .then((run) => {
          applyScriptRunToNode(nodeId, run, 'GENERATE')
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            pollScriptRun(nodeId, run.run_id, 'GENERATE')
          }
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '剧本生成失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { runStatus: 'ERROR', runErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [applyScriptRunToNode, onChangeFields, pollScriptRun, projectId, workspaceId],
  )

  // Canonical Generation Card diverge — TOTAL 3 = CURRENT node (A,
  // siblingIndex=0) + exactly 2 NEW placeholders (B/C). One AI run
  // returns 3 scripts; placeholders appear after dispatch succeeds.
  // No visible A→B / A→C sibling edges (only DERIVED_FROM to Creative).
  const onDivergeScript = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      if (
        ACTIVE_RUN_STATUSES_SCRIPT.has((fields.divergeRunStatus as string) || '') ||
        ACTIVE_RUN_STATUSES_SCRIPT.has((fields.runStatus as string) || '')
      ) {
        return
      }
      const inputReady = evaluateScriptGenerationInputReadiness(fields)
      if (!inputReady.ready) {
        message.error('请先连接或选择一个创意来源。')
        return
      }
      const creativeContext = fields.creativeContext as ScriptCreativeContextField
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'

      const divergenceGroupId = genId()
      const siblingBId = genId()
      const siblingCId = genId()

      const patch = patchCanvasFieldsIfChanged(fields, { divergeRunStatus: 'SUBMITTING', divergeRunErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      divergeScriptGeneration(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        input: buildScriptInputPayload(fields),
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-diverge-${Date.now()}`,
        parent_run_id: (fields.lastRunId as string | null) ?? null,
        source_creative_node_id: creativeContext.sourceNodeId,
        source_creative_run_id: creativeContext.sourceRunId,
        source_creative_candidate_id: creativeContext.candidateId,
        source_creative_fingerprint: creativeContext.fingerprint,
      })
        .then((run) => {
          const def = getNodeDefinition('script_generation')
          const origin = nodesRef.current.find((n) => n.id === nodeId)
          const positions = origin
            ? placeSiblingNodesBesideSource(origin, 2, {})
            : [
                { x: node.position.x + 520, y: node.position.y },
                { x: node.position.x + 520, y: node.position.y + 280 },
              ]
          const originFieldsNow = origin
            ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)
            : fields

          const newSiblingNodes: Node[] = [
            { id: siblingBId, idx: 1 as const },
            { id: siblingCId, idx: 2 as const },
          ].map(({ id: sid, idx }, arrIdx) => ({
            id: sid,
            type: def.family,
            position: positions[arrIdx],
            data: {
              nodeType: 'script_generation',
              category: def.category,
              entityType: null,
              entityId: null,
              collapsed: false,
              latestRunId: null,
              fields: buildScriptDivergePlaceholderFields(originFieldsNow, {
                divergenceGroupId,
                siblingIndex: idx,
                originNodeId: nodeId,
                runId: run.run_id,
              }),
            } as unknown as Record<string, unknown>,
          }))
          commitNodes((nds) => [...nds, ...newSiblingNodes])

          // Replicate DERIVED_FROM creative-context edges to B/C — NO sibling edges.
          const sourceEdge = edgesRef.current.find(
            (e) =>
              e.target === nodeId && (e.data as { relationType?: string } | undefined)?.relationType === CREATIVE_CONTEXT_EDGE_RELATION,
          )
          if (sourceEdge) {
            commitEdges((eds) => {
              const newEdges: Edge[] = newSiblingNodes.map((n) => ({
                id: genId(),
                source: sourceEdge.source,
                target: n.id,
                data: { relationType: CREATIVE_CONTEXT_EDGE_RELATION },
                className: 'pcv-edge',
              }))
              const next = [...eds, ...newEdges]
              scheduleFromState(nodesRef.current, next)
              return next
            })
          }

          const applyAndTrack = (r: ScriptGenerationRunState) => {
            const cur = nodesRef.current.find((n) => n.id === nodeId)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeDivergeRunId: r.run_id,
                divergeRunStatus: r.status,
                divergeRunErrorMessage: r.error_message,
                divergenceGroupId,
                divergenceSiblingIndex: 0,
                divergenceOriginNodeId: nodeId,
              })
              if (patched) onChangeFields(nodeId, patched)
            }
            if (r.status === 'SUCCESS') {
              applyScriptDivergenceGroupResult(nodeId, r)
              clearScriptPoll(r.run_id)
            } else if (r.status === 'ERROR') {
              applyScriptDivergenceGroupError(nodeId, divergenceGroupId, r.error_message || '受控发散失败')
              clearScriptPoll(r.run_id)
            }
          }

          applyAndTrack(run)
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            clearScriptPoll(run.run_id)
            const tick = async () => {
              try {
                const next = await getScriptGenerationRun(workspaceId, run.run_id)
                applyAndTrack(next)
                if (next.status === 'QUEUED' || next.status === 'GENERATING' || next.status === 'SUBMITTING') {
                  const timer = window.setTimeout(tick, 2500)
                  scriptPollTimersRef.current.set(run.run_id, timer)
                }
              } catch {
                const timer = window.setTimeout(tick, 5000)
                scriptPollTimersRef.current.set(run.run_id, timer)
              }
            }
            void tick()
          }

          // Keep A expanded so the user sees the origin generating; B/C stay collapsed.
          setExpandedNodeId(nodeId)
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '受控发散失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { divergeRunStatus: 'ERROR', divergeRunErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [
      applyScriptDivergenceGroupError,
      applyScriptDivergenceGroupResult,
      clearScriptPoll,
      commitEdges,
      commitNodes,
      onChangeFields,
      projectId,
      scheduleFromState,
      workspaceId,
    ],
  )

  // Resume polling any run left QUEUED/GENERATING/SUBMITTING across a
  // refresh — mirrors the Creative Ideation hydration effect above.
  const scriptHydratedRef = useRef(false)
  useEffect(() => {
    if (scriptHydratedRef.current) return
    if (canvasHydrating) return
    scriptHydratedRef.current = true
    for (const n of nodesRef.current) {
      const data = n.data as unknown as FlowNodeData
      if (data.nodeType !== 'script_generation') continue
      const fields = (data.fields || {}) as Record<string, unknown>
      if (ACTIVE_RUN_STATUSES_SCRIPT.has((fields.runStatus as string) || '') && fields.activeRunId) {
        pollScriptRun(n.id, fields.activeRunId as string, 'GENERATE')
      }
      if (ACTIVE_RUN_STATUSES_SCRIPT.has((fields.divergeRunStatus as string) || '') && fields.activeDivergeRunId) {
        pollScriptRun(n.id, fields.activeDivergeRunId as string, 'DIVERGE')
      }
    }
  }, [canvasHydrating, pollScriptRun])

  // -------------------------------------------------------------------
  // VISTOERA_CANVAS_STORYBOARD_GENERATION_NODE_SCRIPT_CONTEXT_AND_SHOT_
  // ARTIFACT_V1 — Generate writes in-place; Diverge uses Canonical V3
  // placeholder siblings (B+C created after dispatch succeeds, like
  // `onDivergeCreative`), then applies storyboard[0]/[1]/[2] on SUCCESS.
  // -------------------------------------------------------------------
  const materializedStoryboardDivergeRunIdsRef = useRef<Set<string>>(new Set())

  useEffect(
    () => () => {
      storyboardPollTimersRef.current.forEach((timer) => window.clearTimeout(timer))
      storyboardPollTimersRef.current.clear()
    },
    [],
  )

  const clearStoryboardPoll = useCallback((runId: string) => {
    const timer = storyboardPollTimersRef.current.get(runId)
    if (timer != null) {
      window.clearTimeout(timer)
      storyboardPollTimersRef.current.delete(runId)
    }
  }, [])

  const applyStoryboardDivergenceGroupResult = useCallback(
    (originNodeId: string, run: StoryboardGenerationRunState) => {
      if (materializedStoryboardDivergeRunIdsRef.current.has(run.run_id)) return
      materializedStoryboardDivergeRunIdsRef.current.add(run.run_id)
      const storyboards = run.storyboards || []
      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      const originFields = origin ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>) : {}
      const groupId = originFields.divergenceGroupId as string | undefined
      const runMetaBase = {
        runId: run.run_id,
        skillKey: run.skill_key,
        skillVersion: run.skill_version,
        providerOptionId: run.resolved_provider_option_id,
      }

      const siblings = groupId
        ? nodesRef.current.filter(
            (n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId,
          )
        : []
      const siblingAt = (idx: number) =>
        siblings.find((n) => ((n.data as unknown as FlowNodeData).fields || {}).divergenceSiblingIndex === idx) || null

      const applyToOrigin = () => {
        const cur = nodesRef.current.find((n) => n.id === originNodeId)
        if (!cur) return
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const sb = storyboards[0]
        if (sb) {
          const next = {
            ...applyGeneratedStoryboard(curFields as Record<string, unknown>, sb, runMetaBase),
            activeDivergeRunId: null,
            divergeRunStatus: null,
            divergeRunErrorMessage: null,
          }
          onChangeFields(originNodeId, next)
        } else {
          const next = patchCanvasFieldsIfChanged(curFields, {
            activeDivergeRunId: null,
            divergeRunStatus: 'ERROR',
            divergeRunErrorMessage: '该方向未返回有效结果，可重新发散或单独重新生成',
          })
          if (next) onChangeFields(originNodeId, next)
        }
      }

      const applyToSibling = (targetId: string | null, idx: number) => {
        if (!targetId) return
        const cur = nodesRef.current.find((n) => n.id === targetId)
        if (!cur) return
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const sb = storyboards[idx]
        if (sb) {
          const next = buildStoryboardDivergeSiblingResultFields(originFields, sb, {
            ...runMetaBase,
            divergenceGroupId: (groupId as string) || '',
            siblingIndex: idx,
            originNodeId,
          })
          onChangeFields(targetId, next)
        } else {
          const next = patchCanvasFieldsIfChanged(curFields, {
            activeRunId: null,
            runStatus: 'ERROR',
            runErrorMessage: '该方向未返回有效结果，可重新发散或单独重新生成',
          })
          if (next) onChangeFields(targetId, next)
        }
      }

      applyToOrigin()
      applyToSibling(siblingAt(1)?.id ?? null, 1)
      applyToSibling(siblingAt(2)?.id ?? null, 2)
    },
    [onChangeFields],
  )

  const applyStoryboardDivergenceGroupError = useCallback(
    (originNodeId: string, groupId: string | undefined, message_: string) => {
      const cur = nodesRef.current.find((n) => n.id === originNodeId)
      if (cur) {
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(curFields, {
          activeDivergeRunId: null,
          divergeRunStatus: 'ERROR',
          divergeRunErrorMessage: message_,
        })
        if (next) onChangeFields(originNodeId, next)
      }
      if (!groupId) return
      const siblings = nodesRef.current.filter(
        (n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId,
      )
      for (const sib of siblings) {
        const sibFields = (sib.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(sibFields, { activeRunId: null, runStatus: 'ERROR', runErrorMessage: message_ })
        if (next) onChangeFields(sib.id, next)
      }
    },
    [onChangeFields],
  )

  const removeStoryboardOrchestratorShotEdges = useCallback(
    (orchestratorNodeId: string) => {
      commitEdges((eds) => {
        const next = eds.filter((e) => {
          if (e.source !== orchestratorNodeId) return true
          const shotId = (e.data as { shotId?: string } | undefined)?.shotId
          return !shotId
        })
        if (next.length === eds.length) return eds
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, scheduleFromState],
  )

  /** Option A detach or Option B remove — batch, no full canvas reload. */
  const applyStoryboardRegenerateCanvasCleanup = useCallback(
    (
      orchestratorNodeId: string,
      mode: StoryboardRegenerateMode,
      bindingsBeforeClear: ShotBindingMap,
      supersededSnapshot: ReturnType<typeof readSupersededStoryboardSnapshot>,
    ) => {
      const superseded = supersededSnapshot
      const storyboardVersion = superseded?.version ?? 1
      const storyboardArtifactId = superseded?.storyboardArtifactId ?? null
      const shotIndexById = new Map<string, number>()
      for (const shot of superseded?.storyboard?.shots || []) {
        shotIndexById.set(shot.shot_id, shot.shot_index)
      }

      const derivedNodeIds = collectStoryboardDerivedCanvasNodeIds(
        orchestratorNodeId,
        bindingsBeforeClear,
        nodesRef.current,
      )
      if (mode === 'remove_canvas_nodes') {
        if (derivedNodeIds.length) performDeleteNodes(derivedNodeIds)
      } else {
        const videoNodeIds = derivedNodeIds.filter((nodeId) => {
          const target = nodesRef.current.find((n) => n.id === nodeId)
          if (!target) return false
          return (target.data as unknown as FlowNodeData).nodeType === 'generate_video'
        })
        if (videoNodeIds.length) {
          commitNodes((nds) => {
            const next = nds.map((n) => {
              if (!videoNodeIds.includes(n.id)) return n
              const nodeData = n.data as unknown as FlowNodeData
              const curFields = (nodeData.fields || {}) as Record<string, unknown>
              const shotId = String(curFields.boundShotId || '')
              const patched = patchCanvasFieldsIfChanged(
                curFields,
                buildDetachedVideoNodeFieldsPatch(curFields, {
                  shotId,
                  storyboardVersion,
                  storyboardArtifactId,
                  shotIndex: shotIndexById.get(shotId) ?? null,
                }),
              )
              if (!patched) return n
              return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
            })
            scheduleFromState(next, edgesRef.current)
            return next
          })
        }
      }
      removeStoryboardOrchestratorShotEdges(orchestratorNodeId)
    },
    [commitNodes, performDeleteNodes, removeStoryboardOrchestratorShotEdges, scheduleFromState],
  )

  const applyStoryboardGenerateRunToNode = useCallback(
    (trackedNodeId: string, run: StoryboardGenerationRunState) => {
      const displayError =
        run.status === 'ERROR'
          ? localizeStoryboardGenerationError({ code: run.error_code, message: run.error_message }, t)
          : run.error_message
      if (run.status === 'ERROR') {
        logStoryboardGenerationErrorDev(run.error_code, run.error_message)
      }
      const node = nodesRef.current.find((n) => n.id === trackedNodeId)
      if (node) {
        const data = node.data as unknown as FlowNodeData
        const curFields = (data.fields || {}) as Record<string, unknown>
        const isRegenerate = Boolean(curFields.storyboardRegenerateActive || curFields.supersededStoryboardSnapshot)
        const patch = patchCanvasFieldsIfChanged(curFields, {
          activeRunId: run.run_id,
          runStatus: run.status,
          runErrorMessage: displayError,
          // Never re-hydrate old current result while a regenerate run is active.
          ...(isRegenerate && run.status !== 'SUCCESS' ? { storyboardResult: null } : {}),
        })
        if (patch) onChangeFields(trackedNodeId, patch)
      }
      if (run.status === 'SUCCESS' && run.storyboards && run.storyboards.length) {
        const cur = nodesRef.current.find((n) => n.id === trackedNodeId)
        if (cur) {
          const curFields = ((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
          const runMeta = {
            runId: run.run_id,
            skillKey: run.skill_key,
            skillVersion: run.skill_version,
            providerOptionId: run.resolved_provider_option_id,
          }
          const applied =
            curFields.storyboardRegenerateActive || curFields.supersededStoryboardSnapshot
              ? finalizeStoryboardRegenerateSuccess(curFields, run.storyboards[0], runMeta)
              : (() => {
                  const base = applyGeneratedStoryboard(curFields, run.storyboards[0], runMeta)
                  return base.runStatus === 'ERROR'
                    ? { ...base, activeRunId: null }
                    : {
                        ...base,
                        activeRunId: null,
                        runStatus: null,
                        runErrorMessage: null,
                        shotBindings: {},
                        finalVideoArtifact: null,
                        assemblyRunStatus: null,
                        assemblyErrorMessage: null,
                        activeAssemblyId: null,
                      }
                })()
          onChangeFields(trackedNodeId, applied)
          const appliedRun = readStoryboardRunFields(applied)
          if (appliedRun.runStatus !== 'ERROR') {
            removeStoryboardOrchestratorShotEdges(trackedNodeId)
          }
          if (appliedRun.runStatus === 'ERROR') {
            message.error(String(appliedRun.runErrorMessage || t('storyboardErrors.generationFailed')))
          }
        }
        clearStoryboardPoll(run.run_id)
      } else if (run.status === 'ERROR') {
        message.error(displayError || t('storyboardErrors.generationFailed'))
        clearStoryboardPoll(run.run_id)
      }
    },
    [clearStoryboardPoll, onChangeFields, removeStoryboardOrchestratorShotEdges, t],
  )

  const pollStoryboardGenerateRun = useCallback(
    (trackedNodeId: string, runId: string) => {
      clearStoryboardPoll(runId)
      const tick = async () => {
        try {
          const run = await getStoryboardGenerationRun(workspaceId, runId)
          applyStoryboardGenerateRunToNode(trackedNodeId, run)
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            const timer = window.setTimeout(tick, 2500)
            storyboardPollTimersRef.current.set(runId, timer)
          }
        } catch {
          const timer = window.setTimeout(tick, 5000)
          storyboardPollTimersRef.current.set(runId, timer)
        }
      }
      void tick()
    },
    [applyStoryboardGenerateRunToNode, clearStoryboardPoll, workspaceId],
  )

  const onLinkScriptContext = useCallback(
    (storyboardNodeId: string, payload: ScriptContextDragPayload) => {
      const storyboardNode = nodesRef.current.find((n) => n.id === storyboardNodeId)
      if (!storyboardNode) return
      const storyboardFields = ((storyboardNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>

      const assetsNode = payload.assetsSourceNodeId ? nodesRef.current.find((n) => n.id === payload.assetsSourceNodeId) : null
      const assetsFields = assetsNode ? ((assetsNode.data as unknown as FlowNodeData).fields || {}) : {}
      const scriptSupporting = Array.isArray((assetsFields as Record<string, unknown>).supportingAssets)
        ? ((assetsFields as Record<string, unknown>).supportingAssets as Array<Record<string, unknown>>)
        : []

      const contextField: StoryboardScriptContextField = buildScriptContextField(payload, scriptSupporting)

      const existingAssets = Array.isArray(storyboardFields.supportingAssets)
        ? (storyboardFields.supportingAssets as Array<Record<string, unknown>>)
        : []
      const existingKeys = new Set(existingAssets.map((a) => (a.asset_id as string) || (a.display_name as string)))
      const mergedAssets = [
        ...existingAssets,
        ...contextField.linkedAssetRefs.filter((a) => !existingKeys.has(a.asset_id || a.display_name)),
      ]

      const patch = patchCanvasFieldsIfChanged(
        storyboardFields,
        reconcileStoryboardResultAfterContextChange({
          ...storyboardFields,
          scriptContext: contextField,
          supportingAssets: mergedAssets,
        }),
      )
      if (patch) onChangeFields(storyboardNodeId, patch)

      commitEdges((eds) => {
        const withoutOldContext = eds.filter(
          (e) =>
            !(e.target === storyboardNodeId && (e.data as { relationType?: string } | undefined)?.relationType === SCRIPT_CONTEXT_EDGE_RELATION),
        )
        const newEdge: Edge = {
          id: genId(),
          source: payload.sourceNodeId,
          target: storyboardNodeId,
          data: { relationType: SCRIPT_CONTEXT_EDGE_RELATION },
          className: 'pcv-edge',
        }
        const next = [...withoutOldContext, newEdge]
        scheduleFromState(nodesRef.current, next)
        return next
      })
    },
    [commitEdges, onChangeFields, scheduleFromState],
  )
  onLinkScriptContextRef.current = onLinkScriptContext

  const onQuickCreateStoryboardFromScript = useCallback(
    (sourceNodeId: string) => {
      const source = nodesRef.current.find((n) => n.id === sourceNodeId)
      if (!source) return
      const sourceFields = ((source.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const payload = buildScriptContextDragPayloadFromScriptNode(sourceNodeId, sourceFields)
      if (!payload) return

      const scriptSupporting = Array.isArray(sourceFields.supportingAssets)
        ? (sourceFields.supportingAssets as Array<Record<string, unknown>>)
        : []

      const def = getNodeDefinition('storyboard_generation')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(source, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'storyboard_generation',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          // Atomic create-with-binding: bake scriptContext into the new
          // node in the SAME commitNodes as node creation (fixes P0
          // EDGE EXISTS / SCRIPT CONTEXT MISSING race).
          fields: buildStoryboardFieldsWithScriptContext(
            { ...(def.defaultFields as Record<string, unknown>) },
            payload,
            scriptSupporting,
          ),
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      if (source.parentId) {
        newNode.parentId = source.parentId
        newNode.extent = 'parent'
      }
      commitNodes((nds) => [...nds, newNode])
      commitEdges((eds) => {
        const next = [
          ...eds,
          {
            id: genId(),
            source: payload.sourceNodeId,
            target: newId,
            data: { relationType: SCRIPT_CONTEXT_EDGE_RELATION },
            className: 'pcv-edge',
          } as Edge,
        ]
        scheduleFromState(nodesRef.current, next)
        return next
      })
      setSelectedNodeId(newId)
      setExpandedNodeId(newId)
    },
    [commitEdges, commitNodes, scheduleFromState],
  )

  const onRestoreSupersededStoryboard = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const fields = ((node.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const restored = restoreSupersededStoryboardFields(fields)
      if (!restored) return
      onChangeFields(nodeId, restored)
    },
    [onChangeFields],
  )

  const onGenerateStoryboard = useCallback(
    (nodeId: string, options?: { regenerateMode?: StoryboardRegenerateMode }) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      if (ACTIVE_RUN_STATUSES_STORYBOARD.has((fields.runStatus as string) || '')) return
      const scriptContext = fields.scriptContext as StoryboardScriptContextField | null
      if (!hasValidStructuredScriptContext(scriptContext) || !scriptContext) {
        message.error('请先拖入一个剧本结果作为分镜生成的剧本上下文')
        return
      }
      const productionFormat = readProductionFormat(fields)
      if (!isProductionFormatComplete(productionFormat)) {
        message.error('请先选择成片规格（长宽比与清晰度）')
        return
      }
      const isRegenerate = Boolean(fields.storyboardResult || fields.supersededStoryboardSnapshot)
      const bindingsBeforeClear = readVideoProductionShotBindings(fields)
      const hadVisibleStoryboard = Boolean(fields.storyboardResult)
      let fieldsForApi = fields
      if (isRegenerate) {
        const regeneratePatch = beginStoryboardRegenerateOrchestratorPatch(fields)
        if (regeneratePatch) {
          onChangeFields(nodeId, regeneratePatch)
          fieldsForApi = { ...fields, ...regeneratePatch }
          if (hadVisibleStoryboard) {
            applyStoryboardRegenerateCanvasCleanup(
              nodeId,
              options?.regenerateMode ?? 'keep_video_nodes',
              bindingsBeforeClear,
              readSupersededStoryboardSnapshot(regeneratePatch),
            )
          }
        }
      } else {
        const patch = patchCanvasFieldsIfChanged(fields, { runStatus: 'SUBMITTING', runErrorMessage: null })
        if (patch) onChangeFields(nodeId, patch)
        fieldsForApi = patch ? { ...fields, ...patch } : fields
      }

      const boundContext = scriptContext
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'

      generateStoryboardGeneration(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        input: buildStoryboardInputPayload(fieldsForApi),
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-${Date.now()}`,
        source_script_node_id: boundContext.sourceNodeId,
        source_script_run_id: boundContext.sourceRunId,
        source_script_artifact_id: boundContext.scriptArtifactId,
        source_script_version: boundContext.scriptVersion != null ? String(boundContext.scriptVersion) : null,
        source_script_fingerprint: boundContext.fingerprint,
      })
        .then((run) => {
          applyStoryboardGenerateRunToNode(nodeId, run)
          if (run.status === 'QUEUED' || run.status === 'GENERATING' || run.status === 'SUBMITTING') {
            pollStoryboardGenerateRun(nodeId, run.run_id)
          }
        })
        .catch((err) => {
          const apiErr = err as SaasApiError
          const display = localizeStoryboardGenerationError(
            { code: apiErr.code, message: apiErr.message },
            t,
          )
          logStoryboardGenerationErrorDev(apiErr.code, apiErr.message)
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { runStatus: 'ERROR', runErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [
      applyStoryboardGenerateRunToNode,
      applyStoryboardRegenerateCanvasCleanup,
      onChangeFields,
      pollStoryboardGenerateRun,
      projectId,
      t,
      workspaceId,
    ],
  )

  const onDivergeStoryboard = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      if (
        ACTIVE_RUN_STATUSES_STORYBOARD.has((fields.divergeRunStatus as string) || '') ||
        ACTIVE_RUN_STATUSES_STORYBOARD.has((fields.runStatus as string) || '')
      ) {
        return
      }
      const scriptContext = fields.scriptContext as StoryboardScriptContextField | null
      if (!hasValidStructuredScriptContext(scriptContext) || !scriptContext) {
        message.error('请先拖入一个剧本结果作为分镜生成的剧本上下文')
        return
      }
      const boundContext = scriptContext
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'

      const divergenceGroupId = genId()
      const siblingBId = genId()
      const siblingCId = genId()

      const patch = patchCanvasFieldsIfChanged(fields, { divergeRunStatus: 'SUBMITTING', divergeRunErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      divergeStoryboardGeneration(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        input: buildStoryboardInputPayload(fields),
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-diverge-${Date.now()}`,
        parent_run_id: (fields.lastRunId as string | null) ?? null,
        source_script_node_id: boundContext.sourceNodeId,
        source_script_run_id: boundContext.sourceRunId,
        source_script_artifact_id: boundContext.scriptArtifactId,
        source_script_version: boundContext.scriptVersion != null ? String(boundContext.scriptVersion) : null,
        source_script_fingerprint: boundContext.fingerprint,
      })
        .then((run) => {
          // PRE_DISPATCH_FAILURE: B/C only created HERE after run exists.
          const def = getNodeDefinition('storyboard_generation')
          const origin = nodesRef.current.find((n) => n.id === nodeId)
          const positions = origin
            ? placeSiblingNodesBesideSource(origin, 2, {})
            : [
                { x: node.position.x + 520, y: node.position.y },
                { x: node.position.x + 520, y: node.position.y + 280 },
              ]
          const originFieldsNow = origin
            ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>)
            : fields

          const newSiblingNodes: Node[] = [
            { id: siblingBId, idx: 1 as const },
            { id: siblingCId, idx: 2 as const },
          ].map(({ id: sid, idx }, arrIdx) => ({
            id: sid,
            type: def.family,
            position: positions[arrIdx],
            data: {
              nodeType: 'storyboard_generation',
              category: def.category,
              entityType: null,
              entityId: null,
              collapsed: false,
              latestRunId: null,
              fields: buildStoryboardDivergePlaceholderFields(originFieldsNow, {
                divergenceGroupId,
                siblingIndex: idx,
                originNodeId: nodeId,
                runId: run.run_id,
              }),
            } as unknown as Record<string, unknown>,
          }))
          commitNodes((nds) => [...nds, ...newSiblingNodes])

          // Replicate DERIVED_FROM script-context edges to B/C — NO GENERATED_FROM sibling edges.
          const sourceEdge = edgesRef.current.find(
            (e) =>
              e.target === nodeId && (e.data as { relationType?: string } | undefined)?.relationType === SCRIPT_CONTEXT_EDGE_RELATION,
          )
          if (sourceEdge) {
            commitEdges((eds) => {
              const newEdges: Edge[] = newSiblingNodes.map((n) => ({
                id: genId(),
                source: sourceEdge.source,
                target: n.id,
                data: { relationType: SCRIPT_CONTEXT_EDGE_RELATION },
                className: 'pcv-edge',
              }))
              const next = [...eds, ...newEdges]
              scheduleFromState(nodesRef.current, next)
              return next
            })
          }

          const applyAndTrack = (r: StoryboardGenerationRunState) => {
            const cur = nodesRef.current.find((n) => n.id === nodeId)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeDivergeRunId: r.run_id,
                divergeRunStatus: r.status,
                divergeRunErrorMessage: r.error_message,
                divergenceGroupId,
                divergenceSiblingIndex: 0,
                divergenceOriginNodeId: nodeId,
              })
              if (patched) onChangeFields(nodeId, patched)
            }
            if (r.status === 'SUCCESS') {
              applyStoryboardDivergenceGroupResult(nodeId, r)
              clearStoryboardPoll(r.run_id)
            } else if (r.status === 'ERROR') {
              applyStoryboardDivergenceGroupError(nodeId, divergenceGroupId, r.error_message || '受控发散失败')
              message.error(r.error_message || '受控发散失败')
              clearStoryboardPoll(r.run_id)
            } else if (r.status === 'QUEUED' || r.status === 'GENERATING' || r.status === 'SUBMITTING') {
              clearStoryboardPoll(r.run_id)
              const tick = async () => {
                try {
                  const latest = await getStoryboardGenerationRun(workspaceId, r.run_id)
                  applyAndTrack(latest)
                  if (latest.status === 'QUEUED' || latest.status === 'GENERATING' || latest.status === 'SUBMITTING') {
                    const timer = window.setTimeout(tick, 2500)
                    storyboardPollTimersRef.current.set(r.run_id, timer)
                  }
                } catch {
                  const timer = window.setTimeout(tick, 5000)
                  storyboardPollTimersRef.current.set(r.run_id, timer)
                }
              }
              const timer = window.setTimeout(tick, 2500)
              storyboardPollTimersRef.current.set(r.run_id, timer)
            }
          }
          applyAndTrack(run)
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '受控发散失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { divergeRunStatus: 'ERROR', divergeRunErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [
      applyStoryboardDivergenceGroupError,
      applyStoryboardDivergenceGroupResult,
      clearStoryboardPoll,
      commitEdges,
      commitNodes,
      onChangeFields,
      projectId,
      scheduleFromState,
      workspaceId,
    ],
  )

  const storyboardHydratedRef = useRef(false)
  useEffect(() => {
    if (storyboardHydratedRef.current) return
    if (canvasHydrating) return
    storyboardHydratedRef.current = true
    for (const n of nodesRef.current) {
      const data = n.data as unknown as FlowNodeData
      if (data.nodeType !== 'storyboard_generation') continue
      const fields = (data.fields || {}) as Record<string, unknown>
      const divergenceSiblingIndex = fields.divergenceSiblingIndex as number | null | undefined
      const divergenceOriginNodeId = fields.divergenceOriginNodeId as string | null | undefined
      if (ACTIVE_RUN_STATUSES_STORYBOARD.has((fields.runStatus as string) || '') && fields.activeRunId) {
        const runId = fields.activeRunId as string
        const isDivergenceSibling = Boolean(divergenceSiblingIndex) && Boolean(divergenceOriginNodeId)
        if (isDivergenceSibling) {
          clearStoryboardPoll(runId)
          const tick = async () => {
            try {
              const latest = await getStoryboardGenerationRun(workspaceId, runId)
              if (latest.status === 'SUCCESS') {
                applyStoryboardDivergenceGroupResult(divergenceOriginNodeId as string, latest)
                clearStoryboardPoll(runId)
              } else if (latest.status === 'ERROR') {
                applyStoryboardDivergenceGroupError(
                  divergenceOriginNodeId as string,
                  fields.divergenceGroupId as string | undefined,
                  latest.error_message || '受控发散失败',
                )
                clearStoryboardPoll(runId)
              } else {
                const timer = window.setTimeout(tick, 2500)
                storyboardPollTimersRef.current.set(runId, timer)
              }
            } catch {
              const timer = window.setTimeout(tick, 5000)
              storyboardPollTimersRef.current.set(runId, timer)
            }
          }
          void tick()
        } else {
          pollStoryboardGenerateRun(n.id, runId)
        }
      } else if (ACTIVE_RUN_STATUSES_STORYBOARD.has((fields.divergeRunStatus as string) || '') && fields.activeDivergeRunId) {
        const runId = fields.activeDivergeRunId as string
        clearStoryboardPoll(runId)
        const tick = async () => {
          try {
            const latest = await getStoryboardGenerationRun(workspaceId, runId)
            const cur = nodesRef.current.find((nn) => nn.id === n.id)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeDivergeRunId: latest.run_id,
                divergeRunStatus: latest.status,
                divergeRunErrorMessage: latest.error_message,
              })
              if (patched) onChangeFields(n.id, patched)
            }
            if (latest.status === 'SUCCESS') {
              applyStoryboardDivergenceGroupResult(n.id, latest)
              clearStoryboardPoll(runId)
            } else if (latest.status === 'ERROR') {
              applyStoryboardDivergenceGroupError(n.id, fields.divergenceGroupId as string | undefined, latest.error_message || '受控发散失败')
              clearStoryboardPoll(runId)
            } else {
              const timer = window.setTimeout(tick, 2500)
              storyboardPollTimersRef.current.set(runId, timer)
            }
          } catch {
            const timer = window.setTimeout(tick, 5000)
            storyboardPollTimersRef.current.set(runId, timer)
          }
        }
        void tick()
      }
    }
  }, [
    applyStoryboardDivergenceGroupError,
    applyStoryboardDivergenceGroupResult,
    canvasHydrating,
    clearStoryboardPoll,
    onChangeFields,
    pollStoryboardGenerateRun,
    workspaceId,
  ])

  // ------------------------------------------------------------------
  // VISTOERA_STORYBOARD_UNIFIED_VIDEO_ORCHESTRATION — shot/assembly
  // live on `storyboard_generation` only (standalone video_production removed).
  // ------------------------------------------------------------------

  /** ShotUnit[] per `storyboard_generation` node — live-derived from local
   * `storyboardResult` + `shotBindings` + CURRENT fields of every bound
   * `generate_video` node. Recomputed whenever `nodes` change. */
  const videoProductionUnitsByNodeId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof deriveShotUnits>>()
    for (const n of nodes) {
      const data = n.data as unknown as FlowNodeData
      if (data.nodeType !== 'storyboard_generation') continue
      const fields = (data.fields || {}) as Record<string, unknown>
      const local = fields.storyboardResult as StoryboardArtifact | null
      const storyboard = local && isValidStoryboardArtifact(local) ? local : null
      if (!storyboard) {
        map.set(n.id, [])
        continue
      }
      const bindings = (fields.shotBindings || {}) as ShotBindingMap
      const boundNodesById: Record<string, BoundVideoNodeSnapshot | undefined> = {}
      for (const shotId of Object.keys(bindings)) {
        const binding = bindings[shotId]
        if (!binding) continue
        const boundNode = nodes.find((nn) => nn.id === binding.advancedVideoNodeId)
        if (!boundNode) continue
        const bf = ((boundNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
        const generatedTaskId = (bf.generatedTaskId as string | undefined) ?? null
        boundNodesById[binding.advancedVideoNodeId] = {
          nodeId: binding.advancedVideoNodeId,
          status: (bf.status as string | undefined) ?? null,
          generatedTaskId,
          generatedVideoUrl: (bf.generatedVideoUrl as string | undefined) ?? null,
          posterUrl: generatedTaskId ? videoGenTaskPosterUrl(workspaceId, generatedTaskId) : null,
          outputDurationSeconds: null,
          generatedAgainstFormatVersion:
            typeof bf.generatedAgainstFormatVersion === 'number' ? bf.generatedAgainstFormatVersion : null,
          formatCompatibility: (bf.formatCompatibility as 'ok' | 'format_mismatch' | null | undefined) ?? null,
        }
      }
      const fmt = fields.productionFormat as { version?: number } | null
      const formatVersion = typeof fmt?.version === 'number' ? fmt.version : null
      map.set(n.id, deriveShotUnits(storyboard, bindings, boundNodesById, formatVersion))
    }
    return map
  }, [nodes, workspaceId])

  /** Storyboard → whether any bound Shot keyframe image currently has a valid output. */
  const storyboardKeyframeOutputByNodeId = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const n of nodes) {
      const data = n.data as unknown as FlowNodeData
      if (data.nodeType !== 'storyboard_generation') continue
      const fields = (data.fields || {}) as Record<string, unknown>
      const bindings = readVideoProductionShotBindings(fields)
      const generatorIds = new Set(
        Object.values(bindings)
          .map((b) => b?.advancedVideoNodeId)
          .filter((id): id is string => Boolean(id)),
      )
      for (const gn of nodes) {
        const gd = gn.data as unknown as FlowNodeData
        if (gd.nodeType !== 'generate_video') continue
        const gf = (gd.fields || {}) as Record<string, unknown>
        if (gf.boundOrchestratorNodeId === n.id || gf.boundVideoProductionNodeId === n.id) {
          generatorIds.add(gn.id)
        }
      }
      let has = false
      for (const img of nodes) {
        const idata = img.data as unknown as FlowNodeData
        if (idata.nodeType !== 'generate_image') continue
        const f = (idata.fields || {}) as Record<string, unknown>
        if (!f.generatedOutputId) continue
        if (f.formatCompatibility === 'format_mismatch') continue
        const candidate = f.candidateForVideoKeyframe as { videoNodeId?: string } | null | undefined
        const boundVideo = (f.boundVideoNodeId as string | undefined) || candidate?.videoNodeId
        const boundOrch = f.boundOrchestratorNodeId as string | undefined
        if (boundOrch === n.id || (boundVideo && generatorIds.has(boundVideo))) {
          has = true
          break
        }
      }
      map.set(n.id, has)
    }
    return map
  }, [nodes])

  /** Canonical Shot → Advanced Video Generation open/create/focus action
   * (VISTOERA_VIDEO_PRODUCTION_STORYBOARD_SHOT_BINDING_AND_SHOT_TO_
   * ADVANCED_VIDEO_GENERATION_FOCUS_P0_V1 §三/§十/§十一/§十五/§十六) — the
   * ONE function both the Shot #/thumb click AND the "..." → "高级编辑"
   * menu item call (two UI entry points, one canonical action per §十五
   * "不要存在两套不同的 node creation/binding 逻辑").
   *
   * CASE 1 (Shot already bound to a live `generate_video` node): select
   * + expand + focus (preserve zoom) that EXISTING node — never re-reads/overwrites
   * its fields, so any user edit (prompt/provider/skill/keyframes/
   * params) already made on it survives untouched (§十).
   *
   * CASE 2 (Shot not yet bound, or its binding is stale — the bound node
   * id was independently deleted): create exactly ONE new `generate_video`
   * node, fully hydrated from the REAL upstream `ShotArtifact` via the
   * SAME canonical `buildVideoContextFromShot` hydration
   * (`onCreateVideoFromShot`'s Storyboard-direct creation path already
   * used this — Video Production's OWN creation path had drifted to a
   * bare `promptText`/`durationSeconds` subset only; unified here, §五/
   * §六/§七/§八 — no LLM re-extraction, no duplicate field schema, direct
   * reuse of the canonical `ShotArtifact` fields: `keyframe_intents` →
   * `keyframeTimeline`, `dialogue` → `dialogueSegments`,
   * `characters`/`scene`/`objects`/`products` → `inputAssetRefs`,
   * `title`/`description` → `overallPrompt`). Placed via the shared
   * `placeNodeBesideSource` helper (§十三 — to the Production card's
   * RIGHT, never below, never a screenshot-specific magic-number
   * offset), then selected + expanded + focus'd (preserve zoom) like CASE 1.
   *
   * `expandedNodeId` is a single Canvas-wide value (Shell V2's own
   * "Canvas-level single expand" contract) — setting it here for either
   * branch is ALSO the entire single-focus-mode implementation (§三 CASE
   * 1 point 6 "如果其他 Advanced Video Generation card 已展开：自动
   * collapse 原来的") — no second, parallel focus system needed.
   *
   * CASE 3 (repeat click on the same Shot): identical to CASE 1 — the
   * existing binding is found again, no new node is ever created
   * (§三/§十六 "严格保证 ONE SHOT → AT MOST ONE 生成节点").
   *
   * Never fetches anything over the network and never calls an LLM —
   * every field read here (`storyboardResult`, `shotBindings`) is
   * already-hydrated Canvas state (§十九/§九). */
  const onOpenOrCreateShotVideoNode = useCallback(
    (orchestratorNodeId: string, shotId: string) => {
      /** Shot click MUST preserve current Canvas zoom (P0_V1 §1-§4).
       * `fitView` is forbidden here — it recomputes zoom from the target
       * node's bbox and caused the full-canvas zoom-out regression. */
      const focusShotGenerator = (generatorNodeId: string) => {
        setSelectedNodeId(generatorNodeId)
        setExpandedNodeId((prev) => {
          if (prev && prev !== generatorNodeId) bumpPlaybackReset()
          return generatorNodeId
        })
        requestAnimationFrame(() => {
          focusNodePreserveZoom(rf, generatorNodeId, { duration: 400 })
        })
      }

      const orchNode = nodesRef.current.find((n) => n.id === orchestratorNodeId)
      if (!orchNode) return
      const orchData = orchNode.data as unknown as FlowNodeData
      if (orchData.nodeType !== 'storyboard_generation') return
      const sourceHandle = 'storyboard-generation-out'

      /** One Shot → one canonical orchestrator→Generate Video edge with `shotId`
       * metadata (P0_V1 §6-§9). Reuses existing edge; never duplicates. */
      const ensureShotGeneratorEdge = (generatorNodeId: string) => {
        commitEdges((eds) => {
          const shotData = {
            relationType: 'DERIVED_FROM' as const,
            shotId,
            sourceVideoMergeNodeId: orchestratorNodeId,
            sourceShotId: shotId,
            targetGenerateVideoNodeId: generatorNodeId,
          }
          // Prefer exact shotId match; also reuse a bare orchestrator→generator wire
          // (shotId is stripped by canvas DTO save — bindings are the SOT).
          const existingIdx = eds.findIndex(
            (e) =>
              e.source === orchestratorNodeId &&
              e.target === generatorNodeId &&
              ((e.data as { shotId?: string } | undefined)?.shotId === shotId ||
                (e.data as { shotId?: string } | undefined)?.shotId == null),
          )
          if (existingIdx >= 0) {
            const existing = eds[existingIdx]
            const cur = (existing.data || {}) as Record<string, unknown>
            if (
              cur.shotId === shotId &&
              cur.sourceShotId === shotId &&
              cur.targetGenerateVideoNodeId === generatorNodeId &&
              existing.sourceHandle === sourceHandle
            ) {
              return eds
            }
            const next = eds.map((e, i) =>
              i === existingIdx
                ? { ...e, sourceHandle: e.sourceHandle || sourceHandle, data: { ...cur, ...shotData }, className: e.className || 'pcv-edge' }
                : e,
            )
            scheduleFromState(nodesRef.current, next)
            return next
          }
          // Drop any stale duplicate edge for the same shot targeting a
          // different generator (binding is the SOT; edge follows it).
          const cleaned = eds.filter(
            (e) =>
              !(
                e.source === orchestratorNodeId &&
                (e.data as { shotId?: string } | undefined)?.shotId === shotId &&
                e.target !== generatorNodeId
              ),
          )
          const next = addEdge(
            {
              id: genId(),
              source: orchestratorNodeId,
              sourceHandle,
              target: generatorNodeId,
              targetHandle: GENERIC_INPUT_HANDLE,
              data: shotData,
              className: 'pcv-edge',
            },
            cleaned,
          )
          scheduleFromState(nodesRef.current, next)
          return next
        })
      }

      const fields = ((orchNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const bindings = readVideoProductionShotBindings(fields)
      const resolvedGenerator = resolveCanonicalShotGenerator(
        nodesRef.current,
        orchestratorNodeId,
        shotId,
        bindings,
      )
      if (resolvedGenerator.kind === 'bound') {
        if (resolvedGenerator.bindingRepaired) {
          const nextBindings = ensureVideoProductionShotBinding(
            bindings,
            shotId,
            resolvedGenerator.nodeId,
          )
          commitNodes((nds) => {
            const next = nds.map((n) => {
              if (n.id !== orchestratorNodeId) return n
              const nodeData = n.data as unknown as FlowNodeData
              const curFields = (nodeData.fields || {}) as Record<string, unknown>
              const patched = patchCanvasFieldsIfChanged(curFields, { shotBindings: nextBindings })
              if (!patched) return n
              return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
            })
            scheduleFromState(next, edgesRef.current)
            return next
          })
        }
        ensureShotGeneratorEdge(resolvedGenerator.nodeId)
        focusShotGenerator(resolvedGenerator.nodeId)
        return
      }

      const local = fields.storyboardResult as StoryboardArtifact | null
      const storyboard = local && isValidStoryboardArtifact(local) ? local : null
      const shotRaw = storyboard?.shots.find((s) => s.shot_id === shotId)
      if (!storyboard || !shotRaw) return

      const entityBindings = resolveStoryboardEntityBindings({
        script: (fields.scriptContext as StoryboardScriptContextField | null)?.script ?? null,
        supportingAssets: Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as Array<Record<string, unknown>>) : [],
      })
      const shot = enrichShotWithEntityBindings(shotRaw, entityBindings)
      const productionFormat = readProductionFormat(fields)

      const supportingAssets: SupportingAssetLike[] = [
        ...((Array.isArray(fields.supportingAssets) ? fields.supportingAssets : []) as SupportingAssetLike[]),
        ...((((fields.scriptContext as { linkedAssetRefs?: SupportingAssetLike[] } | null)?.linkedAssetRefs) ||
          []) as SupportingAssetLike[]),
      ]
      const ctx = buildVideoContextFromShot(shot, { supportingAssets })
      const format = resolveStoryboardFormatInheritance({
        aspectRatio: storyboard.aspect_ratio,
        qualityTier: storyboard.quality_tier || (fields.qualityTier as string | null | undefined) || null,
        storyboardTitle: storyboard.title,
        productionFormat: (fields.productionFormat as { aspect_ratio?: string; resolution?: string; version?: number } | null) || null,
      })
      const lockedProductionFormat: StoryboardProductionFormat = {
        aspect_ratio: format.ratio as StoryboardProductionFormat['aspect_ratio'],
        resolution: (/720/i.test(format.qualityTier) ? '720p' : '1080p') as StoryboardProductionFormat['resolution'],
        version:
          typeof (fields.productionFormat as { version?: number } | null)?.version === 'number'
            ? (fields.productionFormat as { version: number }).version
            : 1,
      }
      const lockedFields = buildLockedProductionFormatFields(lockedProductionFormat)
      const def = getNodeDefinition('generate_video')
      const newId = genId()
      const newNode: Node = {
        id: newId,
        type: def.family,
        position: placeNodeBesideSource(orchNode, { verticalNudge: NODE_PLACEMENT_VERTICAL_NUDGE }),
        data: {
          nodeType: 'generate_video',
          category: def.category,
          entityType: null,
          entityId: null,
          collapsed: false,
          fields: {
            ...buildGenerateVideoNodeDefaultFields(),
            promptText: ctx.overallPrompt,
            durationSeconds: Math.max(1, Math.round(ctx.durationSeconds)),
            ...(productionFormat && isProductionFormatComplete(productionFormat)
              ? buildLockedProductionFormatFields(productionFormat)
              : lockedFields),
            formatLocked: true,
            inheritedFormatLabel: format.inheritedFormatLabel,
            keyframeTimeline: ctx.keyframeTimeline,
            dialogueSegments: ctx.dialogueSegments,
            inputAssetRefs: ctx.inputAssetRefs,
            referenceAssetsFingerprint: referenceAssetsFingerprint(ctx.inputAssetRefs),
            boundShotId: shotId,
            sourceShotId: ctx.sourceShotId,
            boundOrchestratorNodeId: orchestratorNodeId,
            boundStoryboardArtifactId: storyboard.storyboard_artifact_id,
          },
          latestRunId: null,
        } as unknown as Record<string, unknown>,
      }
      const nextBindings = ensureVideoProductionShotBinding(bindings, shotId, newId)
      // Atomic create: generator node + orchestrator shot binding in ONE
      // commit. Persist once after the edge is attached (below) so debounce
      // never flushes a half-graph without the Shot→Generate Video wire.
      commitNodes((nds) => {
        return [...nds, newNode].map((n) => {
          if (n.id !== orchestratorNodeId) return n
          const nodeData = n.data as unknown as FlowNodeData
          const curFields = (nodeData.fields || {}) as Record<string, unknown>
          const patched = patchCanvasFieldsIfChanged(curFields, { shotBindings: nextBindings })
          if (!patched) return n
          return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
        })
      })
      ensureShotGeneratorEdge(newId)
      focusShotGenerator(newId)
    },
    [bumpPlaybackReset, commitEdges, commitNodes, rf, scheduleFromState],
  )

  /** After Production Format change with existing media — mark bound generators
   * AND their keyframe Generate Image nodes as format_mismatch without deleting
   * artifacts / history. */
  const onInvalidateStoryboardProductionFormat = useCallback(
    (
      orchestratorNodeId: string,
      nextFormat: StoryboardProductionFormat,
      impact?: ProductionFormatChangeImpact,
    ) => {
      const orchNode = nodesRef.current.find((n) => n.id === orchestratorNodeId)
      if (!orchNode) return
      const fields = ((orchNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const previousFormat = readProductionFormat(fields)
      const staleImpact = impact ?? classifyProductionFormatChange(previousFormat, nextFormat)
      const aiStale = Boolean(staleImpact.aiGenerationStale)
      const deliveryStale = Boolean(staleImpact.deliveryRenderStale)
      const bindings = readVideoProductionShotBindings(fields)
      const generatorIds = new Set(
        Object.values(bindings)
          .map((b) => b?.advancedVideoNodeId)
          .filter((id): id is string => Boolean(id)),
      )
      // Also collect generate_video nodes owned by this orchestrator (bindings may lag).
      for (const n of nodesRef.current) {
        const d = n.data as unknown as FlowNodeData
        if (d.nodeType !== 'generate_video') continue
        const f = (d.fields || {}) as Record<string, unknown>
        if (f.boundOrchestratorNodeId === orchestratorNodeId || f.boundVideoProductionNodeId === orchestratorNodeId) {
          generatorIds.add(n.id)
        }
      }
      const locked = buildLockedProductionFormatFields(nextFormat)
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id === orchestratorNodeId) {
            const nodeData = n.data as unknown as FlowNodeData
            const curFields = (nodeData.fields || {}) as Record<string, unknown>
            const hasFinal = Boolean(curFields.finalVideoArtifact)
            const finalStale = hasFinal && (aiStale || deliveryStale)
            const patched = patchCanvasFieldsIfChanged(curFields, {
              productionFormat: nextFormat,
              aspectRatio: nextFormat.aspect_ratio,
              finalVideoFormatMismatch: finalStale,
            })
            if (!patched) return n
            return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
          }
          const nodeData = n.data as unknown as FlowNodeData
          const curFields = (nodeData.fields || {}) as Record<string, unknown>

          if (nodeData.nodeType === 'generate_video' && generatorIds.has(n.id)) {
            const hasVideoOutput = Boolean(curFields.generatedTaskId || curFields.generatedVideoUrl)
            const patched = patchCanvasFieldsIfChanged(curFields, {
              ...locked,
              lockedProductionFormat: nextFormat,
              ratio: nextFormat.aspect_ratio,
              qualityTier: resolutionToQualityTier(nextFormat.resolution),
              productionFormatLocked: true,
              formatCompatibility: aiStale && hasVideoOutput ? 'format_mismatch' : 'ok',
              deliveryRenderStale: (deliveryStale || aiStale) && hasVideoOutput,
              needsRegeneration: aiStale && hasVideoOutput,
            })
            if (!patched) return n
            return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
          }

          if (nodeData.nodeType === 'generate_image') {
            const candidate = curFields.candidateForVideoKeyframe as
              | { videoNodeId?: string; timelineEntryId?: string }
              | null
              | undefined
            const boundOrch = curFields.boundOrchestratorNodeId as string | null | undefined
            const boundVideo = (curFields.boundVideoNodeId as string | null | undefined) || candidate?.videoNodeId
            const isStoryboardKeyframe =
              boundOrch === orchestratorNodeId ||
              (boundVideo != null && generatorIds.has(boundVideo)) ||
              (candidate?.videoNodeId != null && generatorIds.has(candidate.videoNodeId))
            if (!isStoryboardKeyframe) return n
            const hasOutput = Boolean(curFields.generatedOutputId)
            const patched = patchCanvasFieldsIfChanged(curFields, {
              ...locked,
              formatCompatibility: aiStale && hasOutput ? 'format_mismatch' : 'ok',
              deliveryRenderStale: (deliveryStale || aiStale) && hasOutput,
              needsRegeneration: aiStale && hasOutput,
            })
            if (!patched) return n
            return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
          }

          return n
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  /** CASE B — standalone Video Node format change: lock children keyframe images to same contract. */
  const onPropagateVideoNodeProductionFormat = useCallback(
    (
      videoNodeId: string,
      nextFormat: StoryboardProductionFormat,
      opts?: { invalidateExisting?: boolean; impact?: ProductionFormatChangeImpact },
    ) => {
      const videoFormatFields = {
        ...buildStandaloneVideoFormatFields(nextFormat),
        formatSource: 'video_node' as const,
      }
      const childKeyframeFormatFields = {
        ...buildLockedProductionFormatFields(nextFormat),
        formatSource: 'video_node' as const,
      }
      const videoNode = nodesRef.current.find((n) => n.id === videoNodeId)
      const previousFormat = videoNode
        ? deriveProductionFormatFromVideoFields(
            ((videoNode.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>,
          )
        : null
      const staleImpact =
        opts?.impact ??
        (opts?.invalidateExisting
          ? { aspectChanged: true, resolutionChanged: true, aiGenerationStale: true, deliveryRenderStale: true }
          : classifyProductionFormatChange(previousFormat, nextFormat))
      const aiStale = Boolean(staleImpact.aiGenerationStale)
      const deliveryStale = Boolean(staleImpact.deliveryRenderStale)
      commitNodes((nds) => {
        const next = nds.map((n) => {
          if (n.id === videoNodeId) {
            const nodeData = n.data as unknown as FlowNodeData
            if (nodeData.nodeType !== 'generate_video') return n
            const curFields = (nodeData.fields || {}) as Record<string, unknown>
            // Storyboard-bound videos must not invent a parallel format.
            if (curFields.boundOrchestratorNodeId || curFields.boundVideoProductionNodeId) return n
            const hasVideoOutput = Boolean(curFields.generatedTaskId || curFields.generatedVideoUrl)
            const patched = patchCanvasFieldsIfChanged(curFields, {
              ...videoFormatFields,
              formatCompatibility: aiStale && hasVideoOutput ? 'format_mismatch' : 'ok',
              deliveryRenderStale: (deliveryStale || aiStale) && hasVideoOutput,
              needsRegeneration: aiStale && hasVideoOutput,
            })
            if (!patched) return n
            return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
          }
          const nodeData = n.data as unknown as FlowNodeData
          if (nodeData.nodeType !== 'generate_image') return n
          const curFields = (nodeData.fields || {}) as Record<string, unknown>
          const candidate = curFields.candidateForVideoKeyframe as { videoNodeId?: string } | null | undefined
          const boundVideo = (curFields.boundVideoNodeId as string | undefined) || candidate?.videoNodeId
          if (boundVideo !== videoNodeId) return n
          const hasOutput = Boolean(curFields.generatedOutputId)
          const patched = patchCanvasFieldsIfChanged(curFields, {
            ...childKeyframeFormatFields,
            formatCompatibility: aiStale && hasOutput ? 'format_mismatch' : 'ok',
            deliveryRenderStale: (deliveryStale || aiStale) && hasOutput,
            needsRegeneration: aiStale && hasOutput,
          })
          if (!patched) return n
          return { ...n, data: { ...nodeData, fields: patched } as unknown as Record<string, unknown> }
        })
        scheduleFromState(next, edgesRef.current)
        return next
      })
    },
    [commitNodes, scheduleFromState],
  )

  /** "合并生成视频" (spec §19/§22/§24) — synchronous request; server
   * re-validates `ALL_REQUIRED_SHOTS_READY` even though the button
   * itself is already disabled otherwise. Success snapshots the
   * AssemblyManifest + every Shot's CURRENT video Job id as the new
   * Final Video Artifact (spec §25); does not touch any bound
   * `generate_video` node. */
  const onRunVideoProductionAssembly = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const fields = ((node.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
      const units = videoProductionUnitsByNodeId.get(nodeId) || []
      const storyboardArtifactId =
        (fields.storyboardArtifactId as string | null | undefined) ??
        (fields.storyboardResult as StoryboardArtifact | null)?.storyboard_artifact_id ??
        null

      const delivery = buildAssemblyDeliveryContract(fields)
      if (!delivery) {
        message.error('请先在「生成分镜」中选择成片规格（长宽比与清晰度），再合并生成视频')
        return
      }

      let manifest: ReturnType<typeof buildAssemblyManifest>
      try {
        manifest = buildAssemblyManifest(nodeId, storyboardArtifactId, units, delivery)
      } catch {
        message.error('尚有镜头未生成，无法合并生成视频')
        return
      }

      const patch = patchCanvasFieldsIfChanged(fields, { assemblyRunStatus: 'ASSEMBLING', assemblyErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      createVideoProductionAssembly(workspaceId, {
        canvas_node_id: nodeId,
        storyboard_artifact_id: manifest.storyboard_artifact_id,
        provider: manifest.provider,
        skill: manifest.skill,
        shots: manifest.shots,
        production_format: manifest.production_format,
        aspect_ratio: manifest.aspect_ratio,
        delivery_target_width: manifest.delivery_target_width,
        delivery_target_height: manifest.delivery_target_height,
      })
        .then((assembly) => {
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (!cur) return
          const curFields = ((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
          if (assembly.status === 'READY') {
            const snapshot = buildFinalVideoArtifactSnapshot(nodeId, storyboardArtifactId, assembly, manifest)
            const next = patchCanvasFieldsIfChanged(curFields, {
              assemblyRunStatus: null,
              assemblyErrorMessage: null,
              activeAssemblyId: assembly.id,
              finalVideoArtifact: snapshot,
            })
            if (next) onChangeFields(nodeId, next)
            message.success('已合并生成完整视频')
          } else {
            const next = patchCanvasFieldsIfChanged(curFields, {
              assemblyRunStatus: 'FAILED',
              assemblyErrorMessage: assembly.error_message || '合并生成视频失败',
            })
            if (next) onChangeFields(nodeId, next)
            message.error(assembly.error_message || '合并生成视频失败')
          }
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '合并生成视频失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = ((cur.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>
            const next = patchCanvasFieldsIfChanged(curFields, { assemblyRunStatus: 'FAILED', assemblyErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [onChangeFields, videoProductionUnitsByNodeId, workspaceId],
  )

  // Candidate "选择"/"淘汰" (spec §35/§61) — toggles the LOCAL Canvas
  // field AND persists the feedback event server-side (best-effort;
  // never blocks the local toggle on the network call succeeding).
  const onSetCreativeCandidateState = useCallback(
    (candidateNodeId: string, patch: { selected?: boolean; rejected?: boolean }) => {
      const node = nodesRef.current.find((n) => n.id === candidateNodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (next) onChangeFields(candidateNodeId, next)
      const eventType = patch.rejected ? 'REJECTED' : patch.selected ? 'SELECTED' : null
      if (!eventType) return
      recordCreativeCandidateEvent(workspaceId, {
        run_id: (fields.sourceRunId as string) || null,
        candidate_id: (fields.candidateId as string) || null,
        event_type: eventType,
      }).catch(() => {})
    },
    [onChangeFields, workspaceId],
  )

  // Brief edit on a `project_start` node changed a field that actually
  // affects Ideation output (spec §42) — mark every descendant
  // `creative_candidate` stale; NEVER delete/regenerate anything (spec
  // §43 "修改上游不会自动重新生成下游").
  const onProjectStartBriefChanged = useCallback(
    (nodeId: string) => {
      commitNodes((nds) => markCandidatesStale(nds, edgesRef.current, nodeId))
    },
    [commitNodes],
  )

  // -----------------------------------------------------------------
  // VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_
  // GENERATION_NODE_V2 — the unified `generate_creative` node's own
  // Generate/Regenerate + Diverge. Deliberately PARALLEL to (never a
  // rewrite of) the `onGenerateCreativeIdeation`/`onExpandCreativeCandidate`
  // pair above, which stays untouched for legacy `project_start`/
  // `creative_candidate` Canvas nodes (spec §15 "禁止先删除现有能力后
  // 重写"). Reuses the SAME polling/idempotency primitives
  // (`pollIdeationRun`/`applyIdeationRunToNode`/`clearIdeationPoll`) —
  // only WHAT happens on a SUCCESS materialization differs (write onto
  // the same node vs. fan out 3 new legacy candidate nodes), so this
  // round adds a small `onSuccess` seam to `applyIdeationRunToNode`
  // rather than duplicating its polling/error-handling logic wholesale.
  // -----------------------------------------------------------------

  // Normal Generate/Regenerate (V2 §3) — writes the single returned
  // candidate straight onto THIS node's own `fields.currentResult`;
  // never materializes a sibling node.
  const applyGenerateCreativeResult = useCallback(
    (nodeId: string, run: CreativeIdeationRunState) => {
      if (materializedIdeationRunIdsRef.current.has(run.run_id)) return
      const candidate = (run.candidates || [])[0]
      // Fail-closed: SUCCESS with no candidates must never silently leave
      // the card at “未生成”. Backend should already ERROR; this is the
      // last-line Canvas invariant.
      if (!candidate) {
        materializedIdeationRunIdsRef.current.add(run.run_id)
        const node = nodesRef.current.find((n) => n.id === nodeId)
        if (!node) return
        const data = node.data as unknown as FlowNodeData
        const fields = (data.fields || {}) as Record<string, unknown>
        const next = patchCanvasFieldsIfChanged(fields, {
          runStatus: 'ERROR',
          runErrorMessage: run.error_message || '未返回可用创意结果，请重试。',
        })
        if (next) onChangeFields(nodeId, next)
        message.error(run.error_message || '未返回可用创意结果，请重试。')
        return
      }
      materializedIdeationRunIdsRef.current.add(run.run_id)
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      const next = patchCanvasFieldsIfChanged(fields, { currentResult: buildResultFromCandidate(candidate, run.run_id) })
      if (next) onChangeFields(nodeId, next)
    },
    [onChangeFields],
  )

  const onGenerateCreative = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
      if (activeStatuses.has(fields.runStatus as string)) return
      const intent = String(fields.userIntent ?? '').trim()
      if (!intent) {
        message.error('请先填写创作意图')
        return
      }
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'
      const patch = patchCanvasFieldsIfChanged(fields, { runStatus: 'SUBMITTING', runErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      generateCreativeIdeation(workspaceId, {
        canvas_node_id: nodeId,
        project_id: projectId,
        brief: buildBriefPayload(fields),
        // V2 §3 NORMAL_GENERATE_RESULT_COUNT=1 — never omitted/defaulted
        // via the legacy `candidateCount` field (this node type has no
        // such field at all, see nodeRegistry's `generate_creative`
        // `defaultFields`).
        candidate_count: 1,
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-gen-${Date.now()}`,
      })
        .then((run) => {
          const applyAndTrack = (r: CreativeIdeationRunState) => {
            const cur = nodesRef.current.find((n) => n.id === nodeId)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeRunId: r.run_id,
                runStatus: r.status,
                runErrorMessage: r.error_message,
              })
              if (patched) onChangeFields(nodeId, patched)
            }
            if (r.status === 'SUCCESS') {
              applyGenerateCreativeResult(nodeId, r)
              clearIdeationPoll(r.run_id)
            } else if (r.status === 'ERROR') {
              message.error(r.error_message || '生成失败')
              clearIdeationPoll(r.run_id)
            } else if (r.status === 'QUEUED' || r.status === 'GENERATING' || r.status === 'SUBMITTING') {
              clearIdeationPoll(r.run_id)
              const tick = async () => {
                try {
                  const latest = await getCreativeIdeationRun(workspaceId, r.run_id)
                  applyAndTrack(latest)
                  if (latest.status === 'QUEUED' || latest.status === 'GENERATING' || latest.status === 'SUBMITTING') {
                    const timer = window.setTimeout(tick, 2500)
                    ideationPollTimersRef.current.set(r.run_id, timer)
                  }
                } catch {
                  const timer = window.setTimeout(tick, 5000)
                  ideationPollTimersRef.current.set(r.run_id, timer)
                }
              }
              const timer = window.setTimeout(tick, 2500)
              ideationPollTimersRef.current.set(r.run_id, timer)
            }
          }
          applyAndTrack(run)
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '生成失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { runStatus: 'ERROR', runErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [applyGenerateCreativeResult, clearIdeationPoll, onChangeFields, projectId, workspaceId],
  )

  // VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
  // TRIPLET_AND_SINGLE_EXPANSION_V3 §18-§27 Diverge (CORRECTED contract,
  // replaces V2's own "current node untouched + 3 brand-new children +
  // GENERATED_FROM edges" — that pattern is now WRONG for Diverge and is
  // ONLY still read for pre-existing legacy Canvas data, spec §48/§49
  // "LEGACY_DIVERGE_GRAPH_COMPATIBLE=YES", never written by new clicks):
  //
  //   TOTAL 3 result nodes = the CURRENT node (reused, `siblingIndex=0`)
  //   + exactly 2 NEW nodes (`siblingIndex=1/2`) — never +3 on top of an
  //   untouched current node. No visible Canvas edge is ever created
  //   between the 3 (`VISIBLE_PARENT_CHILD_EDGE=NO`) — only non-visible
  //   `divergenceGroupId`/`divergenceSiblingIndex`/`divergenceOriginNodeId`
  //   field provenance ties them together.
  //
  // `applyDivergenceGroupResult` is the ONE place that ever WRITES a
  // divergence candidate onto a node's `currentResult` — called both
  // from `onDivergeCreative`'s own live poll below AND from the
  // page-refresh hydration effect further down, so a browser refresh
  // mid-Diverge resumes into the exact same outcome.
  const applyDivergenceGroupResult = useCallback(
    (originNodeId: string, run: CreativeIdeationRunState) => {
      if (materializedIdeationRunIdsRef.current.has(run.run_id)) return
      materializedIdeationRunIdsRef.current.add(run.run_id)
      const candidates = run.candidates || []

      const origin = nodesRef.current.find((n) => n.id === originNodeId)
      const originFields = origin ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>) : {}
      const groupId = originFields.divergenceGroupId as string | undefined

      // §46 partial-failure guard — a run reaching SUCCESS with FEWER
      // than 3 candidates must still reach a terminal state on every
      // member (never an infinite spinner on the missing slot(s)).
      const candidateAt = (idx: number) => candidates[idx] ?? null

      const siblings = groupId
        ? nodesRef.current.filter((n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId)
        : []
      const siblingAt = (idx: number) =>
        siblings.find((n) => ((n.data as unknown as FlowNodeData).fields || {}).divergenceSiblingIndex === idx) || null

      const applyResultToNode = (targetId: string | null, idx: number, isOrigin: boolean) => {
        if (!targetId) return
        const cur = nodesRef.current.find((n) => n.id === targetId)
        if (!cur) return
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const candidate = candidateAt(idx)
        const resultPatch = candidate
          ? applyDivergenceCandidateResult(candidate, run.run_id)
          : ({ runStatus: 'ERROR', runErrorMessage: '该方向未返回有效结果，可重新发散或单独重新生成' } as Record<string, unknown>)
        const bookkeepingPatch = isOrigin
          ? { activeDivergeRunId: null, divergeRunStatus: candidate ? null : 'ERROR', divergeRunErrorMessage: candidate ? null : resultPatch.runErrorMessage }
          : { activeRunId: null, runStatus: candidate ? null : 'ERROR' }
        const patched = patchCanvasFieldsIfChanged(curFields, { ...resultPatch, ...bookkeepingPatch })
        if (patched) onChangeFields(targetId, patched)
      }

      applyResultToNode(originNodeId, 0, true)
      applyResultToNode(siblingAt(1)?.id ?? null, 1, false)
      applyResultToNode(siblingAt(2)?.id ?? null, 2, false)
    },
    [onChangeFields],
  )

  // §47 POST_DISPATCH_FAILURE — the run itself was created but ultimately
  // failed (e.g. Provider/Harness error) AFTER B/C were already
  // materialized: every member gets a RECOVERABLE error state (never a
  // permanent delete-lock — §24's lock is keyed off `runStatus`/
  // `divergeRunStatus` being ACTIVE, which this clears).
  const applyDivergenceGroupError = useCallback(
    (originNodeId: string, groupId: string | undefined, message_: string) => {
      const cur = nodesRef.current.find((n) => n.id === originNodeId)
      if (cur) {
        const curFields = (cur.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(curFields, { activeDivergeRunId: null, divergeRunStatus: 'ERROR', divergeRunErrorMessage: message_ })
        if (next) onChangeFields(originNodeId, next)
      }
      if (!groupId) return
      const siblings = nodesRef.current.filter(
        (n) => n.id !== originNodeId && ((n.data as unknown as FlowNodeData).fields || {}).divergenceGroupId === groupId,
      )
      for (const sib of siblings) {
        const sibFields = (sib.data as unknown as FlowNodeData).fields || {}
        const next = patchCanvasFieldsIfChanged(sibFields, { activeRunId: null, runStatus: 'ERROR', runErrorMessage: message_ })
        if (next) onChangeFields(sib.id, next)
      }
    },
    [onChangeFields],
  )

  const onDivergeCreative = useCallback(
    (nodeId: string, instruction: string | null) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      if (!node) return
      const data = node.data as unknown as FlowNodeData
      const fields = (data.fields || {}) as Record<string, unknown>
      const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
      if (activeStatuses.has(fields.divergeRunStatus as string) || activeStatuses.has(fields.runStatus as string)) return
      const currentResult = (fields.currentResult as Record<string, unknown> | null) || null
      // §22 — Diverge is now valid on an EMPTY node too (all 3 siblings
      // generate from scratch); only the Brief (same requirement as a
      // plain Generate) is required.
      const intent = String(fields.userIntent ?? '').trim()
      if (!intent) {
        message.error('请先填写创作意图，再发散')
        return
      }
      const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
      const providerSelectionMode = (fields.providerSelectionMode as string) || 'DEFAULT'
      // §20 — Diverge bumps THIS node's own depth too (its current
      // result now also comes from this divergence round, same as the
      // 2 brand-new siblings — all 3 are co-equal members of the SAME
      // new generation, never "children one level below an untouched
      // parent").
      const generationDepth = ((fields.generationDepth as number) || 0) + 1
      const rootRunId = (fields.rootRunId as string) || (currentResult?.sourceRunId as string | undefined) || null

      const divergenceGroupId = genId()
      const siblingBId = genId()
      const siblingCId = genId()

      const patch = patchCanvasFieldsIfChanged(fields, { divergeRunStatus: 'SUBMITTING', divergeRunErrorMessage: null })
      if (patch) onChangeFields(nodeId, patch)

      expandCreativeIdeation(workspaceId, {
        canvas_node_id: nodeId,
        candidate_node_id: nodeId,
        project_id: projectId,
        root_canvas_node_id: (fields.parentNodeId as string) || nodeId,
        root_run_id: rootRunId,
        generation_depth: generationDepth,
        brief: buildBriefPayload(fields),
        parent_candidate: currentResult ? buildParentCandidatePayload(currentResult) : {},
        expand_instruction: instruction && instruction.trim() ? instruction.trim() : null,
        // §21 DIVERGE_NEW_NODE_COUNT=2 + reused current node = §18
        // DIVERGE_TOTAL_RESULT_NODES=3 — the AI request itself is
        // UNCHANGED from V2 (still `candidate_count=3`,
        // AI_TASKS_PER_DIVERGE_RUN=1); only the FRONTEND materialization
        // below changed.
        candidate_count: 3,
        skill_selection_source: skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO',
        skill_key: skillSelectionSource === 'EXPLICIT' ? (fields.skillKey as string | null) : null,
        provider_selection_mode: providerSelectionMode === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
        provider_option_id: providerSelectionMode === 'EXPLICIT' ? (fields.providerOptionId as string | null) : null,
        idempotency_key: `${nodeId}-diverge-${Date.now()}`,
      })
        .then((run) => {
          // §47 PRE_DISPATCH_FAILURE is handled entirely by the `.catch`
          // below (B/C are only ever created HERE, once dispatch is
          // confirmed to have actually created a run) — so nothing to
          // "clean up" on failure: they were simply never materialized.
          const def = getNodeDefinition('generate_creative')
          const origin = nodesRef.current.find((n) => n.id === nodeId)
          const positions = origin ? placeSiblingNodesBesideSource(origin, 2, {}) : [{ x: node.position.x + 480, y: node.position.y }, { x: node.position.x + 480, y: node.position.y + 260 }]
          const originFieldsNow = origin ? (((origin.data as unknown as FlowNodeData).fields || {}) as Record<string, unknown>) : fields

          const newSiblingNodes: Node[] = [
            { id: siblingBId, idx: 1 as const },
            { id: siblingCId, idx: 2 as const },
          ].map(({ id: sid, idx }, arrIdx) => ({
            id: sid,
            type: def.family,
            position: positions[arrIdx],
            data: {
              nodeType: 'generate_creative',
              category: def.category,
              entityType: null,
              entityId: null,
              collapsed: false,
              latestRunId: null,
              fields: buildDivergenceSiblingFields(originFieldsNow, {
                divergenceGroupId,
                siblingIndex: idx,
                originNodeId: nodeId,
                rootRunId: rootRunId || run.run_id,
                generationDepth,
                runId: run.run_id,
              }),
            } as unknown as Record<string, unknown>,
          }))
          // §29-§31 — new siblings default COLLAPSED (never touch
          // `expandedNodeId`); the origin stays whatever it already was
          // (typically still expanded, since Diverge is only reachable
          // from the Footer, which stays visible even collapsed).
          commitNodes((nds) => [...nds, ...newSiblingNodes])

          const applyAndTrack = (r: CreativeIdeationRunState) => {
            const cur = nodesRef.current.find((n) => n.id === nodeId)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeDivergeRunId: r.run_id,
                divergeRunStatus: r.status,
                divergeRunErrorMessage: r.error_message,
                divergenceGroupId,
                divergenceSiblingIndex: 0,
                divergenceOriginNodeId: nodeId,
                rootRunId: rootRunId || r.run_id,
                generationDepth,
              })
              if (patched) onChangeFields(nodeId, patched)
            }
            if (r.status === 'SUCCESS') {
              applyDivergenceGroupResult(nodeId, r)
              clearIdeationPoll(r.run_id)
            } else if (r.status === 'ERROR') {
              applyDivergenceGroupError(nodeId, divergenceGroupId, r.error_message || '发散失败')
              message.error(r.error_message || '发散失败')
              clearIdeationPoll(r.run_id)
            } else if (r.status === 'QUEUED' || r.status === 'GENERATING' || r.status === 'SUBMITTING') {
              clearIdeationPoll(r.run_id)
              const tick = async () => {
                try {
                  const latest = await getCreativeIdeationRun(workspaceId, r.run_id)
                  applyAndTrack(latest)
                  if (latest.status === 'QUEUED' || latest.status === 'GENERATING' || latest.status === 'SUBMITTING') {
                    const timer = window.setTimeout(tick, 2500)
                    ideationPollTimersRef.current.set(r.run_id, timer)
                  }
                } catch {
                  const timer = window.setTimeout(tick, 5000)
                  ideationPollTimersRef.current.set(r.run_id, timer)
                }
              }
              const timer = window.setTimeout(tick, 2500)
              ideationPollTimersRef.current.set(r.run_id, timer)
            }
          }
          applyAndTrack(run)
        })
        .catch((err) => {
          const display = (err as SaasApiError).message || '发散失败'
          const cur = nodesRef.current.find((n) => n.id === nodeId)
          if (cur) {
            const curFields = (cur.data as unknown as FlowNodeData).fields || {}
            const next = patchCanvasFieldsIfChanged(curFields, { divergeRunStatus: 'ERROR', divergeRunErrorMessage: display })
            if (next) onChangeFields(nodeId, next)
          }
          message.error(display)
        })
    },
    [applyDivergenceGroupError, applyDivergenceGroupResult, clearIdeationPoll, commitNodes, onChangeFields, projectId, workspaceId],
  )

  // V2 §12 Stale — marks THIS node's own `currentResult.stale = true`
  // only (no descendant walk, see `markGenerateCreativeResultStale`'s
  // own doc comment for why that's correct for the unified node model).
  const onGenerateCreativeBriefChanged = useCallback(
    (nodeId: string) => {
      commitNodes((nds) => markGenerateCreativeResultStale(nds, nodeId))
    },
    [commitNodes],
  )

  // Resume polling a `generate_creative` node's own in-flight Generate/
  // Diverge run across a refresh — same rationale as the legacy
  // hydration effect above, kept separate rather than merged into it so
  // neither code path needs to branch on node type internally.
  const generateCreativeHydratedRef = useRef(false)
  useEffect(() => {
    if (generateCreativeHydratedRef.current) return
    if (canvasHydrating) return
    generateCreativeHydratedRef.current = true
    const activeStatuses = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
    for (const n of nodesRef.current) {
      const data = n.data as unknown as FlowNodeData
      if (data.nodeType !== 'generate_creative') continue
      const fields = (data.fields || {}) as Record<string, unknown>
      // A brand-new Diverge sibling (B/C, `divergenceSiblingIndex` 1/2)
      // was materialized with a plain `runStatus`/`activeRunId` pair
      // (spec §54 "reuses the SAME EMPTY/GENERATING/SUCCESS/ERROR states
      // a plain Generate already renders") — on resume it must resolve
      // through the SAME `applyDivergenceGroupResult` as the origin (§0),
      // never through `applyGenerateCreativeResult`'s "always candidate
      // index 0" — otherwise every sibling would silently receive the
      // ORIGIN's own candidate instead of its own.
      const divergenceSiblingIndex = fields.divergenceSiblingIndex as number | null | undefined
      const divergenceOriginNodeId = fields.divergenceOriginNodeId as string | null | undefined
      if (activeStatuses.has(fields.runStatus as string) && fields.activeRunId) {
        clearIdeationPoll(fields.activeRunId as string)
        const runId = fields.activeRunId as string
        const isDivergenceSibling = Boolean(divergenceSiblingIndex) && Boolean(divergenceOriginNodeId)
        const tick = async () => {
          try {
            const latest = await getCreativeIdeationRun(workspaceId, runId)
            const cur = nodesRef.current.find((nn) => nn.id === n.id)
            if (cur && !isDivergenceSibling) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeRunId: latest.run_id,
                runStatus: latest.status,
                runErrorMessage: latest.error_message,
              })
              if (patched) onChangeFields(n.id, patched)
            }
            if (latest.status === 'SUCCESS') {
              if (isDivergenceSibling) applyDivergenceGroupResult(divergenceOriginNodeId as string, latest)
              else applyGenerateCreativeResult(n.id, latest)
              clearIdeationPoll(runId)
            } else if (latest.status === 'ERROR') {
              if (isDivergenceSibling) applyDivergenceGroupError(divergenceOriginNodeId as string, fields.divergenceGroupId as string | undefined, latest.error_message || '发散失败')
              clearIdeationPoll(runId)
            } else {
              const timer = window.setTimeout(tick, 2500)
              ideationPollTimersRef.current.set(runId, timer)
            }
          } catch {
            const timer = window.setTimeout(tick, 5000)
            ideationPollTimersRef.current.set(runId, timer)
          }
        }
        void tick()
      } else if (activeStatuses.has(fields.divergeRunStatus as string) && fields.activeDivergeRunId) {
        clearIdeationPoll(fields.activeDivergeRunId as string)
        const runId = fields.activeDivergeRunId as string
        const tick = async () => {
          try {
            const latest = await getCreativeIdeationRun(workspaceId, runId)
            const cur = nodesRef.current.find((nn) => nn.id === n.id)
            if (cur) {
              const curFields = (cur.data as unknown as FlowNodeData).fields || {}
              const patched = patchCanvasFieldsIfChanged(curFields, {
                activeDivergeRunId: latest.run_id,
                divergeRunStatus: latest.status,
                divergeRunErrorMessage: latest.error_message,
              })
              if (patched) onChangeFields(n.id, patched)
            }
            if (latest.status === 'SUCCESS') {
              applyDivergenceGroupResult(n.id, latest)
              clearIdeationPoll(runId)
            } else if (latest.status === 'ERROR') {
              applyDivergenceGroupError(n.id, fields.divergenceGroupId as string | undefined, latest.error_message || '发散失败')
              clearIdeationPoll(runId)
            } else {
              const timer = window.setTimeout(tick, 2500)
              ideationPollTimersRef.current.set(runId, timer)
            }
          } catch {
            const timer = window.setTimeout(tick, 5000)
            ideationPollTimersRef.current.set(runId, timer)
          }
        }
        void tick()
      }
    }
  }, [applyDivergenceGroupError, applyDivergenceGroupResult, applyGenerateCreativeResult, canvasHydrating, clearIdeationPoll, onChangeFields, workspaceId])

  // VistoeraTASK-00134 — visual feedback ("释放以添加图片到画布") while a real
  // OS/file-manager file drag is over the Canvas pane. `'none'` hides the
  // overlay; `'accept'`/`'reject'` pick its message/color. Driven by
  // `onPaneDragEnter`/`onPaneDragOver`/`onPaneDragLeave`/`onDrop` below —
  // a short hide-timer (rather than a raw dragleave->hide) survives the
  // enter/leave pair React Flow's own internal DOM structure fires as
  // the pointer crosses from the pane onto a node/edge/handle and back,
  // so the overlay never flickers while the user is still dragging
  // around inside the SAME Canvas. Never set for anything OTHER than a
  // real OS file drag (§2 "不得因为内部 Canvas node drag 而误触发") — internal
  // React Flow node dragging uses pointer events, never HTML5
  // `DataTransfer` at all, and the palette/creative/script/mention
  // payloads never carry `'Files'` in `dataTransfer.types`.
  const [osFileDragState, setOsFileDragState] = useState<'none' | 'accept' | 'reject'>('none')
  const [imageAssetDragHover, setImageAssetDragHover] = useState(false)
  const osFileDragHideTimerRef = useRef<number | null>(null)
  const clearOsFileDragHideTimer = useCallback(() => {
    if (osFileDragHideTimerRef.current != null) {
      window.clearTimeout(osFileDragHideTimerRef.current)
      osFileDragHideTimerRef.current = null
    }
  }, [])
  const scheduleOsFileDragHide = useCallback(() => {
    clearOsFileDragHideTimer()
    osFileDragHideTimerRef.current = window.setTimeout(() => setOsFileDragState('none'), 80)
  }, [clearOsFileDragHideTimer])
  useEffect(() => () => clearOsFileDragHideTimer(), [clearOsFileDragHideTimer])

  const onDrop = useCallback(
    (event: ReactDragEvent) => {
      event.preventDefault()
      clearOsFileDragHideTimer()
      setOsFileDragState('none')
      setImageAssetDragHover(false)
      // A. Operating System File Drop — real files from Explorer/Finder/
      // the OS file manager (spec §2 taxonomy A-D). Checked FIRST and
      // independently of the Vistoera-internal allowlist below: an OS
      // file drag's `dataTransfer.types` never contains any of
      // Vistoera's own custom MIME strings, so this can never steal a
      // palette/creative/script/mention drop.
      if (isOsFileDrag(event.dataTransfer)) {
        const files = event.dataTransfer?.files
        if (files && files.length > 0) {
          const flowPos = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY })
          const hit = rf
            .getIntersectingNodes({ x: flowPos.x, y: flowPos.y, width: 1, height: 1 })
            .find((n) => {
              const d = n.data as unknown as FlowNodeData
              return d.nodeType === 'image_asset' && !d.entityId && !d.localUpload
            })
          handleOsMediaFilesDropped(Array.from(files), flowPos, { bindNodeId: hit?.id })
        }
        return
      }
      // B. Canvas-internal Asset/node-type Drag — ALLOWLIST ONLY, never
      // create from a plain text/plain / text/html drag.
      if (!isSupportedCanvasPaneDragOver(event.dataTransfer)) return
      const flowPos = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY })
      // B1. Image Asset Picker thumbnail → place one image_asset at drop.
      if (isImageAssetPickerDrag(event.dataTransfer)) {
        const payload = parseImageAssetDragPayload(event.dataTransfer.getData(PCV_IMAGE_ASSET_DND_MIME))
        if (!payload?.pickerItemId) return
        void handleImageAssetPickerDropped(payload.pickerItemId, payload.filename, flowPos)
        return
      }
      const nodeType = event.dataTransfer.getData(PCV_NODE_TYPE_DND_MIME)
      if (!nodeType) return
      if (nodeType === 'image_asset') {
        openBatchImageAssetPickerRef.current(flowPos)
        return
      }
      const intersectionTarget = rf.getIntersectingNodes({ x: flowPos.x, y: flowPos.y, width: 1, height: 1 }).find((n) => n.type === 'scene')
      addNodeAt(nodeType, flowPos.x, flowPos.y, intersectionTarget?.id)
    },
    [addNodeAt, rf, clearOsFileDragHideTimer, handleOsMediaFilesDropped, handleImageAssetPickerDropped],
  )

  const onPaneDragOver = useCallback(
    (event: ReactDragEvent) => {
      if (isOsFileDrag(event.dataTransfer)) {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
        clearOsFileDragHideTimer()
        setOsFileDragState(dragHasLikelyAcceptedMediaFile(event.dataTransfer) ? 'accept' : 'reject')
        return
      }
      if (!isSupportedCanvasPaneDragOver(event.dataTransfer)) return
      event.preventDefault()
      if (isImageAssetPickerDrag(event.dataTransfer)) {
        event.dataTransfer.dropEffect = 'copy'
        setImageAssetDragHover(true)
        return
      }
      event.dataTransfer.dropEffect = 'move'
    },
    [clearOsFileDragHideTimer],
  )

  const onPaneDragEnter = useCallback(
    (event: ReactDragEvent) => {
      if (isImageAssetPickerDrag(event.dataTransfer)) {
        event.preventDefault()
        setImageAssetDragHover(true)
        return
      }
      if (!isOsFileDrag(event.dataTransfer)) return
      event.preventDefault()
      clearOsFileDragHideTimer()
      setOsFileDragState(dragHasLikelyAcceptedMediaFile(event.dataTransfer) ? 'accept' : 'reject')
    },
    [clearOsFileDragHideTimer],
  )

  const onPaneDragLeave = useCallback(
    (event: ReactDragEvent) => {
      if (isImageAssetPickerDrag(event.dataTransfer)) {
        setImageAssetDragHover(false)
        return
      }
      if (!isOsFileDrag(event.dataTransfer)) return
      scheduleOsFileDragHide()
    },
    [scheduleOsFileDragHide],
  )

  // Block browser-native text/image drags that leak out of node chrome
  // onto the blank canvas (ALLOWLIST sources use data-vistoera-dnd).
  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const onDragStart = (e: DragEvent) => blockNativeCanvasDragStartIfNeeded(e)
    root.addEventListener('dragstart', onDragStart, true)
    return () => root.removeEventListener('dragstart', onDragStart, true)
  }, [])

  // Derive per-render display data: child counts for Scene headers +
  // wiring the two node-level callbacks every custom node needs, without
  // persisting either onto the node's own `data` (spec §15 note above
  // `types.ts`'s `childCount` field).
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const n of nodes) {
      if (n.parentId) counts.set(n.parentId, (counts.get(n.parentId) || 0) + 1)
    }
    return counts
  }, [nodes])

  const collapsedSceneIds = useMemo(
    () => new Set(nodes.filter((n) => n.type === 'scene' && (n.data as unknown as FlowNodeData).collapsed).map((n) => n.id)),
    [nodes],
  )

  /** VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
   * §P2 — the ONE explicit "collapse control" reachable in the product
   * today (`CanvasNodeContextMenu`'s Expand/Collapse row, wired below at
   * `onToggleExpand`) must never leave a node in the contradictory
   * "Pin shows enabled, node renders collapsed" state (§P2 "不要出现...
   * 这种矛盾状态"). Every OTHER path that changes `expandedNodeId`
   * (`onExpandFunctionCardDetail`, opening a different node, a blank-
   * canvas click) is an IMPLICIT/auto-collapse trigger a pinned node
   * must survive untouched (§P1 "之后即使发生...节点仍保持展开") — this
   * function is deliberately the ONLY place that ever clears
   * `pinnedExpanded`. */
  const onToggleDetail = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId)
      const fields = (node?.data as unknown as FlowNodeData | undefined)?.fields
      const wasPinned = isNodePinnedExpanded(fields)
      if (wasPinned) {
        commitNodes((nds) => {
          const next = nds.map((n) => {
            if (n.id !== nodeId) return n
            const d = n.data as unknown as FlowNodeData
            return {
              ...n,
              data: { ...d, fields: { ...d.fields, [PINNED_EXPANDED_FIELD]: false } } as unknown as Record<string, unknown>,
            }
          })
          scheduleFromState(next, edgesRef.current)
          return next
        })
      }
      setExpandedNodeId((prev) => {
        if (prev === nodeId || wasPinned) {
          bumpPlaybackReset()
          return null
        }
        if (prev && prev !== nodeId) bumpPlaybackReset()
        setSelectedNodeId(nodeId)
        return nodeId
      })
    },
    [bumpPlaybackReset, commitNodes, scheduleFromState],
  )

  // Unified "输入素材 / Input Materials" (P0 §9–§15, generalizing P1's
  // image-asset-only `referenceNodesByGenerateNodeId`) — re-derived
  // fresh from the live `edges`/`nodes` graph every render, NEVER cached
  // anywhere, so an edge add/remove, an upstream `image_asset`'s "更换
  // 图片"/`text_asset`'s edit, a `generate_image`/`generate_video`
  // upstream completing its own run (gen-to-gen, §7/§8), or a page
  // reload all show up here with zero extra plumbing. Every entry knows
  // its own `edgeId` + `sourceLabel` (§15 source traceability).
  const inputMaterialsByNodeId = useMemo(() => {
    const map = new Map<string, InputMaterial[]>()
    for (const e of edges) {
      const targetHandle = e.targetHandle ?? null
      // VistoeraTASK-00144 — a `generate_image` reference edge now
      // carries its own `::<edgeId>`-scoped handle (never the bare
      // literal); every OTHER consumer of this generic input handle
      // (generate_text/generate_video/generate_tryon) still uses the
      // bare id unchanged, so widening this check to
      // `isImageReferenceAssetHandle` is a pure superset — it must never
      // narrow to fewer materials than before.
      if (targetHandle !== null && !isImageReferenceAssetHandle(targetHandle)) continue
      const sourceNode = nodes.find((n) => n.id === e.source)
      if (!sourceNode) continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const sType = sData.nodeType
      const sourceLabel = (sData.fields?.label as string | undefined) || getNodeDefinition(sType).label
      const base = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, inputRole: 'GENERAL_CONTEXT' as const }
      let material: InputMaterial | null = null
      if (sType === 'image_asset' && sData.entityId) {
        material = { ...base, kind: 'IMAGE', ready: true, entityId: sData.entityId }
      } else if (sType === 'text_asset') {
        material = { ...base, kind: 'TEXT', ready: true, text: (sData.fields?.text as string | undefined) || '' }
      } else if (sType === 'generate_image') {
        const outputId = sData.fields?.generatedOutputId as string | undefined
        const siteAssetId = sData.fields?.outputAssetId as string | undefined
        material = {
          ...base,
          kind: 'IMAGE',
          ready: !!(outputId || siteAssetId),
          entityId: siteAssetId || undefined,
          generatedOutputId: outputId,
        }
      } else if (sType === 'generate_video') {
        const url = sData.fields?.generatedVideoUrl as string | undefined
        material = { ...base, kind: 'VIDEO', ready: !!url, url }
      } else if (sType === 'video_asset' && sData.entityId) {
        const snapshot = sData.fields?.videoAssetSnapshot as VideoAssetBindSnapshot | undefined
        const url = resolveBoundVideoPlaybackUrl(workspaceId, sData.entityId, snapshot)
        material = { ...base, kind: 'VIDEO', ready: !!url, url }
      } else if (sType === 'generate_voice') {
        const assetId = sData.fields?.audioAssetId as string | undefined
        const durationMs = sData.fields?.durationMs as number | undefined
        const ready =
          sData.fields?.status === 'COMPLETED' && !!assetId && !sData.fields?.needsRegeneration
        material = {
          ...base,
          kind: 'AUDIO',
          ready,
          durationSeconds: durationMs ? durationMs / 1000 : undefined,
        }
      } else if (sType === 'generate_tryon') {
        const siteAssetId = sData.fields?.outputAssetId as string | undefined
        const outputId = sData.fields?.generatedOutputId as string | undefined
        material = {
          ...base,
          kind: 'IMAGE',
          ready: !!(siteAssetId || outputId),
          entityId: siteAssetId,
          generatedOutputId: outputId,
        }
      } else if (sType === 'generate_text') {
        const currentResult = sData.fields?.currentResult as { text?: string } | null | undefined
        const text = typeof currentResult?.text === 'string' ? currentResult.text : ''
        material = { ...base, kind: 'TEXT', ready: Boolean(text.trim()), text }
      } else if (sType === 'generate_voice_template') {
        const templateName = (sData.fields?.templateName as string | undefined) || sourceLabel
        material = { ...base, kind: 'TEXT', ready: false, text: templateName }
      } else if (sType === 'voice' || sType === 'music_sfx') {
        // Canvas Unified Video Generation Node P0 (spec §21) — a plain
        // Source/Asset node (like `image_asset`/`text_asset`), always
        // "ready" the moment the edge exists; no real waveform/duration
        // backend this round (spec §21 "不要为了这轮新造复杂 waveform
        // engine").
        material = { ...base, kind: 'AUDIO', ready: true }
      }
      if (!material) continue
      const list = map.get(e.target) || []
      list.push(material)
      map.set(e.target, list)
    }
    return map
  }, [edges, nodes, workspaceId])

  // VistoeraTASK-00187 §八/§九 — extracted to the canonical, unit-tested
  // `canvasVoiceTemplateBinding.buildConnectedVoiceTemplateMap` (reads
  // ONLY already-hydrated in-memory `nodes`/`edges`, zero network calls)
  // instead of an inline copy of the same resolution logic, so Canvas
  // edge-derived binding stays the single source of truth.
  const connectedVoiceTemplateByNodeId = useMemo(
    () => buildConnectedVoiceTemplateMap(nodes, edges, getNodeDefinition('generate_voice_template').label),
    [edges, nodes],
  )

  const connectedTextByNodeId = useMemo(() => {
    const map = new Map<string, { text: string; sourceLabel: string; ready: boolean }>()
    for (const e of edges) {
      // VistoeraTASK-00074 — accepts the legacy shared `GENERIC_INPUT_HANDLE`
      // (pre-existing persisted edges) AND the new dedicated
      // `AUDIO_TEXT_INPUT_HANDLE` (edges created from now on — see
      // `onConnect`'s targetHandle override above).
      const targetHandle = e.targetHandle ?? null
      if (targetHandle !== null && targetHandle !== GENERIC_INPUT_HANDLE && targetHandle !== AUDIO_TEXT_INPUT_HANDLE) {
        continue
      }
      const sourceNode = nodes.find((n) => n.id === e.source)
      const targetNode = nodes.find((n) => n.id === e.target)
      if (!sourceNode || !targetNode) continue
      const tData = targetNode.data as unknown as FlowNodeData
      if (tData.nodeType !== 'generate_voice') continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const sType = sData.nodeType
      if (sType !== 'text_asset' && sType !== 'generate_text') continue
      const sourceLabel = (sData.fields?.label as string | undefined) || getNodeDefinition(sType).label
      // Forensic finding (VistoeraTASK-00074 P11/P12) — a `generate_text`
      // source previously always resolved to an empty, permanently
      // `ready: false` entry here (its real output lives at
      // `fields.currentResult.text`, never read by the old inline
      // check), making a Generate Text → Generate Audio edge a visually
      // real but functionally dead ("假连线") connection — the EXACT
      // failure mode `extractTextFromTextSourceNode` (already the
      // canonical TEXT-source-node reader used elsewhere in this file)
      // exists to prevent. Reused verbatim, never a second copy.
      const text = extractTextFromTextSourceNode(sourceNode, sType)
      map.set(e.target, { text, sourceLabel, ready: Boolean(text) })
    }
    return map
  }, [edges, nodes])

  const connectedVideoForMotionReconByNodeId = useMemo(() => {
    const map = new Map<string, { videoAssetId: string; label: string; ready: boolean }>()
    for (const e of edges) {
      if (e.targetHandle && e.targetHandle !== 'video-in') continue
      if (e.sourceHandle && e.sourceHandle !== 'video-out') continue
      const sourceNode = nodes.find((n) => n.id === e.source)
      const targetNode = nodes.find((n) => n.id === e.target)
      if (!sourceNode || !targetNode) continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const tData = targetNode.data as unknown as FlowNodeData
      if (sData.nodeType !== 'video_asset' || tData.nodeType !== 'motion_reconstruction') continue
      const videoAssetId = sData.entityId as string | undefined
      const label = (sData.fields?.label as string | undefined) || getNodeDefinition(sData.nodeType).label
      map.set(e.target, {
        videoAssetId: videoAssetId || '',
        label,
        ready: !!videoAssetId,
      })
    }
    return map
  }, [edges, nodes])

  const connectedTryOnByNodeId = useMemo(() => {
    const map = new Map<string, { person?: ConnectedTryOnImage; garment?: ConnectedTryOnImage }>()
    for (const e of edges) {
      if (!isTryOnInputHandle(e.targetHandle)) continue
      const sourceNode = nodes.find((n) => n.id === e.source)
      const targetNode = nodes.find((n) => n.id === e.target)
      if (!sourceNode || !targetNode) continue
      const sData = sourceNode.data as unknown as FlowNodeData
      const tData = targetNode.data as unknown as FlowNodeData
      if (tData.nodeType !== 'generate_tryon') continue
      const sType = sData.nodeType
      const sourceLabel = (sData.fields?.label as string | undefined) || getNodeDefinition(sType).label
      let slot: ConnectedTryOnImage | null = null
      if (sType === 'image_asset' && sData.entityId) {
        slot = { label: sourceLabel, ready: true, entityId: sData.entityId as string }
      } else if (sType === 'generate_image') {
        const outputId = sData.fields?.generatedOutputId as string | undefined
        slot = { label: sourceLabel, ready: !!outputId, generatedOutputId: outputId }
      } else if (sType === 'generate_tryon') {
        const siteAssetId = sData.fields?.outputAssetId as string | undefined
        const outputId = sData.fields?.generatedOutputId as string | undefined
        if (siteAssetId) slot = { label: sourceLabel, ready: true, tryOnOutputAssetId: siteAssetId, tryOnOutputId: outputId }
        else if (outputId) slot = { label: sourceLabel, ready: true, tryOnOutputId: outputId }
      }
      if (!slot) continue
      const entry = map.get(e.target) || {}
      if (e.targetHandle === TRYON_PERSON_HANDLE) entry.person = slot
      else if (e.targetHandle === TRYON_GARMENT_HANDLE) entry.garment = slot
      map.set(e.target, entry)
    }
    return map
  }, [edges, nodes])

  // Layer 2 — directional edge flow highlight. Canonical edges stay visible;
  // direct inbound/outbound neighbors of the selected node receive animated
  // dashed styles derived from graph topology (never screen position).
  // §35 Multi-select policy — single-node selection drives flow colors; multi-
  // select clears directional highlight (drag-box bypasses onNodeClick).
  const onSelectionChange = useCallback(
    (params: { nodes: Node[] }) => {
      if (params.nodes.length > 1) {
        if (expandedNodeId) bumpPlaybackReset()
        setSelectedNodeId(null)
        setExpandedNodeId(null)
        setDragTempCollapsedNodeId(null)
      }
    },
    [bumpPlaybackReset, expandedNodeId],
  )

  const displayEdges = useMemo(
    () =>
      edges.map((e) => ({
        ...e,
        className: resolveEdgeDisplayClass(e, selectedNodeId),
      })),
    [edges, selectedNodeId],
  )

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => {
        const hidden = !!n.parentId && collapsedSceneIds.has(n.parentId)
        const data = n.data as unknown as FlowNodeData
        const isCollapsedScene = n.type === 'scene' && data.collapsed
        return {
          ...n,
          hidden,
          selected: n.id === selectedNodeId,
          style: isCollapsedScene ? { ...(n.style || {}), height: 48 } : n.style,
          data: {
            ...data,
            workspaceId,
            projectId,
            siteId,
            projectTitle,
            childCount: childCounts.get(n.id) || 0,
            onToggleCollapsed,
            onChangeFields,
            onChangeEntity,
            onUnbindRelatedAsset,
            onBindRelatedAssetsFromPicker,
            onMaterializeImageAssets: materializeImageAssetSelections,
            onMaterializeAndConnectImageReferences,
            onChangeLatestRunId,
            onRequestDelete: onRequestDeleteNode,
            // §P1/§P2 — a `pinnedExpanded` node stays expanded through
            // every IMPLICIT collapse trigger (blank-canvas click,
            // another node opening, generation completing, …) since
            // NONE of those ever touch `fields.pinnedExpanded` — only
            // `onToggleDetail`'s explicit-collapse branch above does.
            isDetailExpanded: n.id === expandedNodeId || isNodePinnedExpanded(data.fields),
            isDragTempCollapsed: n.id === dragTempCollapsedNodeId,
            isNodeDragging: n.id === draggingNodeId,
            playbackResetKey,
            onToggleDetail,
            onExpandFunctionCardDetail,
            inputMaterials:
              n.type === 'generator' ||
              n.type === 'video_generator' ||
              n.type === 'audio_generator' ||
              n.type === 'text_generator' ||
              data.nodeType === 'generate_text'
                ? inputMaterialsByNodeId.get(n.id) || []
                : undefined,
            keyframeAssignment:
              n.type === 'video_generator'
                ? keyframeAssignmentByNodeId.get(n.id) || { middle: [], middleByEntryId: new Map<string, KeyframeSlotMaterial>() }
                : undefined,
            dialogueAudioAssignment:
              n.type === 'video_generator' ? dialogueAudioAssignmentByNodeId.get(n.id) || new Map() : undefined,
            structuredAssetAssignment:
              n.type === 'video_generator' ? structuredAssetAssignmentByNodeId.get(n.id) || new Map() : undefined,
            referenceVideoSlot:
              data.nodeType === 'generate_video' ? videoReferenceVideoAssignmentByNodeId.get(n.id) || null : undefined,
            audioReferenceSlots:
              data.nodeType === 'generate_video' ? videoAudioReferenceAssignmentByNodeId.get(n.id) || [] : undefined,
            onCreateVideoKeyframeImageNode: data.nodeType === 'generate_video' ? onCreateVideoKeyframeImageNode : undefined,
            onCreateVideoPromptEnhancerNode:
              data.nodeType === 'generate_video' ? onCreateVideoPromptEnhancerNode : undefined,
            onVideoPromptEnhancementResult:
              data.nodeType === 'generate_text' ? onVideoPromptEnhancementResult : undefined,
            onResolveVideoPromptEnhancementContext:
              data.nodeType === 'generate_text' ? onResolveVideoPromptEnhancementContext : undefined,
            onPropagateVideoNodeProductionFormat:
              data.nodeType === 'generate_video' ? onPropagateVideoNodeProductionFormat : undefined,
            onCreateVideoDialogueAudioNode: data.nodeType === 'generate_video' ? onCreateVideoDialogueAudioNode : undefined,
            onFocusVideoKeyframeSourceNode: data.nodeType === 'generate_video' ? onFocusVideoKeyframeSourceNode : undefined,
            onImportVideoKeyframeImage: data.nodeType === 'generate_video' ? onImportVideoKeyframeImage : undefined,
            onRemoveVideoKeyframeImage: data.nodeType === 'generate_video' ? onRemoveVideoKeyframeImage : undefined,
            onVideoProductionFormatChanged:
              data.nodeType === 'generate_video' ? onVideoProductionFormatChanged : undefined,
            onCreateVideoFromShot: data.nodeType === 'storyboard_generation' ? onCreateVideoFromShot : undefined,
            connectedVoiceTemplate:
              data.nodeType === 'generate_voice' ? connectedVoiceTemplateByNodeId.get(n.id) || null : undefined,
            pendingVoiceTemplateCandidateExists:
              data.nodeType === 'generate_voice'
                ? (() => {
                    const pending = (data.fields?.pendingVoiceTemplateCandidateNodeId as string | null | undefined) ?? null
                    return pending ? nodes.some((candidate) => candidate.id === pending) : false
                  })()
                : undefined,
            connectedText: data.nodeType === 'generate_voice' ? connectedTextByNodeId.get(n.id) || null : undefined,
            onCreateVoiceTemplateNode: data.nodeType === 'generate_voice' ? onCreateVoiceTemplateNode : undefined,
            onQuickCreateGenerateVideo: data.nodeType === 'generate_voice' ? onQuickCreateGenerateVideo : undefined,
            onConfirmCandidateVoiceTemplate:
              data.nodeType === 'generate_voice_template' ? onConfirmCandidateVoiceTemplate : undefined,
            onDisconnectMaterial,
            onSelectNode,
            onRequestEditText,
            onUpdateTextAsset,
            onUpdateTextAssetPresentation,
            onOpenAssetPicker,
            onOpenVideoAssetPicker,
            onOpenAudioAssetPicker,
            onOpenAudioEditor: data.nodeType === 'audio_asset' ? onOpenAudioEditor : undefined,
            onCommitAudioTrim: data.nodeType === 'audio_asset' ? onCommitAudioTrim : undefined,
            onRetryAudioTrim: data.nodeType === 'audio_asset' ? onRetryAudioTrim : undefined,
            onOpenTryOnAssetPicker,
            onDuplicateNode,
            // VistoeraTASK-00440 — extended from `image_asset`-only
            // (TASK-00300) to also cover `generate_image` ("生成图片")
            // result nodes: per product decision §12, EVERY Canvas-hosted
            // Image Editor entry (any node that can mount
            // `CanvasImageEditorOverlay`, see `canvasNodeTypes.ts` for the
            // exhaustive set — today just these two families) must get the
            // SAME "另存编辑图片 → new sibling Canvas node" behavior, not a
            // one-off special case on a single button.
            onSaveEditedImageAsNewAsset:
              data.nodeType === 'image_asset' || data.nodeType === 'generate_image'
                ? onSaveEditedImageAsNewAsset
                : undefined,
            // VistoeraTASK-00134 — Canvas Global OS File Drop "Retry"/"Remove"
            // for a still-uploading/failed local placeholder (only ever set
            // on an `image_asset` node, see `localUpload`'s own doc comment).
            onRetryLocalImageUpload: data.nodeType === 'image_asset' ? retryLocalImageUpload : undefined,
            onRemoveLocalImageUpload: data.nodeType === 'image_asset' ? removeLocalImageUpload : undefined,
            onOsImageFilesDroppedOnNode:
              data.nodeType === 'image_asset' ? handleOsImageFilesDroppedOnNode : undefined,
            onRetryLocalAudioUpload: data.nodeType === 'audio_asset' ? retryLocalMediaUpload : undefined,
            onRemoveLocalAudioUpload: data.nodeType === 'audio_asset' ? removeLocalMediaUpload : undefined,
            onRetryLocalVideoUpload: data.nodeType === 'video_asset' ? retryLocalMediaUpload : undefined,
            onRemoveLocalVideoUpload: data.nodeType === 'video_asset' ? removeLocalMediaUpload : undefined,
            onQuickGenerate,
            onQuickCreateGenerateAudio:
              data.nodeType === 'generate_voice_template' ? onQuickCreateGenerateAudio : undefined,
            onQuickCreateMotionReconstruction:
              data.nodeType === 'video_asset' ? onQuickCreateMotionReconstruction : undefined,
            onQuickCreateGenerateVideoFromMotion:
              data.nodeType === 'motion_reconstruction' ? onQuickCreateGenerateVideoFromMotion : undefined,
            connectedVideo:
              data.nodeType === 'motion_reconstruction'
                ? connectedVideoForMotionReconByNodeId.get(n.id) || null
                : undefined,
            connectedTryOnPerson:
              data.nodeType === 'generate_tryon'
                ? connectedTryOnByNodeId.get(n.id)?.person || null
                : undefined,
            connectedTryOnGarment:
              data.nodeType === 'generate_tryon'
                ? connectedTryOnByNodeId.get(n.id)?.garment || null
                : undefined,
            onSplitGenerationOutputs,
            onCommitImageGenerationBatchFanOut,
            onSyncImageGenerationBatchFromTask,
            onPrepareImageGenerationResultSlots,
            onBindImageGenerationBatchRun,
            onSettleImageGenerationBatchSubmitFailure,
            onDuplicateForRegenerate,
            imageReferenceCapability,
            imageGenerationConfig,
            videoGenerationConfig,
            onGenerateCreativeIdeation: data.nodeType === 'project_start' ? onGenerateCreativeIdeation : undefined,
            onExpandCreativeCandidate: data.nodeType === 'creative_candidate' ? onExpandCreativeCandidate : undefined,
            onSetCreativeCandidateState: data.nodeType === 'creative_candidate' ? onSetCreativeCandidateState : undefined,
            onBriefChanged: data.nodeType === 'project_start' ? onProjectStartBriefChanged : undefined,
            onGenerateCreative: data.nodeType === 'generate_creative' ? onGenerateCreative : undefined,
            onDivergeCreative: data.nodeType === 'generate_creative' ? onDivergeCreative : undefined,
            onGenerateCreativeBriefChanged: data.nodeType === 'generate_creative' ? onGenerateCreativeBriefChanged : undefined,
            onQuickCreateScriptFromCreative: data.nodeType === 'generate_creative' ? onQuickCreateScriptFromCreative : undefined,
            onQuickCreateScriptFromTextAsset: data.nodeType === 'text_asset' ? onQuickCreateScriptFromTextAsset : undefined,
            onQuickCreateGenerateTextFromTextAsset:
              data.nodeType === 'text_asset' ? onQuickCreateGenerateTextFromTextAsset : undefined,
            onQuickCreateGenerateImageFromTextAsset:
              data.nodeType === 'text_asset' ? onQuickCreateGenerateImageFromTextAsset : undefined,
            onQuickCreateGenerateVideoFromTextAsset:
              data.nodeType === 'text_asset' ? onQuickCreateGenerateVideoFromTextAsset : undefined,
            onLinkCreativeContext: data.nodeType === 'script_generation' ? onLinkCreativeContext : undefined,
            onGenerateScript: data.nodeType === 'script_generation' ? onGenerateScript : undefined,
            onDivergeScript: data.nodeType === 'script_generation' ? onDivergeScript : undefined,
            creativeContextLiveFingerprint:
              data.nodeType === 'script_generation' ? liveCreativeContextFingerprint(nodes, edges, n.id) : undefined,
            onQuickCreateStoryboardFromScript:
              data.nodeType === 'script_generation' ? onQuickCreateStoryboardFromScript : undefined,
            onLinkScriptContext: data.nodeType === 'storyboard_generation' ? onLinkScriptContext : undefined,
            onGenerateStoryboard: data.nodeType === 'storyboard_generation' ? onGenerateStoryboard : undefined,
            onRestoreSupersededStoryboard:
              data.nodeType === 'storyboard_generation' ? onRestoreSupersededStoryboard : undefined,
            onDivergeStoryboard: data.nodeType === 'storyboard_generation' ? onDivergeStoryboard : undefined,
            scriptContextLiveFingerprint:
              data.nodeType === 'storyboard_generation' ? liveScriptContextFingerprint(nodes, edges, n.id) : undefined,
            onOpenOrCreateShotVideoNode:
              data.nodeType === 'storyboard_generation' ? onOpenOrCreateShotVideoNode : undefined,
            onInvalidateStoryboardProductionFormat:
              data.nodeType === 'storyboard_generation' ? onInvalidateStoryboardProductionFormat : undefined,
            onRunVideoProductionAssembly:
              data.nodeType === 'storyboard_generation' ? onRunVideoProductionAssembly : undefined,
            videoProductionUnits:
              data.nodeType === 'storyboard_generation' ? videoProductionUnitsByNodeId.get(n.id) : undefined,
            hasStoryboardKeyframeOutputs:
              data.nodeType === 'storyboard_generation' ? storyboardKeyframeOutputByNodeId.get(n.id) === true : undefined,
          } as unknown as Record<string, unknown>,
        }
      }),
    [
      nodes,
      collapsedSceneIds,
      childCounts,
      selectedNodeId,
      onToggleCollapsed,
      onChangeFields,
      onChangeEntity,
      onUnbindRelatedAsset,
      onBindRelatedAssetsFromPicker,
      materializeImageAssetSelections,
      onMaterializeAndConnectImageReferences,
      onChangeLatestRunId,
      onRequestDeleteNode,
      retryLocalImageUpload,
      removeLocalImageUpload,
      retryLocalMediaUpload,
      removeLocalMediaUpload,
      workspaceId,
      siteId,
      projectTitle,
      expandedNodeId,
      dragTempCollapsedNodeId,
      draggingNodeId,
      playbackResetKey,
      onToggleDetail,
      onExpandFunctionCardDetail,
      inputMaterialsByNodeId,
      connectedVoiceTemplateByNodeId,
      connectedTextByNodeId,
      connectedVideoForMotionReconByNodeId,
      connectedTryOnByNodeId,
      onOpenTryOnAssetPicker,
      onOpenAudioAssetPicker,
      onCreateVoiceTemplateNode,
      onQuickCreateGenerateVideo,
      onConfirmCandidateVoiceTemplate,
      onQuickCreateMotionReconstruction,
      onQuickCreateGenerateVideoFromMotion,
      keyframeAssignmentByNodeId,
      dialogueAudioAssignmentByNodeId,
      structuredAssetAssignmentByNodeId,
      videoReferenceVideoAssignmentByNodeId,
      onCreateVideoKeyframeImageNode,
      onCreateVideoPromptEnhancerNode,
      onVideoPromptEnhancementResult,
      onResolveVideoPromptEnhancementContext,
      onCreateVideoDialogueAudioNode,
      onFocusVideoKeyframeSourceNode,
      onPropagateVideoNodeProductionFormat,
      onImportVideoKeyframeImage,
      onRemoveVideoKeyframeImage,
      onVideoProductionFormatChanged,
      onCreateVideoFromShot,
      onDisconnectMaterial,
      onSelectNode,
      onRequestEditText,
      onUpdateTextAsset,
      onUpdateTextAssetPresentation,
      onOpenAssetPicker,
      onOpenVideoAssetPicker,
      onOpenAudioAssetPicker,
      onOpenAudioEditor,
      onCommitAudioTrim,
      onRetryAudioTrim,
      onDuplicateNode,
      onSaveEditedImageAsNewAsset,
      onQuickGenerate,
      onQuickCreateGenerateAudio,
      onSplitGenerationOutputs,
      onCommitImageGenerationBatchFanOut,
      onSyncImageGenerationBatchFromTask,
      onPrepareImageGenerationResultSlots,
      onBindImageGenerationBatchRun,
      onSettleImageGenerationBatchSubmitFailure,
      onDuplicateForRegenerate,
      imageReferenceCapability,
      imageGenerationConfig,
      videoGenerationConfig,
      onGenerateCreativeIdeation,
      onExpandCreativeCandidate,
      onSetCreativeCandidateState,
      onProjectStartBriefChanged,
      onGenerateCreative,
      onDivergeCreative,
      onGenerateCreativeBriefChanged,
      onQuickCreateScriptFromCreative,
      onQuickCreateScriptFromTextAsset,
      onQuickCreateGenerateTextFromTextAsset,
      onQuickCreateGenerateImageFromTextAsset,
      onQuickCreateGenerateVideoFromTextAsset,
      onLinkCreativeContext,
      onGenerateScript,
      onDivergeScript,
      onQuickCreateStoryboardFromScript,
      onLinkScriptContext,
      onGenerateStoryboard,
      onRestoreSupersededStoryboard,
      onDivergeStoryboard,
      onOpenOrCreateShotVideoNode,
      onInvalidateStoryboardProductionFormat,
      onRunVideoProductionAssembly,
      videoProductionUnitsByNodeId,
      storyboardKeyframeOutputByNodeId,
      edges,
    ],
  )

  // A node hidden inside a just-collapsed Scene, or removed outright,
  // must never leave a "floating" detail panel behind with nothing
  // visible for it to attach to (spec §3 "collapse on hide").
  useEffect(() => {
    if (!expandedNodeId) return
    const target = displayNodes.find((n) => n.id === expandedNodeId)
    if (!target || target.hidden) setExpandedNodeId(null)
  }, [displayNodes, expandedNodeId])

  const expandedNode = useMemo(() => nodes.find((n) => n.id === expandedNodeId) || null, [nodes, expandedNodeId])
  const expandedNodeDto = useMemo(() => (expandedNode ? flowNodeToDto(expandedNode) : null), [expandedNode])

  // ---- VistoeraTASK-00556 READ_ONLY layers (see canvasReadOnly.ts) -------
  const readOnlyDisplayNodes = useMemo(
    () =>
      readOnly
        ? displayNodes.map((n) => ({ ...n, data: lockCanvasNodeData(n.data as Record<string, unknown>) }))
        : displayNodes,
    [displayNodes, readOnly],
  )
  const onNodesChangeReadOnly = useCallback(
    (changes: NodeChange[]) => {
      const allowed = filterReadOnlyNodeChanges(changes)
      if (allowed.length > 0) onNodesChange(allowed)
    },
    [onNodesChange],
  )
  const onEdgesChangeReadOnly = useCallback(
    (changes: EdgeChange[]) => {
      const allowed = filterReadOnlyEdgeChanges(changes)
      if (allowed.length > 0) onEdgesChange(allowed)
    },
    [onEdgesChange],
  )
  const readOnlyPreventDefault = useCallback((event: { preventDefault: () => void }) => {
    event.preventDefault()
  }, [])
  const readOnlyDeniedMessage = t('contentProjects:team.canvas.writeDenied')
  useEffect(() => {
    if (!readOnly) return
    setCanvasReadOnlyDeniedNotifier(() => message.info(readOnlyDeniedMessage))
    setCanvasReadOnlyNetworkFence({ workspaceId, projectId, message: readOnlyDeniedMessage })
    const root = containerRef.current
    const uninstallDom = root ? installCanvasReadOnlyDomGuard(root) : null
    return () => {
      uninstallDom?.()
      setCanvasReadOnlyNetworkFence(null)
      setCanvasReadOnlyDeniedNotifier(null)
    }
  }, [readOnly, workspaceId, projectId, readOnlyDeniedMessage])

  return (
    <div
      className={`pcv-root${isStructuralDragging ? ' is-structural-dragging' : ''}`}
      ref={containerRef}
      data-save-status={saveStatus}
      data-canvas-structural-dragging={isStructuralDragging ? 'true' : 'false'}
      data-canvas-node-dragging={isStructuralDragging ? 'true' : 'false'}
    >
      {readOnly ? null : (
        <LeftLibraryPanel workspaceId={workspaceId} contentProjectId={projectId} onAddNode={addNodeFromLibrary} />
      )}
      {/* VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1 §21 —
       * SaaS Shell overlay, deliberately a sibling of `.pcv-canvas-area`
       * (never inside `<ReactFlow>`) and CSS `position: fixed` — see
       * `global-account-entry.css` for why this never moves on
       * pan/zoom/fitView. */}
      <GlobalAccountEntry workspaceId={workspaceId} contentProjectId={projectId} />

      <div className="pcv-canvas-area" data-pcv-canvas-overlay-root data-testid="pcv-canvas-area">
        {/* TASK-00635 — auth /me failure: fail-closed create surfaces + retry */}
        <AuthMatrixLoadBanner workspaceId={workspaceId} />
        <div className="pcv-toolbar">
          <div className="pcv-toolbar-group pcv-toolbar-group--nav">
            <CanvasTopBar
              workspaceId={workspaceId}
              projectId={projectId}
              projectTitle={projectTitle}
              onRenameProject={onRenameProject}
              onDuplicateProject={onDuplicateProject}
              onCreateProject={onCreateProject}
              onDeleteProject={onDeleteProject}
              readOnly={readOnly}
            />
            {readOnly ? (
              <div className="pcv-readonly-banner" role="status" data-testid="pcv-readonly-banner">
                <span className="pcv-readonly-banner-title">
                  {readOnlyCreatorName
                    ? t('contentProjects:team.canvas.readOnlyByCreator', { name: readOnlyCreatorName })
                    : t('contentProjects:team.canvas.readOnlyBanner')}
                </span>
              </div>
            ) : null}
          </div>
          {/* VISTOERA_TASK-00083 — tenant Canvas top actions removed:
           * "添加节点" and "插入示例工作流" are redundant with the left
           * node library and caused a loading flash (ProductionCanvasPage
           * mounts this board with a zero-node placeholder while
           * `canvasHydrating`, which made the sample-workflow CTA appear
           * before real data arrived). Node creation stays on the left
           * library + pane context menu; QuickAddModal remains reachable
           * via Ctrl/Cmd+Shift+A only (no tenant header button). Sample
           * workflow seeding stays available via the backend API for
           * fixtures/E2E — just not as a tenant header affordance. */}
        </div>

        <ReactFlow
          nodes={readOnly ? readOnlyDisplayNodes : displayNodes}
          edges={displayEdges}
          nodeTypes={NODE_TYPES}
          onNodesChange={readOnly ? onNodesChangeReadOnly : onNodesChange}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          edgesReconnectable={!readOnly}
          deleteKeyCode={readOnly || blockingOverlayOpen ? null : 'Backspace'}
          onBeforeDelete={onBeforeDeleteGuard}
          onEdgesChange={readOnly ? onEdgesChangeReadOnly : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onConnectStart={readOnly ? undefined : onConnectStart}
          onConnectEnd={readOnly ? undefined : onConnectEnd}
          isValidConnection={readOnly ? () => false : isValidConnection}
          // Semantic Drop Zones (§4–§9) replace precise 8–10px ports with
          // large card-sized areas; a big EFFECTIVE snap radius (default
          // is 20px — plenty for a small dot, nowhere near enough for a
          // ~130×90px zone) is what actually makes "anywhere in the zone
          // snaps to it" true, since React Flow's own closest-handle
          // matching is a fixed-radius circle around each Handle's
          // measured center (`GenerateVideoNode.tsx`'s zone Handles are
          // sized/positioned to fill their zone, so that center IS the
          // zone's own visual center). A global increase — not a
          // per-node one, React Flow has no such prop — but strictly a
          // *larger forgiving catch radius* for every handle everywhere,
          // never a source of accidental cross-node connections (the
          // closest candidate still always wins, and `isValidConnection`
          // above still rejects anything type-incompatible either way).
          connectionRadius={72}
          // VISTOERA_CANVAS_GLOBAL_NODE_POINTER_INTERACTION_CONTRACT_V1 (V9) —
          // media viewports (video/skeleton preview) are drag surfaces now
          // (see `nodeInteractionContract.ts`), so they need a real
          // click-vs-drag gesture threshold instead of a blanket `nodrag`
          // that blocked dragging by the video entirely. `nodeDragThreshold`
          // is how far the pointer must travel before the node visually
          // starts moving; `nodeClickDistance` is how far it may travel
          // between pointerdown/up and still fire a click. Keeping both at
          // the same value means: <6px of travel -> click/dblclick/hover
          // behave exactly as before, >=6px -> node drag, with no per-node
          // hand-rolled pointer tracking required.
          nodeDragThreshold={6}
          nodeClickDistance={6}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onSelectionDragStart={onSelectionDragStart}
          onSelectionDragStop={onSelectionDragStop}
          onSelectionChange={onSelectionChange}
          onPaneClick={onPaneClick}
          onNodeClick={onNodeClick}
          onPaneContextMenu={readOnly ? readOnlyPreventDefault : onPaneContextMenu}
          onNodeContextMenu={readOnly ? (readOnlyPreventDefault as NodeMouseHandler) : onNodeContextMenu}
          onMove={onMove}
          onMoveEnd={onMoveEnd}
          onDrop={readOnly ? readOnlyPreventDefault : onDrop}
          onDragOver={readOnly ? readOnlyPreventDefault : onPaneDragOver}
          onDragEnter={readOnly ? readOnlyPreventDefault : onPaneDragEnter}
          onDragLeave={readOnly ? undefined : onPaneDragLeave}
          defaultViewport={viewport}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          selectionOnDrag
          panOnScroll
          fitViewOptions={{ padding: 0.3 }}
          colorMode={effectiveTheme}
        >
          {/* VISTOERA_CANVAS_GLOBAL_BRAND_BREATH_V2 — portaled into
           * `.react-flow__renderer` at z-index 0 (below edges/nodes,
           * above grid). Must be a ReactFlow child so the renderer exists
           * when the portal hook runs. */}
          <CanvasGlobalBrandBreath />
          {/* `color` accepts any CSS color value, including a `var(...)`
           * reference (spec §39 — Canvas grid must follow the same
           * light/dark tokens as everything else, not a hardcoded hex). */}
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="var(--pcv-grid-dot)"
            bgColor="transparent"
          />
          <Controls
            showInteractive={false}
            className="pcv-controls"
            position="bottom-right"
            style={{
              left: 'auto',
              right: 'calc(var(--pcv-minimap-inset) + var(--pcv-minimap-width) + var(--pcv-controls-minimap-gap))',
              bottom: 'var(--pcv-minimap-inset)',
            }}
          />
          <MiniMap className="pcv-minimap" pannable zoomable nodeColor={minimapNodeColor} />
        </ReactFlow>

        {/* VistoeraTASK-00134 — Canvas Global OS File Drop visual
         * feedback. Sibling of `<ReactFlow>` (never a child — same
         * pan/zoom/fitView-immune convention `GlobalAccountEntry` above
         * already uses), `pointer-events: none` via CSS so it never
         * intercepts the drag/drop events themselves. */}
        {osFileDragState !== 'none' ? (
          <div
            className={`pcv-os-file-drop-overlay${osFileDragState === 'reject' ? ' pcv-os-file-drop-overlay--reject' : ''}`}
            data-testid="pcv-os-file-drop-overlay"
          >
            <span className="pcv-os-file-drop-overlay-pill">
              {osFileDragState === 'reject' ? t('canvasFileDrop.dropHintReject') : t('canvasFileDrop.dropHint')}
            </span>
          </div>
        ) : null}

        {imageAssetDragHover && osFileDragState === 'none' ? (
          <div className="pcv-os-file-drop-overlay pcv-image-asset-drop-overlay" data-testid="pcv-image-asset-drop-overlay">
            <span className="pcv-os-file-drop-overlay-pill">{t('canvasFileDrop.imageAssetDropHint')}</span>
          </div>
        ) : null}

        {expandedNode && expandedNodeDto && nodeTypeSupportsDetailPanel(expandedNode.type || 'entity') ? (
          (() => {
            const def = getNodeDefinition(expandedNodeDto.node_type)
            const DetailRenderer = getNodeDetailRenderer(expandedNodeDto.node_type)
            const label = (expandedNodeDto.data?.label as string | undefined) || def.label
            return (
              <NodeDetailShell
                nodeId={expandedNode.id}
                title={label}
                icon={def.icon}
                color={def.color}
                width={def.family === 'media' ? 560 : undefined}
                onCollapse={() => setExpandedNodeId(null)}
              >
                <DetailRenderer
                  node={expandedNodeDto}
                  workspaceId={workspaceId}
                  onChangeFields={readOnly ? (readOnlyDeniedNoop as typeof onChangeFields) : onChangeFields}
                  siteId={siteId}
                  onChangeEntity={readOnly ? (readOnlyDeniedNoop as typeof onChangeEntity) : onChangeEntity}
                  onOpenAssetPicker={readOnly ? (readOnlyDeniedNoop as typeof onOpenAssetPicker) : onOpenAssetPicker}
                  onMaterializeImageAssets={
                    readOnly ? (readOnlyDeniedNoop as typeof materializeImageAssetSelections) : materializeImageAssetSelections
                  }
                />
              </NodeDetailShell>
            )
          })()
        ) : null}

        {/* Anchored node-delete confirm — the mouse/icon entry point's
         * ONLY UI (VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13/§14/§15).
         * `CanvasActionPopover` re-derives `anchorEl.getBoundingClientRect()`
         * live on every reposition, so this stays correctly placed
         * across pan/zoom without reading any React Flow world
         * coordinate. */}
        <CanvasVideoNodeDeleteModal
          open={!!videoDeletePlan}
          assetCount={videoDeletePlan?.assetCount ?? 0}
          busy={videoDeleteBusy}
          onCancel={closeVideoDeleteModal}
          onRemoveNodeOnly={() => void finishVideoNodeDelete(false)}
          onRemoveNodeAndAssets={() => void finishVideoNodeDelete(true)}
        />

        {iconDeleteRequest && iconDeleteCopy ? (
          <CanvasActionPopover
            anchorEl={iconDeleteRequest.anchorEl}
            open={!!iconDeleteRequest}
            onClose={closeIconDelete}
            title={iconDeleteCopy.title}
            width={260}
          >
            {iconDeleteCopy.lines.map((line, i) => (
              <p key={i} className="pcv-action-popover-desc">
                {line}
              </p>
            ))}
            <div className="pcv-action-popover-actions">
              <button type="button" className="pcv-action-popover-btn" onClick={closeIconDelete}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="pcv-action-popover-btn pcv-action-popover-btn--primary"
                onClick={confirmIconDelete}
                data-testid="action-confirm-submit"
              >
                {iconDeleteCopy.isGenerating ? t('board.deleteNodeConfirm') : t('common.delete')}
              </button>
            </div>
          </CanvasActionPopover>
        ) : null}

        {!readOnly && contextMenu ? (
          <CanvasContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onPick={(nodeType) => {
              if (nodeType === 'image_asset') {
                openBatchImageAssetPicker({ x: contextMenu.flowX, y: contextMenu.flowY })
                setContextMenu(null)
                return
              }
              addNodeAt(nodeType, contextMenu.flowX, contextMenu.flowY)
              setContextMenu(null)
            }}
          />
        ) : null}

        {!readOnly && nodeContextMenu ? (
          <CanvasNodeContextMenu
            x={nodeContextMenu.x}
            y={nodeContextMenu.y}
            canExpand={(() => {
              const n = nodes.find((nd) => nd.id === nodeContextMenu.nodeId)
              const family = n?.type || 'entity'
              return (
                isGenerationExpandFamily(family) ||
                family === 'video_media' ||
                nodeTypeSupportsDetailPanel(family)
              )
            })()}
            isExpanded={
              expandedNodeId === nodeContextMenu.nodeId ||
              isNodePinnedExpanded((nodes.find((nd) => nd.id === nodeContextMenu.nodeId)?.data as unknown as FlowNodeData | undefined)?.fields)
            }
            onClose={() => setNodeContextMenu(null)}
            onDuplicate={() => onDuplicateNode(nodeContextMenu.nodeId)}
            onDelete={() => onRequestDeleteNode(nodeContextMenu.nodeId)}
            onToggleExpand={() => onToggleDetail(nodeContextMenu.nodeId)}
          />
        ) : null}

        <QuickAddModal
          open={!readOnly && quickAddOpen}
          onClose={() => setQuickAddOpen(false)}
          onPick={(nodeType) => {
            setQuickAddOpen(false)
            addNodeFromLibrary(nodeType)
          }}
        />

        {!readOnly && connectionSuggest ? (
          <ConnectionSuggestPopup
            x={connectionSuggest.x}
            y={connectionSuggest.y}
            sourceNodeType={connectionSuggest.sourceNodeType}
            onClose={() => setConnectionSuggest(null)}
            onPick={(nodeType) => {
              const newId = addNodeAt(nodeType, connectionSuggest.flowX, connectionSuggest.flowY)
              if (!newId) {
                setConnectionSuggest(null)
                return
              }
              const sourceNode = nodesRef.current.find((n) => n.id === connectionSuggest.sourceNodeId)
              const sourceType = connectionSuggest.sourceNodeType
              const semanticSlot = resolveCanvasInputSlot(sourceType, nodeType)
              // Creative / Text → Script: same canonical linker as manual drag + Quick Action.
              if (semanticSlot?.slot === 'PRIMARY_SCRIPT_SOURCE' || semanticSlot?.slot === 'PRIMARY_CREATIVE_CONTEXT') {
                if (sourceType === 'text_asset') {
                  if (!sourceNode) {
                    setConnectionSuggest(null)
                    return
                  }
                  const scriptSource = buildScriptSourceFromTextAsset(sourceNode)
                  if (!scriptSource) {
                    message.warning(t('board.scriptSourceTextRequired'))
                    setConnectionSuggest(null)
                    return
                  }
                  onLinkTextScriptSource(newId, connectionSuggest.sourceNodeId, scriptSource)
                  setConnectionSuggest(null)
                  return
                }
                if (!sourceNode) {
                  setConnectionSuggest(null)
                  return
                }
                const payload = buildCreativePayloadFromNode(sourceNode)
                if (!payload || (!payload.title && !payload.premise && !payload.candidateId)) {
                  message.warning(t('board.creativeContextRequiredBeforeConnect'))
                  setConnectionSuggest(null)
                  return
                }
                const sourceKind: ScriptSourceKind =
                  sourceType === 'creative_candidate' ? 'creative_candidate' : 'generate_creative'
                onLinkCreativeContext(newId, payload, sourceKind)
                setConnectionSuggest(null)
                return
              }
              // Typed Dataflow Contract (P0 §19/§21) — Connection-to-Create
              // must wire the SAME `GENERIC_INPUT_HANDLE` a manual drag would
              // (`onConnect` below), not a handle-less edge; otherwise a
              // brand-new node created THIS way would silently never show up
              // in the new node's own Input Materials UI (§12) even though a
              // manual re-drag of the identical connection would have. Falls
              // back to the old handle-less `INPUT_TO` edge for any node type
              // still outside the typed contract (§54 — untouched legacy
              // behavior for Scene/Character/Dialogue/etc.).
              const targetIO = getNodeIO(nodeType)
              const rawTargetHandle = targetIO?.inputHandleId ?? undefined
              const relationType = rawTargetHandle === GENERIC_INPUT_HANDLE ? 'REFERENCES' : 'INPUT_TO'
              const connectionToCreateEdgeId = genId()
              // VistoeraTASK-00144 — Connection-to-Create onto a brand-new
              // `generate_image` node is the SAME reference-image edge a
              // manual drag would create; scope it the same way from the
              // start.
              const targetHandle =
                (nodeType === 'generate_image' || nodeType === 'generate_text') &&
                rawTargetHandle === GENERIC_INPUT_HANDLE
                  ? imageReferenceAssetHandleId(connectionToCreateEdgeId)
                  : rawTargetHandle
              commitEdges((eds) => {
                const next = addEdge(
                  {
                    id: connectionToCreateEdgeId,
                    source: connectionSuggest.sourceNodeId,
                    target: newId,
                    targetHandle,
                    data: { relationType },
                    className: 'pcv-edge',
                  },
                  eds,
                )
                // `addNodeAt` above already wrote the new node into
                // `nodesRef` synchronously, so this reflects it too.
                scheduleFromState(nodesRef.current, next)
                return next
              })
              setConnectionSuggest(null)
            }}
          />
        ) : null}
      </div>

      {!readOnly && siteId && assetPickerRequest ? (
        <ImageAssetPicker
          open
          workspaceId={workspaceId}
          siteId={siteId}
          purpose={assetPickerRequest.purpose}
          title={assetPickerRequest.title}
          selectionMode={assetPickerRequest.selectionMode}
          maxSelection={assetPickerRequest.maxSelection}
          initialSelection={assetPickerRequest.initialSelection}
          confirmLabel={assetPickerRequest.confirmLabel}
          onCancel={closeAssetPicker}
          onConfirm={(selections: ImageAssetPickerSelection[]) => {
            assetPickerRequest.onConfirm(selections)
            closeAssetPicker()
          }}
        />
      ) : null}

      {!readOnly && videoAssetPickerRequest ? (
        <VideoAssetPicker
          open
          workspaceId={workspaceId}
          title={videoAssetPickerRequest.title}
          initialSelection={videoAssetPickerRequest.initialSelection}
          onCancel={closeVideoAssetPicker}
          onConfirm={(item) => {
            onBindVideoAsset(videoAssetPickerRequest.nodeId, item)
            closeVideoAssetPicker()
          }}
        />
      ) : null}

      {!readOnly && audioAssetPickerRequest ? (
        <AudioAssetPicker
          open
          workspaceId={workspaceId}
          projectId={projectId}
          pickerDomain={
            audioAssetPickerRequest.binding === 'reference_audio' ||
            audioAssetPickerRequest.binding === 'audio_asset' ||
            audioAssetPickerRequest.binding === 'apparel_video_bgm'
              ? 'audio_asset'
              : 'voice_template'
          }
          title={audioAssetPickerRequest.title}
          presentationType={audioAssetPickerRequest.presentationType ?? 'VOICE_TEMPLATE'}
          lockPresentationType={audioAssetPickerRequest.lockPresentationType ?? true}
          allowedCategories={audioAssetPickerRequest.allowedCategories}
          initialCategory={audioAssetPickerRequest.initialCategory}
          initialSelection={audioAssetPickerRequest.initialSelection ?? null}
          enableLocalUpload={
            audioAssetPickerRequest.binding === 'reference_audio' ||
            audioAssetPickerRequest.binding === 'apparel_video_bgm' ||
            (audioAssetPickerRequest.allowedCategories?.some(
              (c) => c === 'REFERENCE_AUDIO' || c === 'RECORDING' || c === 'MUSIC' || c === 'SFX',
            ) ??
              false)
          }
          onCancel={closeAudioAssetPicker}
          onConfirm={(item) => {
            // One canonical picker, FOUR distinct binding targets —
            // dispatched purely off the request's own `binding`
            // discriminator (see `CanvasAudioAssetPickerBinding` in
            // `types.ts`), never node-type sniffing at confirm time:
            //   'audio_asset' (VistoeraTASK-00115) — the requesting
            //     `audio_asset` node binds the SiteAsset as ITS OWN
            //     entity.
            //   'reference_audio' (TASK-00119) — `generate_voice_
            //     template`'s own reference-audio cloning input field.
            //   'apparel_video_bgm' (VistoeraTASK-00161) — 服装视频换装's
            //     optional BGM field on the requesting node.
            //   default/'voice_template' — `generate_voice`'s Related
            //     Assets slot binds a Voice Template identity.
            if (audioAssetPickerRequest.binding === 'audio_asset') {
              onBindAudioAsset(audioAssetPickerRequest.nodeId, item)
            } else if (audioAssetPickerRequest.binding === 'reference_audio') {
              onBindReferenceAudio(audioAssetPickerRequest.nodeId, item)
            } else if (audioAssetPickerRequest.binding === 'apparel_video_bgm') {
              onBindApparelVideoBgm(audioAssetPickerRequest.nodeId, item)
            } else {
              onBindAudioVoiceTemplate(audioAssetPickerRequest.nodeId, item)
            }
            closeAudioAssetPicker()
          }}
        />
      ) : null}

      {!readOnly && siteId && tryonAssetPickerRequest ? (
        <ImageAssetPicker
          open
          workspaceId={workspaceId}
          siteId={siteId}
          purpose="generic"
          title={tryonAssetPickerRequest.title}
          initialSelection={tryonAssetPickerRequest.initialSelection ? [tryonAssetPickerRequest.initialSelection] : []}
          onCancel={closeTryonAssetPicker}
          onConfirm={(selections: ImageAssetPickerSelection[]) => {
            const first = selections[0]
            if (first) onBindTryOnAsset(tryonAssetPickerRequest.nodeId, tryonAssetPickerRequest.slot, first)
            closeTryonAssetPicker()
          }}
        />
      ) : null}

      <AudioEditorModal
        open={Boolean(audioEditorRequest)}
        request={audioEditorRequest}
        onClose={closeAudioEditor}
        onCommitTrim={onCommitAudioTrim}
      />
    </div>
  )
}

function minimapNodeColor(n: Node): string {
  const data = n.data as unknown as FlowNodeData | undefined
  if (!data) return '#5B5FF5'
  return getNodeDefinition(data.nodeType).color
}
